import { createCapyShieldElement } from "./capyShield";
import { ActivityHistory } from "./activityHistory";
import { isGameActivity } from "../../shared/activityHistory";
import { DEFAULT_RELIC_RUN_SOURCE_TEXT, DEFAULT_PICTIONARY_SOURCE_TEXT, DEFAULT_VAULT_SOURCE_TEXT } from "./gameSamples";
import { PLANET_HOPPERS_MODE, parsePHSource, phIssues } from "../../shared/planetHoppers";
import { PlanetHoppersEditor, readPHLocalDraft, loadPHLocalDraft } from "./planetHoppersEditor";
import { SetupLibrary } from "./setupLibrary";
import type { SavedSetup, SetupLibraryItem, SetupMode } from "../../shared/setupLibrary";
import { PlanetHoppersView, phText, phPlanet } from "./planetHoppersView";
import {
    buildRelicRunLevels,
    buildPictionaryEntries,
    DEFAULT_HOST_NAME,
    DEFAULT_PLAYER_NAME,
    RELIC_RUN_MODE,
    ROOM_CONNECTION_HEARTBEAT_MESSAGE,
    ROOM_CODE_MAX_LENGTH,
    PICTURE_DETECTIVES_MODE,
    VAULT_BREAKERS_MODE,
    isValidRoomCode,
    normalizePlayerName,
    normalizeRoomCode,
    getPictionarySourceEntryLineIndexes,
    getVaultBreakerSourceGuideLines,
    parseVaultBreakerSource,
    parsePictionarySourceRows,
    serializePictionarySourceRows,
    tokenizeSourceText,
    type ClientMessage,
    type CreateRoomResponse,
    type GridPosition,
    type PictionaryCanvasPoint,
    type PictionaryCanvasSegmentMessage,
    type FinalResultGroupView,
    type PictionarySourceRow,
    type RoomMode,
    type RoomParticipantView,
    type RoomSummaryResponse,
    type RoomView,
    type ServerMessage,
    type VaultBreakerEntryDefinition
} from "../../shared/game";
import { ZIAMEZA_IDENTITY, ZIAMEZA_LINKS } from "../../shared/ziaMezaLinks";
import { APP_PAGE_METADATA, SITE_PATHS, appToolForPath, canonicalPageUrl, joinPagePath } from "../../shared/siteRoutes";
import {
    GIMCHEON_SCHOOL_DEMO,
    getUpcomingGimcheonSchoolEvents,
    type GimcheonSchoolNewsItem,
    type GimcheonSchoolProfileFact,
    type GimcheonSchoolScheduleItem,
    type GimcheonSchoolSymbol
} from "../../shared/gimcheonSchoolDemo";
import { appendCodexDebugLog } from "./codexDebugLog";
import { syncClassroomDisplayTypography } from "./classroomDisplayTypography";
import { calculateReconnectDelayMs, isConnectionHeartbeatTimedOut } from "./connectionReliability";
import { getFinalResultRevealDelayMs, getFinalResultsFullListDelayMs } from "./finalResultsTiming";
import {
    formatHereCount,
    formatPictionarySetupSummary,
    formatRelicRunSetupSummary,
    formatVaultBreakerSetupSummary,
    formatViewerBadge,
    getLanguage,
    initializeLanguage,
    localizeModeDescription,
    localizeModeLabel,
    localizeRuntimeText,
    t,
    toggleLanguage,
    setLanguage
} from "./i18n";
import { AccountLanguageSync } from "./accountLanguage";
import { LobbyPlayground, createLobbyPlayground } from "./lobbyPlayground";
import { getLobbyRosterParticipants, getConnectedLobbyRosterIds, getLobbyPlaygroundIds, hasLobbyRosterMismatch } from "./lobbyRoster";
import { MazeView, createMazeView } from "./mazeView";
import { RoomAudio } from "./roomAudio";
import { RenamePromptFeedback } from "./renamePromptFeedback";
import {
    getPlayerRosterDisplayName,
    getPlayerRosterListClass,
    getPlayerRosterPlacementClass,
    getPlayerRenamePendingClass,
    installPlayerRosterActionsMenuPositioning,
    renderPlayerRosterActionsMenu,
    syncPlayerRosterLayout
} from "./playerRoster";
import {
    createInitialRandomTeamsToolState,
    createRandomTeamsTool,
    type RandomTeamsToolState
} from "./randomTeamsTool";
import {
    createInitialWordListToolState,
    createWordListTool,
    type WordListEntry,
    type WordListOutputMode,
    type WordListToolState
} from "./wordListTool";
import {
    createAutotranslatorTool,
    createInitialAutotranslatorState,
    type AutotranslatorState
} from "./autotranslatorTool";
import { readFileAsArrayBuffer, readFileAsText } from "./browserCompatibility";
import { createVocabularyReviewTool, type VocabularyReviewToolState } from "./vocabularyReviewTool";
import { VocabularyDraftSync, type VocabularySyncStatus } from "./vocabularyDraftSync";
import { createPictionaryView, type PictionaryView } from "./pictionaryView";
import {
    downloadPlayerHistory,
    renderPlayerHistoryHeaderMenu
} from "./playerHistoryDownload";
import { applySiteTuningCssVariables, SITE_TUNING } from "./siteTuning";
import { createVaultBreakersView, type VaultBreakersView } from "./vaultBreakersView";
import { renderZiarcadiaIcon } from "./ziarcadiaIcons";
import { strFromU8, unzipSync } from "fflate";
import QRCode from "qrcode";

declare const __ZIARCADIA_BUILD_ID__: string;
declare const __MASTER1000_TOOL_AVAILABLE__: boolean;
declare const __MASTER1000_TOOL_EMBED_URL__: string;
declare const __MASTER1000_TOOL_SOURCE_DIR__: string;

type GoogleCredentialResponse = {
    credential?: string;
};

type GoogleAccountsIdApi = {
    initialize: (options: {
        client_id: string;
        callback: (response: GoogleCredentialResponse) => void;
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
    }) => void;
    renderButton: (
        parent: HTMLElement,
        options: {
            theme?: "outline" | "filled_blue" | "filled_black";
            size?: "large" | "medium" | "small";
            shape?: "rectangular" | "pill" | "circle" | "square";
            text?: "signin_with" | "signup_with" | "continue_with" | "signin";
            width?: number;
        }
    ) => void;
    disableAutoSelect?: () => void;
};

type GoogleIdentityWindow = Window & {
    google?: {
        accounts?: {
            id?: GoogleAccountsIdApi;
        };
    };
};

type Screen =
    | "landing"
    | "qr-join"
    | "connecting"
    | "room"
    | "tool-random-teams"
    | "tool-word-list"
    | "tool-vocabulary-review"
    | "tool-master1000"
    | "tool-autotranslator"
    | "tool-gimcheon-school"
    | "account";
type SessionRole = "host" | "player" | "controller";
type RoomVariant = "host-lobby" | "player-lobby" | "controller-lobby" | "active" | "results";
type HostModePanelView = "selection" | "config";
type SourceEditorMode = "text" | "table";
type PictionaryEditorMode = SourceEditorMode;
type VaultEditorMode = SourceEditorMode;
type SourceTranslationAction = "add" | "replace";
type SourceTranslationItem = {
    index: number;
    term: string;
    currentTranslation: string;
    context: string;
};
type SourceTranslationResponse = {
    items: Array<{
        index: number;
        translation: string;
    }>;
    model?: string;
};
type PictionaryDraftRow = PictionarySourceRow & { id: string };
type PictionaryDraftField = keyof Pick<PictionarySourceRow, "english" | "korean" | "stem">;
type VaultDraftRow = Pick<VaultBreakerEntryDefinition, "category" | "prompt" | "answer" | "korean"> & {
    id: string;
    value: string;
};
type VaultDraftField = keyof Pick<VaultDraftRow, "category" | "value" | "prompt" | "answer" | "korean">;

type HostDraft = {
    mode: RoomMode | null;
    sourceText: string;
    relicSourceText: string;
    pictionarySourceText: string;
    vaultSourceText: string;
    singleLineMode: boolean;
    hostParticipates: boolean;
    pictionaryStem: string;
    pictionaryNoHints: boolean;
    pictionaryNoTranslations: boolean;
    pictionarySameStem: boolean;
    pictionaryHostIsController: boolean;
    pictionaryRows: PictionaryDraftRow[];
    vaultRows: VaultDraftRow[];
};

type HostSyncSnapshot = {
    mode: RoomMode | null;
    sourceText: string;
    singleLineMode: boolean;
    hostParticipates: boolean;
    pictionaryStem: string;
    pictionaryNoHints: boolean;
    pictionaryNoTranslations: boolean;
    pictionarySameStem: boolean;
    pictionaryHostIsController: boolean;
};

type ConnectionDetails = {
    roomCode: string;
    name: string;
    hostToken: string | null;
    role: SessionRole;
    sessionId: string;
};

type RoomAudioState = {
    roomCode: string;
    initialized: boolean;
    phase: RoomView["phase"] | null;
    mode: RoomMode | null;
    connectedLobbyParticipantIds: Set<string>;
    pictionaryControllerConnected: boolean;
    localJoinSoundKey: string;
    localControllerFoundSoundKey: string;
    modeStartSoundKey: string;
};

type AccountProfile = {
    accountId: string;
    email: string;
    name: string;
    picture: string;
};

type AccountModePreference = {
    sourceText: string;
    singleLineMode: boolean;
    hostParticipates: boolean;
    pictionaryNoHints: boolean;
    pictionaryNoTranslations: boolean;
    pictionarySameStem: boolean;
    pictionaryHostIsController: boolean;
    updatedAt: number;
};

type AccountPreferences = Partial<Record<RoomMode, AccountModePreference>>;

type AccountUploadItem = {
    id: string;
    mode: RoomMode;
    fileName: string;
    fileType: string;
    size: number;
    sourceText: string;
    uploadedAt: number;
};

type AccountHostedGameItem = {
    id: string;
    mode: RoomMode;
    playerCount: number;
    roomCode: string;
    hostedAt: number;
    playerResults?: AccountHostedGamePlayerResult[];
};

type AccountHostedGamePlayerResult = {
    sessionId: string;
    name: string;
    placement: number | null;
    score: number | null;
    elapsedMs: number | null;
};

type AccountPlayedSourceItem = {
    id: string;
    mode: RoomMode;
    playerCount: number;
    roomCode: string;
    sourceText: string;
    playedAt: number;
};

type AccountPlayedSources = Partial<Record<RoomMode, AccountPlayedSourceItem>>;

type AccountWordListItem = {
    id: string;
    title: string;
    mode: WordListOutputMode;
    customTemplate: string;
    sourceText: string;
    entries: WordListEntry[];
    termCount: number;
    createdAt: number;
    updatedAt: number;
};

type AccountSnapshotResponse = {
    language?: "en" | "ko" | null;
    account: AccountProfile | null;
    preferences: AccountPreferences;
    uploads: AccountUploadItem[];
    hostedGames: AccountHostedGameItem[];
    playedSources: AccountPlayedSources;
    sourceHistory?: AccountPlayedSourceItem[];
    wordLists: AccountWordListItem[];
};

type AccountConfigResponse = {
    googleClientId?: string;
};

type AccountState = {
    googleClientId: string;
    loading: boolean;
    initialized: boolean;
    account: AccountProfile | null;
    preferences: AccountPreferences;
    uploads: AccountUploadItem[];
    hostedGames: AccountHostedGameItem[];
    playedSources: AccountPlayedSources;
    sourceHistory: AccountPlayedSourceItem[];
    wordLists: AccountWordListItem[];
    error: string;
};

type GimcheonSchoolChatMessage = {
    id: string;
    role: "assistant" | "user";
    text: string;
};

type GimcheonSchoolChatState = {
    open: boolean;
    input: string;
    loading: boolean;
    messages: GimcheonSchoolChatMessage[];
};

type GimcheonSchoolChatResponse = {
    answer: string;
    fallback?: boolean;
};

type AppState = {
    screen: Screen;
    room: RoomView | null;
    error: string;
    info: string;
    account: AccountState;
    gimcheonSchoolChat: GimcheonSchoolChatState;
    hostName: string;
    joinName: string;
    joinRoomCode: string;
    joinFromRoomLink: boolean;
    joinFromQrLink: boolean;
    qrJoinMode: RoomMode | null;
    hostPanelExpanded: boolean;
    toolsPanelExpanded: boolean;
    hostDraft: HostDraft;
    hostDraftDirty: boolean;
    hostModePanelView: HostModePanelView;
    hostPictionaryEditorMode: PictionaryEditorMode;
    hostVaultEditorMode: VaultEditorMode;
    hostPictionarySourceFogged: boolean;
    hostVaultSourceFogged: boolean;
    hostControllerQrExpanded: boolean;
    hostSourceImportOpen: boolean;
    hostSourceTranslationOpen: boolean;
    hostSourceTranslationLoading: boolean;
    hostSourceTranslationError: string;
    qrPreviewKind: "join" | "controller" | null;
    finalResultsPlayerListOpen: boolean;
    renameDialogOpen: boolean;
    renameDialogAnchorSelector: string | null;
    renameDialogSessionId: string | null;
    renameDialogPrompted: boolean;
    pendingHostSync: HostSyncSnapshot | null;
    hostStartPending: boolean;
    currentConnection: ConnectionDetails | null;
    randomTeamsTool: RandomTeamsToolState;
    wordListTool: WordListToolState;
    vocabularyReviewTool: VocabularyReviewToolState;
    autotranslator: AutotranslatorState;
};

type ScreenRefs = {
    banner: HTMLElement | null;
    viewerBadge: HTMLElement | null;
    playerStrip: HTMLElement | null;
    utilitySlot: HTMLElement | null;
    overlayRoot: HTMLElement | null;
    content: HTMLElement | null;
    connectingMessage: HTMLElement | null;
    toolContent: HTMLElement | null;
};

type FullscreenCapableElement = HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void> | void;
};

type WebkitFullscreenDocument = Document & {
    webkitExitFullscreen?: () => Promise<void> | void;
    webkitFullscreenElement?: Element | null;
};

type ScrollLockSnapshot = {
    documentOverflow: string;
    documentOverscrollBehavior: string;
    bodyOverflow: string;
    bodyOverscrollBehavior: string;
};

const createClientId = (): string => {
    const browserCrypto = globalThis.crypto;

    if (typeof browserCrypto?.randomUUID === "function") {
        return browserCrypto.randomUUID().replace(/-/g, "");
    }

    if (typeof browserCrypto?.getRandomValues === "function") {
        const bytes = new Uint8Array(16);
        browserCrypto.getRandomValues(bytes);
        return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
    }

    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 14)}`;
};

const createPictionaryDraftRow = (row: Partial<PictionarySourceRow> = {}): PictionaryDraftRow => ({
    id: createClientId(),
    english: row.english ?? "",
    korean: row.korean ?? "",
    stem: row.stem ?? ""
});

const MIN_PICTIONARY_DRAFT_ROWS = 5;

const isPictionaryDraftRowBlank = (row: Pick<PictionaryDraftRow, "english" | "korean" | "stem">): boolean =>
    row.english.trim().length === 0 && row.korean.trim().length === 0 && row.stem.trim().length === 0;

const ensurePictionaryDraftRowTail = (rows: readonly PictionaryDraftRow[]): PictionaryDraftRow[] => {
    const normalized = rows.map((row) => ({
        ...row
    }));

    while (
        normalized.length > MIN_PICTIONARY_DRAFT_ROWS &&
        isPictionaryDraftRowBlank(normalized[normalized.length - 1]!) &&
        isPictionaryDraftRowBlank(normalized[normalized.length - 2]!)
    ) {
        normalized.pop();
    }

    while (normalized.length < MIN_PICTIONARY_DRAFT_ROWS) {
        normalized.push(createPictionaryDraftRow());
    }

    if (normalized.length === 0 || !isPictionaryDraftRowBlank(normalized[normalized.length - 1]!)) {
        normalized.push(createPictionaryDraftRow());
    }

    return normalized;
};

const getPictionarySourceRows = (rows: readonly PictionaryDraftRow[]): PictionarySourceRow[] =>
    rows
        .filter((row) => row.english.trim().length > 0)
        .map(({ english, korean, stem }) => ({
            english,
            korean,
            stem
        }));

const serializePictionaryDraftRows = (rows: readonly PictionaryDraftRow[]): string =>
    serializePictionarySourceRows(getPictionarySourceRows(rows));

const hydratePictionaryDraftRows = (
    sourceText: string,
    fallbackStem = ""
): PictionaryDraftRow[] =>
    ensurePictionaryDraftRowTail(
        parsePictionarySourceRows(sourceText, {
            fallbackStem
        }).map((row) => createPictionaryDraftRow(row))
    );

const createVaultDraftRow = (row: Partial<VaultDraftRow> = {}): VaultDraftRow => ({
    id: createClientId(),
    category: row.category ?? "",
    value: row.value ?? "",
    prompt: row.prompt ?? "",
    answer: row.answer ?? "",
    korean: row.korean ?? ""
});

const MIN_VAULT_DRAFT_ROWS = 5;

const isVaultDraftRowBlank = (row: Pick<VaultDraftRow, "category" | "value" | "prompt" | "answer" | "korean">): boolean =>
    row.category.trim().length === 0 &&
    row.value.trim().length === 0 &&
    row.prompt.trim().length === 0 &&
    row.answer.trim().length === 0 &&
    row.korean.trim().length === 0;

const ensureVaultDraftRowTail = (rows: readonly VaultDraftRow[]): VaultDraftRow[] => {
    const normalized = rows.map((row) => ({
        ...row
    }));

    while (
        normalized.length > MIN_VAULT_DRAFT_ROWS &&
        isVaultDraftRowBlank(normalized[normalized.length - 1]!) &&
        isVaultDraftRowBlank(normalized[normalized.length - 2]!)
    ) {
        normalized.pop();
    }

    while (normalized.length < MIN_VAULT_DRAFT_ROWS) {
        normalized.push(createVaultDraftRow());
    }

    if (normalized.length === 0 || !isVaultDraftRowBlank(normalized[normalized.length - 1]!)) {
        normalized.push(createVaultDraftRow());
    }

    return normalized;
};

const getVaultSourceRows = (rows: readonly VaultDraftRow[]): VaultDraftRow[] =>
    rows
        .filter((row) => row.prompt.trim().length > 0 || row.answer.trim().length > 0)
        .map(({ id: _id, category, value, prompt, answer, korean }) => ({
            id: "",
            category: category.trim(),
            value: value.trim(),
            prompt: prompt.trim(),
            answer: answer.trim(),
            korean: korean.trim()
        }));

const serializeVaultDraftRows = (rows: readonly VaultDraftRow[]): string => {
    const sourceRows = getVaultSourceRows(rows);
    const lines: string[] = [];
    let currentCategory = "";

    sourceRows.forEach((row) => {
        const category = row.category.trim();

        if (category && category !== currentCategory) {
            if (lines.length > 0) {
                lines.push("");
            }
            lines.push(category);
            currentCategory = category;
        }

        const valuePrefix = row.value ? `${row.value} - ` : "";
        const answerParts = [row.answer, row.korean].filter(Boolean).join("; ");
        const answerSuffix = answerParts ? `; ${answerParts}` : "";
        lines.push(`${valuePrefix}${row.prompt}${answerSuffix}`);
    });

    return lines.join("\n");
};

const hydrateVaultDraftRows = (sourceText: string): VaultDraftRow[] =>
    ensureVaultDraftRowTail(
        parseVaultBreakerSource(sourceText).map((entry) =>
            createVaultDraftRow({
                category: entry.category,
                value: String(entry.value),
                prompt: entry.prompt,
                answer: entry.answer,
                korean: entry.korean
            })
        )
    );

const PICTIONARY_TABLE_HEADER_ALIASES: Record<PictionaryDraftField, Set<string>> = {
    english: new Set(["answer", "case", "english", "prompt", "term", "word"]),
    korean: new Set(["definition", "korean", "meaning", "translation"]),
    stem: new Set(["clue", "example", "hint", "sentence", "stem"])
};

const VAULT_TABLE_HEADER_ALIASES: Record<VaultDraftField, Set<string>> = {
    category: new Set(["category", "column", "group", "topic"]),
    value: new Set(["money", "point", "points", "score", "value"]),
    prompt: new Set(["clue", "prompt", "question", "sentence"]),
    answer: new Set(["answer", "term", "word"]),
    korean: new Set(["definition", "info", "korean", "meaning", "moreinfo", "note", "notes", "translation"])
};

const normalizePictionaryTableHeader = (value: string): string =>
    value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "");

const getPictionaryTableHeaderIndexes = (
    row: readonly string[]
): Partial<Record<PictionaryDraftField, number>> | null => {
    const indexes: Partial<Record<PictionaryDraftField, number>> = {};

    row.forEach((cell, index) => {
        const normalized = normalizePictionaryTableHeader(cell);

        (Object.keys(PICTIONARY_TABLE_HEADER_ALIASES) as PictionaryDraftField[]).forEach((field) => {
            if (indexes[field] === undefined && PICTIONARY_TABLE_HEADER_ALIASES[field].has(normalized)) {
                indexes[field] = index;
            }
        });
    });

    return Object.keys(indexes).length > 0 ? indexes : null;
};

const getVaultTableHeaderIndexes = (row: readonly string[]): Partial<Record<VaultDraftField, number>> | null => {
    const indexes: Partial<Record<VaultDraftField, number>> = {};

    row.forEach((cell, index) => {
        const normalized = normalizePictionaryTableHeader(cell);

        (Object.keys(VAULT_TABLE_HEADER_ALIASES) as VaultDraftField[]).forEach((field) => {
            if (indexes[field] === undefined && VAULT_TABLE_HEADER_ALIASES[field].has(normalized)) {
                indexes[field] = index;
            }
        });
    });

    return Object.keys(indexes).length > 0 ? indexes : null;
};

const parseDelimitedImportRows = (sourceText: string, delimiter: "," | "\t"): string[][] => {
    const normalizedText = sourceText.replace(/\r\n?/g, "\n");
    const rows: string[][] = [];
    let row: string[] = [];
    let cell = "";
    let quoted = false;

    for (let index = 0; index < normalizedText.length; index += 1) {
        const character = normalizedText[index] ?? "";
        const nextCharacter = normalizedText[index + 1] ?? "";

        if (character === '"') {
            if (quoted && nextCharacter === '"') {
                cell += '"';
                index += 1;
            } else {
                quoted = !quoted;
            }
            continue;
        }

        if (!quoted && character === delimiter) {
            row.push(cell.trim());
            cell = "";
            continue;
        }

        if (!quoted && character === "\n") {
            row.push(cell.trim());
            rows.push(row);
            row = [];
            cell = "";
            continue;
        }

        cell += character;
    }

    row.push(cell.trim());
    rows.push(row);

    return rows.filter((candidateRow) => candidateRow.some((candidateCell) => candidateCell.length > 0));
};

const getSpreadsheetColumnIndex = (reference: string): number => {
    const column = reference.match(/^[A-Z]+/i)?.[0]?.toUpperCase() ?? "";

    return column.split("").reduce((index, character) => index * 26 + character.charCodeAt(0) - 64, 0) - 1;
};

const parseXmlDocument = (sourceText: string): Document =>
    new DOMParser().parseFromString(sourceText, "application/xml");

const getSpreadsheetTextContent = (element: Element): string =>
    Array.from(element.getElementsByTagName("t"))
        .map((textElement) => textElement.textContent ?? "")
        .join("");

const parseSpreadsheetRows = async (file: File): Promise<string[][] | null> => {
    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();
    const isXlsx =
        fileName.endsWith(".xlsx") ||
        fileType.includes("spreadsheetml");

    if (!isXlsx) {
        return null;
    }

    const workbook = unzipSync(new Uint8Array(await readFileAsArrayBuffer(file)));
    const worksheetPath =
        workbook["xl/worksheets/sheet1.xml"] !== undefined
            ? "xl/worksheets/sheet1.xml"
            : Object.keys(workbook).find((path) => /^xl\/worksheets\/sheet\d+\.xml$/i.test(path));

    if (!worksheetPath) {
        return null;
    }

    const sharedStringsDocument = workbook["xl/sharedStrings.xml"]
        ? parseXmlDocument(strFromU8(workbook["xl/sharedStrings.xml"]!))
        : null;
    const sharedStrings = sharedStringsDocument
        ? Array.from(sharedStringsDocument.getElementsByTagName("si")).map(getSpreadsheetTextContent)
        : [];
    const worksheetDocument = parseXmlDocument(strFromU8(workbook[worksheetPath]!));

    return Array.from(worksheetDocument.getElementsByTagName("row"))
        .map((rowElement) => {
            const row: string[] = [];
            let nextCellIndex = 0;

            Array.from(rowElement.getElementsByTagName("c")).forEach((cellElement) => {
                const reference = cellElement.getAttribute("r") ?? "";
                const columnIndex = reference ? getSpreadsheetColumnIndex(reference) : nextCellIndex;
                const type = cellElement.getAttribute("t") ?? "";
                const valueElement = cellElement.getElementsByTagName("v")[0] ?? null;
                const rawValue = valueElement?.textContent ?? "";
                const value =
                    type === "s"
                        ? sharedStrings[Number.parseInt(rawValue, 10)] ?? ""
                        : type === "inlineStr"
                          ? getSpreadsheetTextContent(cellElement)
                          : rawValue;

                row[columnIndex] = value.trim();
                nextCellIndex = columnIndex + 1;
            });

            return row.map((cell) => cell ?? "");
        })
        .filter((row) => row.some((cell) => cell.trim().length > 0));
};

const serializeDelimitedImportRows = (rows: readonly (readonly string[])[], delimiter: "," | "\t"): string =>
    rows
        .map((row) =>
            row
                .map((cell) => {
                    const normalized = cell.replace(/\r\n?/g, "\n");
                    const shouldQuote =
                        normalized.includes(delimiter) || normalized.includes("\n") || normalized.includes('"');

                    return shouldQuote ? `"${normalized.replace(/"/g, '""')}"` : normalized;
                })
                .join(delimiter)
        )
        .join("\n");

const readImportedSourceText = async (file: File): Promise<string> => {
    const spreadsheetRows = await parseSpreadsheetRows(file);

    if (spreadsheetRows) {
        return serializeDelimitedImportRows(spreadsheetRows, "\t");
    }

    return readFileAsText(file);
};

const hasLikelyCsvTable = (sourceText: string): boolean => {
    const rows = parseDelimitedImportRows(sourceText, ",");
    const rowsWithMultipleCells = rows.filter((row) => row.length >= 2);

    return rowsWithMultipleCells.length >= Math.min(rows.length, 2) && rowsWithMultipleCells.length > 0;
};

const parsePictionaryImportedTableRows = (
    sourceText: string,
    file: File
): PictionarySourceRow[] | null => {
    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();
    const isTabularText =
        fileName.endsWith(".tsv") ||
        fileType.includes("tab-separated") ||
        sourceText.includes("\t");
    const isCommaTable =
        fileName.endsWith(".csv") ||
        fileType.includes("csv") ||
        (!isTabularText && hasLikelyCsvTable(sourceText));
    const delimiter: "," | "\t" | null = isTabularText ? "\t" : isCommaTable ? "," : null;

    if (!delimiter) {
        return null;
    }

    const rows = parseDelimitedImportRows(sourceText, delimiter);

    if (rows.length === 0) {
        return null;
    }

    const headerIndexes = getPictionaryTableHeaderIndexes(rows[0] ?? []);
    const maxColumnCount = Math.max(...rows.map((row) => row.length));

    if (!headerIndexes && maxColumnCount < 2) {
        return null;
    }

    const englishIndex = headerIndexes?.english ?? 0;
    const koreanIndex = headerIndexes?.korean ?? 1;
    const stemIndex = headerIndexes?.stem ?? 2;
    const dataRows = headerIndexes ? rows.slice(1) : rows;
    const sourceRows = dataRows
        .map((row) => ({
            english: row[englishIndex]?.trim() ?? "",
            korean: row[koreanIndex]?.trim() ?? "",
            stem: row[stemIndex]?.trim() ?? ""
        }))
        .filter((row) => row.english.length > 0);

    return sourceRows.length > 0 ? sourceRows : null;
};

const normalizeImportedPictionarySourceText = (sourceText: string, file: File): string => {
    const tableRows = parsePictionaryImportedTableRows(sourceText, file);

    return tableRows ? serializePictionarySourceRows(tableRows) : sourceText;
};

const isLikelyVaultPointValue = (value: string): boolean => /^\s*\d{1,5}\s*$/.test(value);

const getVaultTableFallbackIndexes = (
    maxColumnCount: number,
    firstRow: readonly string[]
): Partial<Record<VaultDraftField, number>> | null => {
    if (maxColumnCount >= 5) {
        return {
            category: 0,
            value: 1,
            prompt: 2,
            answer: 3,
            korean: 4
        };
    }

    if (maxColumnCount === 4) {
        return isLikelyVaultPointValue(firstRow[0] ?? "")
            ? {
                  value: 0,
                  prompt: 1,
                  answer: 2,
                  korean: 3
              }
            : {
                  category: 0,
                  prompt: 1,
                  answer: 2,
                  korean: 3
              };
    }

    if (maxColumnCount === 3) {
        return {
            prompt: 0,
            answer: 1,
            korean: 2
        };
    }

    return null;
};

const parseVaultImportedTableRows = (sourceText: string, file: File): VaultDraftRow[] | null => {
    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();
    const isTabularText =
        fileName.endsWith(".tsv") ||
        fileName.endsWith(".xlsx") ||
        fileType.includes("tab-separated") ||
        fileType.includes("spreadsheetml") ||
        sourceText.includes("\t");
    const isCommaTable =
        fileName.endsWith(".csv") ||
        fileType.includes("csv") ||
        (!isTabularText && hasLikelyCsvTable(sourceText));
    const delimiter: "," | "\t" | null = isTabularText ? "\t" : isCommaTable ? "," : null;

    if (!delimiter) {
        return null;
    }

    const rows = parseDelimitedImportRows(sourceText, delimiter);

    if (rows.length === 0) {
        return null;
    }

    const headerIndexes = getVaultTableHeaderIndexes(rows[0] ?? []);
    const maxColumnCount = Math.max(...rows.map((row) => row.length));
    const indexes = headerIndexes ?? getVaultTableFallbackIndexes(maxColumnCount, rows[0] ?? []);

    if (!indexes || indexes.prompt === undefined || indexes.answer === undefined) {
        return null;
    }

    let currentCategory = "";
    const dataRows = headerIndexes ? rows.slice(1) : rows;
    const sourceRows = dataRows
        .map((row) => {
            const category = row[indexes.category ?? -1]?.trim() ?? "";
            const value = row[indexes.value ?? -1]?.trim() ?? "";
            const prompt = row[indexes.prompt ?? -1]?.trim() ?? "";
            const answer = row[indexes.answer ?? -1]?.trim() ?? "";
            const korean = row[indexes.korean ?? -1]?.trim() ?? "";

            if (category) {
                currentCategory = category;
            }

            return createVaultDraftRow({
                category: category || currentCategory,
                value,
                prompt,
                answer,
                korean
            });
        })
        .filter((row) => row.prompt.length > 0 && row.answer.length > 0);

    return sourceRows.length > 0 ? sourceRows : null;
};

const normalizeImportedVaultSourceText = (sourceText: string, file: File): string => {
    const tableRows = parseVaultImportedTableRows(sourceText, file);

    return tableRows ? serializeVaultDraftRows(tableRows) : sourceText;
};

const createDefaultHostDraft = (): HostDraft => ({
    mode: null,
    sourceText: "",
    relicSourceText: DEFAULT_RELIC_RUN_SOURCE_TEXT,
    pictionarySourceText: DEFAULT_PICTIONARY_SOURCE_TEXT,
    vaultSourceText: DEFAULT_VAULT_SOURCE_TEXT,
    singleLineMode: false,
    hostParticipates: false,
    pictionaryStem: "",
    pictionaryNoHints: false,
    pictionaryNoTranslations: false,
    pictionarySameStem: false,
    pictionaryHostIsController: false,
    pictionaryRows: hydratePictionaryDraftRows(DEFAULT_PICTIONARY_SOURCE_TEXT),
    vaultRows: hydrateVaultDraftRows(DEFAULT_VAULT_SOURCE_TEXT)
});

const createInitialGimcheonSchoolChatState = (): GimcheonSchoolChatState => ({
    open: false,
    input: "",
    loading: false,
    messages: [
        {
            id: "gimcheon-school-chat-welcome",
            role: "assistant",
            text: "안녕하세요. 김천중학교 학부모 안내 도우미입니다. 공개된 학사일정, 공지, 급식 정보를 기준으로 확인해 드릴게요."
        }
    ]
});

type RenamePopoverLayout = {
    left: number;
    top: number;
    width: number;
    arrowLeft: number;
    placement: "below" | "above";
    anchored: boolean;
};

const MASTER1000_LOCKED_VIEWPORT_CONTENT =
    "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover";
const ACTIVE_ROOM_LOCKED_VIEWPORT_CONTENT =
    "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover";

const CLIENT_DEBUG_PREFIX = "[Ziarcadia][Client]";
const isClientDebugEnabled = (() => {
    let cached: boolean | null = null;

    return (): boolean => {
        if (cached !== null) {
            return cached;
        }

        try {
            const params = new URLSearchParams(window.location.search);
            cached =
                params.has("debug") ||
                params.has("clientDebug") ||
                localStorage.getItem("ziarcadia:debug") === "1" ||
                localStorage.getItem("ziarcadia:client-debug") === "1";
        } catch {
            cached = false;
        }

        return cached;
    };
})();
const logClientDebug = (message: string, details?: Record<string, unknown>) => {
    if (!isClientDebugEnabled()) {
        return;
    }

    appendCodexDebugLog("client", "info", message, details);
    if (details) {
        console.info(CLIENT_DEBUG_PREFIX, message, details);
        return;
    }

    console.info(CLIENT_DEBUG_PREFIX, message);
};
const shouldLogRoomSyncDiagnostics = (): boolean => {
    try {
        const params = new URLSearchParams(window.location.search);
        return (
            params.get("roomSyncDebug") !== "0" &&
            localStorage.getItem("ziarcadia:room-sync-diagnostics") !== "0"
        );
    } catch {
        return true;
    }
};
const logRoomSyncDiagnostic = (message: string) => {
    if (!shouldLogRoomSyncDiagnostics()) {
        return;
    }

    appendCodexDebugLog("room-sync", "info", message);
    console.info(`${CLIENT_DEBUG_PREFIX} ${message}`);
};

const RELEASE_WATCH_STORAGE_KEY = "ziarcadia:release-watch";
const RELEASE_WATCH_LAST_BUILD_KEY = "ziarcadia:release-watch:last-build";
const RELEASE_WATCH_PARAM_NAMES = ["releaseWatch", "release-watch"];
const RELEASE_WATCH_INDICATOR_HOLD_MS = 5000;
const RELEASE_WATCH_INDICATOR_FADE_MS = 2600;
const CURRENT_BUILD_ID = typeof __ZIARCADIA_BUILD_ID__ === "string" ? __ZIARCADIA_BUILD_ID__ : "dev";
const MASTER1000_CANONICAL_URL = ZIAMEZA_LINKS.master1000.href;
const MASTER1000_TOOL_AVAILABLE =
    typeof __MASTER1000_TOOL_AVAILABLE__ === "boolean" ? __MASTER1000_TOOL_AVAILABLE__ : false;
const MASTER1000_TOOL_EMBED_URL =
    typeof __MASTER1000_TOOL_EMBED_URL__ === "string"
        ? __MASTER1000_TOOL_EMBED_URL__
        : MASTER1000_CANONICAL_URL;
const MASTER1000_TOOL_SOURCE_DIR =
    typeof __MASTER1000_TOOL_SOURCE_DIR__ === "string"
        ? __MASTER1000_TOOL_SOURCE_DIR__
        : MASTER1000_CANONICAL_URL;
// Stable internal tool IDs; public URLs are owned by shared/siteRoutes.ts.
const RANDOM_TEAMS_TOOL_QUERY = "random-teams-orders";
const WORD_LIST_TOOL_QUERY = "word-list";
const VOCABULARY_REVIEW_TOOL_QUERY = "vocabulary-review";
// Keep the experimental builder available by direct URL without advertising it in the main UI.
const WORD_LIST_TOOL_DISCOVERABLE = false;
const AUTOTRANSLATOR_TOOL_QUERY = "autotranslator";
const BASE_DOCUMENT_TITLE = "Ziarcadia";
const GOOGLE_IDENTITY_SCRIPT_URL = "https://accounts.google.com/gsi/client";
const ACCOUNT_PREFERENCE_SAVE_DELAY_MS = 700;
const ACCOUNT_RECENT_UPLOAD_COUNT = 3;
const ACCOUNT_PROFILE_SOURCE_HISTORY_COUNT = 8;
const ACCOUNT_LOAD_RETRY_DELAYS_MS = [180, 540];
const HOST_RENAME_BUTTON_SELECTOR = "#host-edit-name-button";
const PLAYER_RENAME_BUTTON_SELECTOR = "#player-lobby-playground-root .lobby-playground-name-button";
const PARTICIPANT_RENAME_BUTTON_ID_PREFIX = "participant-rename-";
const PARTICIPANT_ACTIONS_TRIGGER_ID_PREFIX = "participant-actions-";
const HOST_KICK_CLOSE_CODE = 1008;
const HOST_KICK_CLOSE_REASON = "Removed by host";
const RENAME_POPOVER_MAX_WIDTH_PX = 320;
const RENAME_REQUEST_DIALOG_MAX_WIDTH_PX = 480;
const RENAME_POPOVER_VIEWPORT_PADDING_PX = 14;
const RENAME_POPOVER_OFFSET_PX = 10;
const RENAME_POPOVER_ESTIMATED_HEIGHT_PX = 154;
const RENAME_POPOVER_ARROW_EDGE_PADDING_PX = 26;
const PRESENCE_INTERACTION_THROTTLE_MS = 1_200;
const CONNECTION_LOST_NOTICE_GRACE_MS = 4_500;
const LOBBY_SUMMARY_POLL_INTERVAL_MS = SITE_TUNING.room.lobbySummaryPollMs;
const HOST_START_LOADING_OVERLAY_DELAY_MS = SITE_TUNING.room.hostStartLoadingOverlayDelayMs;
const HOST_START_CONFIRM_DELAYS_MS = [160, 520, 1250, 2200];
const HOST_START_ROOM_RESYNC_DELAYS_MS = [80, 240];
const LOBBY_ROSTER_RESYNC_COOLDOWN_MS = 700;
const ACTIVE_ROOM_BOUNDARY_RESYNC_GRACE_MS = 450;
const ACTIVE_ROOM_BOUNDARY_RESYNC_RETRY_MS = 1400;
const ACTIVE_ROOM_ACTION_RESYNC_DELAYS_MS = [700, 1800] as const;
const VAULT_ANSWER_RECOVERY_RECONNECT_MS = 1200;
const COPY_JOIN_LINK_ICON = renderZiarcadiaIcon("link", { className: "host-qr-copy-link-icon" });
const ACCOUNT_SIGN_IN_MARK_ICON = renderZiarcadiaIcon("gmail", { className: "account-signin-gmail-icon" });
const GOOGLE_SIGN_IN_MARK_ICON = renderZiarcadiaIcon("googleG", { className: "account-google-g-icon" });
const EXPAND_ICON = renderZiarcadiaIcon("expand", { className: "host-controller-qr-expand-svg" });
const FOG_ICON = renderZiarcadiaIcon("eyeOff", { className: "host-pictionary-fog-icon" });
const VAULT_FOG_ICON = renderZiarcadiaIcon("eyeOff", { className: "host-vault-fog-icon" });
const PICTIONARY_FOG_MARK_ICON = renderZiarcadiaIcon("magnifier", { className: "host-pictionary-fog-mark-icon" });
const SOURCE_TRANSLATE_ICON = renderZiarcadiaIcon("translate", { className: "host-source-translate-icon" });

type BuildInfo = {
    buildId?: string;
    builtAt?: string;
};

const normalizeReleaseWatchToggle = (value: string | null): boolean => {
    const normalized = (value ?? "1").trim().toLowerCase();
    return !["0", "false", "off", "no"].includes(normalized);
};

const consumeReleaseWatchQueryToggle = (): boolean | null => {
    try {
        const url = new URL(window.location.href);
        const paramName = RELEASE_WATCH_PARAM_NAMES.find((name) => url.searchParams.has(name));

        if (!paramName) {
            return null;
        }

        const isEnabled = normalizeReleaseWatchToggle(url.searchParams.get(paramName));

        if (isEnabled) {
            localStorage.setItem(RELEASE_WATCH_STORAGE_KEY, "1");
        } else {
            localStorage.setItem(RELEASE_WATCH_STORAGE_KEY, "0");
            localStorage.removeItem(RELEASE_WATCH_LAST_BUILD_KEY);
        }

        RELEASE_WATCH_PARAM_NAMES.forEach((name) => {
            url.searchParams.delete(name);
        });
        window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);

        return isEnabled;
    } catch {
        return null;
    }
};

const isReleaseWatchEnabled = (queryToggle: boolean | null): boolean => {
    if (queryToggle !== null) {
        return queryToggle;
    }

    try {
        return localStorage.getItem(RELEASE_WATCH_STORAGE_KEY) !== "0";
    } catch {
        return true;
    }
};

const showReleaseWatchIndicator = () => {
    const existingIndicator = document.querySelector<HTMLElement>(".release-watch-indicator");
    existingIndicator?.remove();

    const indicator = document.createElement("div");
    indicator.className = "release-watch-indicator";
    indicator.setAttribute("aria-hidden", "true");
    document.body.append(indicator);

    window.requestAnimationFrame(() => {
        indicator.classList.add("is-visible");
    });

    window.setTimeout(() => {
        indicator.classList.add("is-fading");
    }, RELEASE_WATCH_INDICATOR_HOLD_MS);

    window.setTimeout(() => {
        indicator.remove();
    }, RELEASE_WATCH_INDICATOR_HOLD_MS + RELEASE_WATCH_INDICATOR_FADE_MS + 180);
};

const fetchLatestBuildInfo = async (): Promise<BuildInfo | null> => {
    try {
        const response = await fetch(`/build-info.json?releaseWatch=${encodeURIComponent(String(Date.now()))}`, {
            cache: "no-store"
        });

        if (!response.ok) {
            return null;
        }

        const data = (await response.json()) as BuildInfo;
        return data && typeof data === "object" ? data : null;
    } catch {
        return null;
    }
};

const syncReleaseWatch = async () => {
    const queryToggle = consumeReleaseWatchQueryToggle();

    if (!isReleaseWatchEnabled(queryToggle)) {
        return;
    }

    let lastSeenBuildId = "";

    try {
        lastSeenBuildId = localStorage.getItem(RELEASE_WATCH_LAST_BUILD_KEY) ?? "";
    } catch {
        return;
    }

    const latestBuildInfo = await fetchLatestBuildInfo();
    const latestBuildId = latestBuildInfo?.buildId?.trim() || CURRENT_BUILD_ID;

    if (latestBuildId && latestBuildId !== CURRENT_BUILD_ID) {
        return;
    }

    if (lastSeenBuildId && lastSeenBuildId !== CURRENT_BUILD_ID) {
        showReleaseWatchIndicator();
    }

    try {
        localStorage.setItem(RELEASE_WATCH_LAST_BUILD_KEY, CURRENT_BUILD_ID);
    } catch {
        // Private diagnostic only; blocked storage should not affect the app.
    }
};

void syncReleaseWatch();
applySiteTuningCssVariables();
syncClassroomDisplayTypography();

const app = document.querySelector<HTMLElement>("#app");

if (!app) {
    throw new Error("App root not found.");
}

const STORAGE_KEYS = {
    hostName: "ziarcadia:host-name",
    playerName: "ziarcadia:player-name",
    qrJoinName: "ziarcadia:qr-join-name",
    master1000Fullscreen: "ziarcadia:master1000-fullscreen",
    session: (roomCode: string, role: SessionRole) => `ziarcadia:session:${roomCode}:${role}`
};

const readStoredValue = (key: string): string | null => {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
};

const writeStoredValue = (key: string, value: string) => {
    try {
        localStorage.setItem(key, value);
    } catch {
        // Blocked storage should not stop room entry.
    }
};

const removeStoredValue = (key: string) => {
    try {
        localStorage.removeItem(key);
    } catch {
        // Blocked storage should not stop room entry.
    }
};

const readMaster1000FullscreenPreference = (): boolean =>
    readStoredValue(STORAGE_KEYS.master1000Fullscreen) === "1";

const writeMaster1000FullscreenPreference = (enabled: boolean) => {
    writeStoredValue(STORAGE_KEYS.master1000Fullscreen, enabled ? "1" : "0");
};

const isAutotranslatorAccount = (): boolean =>
    state.account.account?.email.trim().toLowerCase() === ZIAMEZA_IDENTITY.contactEmail;

const getDocumentTitle = (): string => {
    if (state.screen === "landing") return t("siteTitle");
    if (state.screen === "tool-autotranslator") {
        return `${t("createAutotranslatorTool")} - ${BASE_DOCUMENT_TITLE}`;
    }

    const page = APP_PAGE_METADATA.find(page => page.path === window.location.pathname);
    return page ? `${page.title} · ${BASE_DOCUMENT_TITLE}` : BASE_DOCUMENT_TITLE;
};

let lastDocumentMetadata = "";
const syncDocumentTitle = () => {
    const nextTitle = getDocumentTitle();

    if (document.title !== nextTitle) {
        document.title = nextTitle;
    }
    const signature = `${nextTitle}|${window.location.pathname}${window.location.search}`;
    if (signature === lastDocumentMetadata) return;
    lastDocumentMetadata = signature;
    const page = APP_PAGE_METADATA.find(page => page.path === window.location.pathname);
    const canonical = `https://ziarcadia.com${page?.path ?? "/"}`;
    document.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.setAttribute("href", canonical);
    document.querySelector<HTMLMetaElement>('meta[property="og:url"]')?.setAttribute("content", canonical);
    document.querySelector<HTMLMetaElement>('meta[property="og:title"]')?.setAttribute("content", nextTitle);
    const description = page?.description ?? "Ziarcadia has games for teaching English and useful tools for your classroom.";
    document.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute("content", description);
    document.querySelector<HTMLMetaElement>('meta[property="og:description"]')?.setAttribute("content", description);
    const sessionUrl = window.location.pathname.startsWith("/join/") ||
        ["room", "join", "host", "controller", "account"].some(key => new URLSearchParams(window.location.search).has(key));
    let robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (sessionUrl || (page && !page.listed)) {
        if (!robots) {
            robots = document.createElement("meta");
            robots.name = "robots";
            document.head.append(robots);
        }
        robots.content = "noindex, nofollow";
    } else robots?.remove();
};

const state: AppState = {
    screen: "landing",
    room: null,
    error: "",
    info: "",
    account: {
        googleClientId: "",
        loading: true,
        initialized: false,
        account: null,
        preferences: {},
        uploads: [],
        hostedGames: [],
        playedSources: {},
        sourceHistory: [],
        wordLists: [],
        error: ""
    },
    gimcheonSchoolChat: createInitialGimcheonSchoolChatState(),
    hostName: readStoredValue(STORAGE_KEYS.hostName) ?? "",
    joinName: readStoredValue(STORAGE_KEYS.playerName) ?? "",
    joinRoomCode: "",
    joinFromRoomLink: false,
    joinFromQrLink: false,
    qrJoinMode: null,
    hostPanelExpanded: false,
    toolsPanelExpanded: false,
    hostDraft: createDefaultHostDraft(),
    hostDraftDirty: false,
    hostModePanelView: "selection",
    hostPictionaryEditorMode: "text",
    hostVaultEditorMode: "text",
    hostPictionarySourceFogged: false,
    hostVaultSourceFogged: false,
    hostControllerQrExpanded: false,
    hostSourceImportOpen: false,
    hostSourceTranslationOpen: false,
    hostSourceTranslationLoading: false,
    hostSourceTranslationError: "",
    qrPreviewKind: null,
    finalResultsPlayerListOpen: false,
    renameDialogOpen: false,
    renameDialogAnchorSelector: null,
    renameDialogSessionId: null,
    renameDialogPrompted: false,
    pendingHostSync: null,
    hostStartPending: false,
    currentConnection: null,
    randomTeamsTool: createInitialRandomTeamsToolState(),
    wordListTool: createInitialWordListToolState(),
    // Resolve the owner before reading a guest/account backup; early typing stays in memory.
    vocabularyReviewTool: { sourceText: "", index: 0 },
    autotranslator: createInitialAutotranslatorState()
};

const syncThemeClass = () => {
    const hasRelicRunTheme =
        state.screen === "room" &&
        state.room?.phase !== "lobby" &&
        state.room?.mode === RELIC_RUN_MODE;
    const hasPictionaryTheme =
        state.screen === "room" &&
        state.room?.mode === PICTURE_DETECTIVES_MODE &&
        (state.room.phase !== "lobby" || state.room.viewer.isController);
    const hasVaultBreakersTheme =
        state.screen === "room" &&
        state.room?.mode === VAULT_BREAKERS_MODE &&
        state.room.phase !== "lobby";

    app.classList.toggle("theme-relic-run", hasRelicRunTheme);
    app.classList.toggle("theme-pictionary", hasPictionaryTheme);
    app.classList.toggle("theme-vault-breakers", hasVaultBreakersTheme);
    app.classList.toggle("theme-planet-hoppers", state.screen === "room" && (state.room?.phase === "lobby" && state.room.viewer.isHost ? state.hostDraft.mode : state.room?.mode) === PLANET_HOPPERS_MODE);
    app.classList.toggle("gimcheon-school-active", state.screen === "tool-gimcheon-school");
    app.classList.toggle("autotranslator-active", state.screen === "tool-autotranslator");
};

const refs: ScreenRefs = {
    banner: null,
    viewerBadge: null,
    playerStrip: null,
    utilitySlot: null,
    overlayRoot: null,
    content: null,
    connectingMessage: null,
    toolContent: null
};

let socket: WebSocket | null = null;
let reconnectTimer: number | null = null;
let reconnectAttempt = 0;
let connectionLostNoticeTimer: number | null = null;
let connectionHeartbeatTimer: number | null = null;
let connectionHeartbeatSocket: WebSocket | null = null;
let lastConnectionMessageAt = 0;
let intentionalDisconnect = false;
let currentScreen: Screen | null = null;
let currentRoomVariant: RoomVariant | null = null;
let mazeView: MazeView | null = null;
let pictionaryView: PictionaryView | null = null;
let vaultBreakersView: VaultBreakersView | null = null;
let planetHoppersView: PlanetHoppersView | null = null;
let planetHoppersEditor: PlanetHoppersEditor | null = null;
let planetEditorRoot: HTMLElement | null = null;
let setupLibrary: SetupLibrary | null = null;
let setupLibraryRoot: HTMLElement | null = null;
let lobbyPlayground: LobbyPlayground | null = null;
let randomTeamsToolView: { destroy: () => void; refreshLanguage: () => void } | null = null;
let wordListToolView: { destroy: () => void; refreshLanguage: () => void } | null = null;
let vocabularyReviewToolView: ReturnType<typeof createVocabularyReviewTool> | null = null;
let vocabularySyncStatus: VocabularySyncStatus = "loading";
const vocabularyDraftSync = new VocabularyDraftSync({
    apply: sourceText => {
        state.vocabularyReviewTool.sourceText = sourceText;
        state.vocabularyReviewTool.index = 0;
        vocabularyReviewToolView?.restoreSource(sourceText);
    },
    status: status => {
        vocabularySyncStatus = status;
        vocabularyReviewToolView?.setSyncStatus(status);
    }
});
let autotranslatorToolView: { destroy: () => void; refreshLanguage: () => void } | null = null;
let currentOverlaySignature = "";
let currentActiveHostUtilitySignature = "";
let currentHostLobbyStructureSignature = "";
let currentFinalResultsSignature = "";
let openRoomParticipantRosterActionsKey: string | null = null;
let hostPlayerHistoryMenuOpen = false;
let previousHostControllerSeatClaimed = false;
let lobbySummaryPollTimer: number | null = null;
let hostStartConfirmTimers: number[] = [];
let hostStartLoadingOverlayTimer: number | null = null;
let hostStartLoadingOverlayVisible = false;
let lobbySummaryPollController: AbortController | null = null;
let lastLobbyRosterResyncAt = 0;
let lastLobbyRosterResyncSignature = "";
let qrJoinSummaryPollTimer: number | null = null;
let qrJoinSummaryPollInFlight = false;
let qrJoinSummaryRoomCode = "";
let activeRoomBoundaryResyncTimers: number[] = [];
let activeRoomBoundaryResyncSignature = "";
let activeRoomActionResyncTimers: number[] = [];
let activeRoomActionResyncSignature = "";
let vaultAnswerRecoveryTimer: number | null = null;
let vaultAnswerRecoverySignature = "";
let viewerPresenceInteractionPending = false;
let lastPictionaryCanvasStateRefreshAt = 0;
let lastViewerPresenceSentAt = 0;
let lastViewerPresenceHidden: boolean | null = null;
let hostLobbyRosterLayoutFrameId: number | null = null;
let master1000ShellScrollLockSnapshot: ScrollLockSnapshot | null = null;
let master1000ViewportMetaSnapshot: string | null = null;
let master1000ShellImmersive = false;
let master1000FullscreenRestoreAttempted = false;
let master1000WasStageFullscreen = false;
const roomAudio = new RoomAudio();
type RoomQrTarget = "join" | "controller" | "preview";
const currentQrLinks: Record<RoomQrTarget, string> = {
    join: "",
    controller: "",
    preview: ""
};
const intentionallyClosedSockets = new WeakSet<WebSocket>();
const roomQrCache = new Map<string, string>();
const createRoomAudioState = (): RoomAudioState => ({
    roomCode: "",
    initialized: false,
    phase: null,
    mode: null,
    connectedLobbyParticipantIds: new Set<string>(),
    pictionaryControllerConnected: false,
    localJoinSoundKey: "",
    localControllerFoundSoundKey: "",
    modeStartSoundKey: ""
});
let roomAudioState = createRoomAudioState();
let finalResultsSequenceRenderKey = "";
let finalResultsSoundTimerIds: number[] = [];
let activeHostedGameHistoryId: string | null = null;
let accountHistoryOpen = new URLSearchParams(window.location.search).has("history");
let accountActivityHistory: ActivityHistory | null = null;
let pendingHistoryDraft: HostDraft | null = null;
const pendingHostedGameRecords = new Set<string>();
let activeHostedGameHistoryRoomCode = "";
let recordedHostedGameResultsKey = "";
const expandedAccountHostedGameIds = new Set<string>();

const resetRoomAudioState = () => {
    roomAudioState = createRoomAudioState();
};

const primeRoomAudio = () => {
    void roomAudio.prepare();
};

document.addEventListener("pointerdown", primeRoomAudio, true);
document.addEventListener("keydown", primeRoomAudio, true);

const clampNumber = (value: number, minimum: number, maximum: number): number =>
    Math.min(maximum, Math.max(minimum, value));

const escapeHtml = (value: string): string =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

const MODE_DESCRIPTION_FOCUS_TEXT: Record<RoomMode, { en: string; ko: string }> = {
    [RELIC_RUN_MODE]: {
        en: "Practice memorizing sentences",
        ko: "\uBB38\uC7A5\uC744 \uC678\uC6CC\uC694"
    },
    [PICTURE_DETECTIVES_MODE]: {
        en: "Pictionary-style",
        ko: "Pictionary-style"
    },
    [PLANET_HOPPERS_MODE]: { en: "Answer Trivia", ko: "퀴즈에 답하세요" },
    [VAULT_BREAKERS_MODE]: {
        en: "Jeopardy-style",
        ko: "Jeopardy-style"
    }
};

const renderModeDescriptionMarkup = (mode: RoomMode, description: string): string => {
    const focusText = MODE_DESCRIPTION_FOCUS_TEXT[mode][getLanguage()];

    if (!description.startsWith(focusText)) {
        return escapeHtml(description);
    }

    return `<strong class="relic-mode-kicker-focus">${escapeHtml(focusText)}</strong>${escapeHtml(
        description.slice(focusText.length)
    )}`;
};

const fullscreenDocument = document as WebkitFullscreenDocument;

const getMaster1000Stage = (): HTMLElement | null => app.querySelector<HTMLElement>("[data-master1000-stage]");

const getViewportMeta = (): HTMLMetaElement | null =>
    document.querySelector<HTMLMetaElement>("meta[name='viewport']");

const getActiveFullscreenElement = (): Element | null =>
    document.fullscreenElement ?? fullscreenDocument.webkitFullscreenElement ?? null;

const isMaster1000StageFullscreen = (): boolean => {
    const stage = getMaster1000Stage();
    return Boolean(stage && getActiveFullscreenElement() === stage);
};

const isMaster1000ShellImmersive = (): boolean =>
    state.screen === "tool-master1000" && master1000ShellImmersive;

const isMaster1000PresentationActive = (): boolean =>
    isMaster1000ShellImmersive() || isMaster1000StageFullscreen();

const shouldPreferMaster1000ShellImmersive = (): boolean =>
    window.matchMedia("(max-width: 860px), (pointer: coarse)").matches;

const canToggleMaster1000Fullscreen = (): boolean => {
    return Boolean(getMaster1000Stage());
};

const syncMaster1000FullscreenButton = () => {
    const buttons = app.querySelectorAll<HTMLButtonElement>("[data-master1000-fullscreen]");

    const label = t(isMaster1000PresentationActive() ? "exitFullscreen" : "fullscreen");
    const isAvailable = canToggleMaster1000Fullscreen();

    buttons.forEach((button) => {
        button.disabled = !isAvailable;
        button.classList.toggle("is-presenting", isMaster1000PresentationActive());
        button.setAttribute("aria-label", label);
        button.setAttribute("title", label);
        button.querySelector<HTMLElement>("[data-master1000-fullscreen-label]")?.replaceChildren(label);
    });
};

const scrollViewportToTop = () => {
    window.scrollTo({ top: 0, left: 0 });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
};

const scheduleViewportTopReset = () => {
    window.requestAnimationFrame(() => {
        scrollViewportToTop();
        window.setTimeout(scrollViewportToTop, 0);
    });
};

const shouldLockActiveRoomViewport = (): boolean =>
    state.screen === "room" && state.room?.phase === "active";

const syncViewportLock = () => {
    const viewportMeta = getViewportMeta();

    if (!viewportMeta) {
        return;
    }

    const lockedContent = state.screen === "tool-master1000"
        ? MASTER1000_LOCKED_VIEWPORT_CONTENT
        : shouldLockActiveRoomViewport()
          ? ACTIVE_ROOM_LOCKED_VIEWPORT_CONTENT
          : null;

    if (lockedContent) {
        if (master1000ViewportMetaSnapshot === null) {
            master1000ViewportMetaSnapshot = viewportMeta.getAttribute("content") ?? "";
        }

        if (viewportMeta.getAttribute("content") !== lockedContent) {
            viewportMeta.setAttribute("content", lockedContent);
        }
        return;
    }

    if (master1000ViewportMetaSnapshot === null) {
        return;
    }

    viewportMeta.setAttribute("content", master1000ViewportMetaSnapshot);
    master1000ViewportMetaSnapshot = null;
};

const syncMaster1000ShellScrollLock = () => {
    const shouldLock = state.screen === "tool-master1000";

    if (shouldLock) {
        if (!master1000ShellScrollLockSnapshot) {
            master1000ShellScrollLockSnapshot = {
                documentOverflow: document.documentElement.style.overflow,
                documentOverscrollBehavior: document.documentElement.style.overscrollBehavior,
                bodyOverflow: document.body.style.overflow,
                bodyOverscrollBehavior: document.body.style.overscrollBehavior
            };
        }

        document.documentElement.style.overflow = "hidden";
        document.documentElement.style.overscrollBehavior = "none";
        document.body.style.overflow = "hidden";
        document.body.style.overscrollBehavior = "none";
        return;
    }

    if (!master1000ShellScrollLockSnapshot) {
        return;
    }

    document.documentElement.style.overflow = master1000ShellScrollLockSnapshot.documentOverflow;
    document.documentElement.style.overscrollBehavior = master1000ShellScrollLockSnapshot.documentOverscrollBehavior;
    document.body.style.overflow = master1000ShellScrollLockSnapshot.bodyOverflow;
    document.body.style.overscrollBehavior = master1000ShellScrollLockSnapshot.bodyOverscrollBehavior;
    master1000ShellScrollLockSnapshot = null;
};

const syncMaster1000ShellPresentationClass = () => {
    app.classList.toggle("master1000-shell-immersive", state.screen === "tool-master1000" && master1000ShellImmersive);
};

const setMaster1000ShellImmersive = (next: boolean) => {
    if (master1000ShellImmersive === next) {
        return;
    }

    master1000ShellImmersive = next;
    syncMaster1000ShellPresentationClass();
    scheduleViewportTopReset();
    syncMaster1000FullscreenButton();
};

const exitMaster1000NativeFullscreen = async () => {
    if (document.fullscreenElement) {
        await document.exitFullscreen();
        return;
    }

    if (fullscreenDocument.webkitFullscreenElement && fullscreenDocument.webkitExitFullscreen) {
        await Promise.resolve(fullscreenDocument.webkitExitFullscreen());
    }
};

const enterMaster1000Fullscreen = async (options: { rememberPreference?: boolean } = {}) => {
    const stage = getMaster1000Stage() as FullscreenCapableElement | null;

    if (!stage) {
        return;
    }

    if (options.rememberPreference !== false) {
        writeMaster1000FullscreenPreference(true);
    }

    if (isMaster1000PresentationActive()) {
        syncMaster1000FullscreenButton();
        return;
    }

    try {
        if (shouldPreferMaster1000ShellImmersive()) {
            setMaster1000ShellImmersive(true);
        } else {
            if (stage.requestFullscreen) {
                await Promise.resolve(stage.requestFullscreen({ navigationUI: "hide" }));
            } else if (stage.webkitRequestFullscreen) {
                await Promise.resolve(stage.webkitRequestFullscreen());
            } else {
                setMaster1000ShellImmersive(true);
            }
        }
    } catch {
        // If the browser blocks fullscreen, keep the shell usable with the
        // in-page immersive fallback instead of leaving mobile stuck half-way.
        setMaster1000ShellImmersive(true);
    }

    syncMaster1000FullscreenButton();
};

const exitMaster1000Fullscreen = async (options: { rememberPreference?: boolean } = {}) => {
    if (options.rememberPreference !== false) {
        writeMaster1000FullscreenPreference(false);
    }

    if (isMaster1000ShellImmersive()) {
        setMaster1000ShellImmersive(false);
    } else if (isMaster1000StageFullscreen()) {
        await exitMaster1000NativeFullscreen();
    }

    syncMaster1000FullscreenButton();
};

const toggleMaster1000Fullscreen = async () => {
    if (isMaster1000PresentationActive()) {
        await exitMaster1000Fullscreen();
    } else {
        await enterMaster1000Fullscreen();
    }
};

const restoreMaster1000FullscreenPreference = () => {
    if (
        master1000FullscreenRestoreAttempted ||
        !readMaster1000FullscreenPreference() ||
        !canToggleMaster1000Fullscreen()
    ) {
        return;
    }

    master1000FullscreenRestoreAttempted = true;
    void enterMaster1000Fullscreen({ rememberPreference: false });
};

const handleMaster1000FullscreenChange = () => {
    const isStageFullscreen = isMaster1000StageFullscreen();

    if (
        master1000WasStageFullscreen &&
        !isStageFullscreen &&
        state.screen === "tool-master1000" &&
        !isMaster1000ShellImmersive()
    ) {
        writeMaster1000FullscreenPreference(false);
    }

    master1000WasStageFullscreen = isStageFullscreen;
    syncMaster1000FullscreenButton();
};

const syncHostDraftPictionarySourceText = (draft = state.hostDraft) => {
    const sourceText = draft.sourceText || draft.pictionarySourceText;
    draft.pictionarySourceText = sourceText;
    draft.pictionaryRows = hydratePictionaryDraftRows(sourceText, "");
    draft.pictionaryStem = "";
};

const syncHostDraftPictionaryRowsToSourceText = (draft = state.hostDraft) => {
    draft.pictionaryRows = ensurePictionaryDraftRowTail(draft.pictionaryRows);
    const sourceText = serializePictionaryDraftRows(draft.pictionaryRows);
    draft.sourceText = sourceText;
    draft.pictionarySourceText = sourceText;
    draft.pictionaryStem = "";
};

const syncHostDraftVaultSourceText = (draft = state.hostDraft) => {
    const sourceText = draft.sourceText || draft.vaultSourceText;
    draft.vaultSourceText = sourceText;
    draft.vaultRows = hydrateVaultDraftRows(sourceText);
};

const syncHostDraftVaultRowsToSourceText = (draft = state.hostDraft) => {
    draft.vaultRows = ensureVaultDraftRowTail(draft.vaultRows);
    const sourceText = serializeVaultDraftRows(draft.vaultRows);
    draft.sourceText = sourceText;
    draft.vaultSourceText = sourceText;
};

let hostSourceTranslationRequestId = 0;

const resetHostSourceTranslation = () => {
    hostSourceTranslationRequestId += 1;
    state.hostSourceTranslationOpen = false;
    state.hostSourceTranslationLoading = false;
    state.hostSourceTranslationError = "";
};

const syncHostTranslationRowsFromText = () => {
    if (state.hostDraft.mode === PICTURE_DETECTIVES_MODE && state.hostPictionaryEditorMode === "text") {
        syncHostDraftPictionarySourceText();
    } else if (state.hostDraft.mode === VAULT_BREAKERS_MODE && state.hostVaultEditorMode === "text") {
        syncHostDraftVaultSourceText();
    }
};

const getHostSourceTranslationItems = (): SourceTranslationItem[] => {
    if (state.hostDraft.mode === PICTURE_DETECTIVES_MODE) {
        return state.hostDraft.pictionaryRows
            .filter((row) => row.english.trim().length > 0)
            .map((row, index) => ({
                index,
                term: row.english.trim(),
                currentTranslation: row.korean.trim(),
                context: row.stem.trim()
            }));
    }

    if (state.hostDraft.mode === VAULT_BREAKERS_MODE) {
        return state.hostDraft.vaultRows
            .filter((row) => row.answer.trim().length > 0)
            .map((row, index) => ({
                index,
                term: row.answer.trim(),
                currentTranslation: row.korean.trim(),
                context: [row.category.trim(), row.value.trim(), row.prompt.trim()].filter(Boolean).join(" | ")
            }));
    }

    return [];
};

const findHostPictionaryTableInput = (
    rowId: string,
    field: PictionaryDraftField
): HTMLInputElement | null =>
    Array.from(app.querySelectorAll<HTMLInputElement>(".host-pictionary-table-input")).find(
        (input) => input.dataset.pictionaryRowId === rowId && input.dataset.pictionaryField === field
    ) ?? null;

const findHostVaultTableInput = (rowId: string, field: VaultDraftField): HTMLInputElement | null =>
    Array.from(app.querySelectorAll<HTMLInputElement>(".host-vault-table-input")).find(
        (input) => input.dataset.vaultRowId === rowId && input.dataset.vaultField === field
    ) ?? null;

const focusHostPictionaryTableInput = (
    rowId: string,
    field: PictionaryDraftField,
    selectionStart: number | null,
    selectionEnd: number | null
) => {
    const input = findHostPictionaryTableInput(rowId, field);

    if (!input) {
        return;
    }

    input.focus();

    if (selectionStart !== null && selectionEnd !== null) {
        input.setSelectionRange(selectionStart, selectionEnd);
    }
};

const focusHostVaultTableInput = (
    rowId: string,
    field: VaultDraftField,
    selectionStart: number | null,
    selectionEnd: number | null
) => {
    const input = findHostVaultTableInput(rowId, field);

    if (!input) {
        return;
    }

    input.focus();

    if (selectionStart !== null && selectionEnd !== null) {
        input.setSelectionRange(selectionStart, selectionEnd);
    }
};

const createHostSyncSnapshot = (draft = state.hostDraft): HostSyncSnapshot => ({
    mode: draft.mode,
    sourceText: draft.sourceText,
    singleLineMode: draft.singleLineMode,
    hostParticipates: draft.hostParticipates,
    pictionaryStem: draft.pictionaryStem,
    pictionaryNoHints: draft.pictionaryNoHints,
    pictionaryNoTranslations: draft.pictionaryNoTranslations,
    pictionarySameStem: draft.pictionarySameStem,
    pictionaryHostIsController: draft.pictionaryHostIsController
});

const applySetupToHostDraft = (
    setup: RoomView["setup"],
    previousDraft: HostDraft = state.hostDraft
): HostDraft => {
    const nextDraft: HostDraft = {
        ...previousDraft,
        mode: setup.selectedMode,
        singleLineMode: setup.singleLineMode,
        hostParticipates: setup.hostParticipates,
        pictionaryStem: setup.pictionaryStem,
        pictionaryNoHints: setup.pictionaryNoHints,
        pictionaryNoTranslations: setup.pictionaryNoTranslations,
        pictionarySameStem: setup.pictionarySameStem,
        pictionaryHostIsController: setup.pictionaryHostIsController
    };

    if (setup.selectedMode === RELIC_RUN_MODE) {
        nextDraft.relicSourceText = setup.sourceText;
        nextDraft.sourceText = setup.sourceText;
    } else if (setup.selectedMode === PICTURE_DETECTIVES_MODE) {
        nextDraft.sourceText = setup.sourceText;
        nextDraft.pictionarySourceText = setup.sourceText;

        if (setup.sourceText === previousDraft.sourceText || setup.sourceText === previousDraft.pictionarySourceText) {
            nextDraft.pictionaryRows = ensurePictionaryDraftRowTail(previousDraft.pictionaryRows);
            nextDraft.pictionaryStem = "";
        } else {
            syncHostDraftPictionarySourceText(nextDraft);
        }
    } else if (setup.selectedMode === PLANET_HOPPERS_MODE) {
        nextDraft.sourceText = setup.sourceText;
    } else if (setup.selectedMode === VAULT_BREAKERS_MODE) {
        nextDraft.vaultSourceText = setup.sourceText;
        nextDraft.sourceText = setup.sourceText;

        if (setup.sourceText === previousDraft.sourceText || setup.sourceText === previousDraft.vaultSourceText) {
            nextDraft.vaultRows = ensureVaultDraftRowTail(previousDraft.vaultRows);
        } else {
            syncHostDraftVaultSourceText(nextDraft);
        }
    } else {
        nextDraft.sourceText = "";
    }

    return nextDraft;
};

const getHostDraftLevels = () =>
    state.hostDraft.mode === RELIC_RUN_MODE
        ? buildRelicRunLevels(state.hostDraft.sourceText, state.hostDraft.singleLineMode)
        : [];

const getHostDraftPictionaryEntries = () =>
    state.hostDraft.mode === PICTURE_DETECTIVES_MODE
        ? buildPictionaryEntries(state.hostDraft.sourceText, {
              fallbackStem: "",
              noHints: state.hostDraft.pictionaryNoHints,
              noTranslations: state.hostDraft.pictionaryNoTranslations,
              sameStem: state.hostDraft.pictionarySameStem
          })
        : [];

const areHostDraftsEqual = (
    left: HostSyncSnapshot | null,
    right: HostSyncSnapshot | null
): boolean =>
    left?.mode === right?.mode &&
    left?.sourceText === right?.sourceText &&
    left?.singleLineMode === right?.singleLineMode &&
    left?.hostParticipates === right?.hostParticipates &&
    left?.pictionaryStem === right?.pictionaryStem &&
    left?.pictionaryNoHints === right?.pictionaryNoHints &&
    left?.pictionaryNoTranslations === right?.pictionaryNoTranslations &&
    left?.pictionarySameStem === right?.pictionarySameStem &&
    left?.pictionaryHostIsController === right?.pictionaryHostIsController;

const getHostDraftTokenCount = (): number =>
    getHostDraftLevels().reduce((total, level) => total + level.tokenCount, 0);

const getHostDraftLevelCount = (): number => getHostDraftLevels().length;

const getHostDraftPictionaryEntryCount = (): number => getHostDraftPictionaryEntries().length;

const getHostDraftVaultEntries = () =>
    state.hostDraft.mode === VAULT_BREAKERS_MODE ? parseVaultBreakerSource(state.hostDraft.sourceText) : [];

const getHostDraftVaultEntryCount = (): number => getHostDraftVaultEntries().length;

const isHostModeSelectionOpen = (): boolean =>
    state.hostDraft.mode === null || state.hostModePanelView === "selection";

const getHostLobbyParticipants = (room = state.room): RoomParticipantView[] =>
    room ? getLobbyRosterParticipants(room) : [];

const getVisibleLobbyParticipantCount = (room: RoomView): number =>
    getHostLobbyParticipants(room).filter((participant) => participant.connected).length;

const getVisibleLobbyPlaygroundPlayerCount = (room: RoomView): number => getLobbyPlaygroundIds(room).length;

const getAuthoritativeLobbyPlayerCount = (room: RoomView): number => {
    return getHostLobbyParticipants(room).length;
};

const isHostStartPending = (room = state.room): boolean =>
    Boolean(
        state.hostStartPending &&
            state.screen === "room" &&
            room?.viewer.isHost &&
            room.phase === "lobby" &&
            state.currentConnection?.role === "host" &&
            state.currentConnection.roomCode === room.roomCode
    );

const isHostStartLoadingOverlayVisible = (room = state.room): boolean =>
    hostStartLoadingOverlayVisible && isHostStartPending(room);

const preservedScrollMarkupByElement = new WeakMap<HTMLElement, string>();

const replaceInnerHtmlPreservingScroll = (element: HTMLElement, markup: string) => {
    if (preservedScrollMarkupByElement.get(element) === markup || element.innerHTML === markup) {
        preservedScrollMarkupByElement.set(element, markup);
        return;
    }

    const previousScrollTop = element.scrollTop;

    element.innerHTML = markup;
    preservedScrollMarkupByElement.set(element, markup);
    element.scrollTop = previousScrollTop;

    window.requestAnimationFrame(() => {
        element.scrollTop = previousScrollTop;
    });
};

const scheduleHostLobbyRosterLayoutSync = () => {
    if (hostLobbyRosterLayoutFrameId !== null) {
        return;
    }

    hostLobbyRosterLayoutFrameId = window.requestAnimationFrame(() => {
        hostLobbyRosterLayoutFrameId = null;

        if (currentRoomVariant !== "host-lobby" || !state.room?.viewer.isHost) {
            return;
        }

        const rosterBody = refs.content?.querySelector<HTMLElement>("#lobby-participants") ?? null;
        const rosterList = rosterBody?.querySelector<HTMLElement>(".participant-list.player-roster") ?? null;

        syncPlayerRosterLayout({ rosterBody, rosterList });
    });
};

const getConnectedLobbyParticipantIdsForAudio = (room: RoomView): Set<string> =>
    new Set(
        room.participants
            .filter((participant) => participant.connected && !participant.isController)
            .map((participant) => participant.sessionId)
    );

const getPictionaryControllerConnectedForAudio = (room: RoomView): boolean =>
    room.mode === PICTURE_DETECTIVES_MODE &&
    (room.pictionary
        ? room.pictionary.controllerConnected ||
          room.pictionary.controllerIsHost ||
          Boolean(room.pictionary.controllerReleaseAt && room.pictionary.controllerReleaseAt > Date.now())
        : room.setup.pictionaryControllerConnected);

const FINAL_RESULTS_SEQUENCE_STORAGE_PREFIX = "ziarcadia:final-results-seen:";
const FINAL_RESULT_SOUND_OFFSET_MS = 160;

const getFinalResultsSequenceKey = (room: RoomView): string => {
    const results = room.finalResults ?? null;

    if (room.phase !== "results" || !results || results.groups.length === 0) {
        return "";
    }

    return JSON.stringify({
        roomCode: room.roomCode,
        mode: results.mode ?? room.mode,
        startedAt: results.startedAt ?? null,
        groups: results.groups.map((group) => ({
            placement: group.placement,
            score: group.score ?? null,
            elapsedMs: group.elapsedMs ?? null,
            players: group.players.map((player) => ({
                sessionId: player.sessionId,
                name: player.name
            }))
        }))
    });
};

const hasSeenFinalResultsSequence = (key: string): boolean => {
    if (!key) {
        return false;
    }

    try {
        return localStorage.getItem(`${FINAL_RESULTS_SEQUENCE_STORAGE_PREFIX}${key}`) === "1";
    } catch {
        return false;
    }
};

const markFinalResultsSequenceSeen = (key: string) => {
    if (!key) {
        return;
    }

    try {
        localStorage.setItem(`${FINAL_RESULTS_SEQUENCE_STORAGE_PREFIX}${key}`, "1");
    } catch {
        // Ignore storage denial; the in-memory key still prevents same-render replays.
    }
};

const clearFinalResultsSoundTimers = () => {
    for (const timerId of finalResultsSoundTimerIds) {
        window.clearTimeout(timerId);
    }

    finalResultsSoundTimerIds = [];
};

const playFinalResultPlacementSound = (placement: FinalResultGroupView["placement"]) => {
    if (placement === 1) {
        roomAudio.playFirstPlaceWinner();
    } else if (placement === 2) {
        roomAudio.playSecondPlaceWinner();
    } else if (placement === 3) {
        roomAudio.playThirdPlaceWinner();
    }
};

const scheduleFinalResultsSequenceAudio = (room: RoomView) => {
    const groups = (room.finalResults?.groups ?? []).slice().sort((left, right) => right.placement - left.placement);

    clearFinalResultsSoundTimers();

    finalResultsSoundTimerIds = groups.map((group, index) =>
        window.setTimeout(() => {
            playFinalResultPlacementSound(group.placement);
        }, getFinalResultRevealDelayMs(index) + FINAL_RESULT_SOUND_OFFSET_MS)
    );
};

const syncRoomAudioFeedback = (room: RoomView) => {
    if (roomAudioState.roomCode !== room.roomCode) {
        resetRoomAudioState();
        roomAudioState.roomCode = room.roomCode;
    }

    const wasInitialized = roomAudioState.initialized;
    const previousPhase = roomAudioState.phase;

    if (room.phase === "lobby" || room.phase === "results" || room.mode === PICTURE_DETECTIVES_MODE) {
        void roomAudio.prepare();
    }

    const connectedLobbyParticipantIds = getConnectedLobbyParticipantIdsForAudio(room);
    let shouldPlayPlayerJoin = false;
    let shouldPlayGameModeStart = false;

    if (room.phase === "lobby") {
        if (room.viewer.isHost && roomAudioState.initialized) {
            shouldPlayPlayerJoin = room.participants.some(
                (participant) =>
                    participant.connected &&
                    !participant.isController &&
                    participant.sessionId !== room.viewer.sessionId &&
                    !roomAudioState.connectedLobbyParticipantIds.has(participant.sessionId)
            );
        }

        const localJoinSoundKey = `${room.roomCode}:${room.viewer.sessionId}`;

        if (
            !room.viewer.isHost &&
            !room.viewer.isController &&
            connectedLobbyParticipantIds.has(room.viewer.sessionId) &&
            roomAudioState.localJoinSoundKey !== localJoinSoundKey
        ) {
            shouldPlayPlayerJoin = true;
            roomAudioState.localJoinSoundKey = localJoinSoundKey;
        }
    }

    const modeStartSoundKey = room.phase === "active" && room.mode ? `${room.roomCode}:${room.mode}` : "";

    if (
        modeStartSoundKey &&
        wasInitialized &&
        previousPhase === "lobby" &&
        roomAudioState.modeStartSoundKey !== modeStartSoundKey
    ) {
        shouldPlayGameModeStart = true;
        roomAudioState.modeStartSoundKey = modeStartSoundKey;
    } else if (room.phase !== "active") {
        roomAudioState.modeStartSoundKey = "";
    }

    const finalResultsSequenceKey = room.phase === "results" ? getFinalResultsSequenceKey(room) : "";
    const shouldPlayFinalResultsSequence = Boolean(
        finalResultsSequenceKey &&
            wasInitialized &&
            previousPhase === "active" &&
            !hasSeenFinalResultsSequence(finalResultsSequenceKey)
    );

    finalResultsSequenceRenderKey = shouldPlayFinalResultsSequence ? finalResultsSequenceKey : "";

    if (finalResultsSequenceKey && !shouldPlayFinalResultsSequence) {
        markFinalResultsSequenceSeen(finalResultsSequenceKey);
    }

    const pictionaryControllerConnected = getPictionaryControllerConnectedForAudio(room);
    let shouldPlayHostControllerFound = false;
    let shouldPlayHostControllerLost = false;

    if (room.mode === PICTURE_DETECTIVES_MODE) {
        if (
            room.viewer.isHost &&
            roomAudioState.initialized &&
            pictionaryControllerConnected &&
            !roomAudioState.pictionaryControllerConnected
        ) {
            shouldPlayHostControllerFound = true;
        }

        if (
            room.viewer.isHost &&
            roomAudioState.initialized &&
            room.phase !== "results" &&
            !pictionaryControllerConnected &&
            roomAudioState.pictionaryControllerConnected
        ) {
            shouldPlayHostControllerLost = true;
        }

        const localControllerSoundKey = `${room.roomCode}:${room.viewer.sessionId}`;

        if (
            !room.viewer.isHost &&
            room.viewer.isController &&
            pictionaryControllerConnected &&
            roomAudioState.localControllerFoundSoundKey !== localControllerSoundKey
        ) {
            shouldPlayHostControllerFound = true;
            roomAudioState.localControllerFoundSoundKey = localControllerSoundKey;
        }
    }

    if (shouldPlayPlayerJoin) {
        roomAudio.playPlayerJoin();
    }

    if (shouldPlayGameModeStart) {
        roomAudio.playGameModeStart();
    }

    if (shouldPlayHostControllerFound) {
        roomAudio.playHostControllerFound();
    }

    if (shouldPlayHostControllerLost) {
        roomAudio.playHostControllerLost();
    }

    roomAudioState.connectedLobbyParticipantIds = connectedLobbyParticipantIds;
    roomAudioState.pictionaryControllerConnected = pictionaryControllerConnected;
    roomAudioState.phase = room.phase;
    roomAudioState.mode = room.mode;
    roomAudioState.initialized = true;
};

const canStartHostDraft = (): boolean =>
    state.hostDraft.mode === PLANET_HOPPERS_MODE ? (() => { try { const q = parsePHSource(state.hostDraft.sourceText).quiz; return q.questions.some(x => !x.excluded) && phIssues(q).length === 0; } catch { return false; } })() :
    state.hostDraft.mode === RELIC_RUN_MODE
        ? getHostDraftLevelCount() > 0
        : state.hostDraft.mode === PICTURE_DETECTIVES_MODE
          ? getHostDraftPictionaryEntryCount() > 0 &&
            (state.hostDraft.pictionaryHostIsController || Boolean(state.room?.setup.pictionaryControllerConnected))
          : state.hostDraft.mode === VAULT_BREAKERS_MODE
            ? getHostDraftVaultEntryCount() > 0
          : false;

const getHostStartButtonLabel = (): string =>
    isHostStartPending()
        ? t("startingGame")
        : canStartHostDraft()
        ? t("start")
        : state.hostDraft.mode === PICTURE_DETECTIVES_MODE
          ? getHostDraftPictionaryEntryCount() === 0
              ? t("addWords")
              : t("needHostController")
          : state.hostDraft.mode === VAULT_BREAKERS_MODE
            ? getHostDraftVaultEntryCount() === 0
                ? t("addVaults")
                : t("start")
          : t("inputSomeText");

const renderHostStartButtonContent = (): string => {
    const label = getHostStartButtonLabel();

    if (!isHostStartPending()) {
        return escapeHtml(label);
    }

    return `<span class="spinner host-start-spinner" aria-hidden="true"></span><span>${escapeHtml(label)}</span>`;
};

const renderHostStartLoadingOverlayMarkup = (): string => {
    const visible = isHostStartLoadingOverlayVisible();

    return `
        <div
            id="host-start-loading-overlay"
            class="host-start-loading-overlay ${visible ? "is-visible" : ""}"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            aria-busy="${visible ? "true" : "false"}"
            ${visible ? "" : "hidden"}
        >
            <div class="host-start-loading-photo-shell" aria-hidden="true">
                <img
                    class="host-start-loading-photo"
                    src="/images/capybara-stroll.webp"
                    alt=""
                    loading="eager"
                    decoding="async"
                />
            </div>
            <div class="host-start-loading-status">
                <span class="spinner host-start-loading-spinner" aria-hidden="true"></span>
                <strong>${escapeHtml(t("startingGame"))}</strong>
            </div>
        </div>
    `;
};

const syncHostStartButton = (button: HTMLButtonElement) => {
    const pending = isHostStartPending();

    button.hidden = state.hostDraft.mode === null;
    button.disabled = pending || !canStartHostDraft();
    button.classList.toggle("is-starting", pending);
    button.setAttribute("aria-busy", pending ? "true" : "false");

    const nextMarkup = renderHostStartButtonContent();

    if (button.innerHTML !== nextMarkup) {
        button.innerHTML = nextMarkup;
    }
};

const getTreatAsOneLevelLabel = (): string => t("treatAsOneLevel");

const getHostParticipationLabel = (): string => t("hostIsPlayerToo");

const renderModeIcon = (mode: RoomMode, className = "mode-icon"): string =>
    mode === PLANET_HOPPERS_MODE ? `<svg class="${className}" viewBox="0 0 48 48" aria-hidden="true"><circle cx="24" cy="24" r="12" fill="currentColor"/><ellipse cx="24" cy="24" rx="22" ry="7" fill="none" stroke="currentColor" stroke-width="3" transform="rotate(-25 24 24)"/></svg>` :
    mode === RELIC_RUN_MODE
        ? `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path
                    d="M12 2.75 19.25 9.9 12 21.25 4.75 9.9 12 2.75Z"
                    fill="currentColor"
                    fill-opacity="0.16"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linejoin="round"
                />
                <path
                    d="M12 6.4 15.8 10.1 12 16.3 8.2 10.1 12 6.4Z"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linejoin="round"
                />
            </svg>
        `
        : mode === PICTURE_DETECTIVES_MODE
          ? `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path
                    d="M10.75 4.25a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13Z"
                    fill="currentColor"
                    fill-opacity="0.12"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linejoin="round"
                />
                <path
                    d="m15.35 15.35 4.4 4.4"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.15"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M8.2 10.15c.62-.9 1.47-1.35 2.55-1.35.82 0 1.52.24 2.1.72"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.45"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
            </svg>
        `
          : `
            <svg class="${className}" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <rect
                    x="4.25"
                    y="8.5"
                    width="15.5"
                    height="11"
                    rx="2"
                    fill="currentColor"
                    fill-opacity="0.14"
                    stroke="currentColor"
                    stroke-width="1.8"
                />
                <path
                    d="M8.25 8.5V6.7a3.75 3.75 0 0 1 7.5 0v1.8"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                />
                <path
                    d="M12 12.2v3.2m-1.45-1.6h2.9"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.9"
                    stroke-linecap="round"
                />
            </svg>
        `;

const renderHostModeBackIcon = (): string => `
    <svg class="host-mode-return-glyph" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path
            d="M14.75 5.75 8.5 12l6.25 6.25"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
        />
    </svg>
`;

const renderHostModeButton = (mode: RoomMode): string => {
    if (mode === PLANET_HOPPERS_MODE) { const description = localizeModeDescription(mode) ?? t("planetHoppersDescription"); const selected = state.hostDraft.mode === mode && !isHostModeSelectionOpen(); return `<button id="select-planet-hoppers-button" class="ph-mode-button ${selected ? "selected" : ""}" type="button" aria-expanded="${selected ? "true" : "false"}" aria-controls="host-mode-card-body"><span class="relic-mode-marquee"><span class="relic-mode-title">Planet Hoppers</span><span class="relic-mode-emblem" aria-hidden="true">${renderModeIcon(mode,"relic-mode-emblem-icon")}</span></span><span class="relic-mode-kicker">${renderModeDescriptionMarkup(mode, description)}</span></button>`; }
    const isRelicRun = mode === RELIC_RUN_MODE;
    const isPictionary = mode === PICTURE_DETECTIVES_MODE;
    const isVaultBreakers = mode === VAULT_BREAKERS_MODE;
    const isSelected = state.hostDraft.mode === mode && !isHostModeSelectionOpen();
    const buttonId = isRelicRun
        ? "select-relic-run-button"
        : isPictionary
          ? "select-picture-detectives-button"
          : "select-vault-breakers-button";
    const buttonClass = isRelicRun
        ? "relic-mode-button"
        : isPictionary
          ? "pictionary-mode-button"
          : "vault-mode-button";
    const buttonLabel =
        localizeModeLabel(mode) ??
        (isRelicRun ? t("relicRun") : isPictionary ? t("pictureDetectives") : t("vaultBreakers"));
    const buttonDescription =
        localizeModeDescription(mode) ??
        (isRelicRun
            ? t("relicRunDescription")
            : isPictionary
              ? t("pictureDetectivesDescription")
              : t("vaultBreakersDescription"));
    const titleClass = [
        "relic-mode-title",
        isPictionary ? "pictionary-mode-title" : "",
        isVaultBreakers ? "vault-mode-title" : ""
    ]
        .filter(Boolean)
        .join(" ");

    return `
        <button
            id="${buttonId}"
            class="${buttonClass} ${isSelected ? "selected" : ""}"
            type="button"
            aria-expanded="${isSelected && !isHostModeSelectionOpen() ? "true" : "false"}"
            aria-controls="host-mode-card-body"
            title="${escapeHtml(buttonDescription)}"
        >
            <span class="relic-mode-marquee">
                <span class="${titleClass}">${escapeHtml(buttonLabel)}</span>
                <span class="relic-mode-emblem" aria-hidden="true">${renderModeIcon(mode, "relic-mode-emblem-icon")}</span>
            </span>
            <span class="relic-mode-kicker">${renderModeDescriptionMarkup(mode, buttonDescription)}</span>
        </button>
    `;
};

const getSessionId = (roomCode: string, role: SessionRole): string => {
    const key = STORAGE_KEYS.session(roomCode, role);
    const existing = readStoredValue(key);

    if (existing) {
        return existing;
    }

    const created = createClientId();
    writeStoredValue(key, created);
    return created;
};

const buildRoomUrl = (roomCode: string, hostToken: string | null, role: SessionRole = "player"): string =>
    hostToken
        ? `/?room=${encodeURIComponent(roomCode)}&host=${encodeURIComponent(hostToken)}`
        : role === "controller"
          ? `/?room=${encodeURIComponent(roomCode)}&controller=1`
          : `/?room=${encodeURIComponent(roomCode)}`;

const buildJoinLink = (roomCode: string): string =>
    `${window.location.origin}${joinPagePath(roomCode)}`;

const buildControllerLink = (roomCode: string): string =>
    `${window.location.origin}/?room=${encodeURIComponent(roomCode)}&controller=1`;

const buildHostLink = (roomCode: string, hostToken: string | null): string =>
    `${window.location.origin}${buildRoomUrl(roomCode, hostToken, "host")}`;

const formatRoomScopedLabel = (roomCode: string, label: string): string =>
    roomCode ? `${roomCode} ${label}` : label;

const destroyInteractiveViews = () => {
    if (mazeView) {
        mazeView.destroy();
        mazeView = null;
    }

    if (pictionaryView) {
        pictionaryView.destroy();
        pictionaryView = null;
    }

    if (planetHoppersView) { planetHoppersView.destroy(); planetHoppersView = null; }
    if (planetHoppersEditor) { planetHoppersEditor.destroy(); planetHoppersEditor = null; planetEditorRoot = null; }
    if (vaultBreakersView) {
        vaultBreakersView.destroy();
        vaultBreakersView = null;
    }

    if (lobbyPlayground) {
        lobbyPlayground.destroy();
        lobbyPlayground = null;
    }

    if (randomTeamsToolView) {
        randomTeamsToolView.destroy();
        randomTeamsToolView = null;
    }

    if (wordListToolView) {
        wordListToolView.destroy();
        wordListToolView = null;
    }

    if (vocabularyReviewToolView) {
        vocabularyReviewToolView.destroy();
        vocabularyReviewToolView = null;
    }

    if (autotranslatorToolView) {
        autotranslatorToolView.destroy();
        autotranslatorToolView = null;
    }
};

const buildSocketUrl = (connection: ConnectionDetails): string => {
    const url = new URL(`/api/rooms/${encodeURIComponent(connection.roomCode)}/socket`, window.location.origin);
    url.protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    url.searchParams.set("name", connection.name);
    url.searchParams.set("sessionId", connection.sessionId);

    if (connection.hostToken) {
        url.searchParams.set("hostToken", connection.hostToken);
    }

    if (connection.role === "controller") {
        url.searchParams.set("controller", "1");
    }

    return url.toString();
};

const debounce = <Args extends unknown[]>(callback: (...args: Args) => void, delay: number) => {
    let timer: number | null = null;

    return (...args: Args) => {
        if (timer !== null) {
            window.clearTimeout(timer);
        }

        timer = window.setTimeout(() => {
            timer = null;
            callback(...args);
        }, delay);
    };
};

class HttpRequestError extends Error {
    constructor(
        message: string,
        readonly status: number
    ) {
        super(message);
        this.name = "HttpRequestError";
    }
}

const fetchJson = async <T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> => {
    const response = await fetch(input, init);
    const data = (await response.json().catch(() => ({}))) as { error?: string };

    if (!response.ok) {
        throw new HttpRequestError(data.error ?? "Request failed.", response.status);
    }

    return data as T;
};

const fetchAccountJsonWithRetry = async <T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> => {
    let lastError: unknown = null;

    for (let attempt = 0; attempt <= ACCOUNT_LOAD_RETRY_DELAYS_MS.length; attempt += 1) {
        try {
            return await fetchJson<T>(input, init);
        } catch (error) {
            lastError = error;
            const delayMs = ACCOUNT_LOAD_RETRY_DELAYS_MS[attempt];
            const isTransient = !(error instanceof HttpRequestError) || error.status >= 500;

            if (!isTransient || delayMs === undefined) {
                throw error;
            }

            logClientDebug("account load retry", {
                attempt: attempt + 1,
                delayMs,
                error: error instanceof Error ? error.message : "unknown"
            });
            await new Promise<void>((resolve) => window.setTimeout(resolve, delayMs));
        }
    }

    throw lastError instanceof Error ? lastError : new Error("Could not load account.");
};

const showError = (message: string) => {
    state.hostStartPending = false;
    clearHostStartLoadingOverlay();
    state.error = message;
    state.info = "";
    updateUi();
};

const clearBanners = () => {
    state.error = "";
    state.info = "";
};

let copyFeedbackTimer: number | null = null;

const clearCopyFeedback = () => {
    if (copyFeedbackTimer !== null) {
        window.clearTimeout(copyFeedbackTimer);
        copyFeedbackTimer = null;
    }

    app.querySelectorAll<HTMLElement>("[data-copy].is-copy-confirmed").forEach((button) => {
        button.classList.remove("is-copy-confirmed");
        button.removeAttribute("data-copy-feedback-label");
    });
};

const showCopyFeedback = (button: HTMLElement) => {
    clearCopyFeedback();
    button.setAttribute("data-copy-feedback-label", t("copyConfirmed"));
    button.classList.add("is-copy-confirmed");

    copyFeedbackTimer = window.setTimeout(() => {
        button.classList.remove("is-copy-confirmed");
        button.removeAttribute("data-copy-feedback-label");
        copyFeedbackTimer = null;
    }, 1400);
};

// UX note for future Codex passes:
// See docs/design-guidelines.md for the compiled project-specific brief.
// Favor consolidating related UI into the surface it affects instead of splitting it into lots of explicit,
// separately labeled panels. Aim for intuitive, compact, aesthetic UX. If something only works when paired
// with an explanatory label, the interaction likely needs to be clearer first.

const renderLanguageToggleMarkup = (): string => {
    const isKorean = getLanguage() === "ko";

    return `
        <button
            class="language-toggle ${isKorean ? "is-korean" : "is-english"}"
            type="button"
            role="switch"
            aria-checked="${isKorean ? "true" : "false"}"
            data-language-toggle
            aria-label="${escapeHtml(t("toggleLanguageAria"))}"
            title="${escapeHtml(t("toggleLanguageAria"))}"
        >
            <span class="language-toggle-track" aria-hidden="true">
                <span class="language-toggle-thumb"></span>
                <span class="language-toggle-option language-toggle-option-en">Eng</span>
                <span class="language-toggle-option language-toggle-option-ko">\uD55C\uAD6D\uC5B4</span>
            </span>
        </button>
    `;
};

const renderBannerMarkup = (options: { showInfo?: boolean } = {}): string => {
    const rawMessage = state.error || (options.showInfo === false ? "" : state.info);

    if (!rawMessage) {
        return "";
    }

    const tone = state.error ? "error" : "info";
    const message = localizeRuntimeText(rawMessage);
    const accessibilityAttributes =
        tone === "error" ? 'role="alert" aria-live="assertive"' : 'role="status" aria-live="polite"';
    return `<div class="banner banner-${tone}" ${accessibilityAttributes}>${escapeHtml(message)}</div>`;
};

let googleIdentityScriptPromise: Promise<void> | null = null;

const getAccountDisplayName = (): string => {
    const account = state.account.account;

    if (!account) {
        return t("signIn");
    }

    return account.name.trim() || account.email;
};

const getAccountInitial = (): string => {
    const label = getAccountDisplayName().trim();

    return (label[0] ?? "G").toUpperCase();
};

const getAccountControlSignature = (): string =>
    [
        getLanguage(),
        state.account.account?.accountId ?? "signed-out",
        getAccountInitial(),
        state.account.loading ? "loading" : "ready",
        state.account.googleClientId ? "google-ready" : "google-missing",
        state.account.error
    ].join("|");

const renderSignedOutAccountControlMarkup = (): string => {
    if (state.account.loading) {
        return `
            <span class="account-google-placeholder" aria-label="${escapeHtml(t("signIn"))}" title="${escapeHtml(t("signIn"))}">
                <span class="spinner" aria-hidden="true"></span>
                <span>${escapeHtml(t("signIn"))}</span>
            </span>
        `;
    }

    if (!state.account.googleClientId) {
        return `
            <button
                class="account-google-placeholder is-unavailable"
                type="button"
                aria-label="${escapeHtml(t("googleSignInUnavailable"))}"
                title="${escapeHtml(t("googleSignInUnavailable"))}"
                disabled
            >
                <span class="account-google-face-mark" aria-hidden="true">${GOOGLE_SIGN_IN_MARK_ICON}</span>
                <span>${escapeHtml(t("signIn"))}</span>
            </button>
        `;
    }

    const localizedError = localizeRuntimeText(state.account.error);

    return `
        <div
            class="account-google-direct"
            aria-label="${escapeHtml(t("signIn"))}"
            title="${escapeHtml(t("signIn"))}"
        >
            <span class="account-google-direct-face" aria-hidden="true">
                <span class="account-google-face-mark">${GOOGLE_SIGN_IN_MARK_ICON}</span>
                <span>${escapeHtml(t("signIn"))}</span>
            </span>
            <span
                class="google-signin-slot account-google-signin-slot account-google-direct-action"
                data-google-signin-button
                data-google-signin-variant="direct"
            ></span>
        </div>
        ${
            localizedError
                ? `<span class="account-google-error-dot" aria-label="${escapeHtml(localizedError)}" title="${escapeHtml(
                      localizedError
                  )}">!</span>`
                : ""
        }
    `;
};

const renderSignedInAccountControlMarkup = (): string => {
    const label = t("account");
    const title = getAccountDisplayName();

    return `
        <button
            class="account-chip is-signed-in"
            type="button"
            data-account-button
            aria-label="${escapeHtml(title)}"
            title="${escapeHtml(title)}"
        >
            <span class="account-chip-avatar" aria-hidden="true">${escapeHtml(getAccountInitial())}</span>
            <span class="account-chip-label">${escapeHtml(label)}</span>
        </button>
    `;
};

const renderAccountButtonMarkup = (): string => {
    const signedIn = Boolean(state.account.account);
    const signedOutClass = signedIn ? "" : " is-google-signin";

    return `
        <div
            class="account-menu-root${signedOutClass}"
            data-account-menu-root
            data-account-signature="${escapeHtml(getAccountControlSignature())}"
        >
            ${signedIn ? renderSignedInAccountControlMarkup() : renderSignedOutAccountControlMarkup()}
        </div>
    `;
};

const syncAccountControls = () => {
    const signature = getAccountControlSignature();

    app.querySelectorAll<HTMLElement>("[data-account-menu-root]").forEach((root) => {
        if (root.dataset.accountSignature === signature) {
            return;
        }

        root.outerHTML = renderAccountButtonMarkup();
    });

    renderGoogleSignInButtons();
};

const applyAccountSnapshot = (snapshot: AccountSnapshotResponse) => {
    state.account.account = snapshot.account;
    void vocabularyDraftSync.restore(snapshot.account?.accountId ?? null);
    state.account.preferences = snapshot.preferences ?? {};
    state.account.uploads = Array.isArray(snapshot.uploads) ? snapshot.uploads : [];
    state.account.hostedGames = Array.isArray(snapshot.hostedGames) ? snapshot.hostedGames : [];
    state.account.playedSources = snapshot.playedSources ?? {};
    state.account.sourceHistory = Array.isArray(snapshot.sourceHistory)
        ? snapshot.sourceHistory
        : Object.values(state.account.playedSources).filter(
              (item): item is AccountPlayedSourceItem => Boolean(item)
          );
    state.account.wordLists = Array.isArray(snapshot.wordLists) ? snapshot.wordLists : [];
    state.account.error = "";

    const hostedGameIds = new Set(state.account.hostedGames.map((item) => item.id));
    [...expandedAccountHostedGameIds].forEach((id) => {
        if (!hostedGameIds.has(id)) {
            expandedAccountHostedGameIds.delete(id);
        }
    });
};

const getPreferenceForMode = (mode: RoomMode | null): AccountModePreference | null =>
    mode ? state.account.preferences[mode] ?? null : null;

const applyAccountPreferencesToHostDraft = (options: { preserveMode?: boolean } = {}) => {
    if (!state.account.account) {
        return;
    }

    const previousMode = state.hostDraft.mode;
    const relicPreference = getPreferenceForMode(RELIC_RUN_MODE);
    const pictionaryPreference = getPreferenceForMode(PICTURE_DETECTIVES_MODE);
    const vaultPreference = getPreferenceForMode(VAULT_BREAKERS_MODE);
    const nextDraft: HostDraft = {
        ...state.hostDraft
    };

    if (relicPreference) {
        nextDraft.relicSourceText = relicPreference.sourceText ?? "";
        nextDraft.singleLineMode = relicPreference.singleLineMode;
        nextDraft.hostParticipates = relicPreference.hostParticipates;
    }

    if (pictionaryPreference) {
        nextDraft.pictionarySourceText = pictionaryPreference.sourceText ?? "";
        nextDraft.pictionaryNoHints = pictionaryPreference.pictionaryNoHints;
        nextDraft.pictionaryNoTranslations = pictionaryPreference.pictionaryNoTranslations;
        nextDraft.pictionarySameStem = pictionaryPreference.pictionarySameStem;
        nextDraft.pictionaryHostIsController = pictionaryPreference.pictionaryHostIsController;
    }

    if (vaultPreference) {
        nextDraft.vaultSourceText = vaultPreference.sourceText ?? "";
    }

    if (options.preserveMode !== false && previousMode === RELIC_RUN_MODE && relicPreference) {
        nextDraft.sourceText = nextDraft.relicSourceText;
    } else if (options.preserveMode !== false && previousMode === PICTURE_DETECTIVES_MODE && pictionaryPreference) {
        nextDraft.sourceText = nextDraft.pictionarySourceText;
        syncHostDraftPictionarySourceText(nextDraft);
    } else if (options.preserveMode !== false && previousMode === VAULT_BREAKERS_MODE && vaultPreference) {
        nextDraft.sourceText = nextDraft.vaultSourceText;
        syncHostDraftVaultSourceText(nextDraft);
    } else if (options.preserveMode === false) {
        nextDraft.mode = previousMode;
        nextDraft.sourceText =
            previousMode === RELIC_RUN_MODE
                ? nextDraft.relicSourceText
                : previousMode === PICTURE_DETECTIVES_MODE
                  ? nextDraft.pictionarySourceText
                  : previousMode === VAULT_BREAKERS_MODE
                    ? nextDraft.vaultSourceText
                  : "";

        if (previousMode === PICTURE_DETECTIVES_MODE) {
            syncHostDraftPictionarySourceText(nextDraft);
        } else if (previousMode === VAULT_BREAKERS_MODE) {
            syncHostDraftVaultSourceText(nextDraft);
        }
    }

    state.hostDraft = nextDraft;
};

const buildAccountPreferencePayload = (mode: RoomMode): AccountModePreference => {
    const sourceText =
        mode === PLANET_HOPPERS_MODE ? state.hostDraft.sourceText : mode === RELIC_RUN_MODE
            ? state.hostDraft.mode === RELIC_RUN_MODE
                ? state.hostDraft.sourceText
                : state.hostDraft.relicSourceText
            : mode === PICTURE_DETECTIVES_MODE
              ? state.hostDraft.mode === PICTURE_DETECTIVES_MODE
                  ? state.hostDraft.sourceText
                  : state.hostDraft.pictionarySourceText
              : state.hostDraft.mode === VAULT_BREAKERS_MODE
                ? state.hostDraft.sourceText
                : state.hostDraft.vaultSourceText;
    const existing = state.account.preferences[mode];

    return {
        sourceText,
        singleLineMode: state.hostDraft.singleLineMode,
        hostParticipates: state.hostDraft.hostParticipates,
        pictionaryNoHints: state.hostDraft.pictionaryNoHints,
        pictionaryNoTranslations: state.hostDraft.pictionaryNoTranslations,
        pictionarySameStem: state.hostDraft.pictionarySameStem,
        pictionaryHostIsController: state.hostDraft.pictionaryHostIsController,
        updatedAt: existing?.updatedAt ?? Date.now()
    };
};

const saveAccountPreference = async (mode: RoomMode | null = state.hostDraft.mode) => {
    if (!state.account.account || !mode) {
        return;
    }

    try {
        const snapshot = await fetchJson<AccountSnapshotResponse>("/api/account/preferences", {
            method: "POST",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                mode,
                preference: buildAccountPreferencePayload(mode)
            })
        });
        applyAccountSnapshot(snapshot);
    } catch (error) {
        logClientDebug("account preference save failed", {
            error: error instanceof Error ? error.message : "unknown"
        });
    }
};

const saveAccountPreferenceDebounced = debounce((mode: RoomMode | null) => {
    void saveAccountPreference(mode);
}, ACCOUNT_PREFERENCE_SAVE_DELAY_MS);

const scheduleAccountPreferenceSave = (mode: RoomMode | null = state.hostDraft.mode) => {
    saveAccountPreferenceDebounced(mode);
};

const accountLanguageSync = new AccountLanguageSync({
    getLanguage,
    applyLanguage: (language) => {
        if (getLanguage() === language) return;
        setLanguage(language);
        rerenderForLanguageChange();
    },
    save: (write) => fetchJson<{ language: unknown }>("/api/account/language", {
        method: "POST",
        credentials: "same-origin",
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(write)
    }),
    onError: (error) => logClientDebug("account language save failed", {
        error: error instanceof Error ? error.message : "unknown"
    })
});

let startupLanguageChoiceMade = false;

const loadAccountSnapshot = async (languageReady: Promise<void> = Promise.resolve()) => {
    state.account.loading = true;
    state.account.error = "";
    vocabularyDraftSync.setIdentityStatus("loading");

    try {
        const [config, snapshot] = await Promise.all([
            fetchAccountJsonWithRetry<AccountConfigResponse>("/api/auth/config", {
                credentials: "same-origin",
                cache: "no-store"
            }),
            fetchAccountJsonWithRetry<AccountSnapshotResponse>("/api/account", {
                credentials: "same-origin",
                cache: "no-store"
            })
        ]);
        await languageReady;
        state.account.googleClientId = typeof config.googleClientId === "string" ? config.googleClientId : "";
        applyAccountSnapshot(snapshot);
        const preserveStartupChoice = startupLanguageChoiceMade && !state.account.initialized;
        await accountLanguageSync.restore(snapshot.account?.accountId ?? null,
            preserveStartupChoice ? getLanguage() : snapshot.language);
        if (preserveStartupChoice && snapshot.account) void accountLanguageSync.changed(getLanguage());

        if (snapshot.account && !state.hostDraftDirty && state.room?.phase !== "active") {
            applyAccountPreferencesToHostDraft({ preserveMode: state.hostDraft.mode !== null });
        }
    } catch (error) {
        state.account.error = error instanceof Error ? error.message : "Could not load account.";
        vocabularyDraftSync.setIdentityStatus("identity-error");
    } finally {
        state.account.loading = false;
        state.account.initialized = true;
    }
};

const ensureGoogleIdentityScript = async (): Promise<void> => {
    const googleWindow = window as GoogleIdentityWindow;

    if (googleWindow.google?.accounts?.id) {
        return;
    }

    if (!googleIdentityScriptPromise) {
        googleIdentityScriptPromise = new Promise<void>((resolve, reject) => {
            const existingScript = document.querySelector<HTMLScriptElement>(
                `script[src="${GOOGLE_IDENTITY_SCRIPT_URL}"]`
            );

            if (existingScript) {
                existingScript.addEventListener("load", () => resolve(), { once: true });
                existingScript.addEventListener("error", () => reject(new Error("Could not load Google sign-in.")), {
                    once: true
                });
                return;
            }

            const script = document.createElement("script");
            script.src = GOOGLE_IDENTITY_SCRIPT_URL;
            script.async = true;
            script.defer = true;
            script.addEventListener("load", () => resolve(), { once: true });
            script.addEventListener("error", () => reject(new Error("Could not load Google sign-in.")), {
                once: true
            });
            document.head.append(script);
        });
    }

    await googleIdentityScriptPromise;
};

const signInWithGoogleCredential = async (credential: string) => {
    const wasSignedIn = Boolean(state.account.account);

    state.account.loading = true;
    state.account.error = "";
    updateUi();

    try {
        const snapshot = await fetchJson<AccountSnapshotResponse>("/api/auth/google", {
            method: "POST",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ credential })
        });
        applyAccountSnapshot(snapshot);
        await accountLanguageSync.restore(snapshot.account?.accountId ?? null, snapshot.language);
        if (!wasSignedIn && snapshot.account) {
            roomAudio.playAccountSignIn();
        }
        if (!state.hostDraftDirty && !state.room) {
            applyAccountPreferencesToHostDraft({ preserveMode: state.hostDraft.mode !== null });
        }
        if (state.hostDraft.mode) {
            scheduleAccountPreferenceSave(state.hostDraft.mode);
        }
        clearBanners();
    } catch (error) {
        state.account.error = error instanceof Error ? error.message : "Could not sign in.";
    } finally {
        state.account.loading = false;
        state.account.initialized = true;
        updateUi();
    }
};

const renderGoogleSignInButtons = () => {
    if (!state.account.googleClientId || state.account.account) {
        return;
    }

    const targets = Array.from(app.querySelectorAll<HTMLElement>("[data-google-signin-button]"));

    if (targets.length === 0) {
        return;
    }

    void ensureGoogleIdentityScript()
        .then(() => {
            const googleId = (window as GoogleIdentityWindow).google?.accounts?.id;

            if (!googleId) {
                throw new Error("Could not load Google sign-in.");
            }

            googleId.initialize({
                client_id: state.account.googleClientId,
                callback: (response) => {
                    if (response.credential) {
                        void signInWithGoogleCredential(response.credential);
                    }
                },
                auto_select: false,
                cancel_on_tap_outside: true
            });

            targets.forEach((target) => {
                if (target.dataset.googleRendered === "true") {
                    return;
                }

                target.dataset.googleRendered = "true";
                const isDirectHeaderButton = target.dataset.googleSigninVariant === "direct";

                googleId.renderButton(
                    target,
                    {
                        theme: "outline",
                        size: "large",
                        shape: "pill",
                        text: isDirectHeaderButton ? "signin" : "signin_with",
                        width: isDirectHeaderButton
                            ? Math.min(124, Math.max(96, target.clientWidth || 104))
                            : Math.min(320, Math.max(220, target.clientWidth || 260))
                    }
                );
            });
        })
        .catch((error: unknown) => {
            state.account.error = error instanceof Error ? error.message : "Could not load Google sign-in.";
            updateUi();
        });
};

const signOutAccount = async () => {
    await vocabularyDraftSync.flush();
    accountLanguageSync.disconnect();
    const wasSignedIn = Boolean(state.account.account);

    state.account.loading = true;
    state.account.error = "";
    updateUi();

    try {
        await fetchJson<{ ok: boolean }>("/api/auth/logout", {
            method: "POST",
            credentials: "same-origin"
        });
        (window as GoogleIdentityWindow).google?.accounts?.id?.disableAutoSelect?.();
        applyAccountSnapshot({
            account: null,
            preferences: {},
            uploads: [],
            hostedGames: [],
            playedSources: {},
            sourceHistory: [],
            wordLists: []
        });
        if (wasSignedIn) {
            roomAudio.playAccountSignOut();
        }
    } catch (error) {
        state.account.error = error instanceof Error ? error.message : "Could not sign out.";
        await accountLanguageSync.restore(state.account.account?.accountId ?? null, getLanguage());
    } finally {
        state.account.loading = false;
        state.account.initialized = true;
        updateUi();
    }
};

const getRecentUploadsForMode = (mode: RoomMode | null, count = ACCOUNT_RECENT_UPLOAD_COUNT): AccountUploadItem[] =>
    mode
        ? state.account.uploads
              .filter((upload) => upload.mode === mode)
              .sort((left, right) => right.uploadedAt - left.uploadedAt)
              .slice(0, count)
        : [];

const leaveAccountForHostSetup = () => {
    if (state.screen !== "account") {
        return;
    }

    state.screen = state.room && state.currentConnection ? "room" : "landing";
    state.hostPanelExpanded = !state.room;
    state.toolsPanelExpanded = false;
    clearBanners();
    window.history.replaceState(
        {},
        "",
        state.room && state.currentConnection
            ? buildRoomUrl(
                  state.currentConnection.roomCode,
                  state.currentConnection.hostToken,
                  state.currentConnection.role
              )
            : "/"
    );
    scheduleViewportTopReset();
};

const applyAccountUploadToHostDraft = (upload: AccountUploadItem) => {
    if (upload.mode === PLANET_HOPPERS_MODE) {
        loadPHLocalDraft(upload.sourceText);
        planetHoppersEditor?.loadSource(upload.sourceText);
    }
    state.hostDraft.mode = upload.mode;
    state.hostModePanelView = "config";
    state.hostSourceImportOpen = false;
    resetHostSourceTranslation();
    state.hostDraft.sourceText = upload.sourceText;

    if (upload.mode === PICTURE_DETECTIVES_MODE) {
        state.hostDraft.pictionarySourceText = upload.sourceText;
        syncHostDraftPictionarySourceText();
    } else if (upload.mode === VAULT_BREAKERS_MODE) {
        state.hostDraft.vaultSourceText = upload.sourceText;
        syncHostDraftVaultSourceText();
    } else if (upload.mode !== PLANET_HOPPERS_MODE) {
        state.hostDraft.relicSourceText = upload.sourceText;
    }

    state.hostDraftDirty = true;
    if (upload.mode !== PLANET_HOPPERS_MODE) scheduleAccountPreferenceSave(upload.mode);

    if (state.room) {
        updateHostLobby(state.room);
        pushHostDraftDebounced();
    } else {
        updateUi();
    }
};

const applyAccountPlayedSourceToHostDraft = (source: AccountPlayedSourceItem) => {
    leaveAccountForHostSetup();
    applyAccountUploadToHostDraft({
        id: source.id,
        mode: source.mode,
        fileName: getPlayedSourceTitle(source.sourceText),
        fileType: "text/plain",
        size: source.sourceText.length,
        sourceText: source.sourceText,
        uploadedAt: source.playedAt
    });
    if (!state.room) pendingHistoryDraft = structuredClone(state.hostDraft);
};

const recordAccountUpload = async (file: File, sourceText: string) => {
    if (!state.account.account || !state.hostDraft.mode) {
        return;
    }

    try {
        const snapshot = await fetchJson<AccountSnapshotResponse>("/api/account/uploads", {
            method: "POST",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                mode: state.hostDraft.mode,
                fileName: file.name,
                fileType: file.type,
                size: file.size,
                sourceText
            })
        });
        applyAccountSnapshot(snapshot);
    } catch (error) {
        logClientDebug("account upload archive failed", {
            error: error instanceof Error ? error.message : "unknown"
        });
    }
};

const recordHostedGame = async (room: RoomView) => {
    const mode = room.mode;
    const accountId = state.account.account?.accountId;
    const recordKey = `${accountId}:${room.activityId}`;

    if (!accountId || !room.viewer.isHost || !mode || !room.activityId || pendingHostedGameRecords.has(recordKey)) {
        return;
    }
    pendingHostedGameRecords.add(recordKey);
    try {
        const snapshot = await fetchJson<AccountSnapshotResponse>("/api/account/history", {
            method: "POST",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                id: room.activityId,
                startedAt: room.activityStartedAt,
                mode,
                roomCode: room.roomCode,
                playerCount: getAuthoritativeLobbyPlayerCount(room),
                sourceText: room.setup.sourceText
            })
        });
        if (state.account.account?.accountId !== accountId) return;
        applyAccountSnapshot(snapshot);
        const recordedItem =
            snapshot.hostedGames.find((item) => item.id === room.activityId) ??
            snapshot.hostedGames[0] ??
            null;
        activeHostedGameHistoryId = recordedItem?.id ?? null;
        activeHostedGameHistoryRoomCode = room.roomCode;
        recordedHostedGameResultsKey = "";
    } catch (error) {
        logClientDebug("account hosted-game history failed", {
            error: error instanceof Error ? error.message : "unknown"
        });
    } finally { pendingHostedGameRecords.delete(recordKey); }
};

const getHostedGameHistoryIdForRoom = (room: RoomView): string | null => {
    if (room.activityId) return room.activityId;
    if (activeHostedGameHistoryId && activeHostedGameHistoryRoomCode === room.roomCode) {
        return activeHostedGameHistoryId;
    }

    return (
        state.account.hostedGames.find((item) => item.mode === room.mode && item.roomCode === room.roomCode)?.id ?? null
    );
};

const buildHostedGamePlayerResultPayload = (room: RoomView): AccountHostedGamePlayerResult[] =>
    (room.finalResults?.standings ?? []).map((player) => ({
        sessionId: player.sessionId,
        name: player.name,
        placement: player.placement,
        score: player.score,
        elapsedMs: player.elapsedMs
    }));

const getHostedGameResultsRecordKey = (room: RoomView): string => {
    const players = buildHostedGamePlayerResultPayload(room);

    if (room.phase !== "results" || !room.finalResults || players.length === 0) {
        return "";
    }

    return JSON.stringify({
        roomCode: room.roomCode,
        mode: room.finalResults.mode,
        startedAt: room.finalResults.startedAt,
        players
    });
};

const recordHostedGameResults = async (room: RoomView) => {
    if (!state.account.account || !room.viewer.isHost || room.phase !== "results" || !room.finalResults) {
        return;
    }

    const playerResults = buildHostedGamePlayerResultPayload(room);
    const accountId = state.account.account?.accountId;

    if (playerResults.length === 0) {
        return;
    }

    const resultsKey = getHostedGameResultsRecordKey(room);

    if (!resultsKey || resultsKey === recordedHostedGameResultsKey) {
        return;
    }

    recordedHostedGameResultsKey = resultsKey;

    try {
        const snapshot = await fetchJson<AccountSnapshotResponse>("/api/account/history", {
            method: "POST",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                id: getHostedGameHistoryIdForRoom(room),
                startedAt: room.activityStartedAt,
                mode: room.finalResults.mode,
                roomCode: room.roomCode,
                playerCount: room.finalResults.competitorCount,
                sourceText: room.setup.sourceText,
                playerResults
            })
        });
        if (state.account.account?.accountId !== accountId) return;
        applyAccountSnapshot(snapshot);
        activeHostedGameHistoryId =
            snapshot.hostedGames.find((item) => item.mode === room.finalResults?.mode && item.roomCode === room.roomCode)?.id ??
            activeHostedGameHistoryId;
        activeHostedGameHistoryRoomCode = room.roomCode;
    } catch (error) {
        recordedHostedGameResultsKey = "";
        logClientDebug("account hosted-game results failed", {
            error: error instanceof Error ? error.message : "unknown"
        });
    }
};

const recordWordListHistory = async (wordListState: WordListToolState) => {
    if (!state.account.account) {
        return;
    }

    const entries = wordListState.entries
        .map((entry) => ({
            id: entry.id,
            term: entry.term,
            context: entry.context,
            output: entry.output
        }))
        .filter((entry) => entry.term.trim().length > 0);

    if (entries.length === 0) {
        return;
    }

    try {
        const snapshot = await fetchJson<AccountSnapshotResponse>("/api/account/word-lists", {
            method: "POST",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                id: wordListState.accountWordListId,
                title: wordListState.title,
                mode: wordListState.mode,
                customTemplate: wordListState.customTemplate,
                sourceText: wordListState.sourceText,
                entries
            })
        });
        applyAccountSnapshot(snapshot);
        const savedList = state.account.wordLists.find(
            (item) =>
                item.mode === wordListState.mode &&
                item.title === wordListState.title &&
                item.sourceText === wordListState.sourceText
        );
        wordListState.accountWordListId = savedList?.id ?? wordListState.accountWordListId;
    } catch (error) {
        logClientDebug("account word-list history failed", {
            error: error instanceof Error ? error.message : "unknown"
        });
    }
};

const applyAccountWordListToTool = (wordList: AccountWordListItem) => {
    const fallbackState = createInitialWordListToolState();
    state.wordListTool = {
        ...fallbackState,
        accountWordListId: wordList.id,
        sourceText: wordList.sourceText,
        title: wordList.title || fallbackState.title,
        mode: wordList.mode,
        customTemplate: wordList.customTemplate || fallbackState.customTemplate,
        entries: Array.isArray(wordList.entries) ? wordList.entries.map((entry) => ({ ...entry })) : [],
        loading: false,
        error: ""
    };
    openWordListTool();
};

const getParticipantRenameButtonId = (sessionId: string): string =>
    `${PARTICIPANT_RENAME_BUTTON_ID_PREFIX}${sessionId}`;

const getParticipantActionsKey = (sessionId: string): string => `room-participant:${sessionId}`;

const getParticipantActionsTriggerId = (sessionId: string): string =>
    `${PARTICIPANT_ACTIONS_TRIGGER_ID_PREFIX}${sessionId}`;

const getRenameTargetParticipant = (room = state.room): RoomParticipantView | null => {
    if (!room) {
        return null;
    }

    const targetSessionId = state.renameDialogSessionId ?? room.viewer.sessionId;

    if (targetSessionId === room.viewer.sessionId) {
        return {
            sessionId: room.viewer.sessionId,
            name: room.viewer.name,
            connected: true,
            completed: false,
            isController: room.viewer.isController,
            renamePending: room.viewer.renamePending,
            isAway: false,
            awayReason: null
        };
    }

    return room.participants.find((participant) => participant.sessionId === targetSessionId) ?? null;
};

const renderParticipantStatusBadges = (participant: RoomParticipantView): string => {
    const badges: string[] = [];

    if (participant.completed) {
        badges.push(
            `<span class="participant-badge participant-badge-complete" title="${escapeHtml(
                t("escapedShort")
            )}" aria-label="${escapeHtml(t("escapedShort"))}">◆</span>`
        );
    }

    if (participant.isAway || !participant.connected) {
        const awayReason = participant.awayReason ?? (!participant.connected ? "disconnected" : "away");

        badges.push(
            `<span class="participant-badge participant-badge-away" data-away-reason="${escapeHtml(
                awayReason
            )}" title="${escapeHtml(
                t("away")
            )}" aria-label="${escapeHtml(t("away"))}">${escapeHtml(t("away"))}</span>`
        );
    }

    return badges.length > 0 ? `<span class="participant-statuses">${badges.join("")}</span>` : "";
};

const renderParticipantRows = (
    participants: RoomParticipantView[],
    emptyMessage: string,
    options: {
        allowHostRename?: boolean;
        allowHostPromptRename?: boolean;
        allowHostKick?: boolean;
        openActionsKey?: string | null;
    } = {}
): string => {
    if (participants.length === 0) {
        return `<p class="list-empty">${escapeHtml(emptyMessage)}</p>`;
    }

    return `
        <ul class="${getPlayerRosterListClass("participant-list", participants.length)}">
            ${participants
                .map((participant) => {
                    const isAway = participant.isAway || !participant.connected;
                    const actionsKey = getParticipantActionsKey(participant.sessionId);
                    const displayName = getPlayerRosterDisplayName(
                        participant.name,
                        participant.renamePending,
                        t("renamePendingName")
                    );
                    const actions: Parameters<typeof renderPlayerRosterActionsMenu>[1] = [
                        ...(options.allowHostRename
                            ? [
                                  {
                                      attributes: {
                                          id: getParticipantRenameButtonId(participant.sessionId),
                                          "data-rename-participant": participant.sessionId,
                                          "aria-label": `${t("editName")}: ${displayName}`,
                                          title: `${t("editName")}: ${displayName}`
                                      },
                                      className: "participant-rename-button",
                                      label: t("editName")
                                  }
                              ]
                            : []),
                        ...(options.allowHostPromptRename
                            ? [
                                  {
                                      attributes: {
                                          "data-prompt-rename-participant": participant.sessionId,
                                          "aria-label": `${t("askRename")}: ${displayName}`,
                                          title: `${t("askRename")}: ${displayName}`
                                      },
                                      className: "participant-rename-prompt-button",
                                      label: t("askRename")
                                  }
                              ]
                            : []),
                        ...(options.allowHostKick
                            ? [
                                  {
                                      attributes: {
                                          "data-kick-participant": participant.sessionId,
                                          "aria-label": `${t("kick")}: ${displayName}`,
                                          title: `${t("kick")}: ${displayName}`
                                      },
                                      className: "participant-kick-button",
                                      label: t("kick")
                                  }
                              ]
                            : [])
                    ];

                    return `
                        <li class="participant-row${isAway ? " is-away" : ""}${getPlayerRosterPlacementClass(
                            participant.placement
                        )}${getPlayerRenamePendingClass(participant.renamePending)}">
                            <div class="participant-identity">
                            ${actions.length ? renderPlayerRosterActionsMenu(`${t("playerActions")}: ${displayName}`, actions, {
                                key: actionsKey,
                                open: options.openActionsKey === actionsKey,
                                preferredPlacement: "below",
                                scope: "room-participants",
                                triggerId: getParticipantActionsTriggerId(participant.sessionId),
                                triggerClassName: "participant-name",
                                triggerLabel: displayName
                            }) : `<span class="participant-name">${escapeHtml(displayName)}</span>`}
                            </div>
                            ${renderParticipantStatusBadges(participant)}
                        </li>
                    `;
                })
                .join("")}
        </ul>
    `;
};

const renderLandingMarkup = (disableAnimation = false): string => {
    const hasExpandedPanel = state.hostPanelExpanded || state.toolsPanelExpanded;
    const activeLandingSection = state.hostPanelExpanded ? "host" : state.toolsPanelExpanded ? "tools" : "join";
    const isJoinActive = activeLandingSection === "join";
    const isHostActive = activeLandingSection === "host";
    const isToolsActive = activeLandingSection === "tools";
    const joinTitle = state.joinFromRoomLink ? t("placeName") : t("joinRoom");
    const joinNameLabelClass = state.joinFromRoomLink
        ? "landing-field-label visually-hidden"
        : "landing-field-label";

    return `
        <div class="page-shell landing-shell${disableAnimation ? " page-shell-static" : ""}">
            <header class="brand-header">
                <a class="brand-home brand-mark" href="/" data-home>ZIARCADIA</a>
                <div class="brand-actions">
                    ${renderAccountButtonMarkup()}
                    ${renderLanguageToggleMarkup()}
                </div>
            </header>
            <div id="screen-banner">${renderBannerMarkup()}</div>
            <section class="landing-stack">
                <article class="card landing-card landing-card-join${isJoinActive ? " is-active-section" : ""}">
                    <h2 class="landing-title">${escapeHtml(joinTitle)}</h2>
                    <label class="field">
                        <span class="${joinNameLabelClass}">${escapeHtml(t("name"))}</span>
                        <input
                            id="join-name-input"
                            type="text"
                            maxlength="24"
                            placeholder="${escapeHtml(t("defaultPlayerPlaceholder"))}"
                            value="${escapeHtml(state.joinName)}"
                        />
                    </label>
                    <label class="field">
                        <span class="landing-field-label">${escapeHtml(t("roomCode"))}</span>
                        <input
                            id="join-room-code-input"
                            type="text"
                            maxlength="${ROOM_CODE_MAX_LENGTH}"
                            autocapitalize="characters"
                            placeholder="A7"
                            value="${escapeHtml(state.joinRoomCode)}"
                        />
                    </label>
                    <button
                        id="join-room-button"
                        class="${hasExpandedPanel ? "secondary-button" : "primary-button"} landing-action"
                    >
                        ${escapeHtml(t("joinRoom"))}
                    </button>
                </article>
                <article class="card landing-card landing-host-card ${state.hostPanelExpanded ? "is-expanded" : ""}${
                    isHostActive ? " is-active-section" : ""
                }">
                    ${
                        state.hostPanelExpanded
                            ? `
                                <div class="landing-host-panel">
                                    <div class="landing-panel-head">
                                        <h2 class="landing-title">${escapeHtml(t("hostRoom"))}</h2>
                                        <button
                                            class="panel-collapse-button"
                                            type="button"
                                            data-collapse-panel="host"
                                            aria-label="${escapeHtml(t("minimize"))}"
                                        >
                                            ^
                                        </button>
                                    </div>
                                    <label class="field">
                                        <span class="landing-field-label">${escapeHtml(t("name"))}</span>
                                        <input
                                            id="host-name-input"
                                            type="text"
                                            maxlength="24"
                                            placeholder="${escapeHtml(t("defaultHostPlaceholder"))}"
                                            value="${escapeHtml(state.hostName)}"
                                        />
                                    </label>
                                    <button id="host-room-button" class="primary-button landing-action">${escapeHtml(
                                        t("hostRoom")
                                    )}</button>
                                </div>
                            `
                            : `
                                <button
                                    id="host-expand-button"
                                    class="secondary-button landing-host-toggle"
                                    type="button"
                                    aria-expanded="false"
                                >
                                    ${escapeHtml(t("hostRoom"))}
                                </button>
                            `
                    }
                </article>
                <article class="card landing-card landing-tools-card ${state.toolsPanelExpanded ? "is-expanded" : ""}${
                    isToolsActive ? " is-active-section" : ""
                }">
                    ${
                        state.toolsPanelExpanded
                            ? `
                                <div class="landing-tools-panel">
                                    <div class="landing-panel-head">
                                        <h2 class="landing-title">${escapeHtml(t("tools"))}</h2>
                                        <button
                                            class="panel-collapse-button"
                                            type="button"
                                            data-collapse-panel="tools"
                                            aria-label="${escapeHtml(t("minimize"))}"
                                        >
                                            ^
                                        </button>
                                    </div>
                                     <a
                                         class="landing-tool-option"
                                         href="${SITE_PATHS.randomTeams}"
                                         data-open-random-teams-tool
                                     >
                                        <span class="landing-tool-option-title" data-tool-option-title="random-teams">${escapeHtml(
                                            t("createRandomTeams")
                                        )}</span>
                                        <span class="landing-tool-option-note" data-tool-option-note="random-teams"><em>${escapeHtml(
                                            t("randomTeamsNote")
                                         )}</em></span>
                                     </a>
                                     <a class="landing-tool-option" href="/lyric-learn/">
                                         <span class="landing-tool-option-title">${escapeHtml(t("musicClass"))}</span>
                                         <span class="landing-tool-option-note"><em>${escapeHtml(t("musicClassNote"))}</em></span>
                                     </a>
                                     <a class="landing-tool-option" href="${SITE_PATHS.vocabularyReview}" data-open-vocabulary-review-tool>
                                         <span class="landing-tool-option-title" data-tool-option-title="vocabulary-review">${escapeHtml(t("vocabularyReviewTool"))}</span>
                                         <span class="landing-tool-option-note" data-tool-option-note="vocabulary-review"><em>${escapeHtml(t("vocabularyReviewNote"))}</em></span>
                                     </a>
                                     ${
                                         WORD_LIST_TOOL_DISCOVERABLE
                                             ? `
                                                 <a
                                                     class="landing-tool-option"
                                                     href="${SITE_PATHS.wordList}"
                                                     data-open-word-list-tool
                                                 >
                                                     <span class="landing-tool-option-title" data-tool-option-title="word-list">${escapeHtml(
                                                         t("createWordListTool")
                                                     )}</span>
                                                     <span class="landing-tool-option-note" data-tool-option-note="word-list"><em>${escapeHtml(
                                                         t("wordListToolNote")
                                                     )}</em></span>
                                                 </a>
                                             `
                                             : ""
                                     }
                                     ${
                                         isAutotranslatorAccount()
                                             ? `
                                                 <a
                                                     class="landing-tool-option"
                                                     href="${SITE_PATHS.autotranslator}"
                                                     data-open-autotranslator-tool
                                                 >
                                                     <span class="landing-tool-option-title" data-tool-option-title="autotranslator">${escapeHtml(
                                                         t("createAutotranslatorTool")
                                                     )}</span>
                                                     <span class="landing-tool-option-note" data-tool-option-note="autotranslator"><em>${escapeHtml(
                                                         t("autotranslatorNote")
                                                     )}</em></span>
                                                 </a>
                                             `
                                             : ""
                                     }
                                     <a
                                         class="landing-tool-option"
                                         href="${MASTER1000_CANONICAL_URL}"
                                     >
                                        <span class="landing-tool-option-title" data-tool-option-title="master1000">${escapeHtml(
                                            t("playMaster1000")
                                        )}</span>
                                        <span class="landing-tool-option-note" data-tool-option-note="master1000"><em>${escapeHtml(
                                            t("master1000Note")
                                        )}</em></span>
                                    </a>
                                </div>
                            `
                            : `
                                <button
                                    id="tools-expand-button"
                                    class="secondary-button landing-host-toggle"
                                    type="button"
                                    aria-expanded="false"
                                >
                                    ${escapeHtml(t("tools"))}
                                </button>
                            `
                    }
                </article>
            </section>
            <button
                class="gimcheon-school-secret-hit"
                type="button"
                data-open-gimcheon-school-demo
                aria-label="김천중학교 AI 안내 열기"
                title="김천중학교"
            ></button>
        </div>
    `;
};

const getQrJoinModeClass = (): string =>
    state.qrJoinMode === RELIC_RUN_MODE
        ? " is-relic-selected"
        : state.qrJoinMode === PICTURE_DETECTIVES_MODE
          ? " is-pictionary-selected"
          : "";

const renderQrJoinMarkup = (disableAnimation = false): string => `
    <div class="page-shell qr-join-shell${disableAnimation ? " page-shell-static" : ""}">
        <section class="card qr-join-card${getQrJoinModeClass()}">
            <div id="screen-banner">${renderBannerMarkup()}</div>
            <h1 class="landing-title qr-join-title">${escapeHtml(t("placeName"))}</h1>
            <label class="field qr-join-field">
                <span class="landing-field-label visually-hidden">${escapeHtml(t("name"))}</span>
                <input
                    id="join-name-input"
                    type="text"
                    maxlength="24"
                    autocomplete="nickname"
                    placeholder="${escapeHtml(t("defaultPlayerPlaceholder"))}"
                    value="${escapeHtml(state.joinName)}"
                />
            </label>
            <button
                id="join-room-button"
                type="button"
                class="primary-button landing-action qr-join-action"
            >
                ${escapeHtml(t("join"))}
            </button>
        </section>
    </div>
`;

const renderConnectingMarkup = (): string => `
    <div class="page-shell connecting-shell">
        <section class="card connecting-card">
            <header class="connecting-topbar">
                <a class="brand-home brand-mark compact-brand" href="/" data-home>ZIARCADIA</a>
            </header>
            <div class="spinner" aria-hidden="true"></div>
            <p id="connecting-message" class="connecting-message">${escapeHtml(localizeRuntimeText(state.info || "Connecting..."))}</p>
            <div id="screen-banner">${renderBannerMarkup({ showInfo: false })}</div>
        </section>
    </div>
`;

const renderRandomTeamsToolScreenMarkup = (): string => `
    <div class="page-shell tool-shell random-teams-tool-shell">
        <header class="brand-header tool-brand-header">
            <a class="brand-home brand-mark" href="/" data-home>ZIARCADIA</a>
            <h1 class="random-teams-header-title">${escapeHtml(t("createRandomTeams"))}</h1>
            <div class="brand-actions">
                <a class="secondary-button tool-back-button" href="/" data-tools-back>
                    ${escapeHtml(t("backToTools"))}
                </a>
                ${renderAccountButtonMarkup()}
                ${renderLanguageToggleMarkup()}
            </div>
        </header>
        <div id="screen-banner">${renderBannerMarkup()}</div>
        <section class="tool-shell-stack">
            <div id="tool-content"></div>
        </section>
    </div>
`;

const renderWordListToolScreenMarkup = (): string => `
    <div class="page-shell tool-shell word-list-tool-shell">
        <header class="brand-header tool-brand-header">
            <a class="brand-home brand-mark" href="/" data-home>ZIARCADIA</a>
            <div class="brand-actions">
                ${renderAccountButtonMarkup()}
                ${renderLanguageToggleMarkup()}
            </div>
        </header>
        <div id="screen-banner">${renderBannerMarkup()}</div>
        <section class="tool-shell-stack">
            <article class="card tool-hero-card word-list-hero-card">
                <div class="tool-hero-head">
                    <div class="tool-hero-copy">
                        <h1 class="tool-hero-title">${escapeHtml(t("createWordListTool"))}</h1>
                    </div>
                    <a class="secondary-button tool-back-button" href="/" data-tools-back>
                        ${escapeHtml(t("backToTools"))}
                    </a>
                </div>
            </article>
            <div id="tool-content"></div>
        </section>
    </div>
`;

const renderVocabularyReviewToolScreenMarkup = (): string => `
    <div class="page-shell tool-shell vocabulary-review-tool-shell">
        <header class="brand-header tool-brand-header">
            <a class="brand-home brand-mark" href="/" data-home>ZIARCADIA</a>
            <div class="brand-actions">${renderAccountButtonMarkup()}${renderLanguageToggleMarkup()}</div>
        </header>
        <div id="screen-banner">${renderBannerMarkup()}</div>
        <section class="tool-shell-stack">
            <article class="card tool-hero-card">
                <div class="tool-hero-head">
                    <h1 class="tool-hero-title">${escapeHtml(t("vocabularyReviewTool"))}</h1>
                    <a class="secondary-button tool-back-button" href="/" data-tools-back>${escapeHtml(t("backToTools"))}</a>
                </div>
            </article>
            <div id="tool-content"></div>
        </section>
    </div>
`;

const renderAutotranslatorToolScreenMarkup = (): string => `
    <div class="page-shell tool-shell autotranslator-tool-shell">
        <header class="brand-header tool-brand-header autotranslator-brand-header">
            <a class="brand-home brand-mark autotranslator-brand-mark" href="/" data-home>ZIARCADIA</a>
            <a
                class="secondary-button autotranslator-header-back"
                href="/"
                data-tools-back
                aria-label="${escapeHtml(t("backToTools"))}"
                title="${escapeHtml(t("backToTools"))}"
            >
                ${renderZiarcadiaIcon("back", {
                    className: "autotranslator-header-back-icon"
                })}
                <span data-tools-back-label>${escapeHtml(t("backToTools"))}</span>
            </a>
        </header>
        <div id="screen-banner">${renderBannerMarkup()}</div>
        <section class="tool-shell-stack">
            <div id="tool-content"></div>
        </section>
    </div>
`;

const renderMaster1000ToolScreenMarkup = (): string => `
    <div class="page-shell tool-shell master1000-tool-shell">
        <header class="brand-header tool-brand-header">
            <a class="brand-home brand-mark" href="/" data-home>ZIARCADIA</a>
            <div class="master1000-tool-header-actions">
                ${renderAccountButtonMarkup()}
                <a
                    class="secondary-button master1000-tool-back-button master1000-tool-icon-button"
                    href="/"
                    data-tools-back
                    aria-label="${escapeHtml(t("backToTools"))}"
                    title="${escapeHtml(t("backToTools"))}"
                >
                    <span class="master1000-tool-tools-icon" aria-hidden="true">🛠</span>
                    <span class="master1000-tool-button-label" data-tools-back-label>${escapeHtml(
                        t("backToTools")
                    )}</span>
                </a>
                ${renderLanguageToggleMarkup()}
            </div>
        </header>
        <div id="screen-banner">${renderBannerMarkup()}</div>
        ${
            MASTER1000_TOOL_AVAILABLE
                ? `<section class="master1000-tool-stage" data-master1000-tool-available="true" data-master1000-stage>
                        <div class="master1000-tool-frame-shell">
                            <iframe
                                class="master1000-tool-frame"
                                src="${escapeHtml(MASTER1000_TOOL_EMBED_URL)}"
                                title="${escapeHtml(t("playMaster1000"))}"
                                allow="autoplay; clipboard-read; clipboard-write; fullscreen; gamepad"
                                loading="eager"
                                referrerpolicy="same-origin"
                            ></iframe>
                            <button
                                type="button"
                                class="master1000-tool-overlay-fullscreen"
                                data-master1000-fullscreen
                                aria-label="${escapeHtml(t("fullscreen"))}"
                                title="${escapeHtml(t("fullscreen"))}"
                            >
                                <span class="random-teams-fullscreen-icon master1000-tool-fullscreen-icon" aria-hidden="true">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </span>
                            </button>
                        </div>
                    </section>`
                : `
                    <section class="card master1000-tool-empty-card" data-master1000-tool-available="false">
                        <div class="master1000-tool-empty" data-master1000-tool-unavailable>
                            <h2 class="master1000-tool-empty-title">${escapeHtml(t("master1000MissingTitle"))}</h2>
                            <p class="master1000-tool-empty-body">${escapeHtml(
                                t("master1000MissingBody", { path: MASTER1000_TOOL_SOURCE_DIR })
                            )}</p>
                        </div>
                    </section>
                `
        }
    </div>
`;

const renderGimcheonSchoolScheduleItemMarkup = (item: GimcheonSchoolScheduleItem): string => {
    const sourceLabel = item.source === "official" ? "공식" : "제공";

    return `
        <article class="gimcheon-schedule-card is-${escapeHtml(item.kind)}">
            <div class="gimcheon-schedule-date">${escapeHtml(item.dateLabel)}</div>
            <div class="gimcheon-schedule-copy">
                <div class="gimcheon-schedule-title-row">
                    <h3>${escapeHtml(item.title)}</h3>
                    <span class="gimcheon-source-pill is-${escapeHtml(item.source)}">${escapeHtml(sourceLabel)}</span>
                </div>
                <p>${escapeHtml(item.details)}</p>
            </div>
        </article>
    `;
};

const renderGimcheonSchoolNewsItemMarkup = (item: GimcheonSchoolNewsItem): string => `
    <article class="gimcheon-news-card">
        <span>${escapeHtml(item.dateLabel)}</span>
        <h3>${escapeHtml(item.title)}</h3>
        <p>${escapeHtml(item.summary)}</p>
    </article>
`;

const renderGimcheonSchoolProfileFactMarkup = (item: GimcheonSchoolProfileFact): string => `
    <article class="gimcheon-profile-card">
        <span>${escapeHtml(item.label)}</span>
        <strong>${escapeHtml(item.value)}</strong>
        ${item.note ? `<small>${escapeHtml(item.note)}</small>` : ""}
    </article>
`;

const renderGimcheonSchoolSymbolMarkup = (item: GimcheonSchoolSymbol): string => `
    <article class="gimcheon-symbol-card">
        <span>${escapeHtml(item.label)}</span>
        <strong>${escapeHtml(item.value)}</strong>
        <p>${escapeHtml(item.meaning)}</p>
    </article>
`;

const renderGimcheonSchoolFactTile = (label: string, item: GimcheonSchoolScheduleItem | null): string => `
    <div class="gimcheon-fact-tile">
        <span>${escapeHtml(label)}</span>
        ${
            item
                ? `<strong>${escapeHtml(item.title)}</strong><small>${escapeHtml(item.dateLabel)}</small>`
                : "<strong>확인 중</strong><small>자료 없음</small>"
        }
    </div>
`;

const normalizeGimcheonSchoolChatBubbleText = (text: string): string =>
    text
        .replace(/\r\n?/g, "\n")
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();

const renderGimcheonSchoolInlineMarkdown = (text: string): string =>
    escapeHtml(text)
        .replace(
            /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g,
            '<a href="$2" target="_blank" rel="noreferrer">$1</a>'
        )
        .replace(/`([^`\n]+)`/g, "<code>$1</code>")
        .replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>")
        .replace(/__([^_\n]+)__/g, "<strong>$1</strong>")
        .replace(/\*([^*\n]+)\*/g, "<em>$1</em>");

const renderGimcheonSchoolChatTextMarkup = (text: string): string => {
    const lines = normalizeGimcheonSchoolChatBubbleText(text).split("\n");
    const parts: string[] = [];
    let paragraphLines: string[] = [];
    let listItems: string[] = [];

    const flushParagraph = () => {
        if (paragraphLines.length === 0) {
            return;
        }

        parts.push(`<p>${paragraphLines.map(renderGimcheonSchoolInlineMarkdown).join("<br>")}</p>`);
        paragraphLines = [];
    };

    const flushList = () => {
        if (listItems.length === 0) {
            return;
        }

        parts.push(`<ul>${listItems.map((item) => `<li>${renderGimcheonSchoolInlineMarkdown(item)}</li>`).join("")}</ul>`);
        listItems = [];
    };

    for (const line of lines) {
        const trimmed = line.trim();

        if (!trimmed) {
            flushParagraph();
            flushList();
            continue;
        }

        const bulletMatch = line.match(/^\s*[-*]\s+(.+)$/);
        const headingMatch = line.match(/^\s{0,3}#{1,6}\s+(.+)$/);

        if (bulletMatch) {
            flushParagraph();
            listItems.push(bulletMatch[1]);
            continue;
        }

        if (headingMatch) {
            flushParagraph();
            flushList();
            parts.push(`<p><strong>${renderGimcheonSchoolInlineMarkdown(headingMatch[1])}</strong></p>`);
            continue;
        }

        flushList();
        paragraphLines.push(line);
    }

    flushParagraph();
    flushList();

    return parts.join("");
};

const renderGimcheonSchoolChatMessagesMarkup = (): string =>
    state.gimcheonSchoolChat.messages
        .map(
            (message) => `
                <div class="gimcheon-chat-message is-${message.role}">
                    <div class="gimcheon-chat-bubble">${renderGimcheonSchoolChatTextMarkup(message.text)}</div>
                </div>
            `
        )
        .join("");

const renderGimcheonSchoolChatPanelMarkup = (): string => {
    const chat = state.gimcheonSchoolChat;

    return `
        <button
            class="gimcheon-chat-launcher${chat.open ? " is-open" : ""}"
            type="button"
            data-gimcheon-chat-toggle
            aria-expanded="${chat.open ? "true" : "false"}"
            aria-label="김천중학교 AI 안내 열기"
        >
            <span aria-hidden="true">AI</span>
            <strong>안내</strong>
        </button>
        ${
            chat.open
                ? `
                    <aside class="gimcheon-chat-panel" aria-label="김천중학교 AI 학부모 안내">
                        <header class="gimcheon-chat-head">
                            <div>
                                <span>공개 자료 기준</span>
                                <strong>AI 학부모 안내</strong>
                            </div>
                            <button
                                class="gimcheon-chat-close"
                                type="button"
                                data-gimcheon-chat-close
                                aria-label="AI 안내창 닫기"
                            >
                                &times;
                            </button>
                        </header>
                        <div class="gimcheon-chat-body" data-gimcheon-chat-body aria-live="polite" aria-busy="${chat.loading ? "true" : "false"}">
                            ${renderGimcheonSchoolChatMessagesMarkup()}
                            ${
                                chat.loading
                                    ? `
                                        <div class="gimcheon-chat-message is-assistant is-loading" role="status">
                                            <div class="gimcheon-chat-bubble">
                                                <span class="spinner" aria-hidden="true"></span>
                                                <span>공개 자료를 확인하고 있어요</span>
                                                <span class="gimcheon-chat-typing-dots" aria-hidden="true">
                                                    <span></span>
                                                    <span></span>
                                                    <span></span>
                                                </span>
                                            </div>
                                        </div>
                                    `
                                    : ""
                            }
                        </div>
                        <div class="gimcheon-chat-prompts" aria-label="빠른 질문">
                            ${GIMCHEON_SCHOOL_DEMO.quickQuestions
                                .map(
                                    (question) => `
                                        <button
                                            type="button"
                                            data-gimcheon-chat-prompt="${escapeHtml(question)}"
                                        >${escapeHtml(question)}</button>
                                    `
                                )
                                .join("")}
                        </div>
                        <div class="gimcheon-chat-compose">
                            <textarea
                                id="gimcheon-chat-input"
                                rows="2"
                                maxlength="600"
                                enterkeyhint="send"
                                aria-label="김천중학교 AI 안내 질문"
                                placeholder="학사일정, 공지, 급식, 학교 정보를 물어보세요"
                            >${escapeHtml(chat.input)}</textarea>
                            <button
                                type="button"
                                data-gimcheon-chat-send
                                aria-label="질문 보내기"
                                ${chat.loading ? "disabled" : ""}
                            >
                                ${chat.loading ? "확인 중" : "보내기"}
                            </button>
                        </div>
                    </aside>
                `
                : ""
        }
    `;
};

const shouldRestoreGimcheonSchoolChatInputFocus = (): boolean => {
    if (state.screen !== "tool-gimcheon-school" || !state.gimcheonSchoolChat.open) {
        return false;
    }

    const activeElement = document.activeElement;

    return (
        activeElement instanceof HTMLElement &&
        (activeElement.id === "gimcheon-chat-input" ||
            Boolean(activeElement.closest("[data-gimcheon-chat-send], [data-gimcheon-chat-prompt]")))
    );
};

const focusGimcheonSchoolChatInput = () => {
    window.requestAnimationFrame(() => {
        const input = app.querySelector<HTMLTextAreaElement>("#gimcheon-chat-input");

        if (!input || document.activeElement === input) {
            return;
        }

        input.focus({ preventScroll: true });
        input.setSelectionRange(input.value.length, input.value.length);
    });
};

const renderGimcheonSchoolScreenMarkup = (): string => {
    const upcoming = getUpcomingGimcheonSchoolEvents(GIMCHEON_SCHOOL_DEMO.updatedAt, 7);
    const nextEvent = upcoming[0] ?? null;
    const nextExam = upcoming.find((item) => item.kind === "exam") ?? null;
    const nextHoliday = upcoming.find((item) => item.kind === "holiday") ?? null;
    const nextFieldTrip = upcoming.find((item) => /수학여행|야영|체험/.test(item.title)) ?? null;
    const currentMeal =
        GIMCHEON_SCHOOL_DEMO.mealInfo.menus.find((item) => item.date >= GIMCHEON_SCHOOL_DEMO.updatedAt) ??
        GIMCHEON_SCHOOL_DEMO.mealInfo.menus[0] ??
        null;
    const mealSummary = currentMeal
        ? `${currentMeal.menu.slice(0, 3).join(" · ")} · ${currentMeal.kcal}`
        : "확인 중";

    return `
        <div class="gimcheon-school-page">
            <header class="gimcheon-school-topbar">
                <div class="gimcheon-school-brand" aria-label="${escapeHtml(GIMCHEON_SCHOOL_DEMO.nameKo)}">
                    <span class="gimcheon-school-brand-mark" aria-hidden="true">
                        <img src="${escapeHtml(GIMCHEON_SCHOOL_DEMO.logoUrl)}" alt="" />
                    </span>
                    <span class="gimcheon-school-brand-copy">
                        <strong>${escapeHtml(GIMCHEON_SCHOOL_DEMO.nameKo)}</strong>
                        <small>${escapeHtml(GIMCHEON_SCHOOL_DEMO.nameEn)}</small>
                    </span>
                </div>
                <nav class="gimcheon-school-nav" aria-label="김천중학교 빠른 메뉴">
                    <a href="#gimcheon-schedule">학사일정</a>
                    <a href="#gimcheon-news">학교소식</a>
                    <a href="${escapeHtml(GIMCHEON_SCHOOL_DEMO.officialUrl)}" target="_blank" rel="noreferrer">공식 홈페이지</a>
                    <a href="/" data-home>나가기</a>
                </nav>
            </header>
            <div id="screen-banner">${renderBannerMarkup()}</div>
            <main class="gimcheon-school-shell">
                <section class="gimcheon-school-hero">
                    <img
                        class="gimcheon-school-hero-image"
                        src="${escapeHtml(GIMCHEON_SCHOOL_DEMO.heroImageUrl)}"
                        alt=""
                        loading="eager"
                        referrerpolicy="no-referrer"
                    />
                    <div class="gimcheon-school-hero-copy">
                        <span class="gimcheon-school-hero-mark" aria-hidden="true">
                            <img src="${escapeHtml(GIMCHEON_SCHOOL_DEMO.logoUrl)}" alt="" />
                        </span>
                        <span class="gimcheon-school-kicker">1931년 개교 · 송설학원</span>
                        <h1>${escapeHtml(GIMCHEON_SCHOOL_DEMO.nameKo)}</h1>
                        <p>${escapeHtml(GIMCHEON_SCHOOL_DEMO.slogan)}</p>
                        <p class="gimcheon-school-identity">${escapeHtml(GIMCHEON_SCHOOL_DEMO.identitySummary)}</p>
                        <div class="gimcheon-school-facts">
                            ${renderGimcheonSchoolFactTile("다음 일정", nextEvent)}
                            ${renderGimcheonSchoolFactTile("다음 시험", nextExam)}
                            ${renderGimcheonSchoolFactTile("다음 휴업", nextHoliday)}
                        </div>
                    </div>
                </section>
                <section class="gimcheon-school-profile-grid" aria-label="김천중학교 기본 정보">
                    ${GIMCHEON_SCHOOL_DEMO.profileFacts.map(renderGimcheonSchoolProfileFactMarkup).join("")}
                    <article class="gimcheon-profile-card is-wide">
                        <span>건학이념</span>
                        <strong>${escapeHtml(GIMCHEON_SCHOOL_DEMO.foundingIdea)}</strong>
                        <small>${escapeHtml(GIMCHEON_SCHOOL_DEMO.educationGoal)}</small>
                    </article>
                </section>
                <section class="gimcheon-school-grid">
                    <div class="gimcheon-school-column is-wide" id="gimcheon-schedule">
                        <div class="gimcheon-section-head">
                            <span>Schedule</span>
                            <h2>다가오는 일정</h2>
                        </div>
                        <div class="gimcheon-schedule-list">
                            ${upcoming.map(renderGimcheonSchoolScheduleItemMarkup).join("")}
                        </div>
                    </div>
                    <aside class="gimcheon-school-column" id="gimcheon-news">
                        <div class="gimcheon-section-head">
                            <span>News</span>
                            <h2>학교 소식</h2>
                        </div>
                        <div class="gimcheon-news-list">
                            ${GIMCHEON_SCHOOL_DEMO.news.map(renderGimcheonSchoolNewsItemMarkup).join("")}
                        </div>
                        <div class="gimcheon-section-head is-compact">
                            <span>Symbol</span>
                            <h2>학교 상징</h2>
                        </div>
                        <div class="gimcheon-symbol-list">
                            ${GIMCHEON_SCHOOL_DEMO.symbols.map(renderGimcheonSchoolSymbolMarkup).join("")}
                        </div>
                    </aside>
                </section>
                <section class="gimcheon-school-info-band">
                    <div>
                        <span>문의</span>
                        <strong>${escapeHtml(GIMCHEON_SCHOOL_DEMO.phone)}</strong>
                    </div>
                    <div>
                        <span>행정실</span>
                        <strong>${escapeHtml(GIMCHEON_SCHOOL_DEMO.adminPhone)}</strong>
                    </div>
                    <div>
                        <span>교장·교감</span>
                        <strong>${escapeHtml(`${GIMCHEON_SCHOOL_DEMO.principal} · ${GIMCHEON_SCHOOL_DEMO.vicePrincipal}`)}</strong>
                    </div>
                    <div>
                        <span>다음 체험</span>
                        <strong>${escapeHtml(nextFieldTrip ? `${nextFieldTrip.dateLabel} ${nextFieldTrip.title}` : "확인 중")}</strong>
                    </div>
                    <div>
                        <span>다음 중식</span>
                        <strong>${escapeHtml(mealSummary)}</strong>
                    </div>
                    <p>${escapeHtml(GIMCHEON_SCHOOL_DEMO.privacyBoundary)}</p>
                </section>
            </main>
            ${renderGimcheonSchoolChatPanelMarkup()}
        </div>
    `;
};

const formatAccountDate = (timestamp: number): string => {
    if (!Number.isFinite(timestamp) || timestamp <= 0) {
        return "";
    }

    return new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit"
    }).format(new Date(timestamp));
};

const formatAccountFileSize = (size: number): string => {
    if (!Number.isFinite(size) || size <= 0) {
        return "";
    }

    if (size < 1024) {
        return `${Math.round(size)} B`;
    }

    if (size < 1024 * 1024) {
        return `${Math.round(size / 1024)} KB`;
    }

    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const renderGoogleSignInPanelMarkup = (): string => {
    const signInHeadMarkup = `
        <div class="account-signin-head">
            <span class="account-signin-mark" aria-hidden="true">${ACCOUNT_SIGN_IN_MARK_ICON}</span>
            <h1 class="account-title">${escapeHtml(t("hostAccount"))}</h1>
        </div>
    `;

    if (state.account.loading) {
        return `
            <article class="card account-card account-signin-card">
                <div class="spinner" aria-hidden="true"></div>
            </article>
        `;
    }

    if (!state.account.googleClientId) {
        return `
            <article class="card account-card account-signin-card">
                ${signInHeadMarkup}
                <p class="account-muted">${escapeHtml(t("googleSignInUnavailable"))}</p>
            </article>
        `;
    }

    return `
        <article class="card account-card account-signin-card">
            ${signInHeadMarkup}
            <div class="google-signin-slot" data-google-signin-button></div>
            ${state.account.error ? `<p class="account-error">${escapeHtml(localizeRuntimeText(state.account.error))}</p>` : ""}
        </article>
    `;
};

const getAccountModeClass = (mode: RoomMode): string =>
    mode === RELIC_RUN_MODE
        ? "is-relic-mode"
        : mode === PICTURE_DETECTIVES_MODE
          ? "is-pictionary-mode"
          : mode === VAULT_BREAKERS_MODE
            ? "is-vault-mode"
            : "";

const hasHostedGamePlayerResults = (item: AccountHostedGameItem): boolean =>
    Array.isArray(item.playerResults) && item.playerResults.length > 0;

const renderHostedGameHistoryMarkup = (): string => {
    const items = state.account.hostedGames.slice(0, 10);

    if (items.length === 0) {
        return `<p class="account-empty">${escapeHtml(t("noHostedGamesYet"))}</p>`;
    }

    return `
        <div class="account-history-list">
            ${items
                .map((item) => {
                    const modeLabel = localizeModeLabel(item.mode) ?? t("mode");
                    const dateLabel = formatAccountDate(item.hostedAt);
                    const modeClass = getAccountModeClass(item.mode);
                    const playerResults = hasHostedGamePlayerResults(item) ? item.playerResults ?? [] : [];
                    const expanded = expandedAccountHostedGameIds.has(item.id) && playerResults.length > 0;

                    return `
                        <article class="account-history-row ${modeClass}${expanded ? " is-expanded" : ""}">
                            <div class="account-history-row-summary">
                                <span class="account-row-icon" aria-hidden="true">${renderModeIcon(
                                    item.mode,
                                    "account-row-mode-icon"
                                )}</span>
                                <span class="account-row-main">
                                    <strong>${escapeHtml(modeLabel)}</strong>
                                    <span>${escapeHtml(t("accountPlayerCount", { count: item.playerCount }))}</span>
                                </span>
                                <span class="account-row-meta">${escapeHtml(dateLabel)}</span>
                                ${
                                    playerResults.length > 0
                                        ? `<button
                                                class="account-history-expand-button"
                                                type="button"
                                                data-account-history-toggle="${escapeHtml(item.id)}"
                                                aria-expanded="${expanded ? "true" : "false"}"
                                                aria-label="${escapeHtml(expanded ? t("hidePlayers") : t("showPlayers"))}"
                                                title="${escapeHtml(expanded ? t("hidePlayers") : t("showPlayers"))}"
                                            >
                                                ${renderZiarcadiaIcon("players", { className: "account-history-expand-icon" })}
                                            </button>`
                                        : ""
                                }
                            </div>
                            ${
                                expanded
                                    ? `<div class="account-history-player-results">
                                            ${renderPlayerResultRowsMarkup(playerResults, item.mode, { compact: true })}
                                        </div>`
                                    : ""
                            }
                        </article>
                    `;
                })
                .join("")}
        </div>
    `;
};

const getAccountWordListModeLabel = (mode: WordListOutputMode): string => {
    switch (mode) {
        case "hint":
            return t("wordListModeHint");
        case "sentence":
            return t("wordListModeSentence");
        case "custom":
            return t("wordListModeCustom");
        case "translation":
        default:
            return t("wordListModeTranslation");
    }
};

const renderAccountWordListHistoryMarkup = (): string => {
    const items = state.account.wordLists
        .slice()
        .sort((left, right) => right.updatedAt - left.updatedAt)
        .slice(0, 8);

    if (items.length === 0) {
        return `<p class="account-empty">${escapeHtml(t("noWordListsYet"))}</p>`;
    }

    return `
        <div class="account-word-list-history">
            ${items
                .map((item) => {
                    const dateLabel = formatAccountDate(item.updatedAt);
                    const modeLabel = getAccountWordListModeLabel(item.mode);
                    const termCount = item.termCount || (Array.isArray(item.entries) ? item.entries.length : 0);

                    return `
                        <button
                            class="account-word-list-row"
                            type="button"
                            data-account-word-list-id="${escapeHtml(item.id)}"
                        >
                            <span class="account-word-list-name">${escapeHtml(item.title)}</span>
                            <span class="account-word-list-meta">${escapeHtml(
                                `${modeLabel} / ${t("wordListReadyStatus", { count: termCount })} / ${dateLabel}`
                            )}</span>
                        </button>
                    `;
                })
                .join("")}
        </div>
    `;
};

const getLatestHostedGameForMode = (mode: RoomMode): AccountHostedGameItem | null =>
    state.account.hostedGames
        .filter((item) => item.mode === mode)
        .sort((left, right) => right.hostedAt - left.hostedAt)[0] ?? null;

const getLastPlayedSourceForMode = (mode: RoomMode): AccountPlayedSourceItem | null => {
    const storedSource = state.account.playedSources[mode];

    if (storedSource?.sourceText?.trim()) {
        return storedSource;
    }

    const latestHostedGame = getLatestHostedGameForMode(mode);
    const preference = state.account.preferences[mode];

    if (!latestHostedGame || !preference?.sourceText?.trim()) {
        return null;
    }

    return {
        id: `preference-${mode}-${latestHostedGame.id}`,
        mode,
        playerCount: latestHostedGame.playerCount,
        roomCode: latestHostedGame.roomCode,
        sourceText: preference.sourceText,
        playedAt: latestHostedGame.hostedAt
    };
};

const getPlayedSourceHistoryForMode = (mode: RoomMode): AccountPlayedSourceItem[] => {
    const legacySource = getLastPlayedSourceForMode(mode);
    const history = state.account.sourceHistory.filter(
        (item) => item.mode === mode && item.sourceText.trim().length > 0
    );
    const merged =
        legacySource &&
        !history.some(
            (item) => item.id === legacySource.id || item.sourceText === legacySource.sourceText
        )
            ? [legacySource, ...history]
            : history;

    return merged
        .slice()
        .sort((left, right) => right.playedAt - left.playedAt)
        .slice(0, ACCOUNT_PROFILE_SOURCE_HISTORY_COUNT);
};

const getPlayedSourceTitle = (sourceText: string): string => {
    const firstMeaningfulLine =
        sourceText
            .split(/\r?\n/)
            .map((line) => line.replace(/\t+/g, " / ").replace(/\s+/g, " ").trim())
            .find(Boolean) ?? "";

    return firstMeaningfulLine ? firstMeaningfulLine.slice(0, 96) : t("lastPlayedSource");
};

const renderAccountUploadSectionMarkup = (mode: RoomMode): string => {
    const uploads = state.account.uploads
        .filter((upload) => upload.mode === mode)
        .sort((left, right) => right.uploadedAt - left.uploadedAt)
        .slice(0, 12);
    const playedSources = getPlayedSourceHistoryForMode(mode);
    const rows = [
        ...uploads.map((upload) => ({
            id: upload.id,
            kind: "uploaded" as const,
            title: upload.fileName,
            timestamp: upload.uploadedAt,
            meta: [formatAccountFileSize(upload.size), formatAccountDate(upload.uploadedAt)].filter(Boolean).join(" / ")
        })),
        ...playedSources.map((playedSource) => ({
            id: playedSource.id,
            kind: "played" as const,
            title: getPlayedSourceTitle(playedSource.sourceText),
            timestamp: playedSource.playedAt,
            meta: [
                t("accountPlayerCount", { count: playedSource.playerCount }),
                formatAccountDate(playedSource.playedAt)
            ]
                .filter(Boolean)
                .join(" / ")
        }))
    ].sort((left, right) => right.timestamp - left.timestamp);
    const modeLabel = localizeModeLabel(mode) ?? t("mode");
    const modeClass = getAccountModeClass(mode);

    return `
        <section class="account-upload-section ${modeClass}">
            <div class="account-section-head">
                <span class="account-section-icon" aria-hidden="true">${renderModeIcon(mode, "account-section-mode-icon")}</span>
                <h3>${escapeHtml(modeLabel)}</h3>
            </div>
            ${
                rows.length > 0
                    ? `<div class="account-upload-list">
                            ${rows
                                .map((row) => {
                                    const kindClass =
                                        row.kind === "played" ? "is-played-source" : "is-uploaded-source";
                                    const kindLabel =
                                        row.kind === "played" ? t("sourcePlayed") : t("sourceUploaded");
                                    return `
                                        <button
                                            class="account-upload-row ${kindClass} is-actionable"
                                            type="button"
                                            ${
                                                row.kind === "played"
                                                    ? `data-account-played-source-id="${escapeHtml(row.id)}"`
                                                    : `data-account-upload-id="${escapeHtml(row.id)}"`
                                            }
                                        >
                                            <span class="account-upload-name">${escapeHtml(row.title)}</span>
                                            <span class="account-row-meta account-upload-meta">
                                                <span class="account-source-badge ${kindClass}">${escapeHtml(kindLabel)}</span>
                                                <span class="account-upload-meta-text">${escapeHtml(row.meta)}</span>
                                            </span>
                                        </button>
                                    `;
                                })
                                .join("")}
                        </div>`
                    : `<p class="account-empty">${escapeHtml(t("noGameFilesYet"))}</p>`
            }
        </section>
    `;
};

const renderSignedInAccountMarkup = (): string => {
    const account = state.account.account;

    if (!account) {
        return renderGoogleSignInPanelMarkup();
    }

    if (accountHistoryOpen) return `<section class="account-activity-history"><button class="activity-account-back activity-text-button" type="button" data-activity-back>${renderZiarcadiaIcon("back")}${escapeHtml(t("hostAccount"))}</button><div data-activity-history></div></section>`;

    return `
        <section class="account-dashboard">
            <aside class="card account-card account-profile-card">
                <div class="account-profile-mark">
                    ${
                        account.picture
                            ? `<img src="${escapeHtml(account.picture)}" alt="" referrerpolicy="no-referrer" />`
                            : `<span>${escapeHtml(getAccountInitial())}</span>`
                    }
                </div>
                <div class="account-profile-copy">
                    <h1 class="account-title">${escapeHtml(account.name || t("hostAccount"))}</h1>
                    <p>${escapeHtml(account.email)}</p>
                </div>
                <button class="secondary-button" type="button" data-open-activity-history>${renderZiarcadiaIcon("calendar")}${escapeHtml(t("activityHistory"))}</button>
                <button class="secondary-button account-signout-button" type="button" data-account-sign-out>
                    ${escapeHtml(t("signOut"))}
                </button>
            </aside>
            <article class="card account-card account-history-card">
                <div class="account-card-head">
                    <h2>${escapeHtml(t("hostHistory"))}</h2>
                </div>
                ${renderHostedGameHistoryMarkup()}
            </article>
            ${
                WORD_LIST_TOOL_DISCOVERABLE
                    ? `
                        <article class="card account-card account-word-lists-card">
                            <div class="account-card-head">
                                <h2>${escapeHtml(t("wordLists"))}</h2>
                                <a
                                    class="secondary-button account-card-action"
                                    href="${SITE_PATHS.wordList}"
                                    data-open-word-list-tool
                                >${escapeHtml(t("createWordListTool"))}</a>
                            </div>
                            ${renderAccountWordListHistoryMarkup()}
                        </article>
                    `
                    : ""
            }
            <article class="card account-card account-uploads-card">
                <div class="account-card-head">
                    <h2>${escapeHtml(t("gameFiles"))}</h2>
                </div>
                <div class="account-upload-grid">
                    ${renderAccountUploadSectionMarkup(RELIC_RUN_MODE)}
                    ${renderAccountUploadSectionMarkup(PICTURE_DETECTIVES_MODE)}
                    ${renderAccountUploadSectionMarkup(VAULT_BREAKERS_MODE)}
                </div>
            </article>
        </section>
    `;
};

const getAccountScreenSignature = (): string => {
    const account = state.account.account;
    const accountStatus = account ? "signed-in" : state.account.loading ? "loading" : "signed-out";
    const accountIdentity = account
        ? [account.accountId, account.email, account.name, account.picture].join(":")
        : "none";
    if (accountHistoryOpen) return [getLanguage(), "calendar", accountStatus, accountIdentity].join("|");
    const uploadSignature = state.account.uploads
        .map((upload) => [upload.id, upload.mode, upload.fileName, upload.size, upload.uploadedAt].join(":"))
        .join(",");
    const hostedGameSignature = state.account.hostedGames
        .map((item) =>
            [
                item.id,
                item.mode,
                item.playerCount,
                item.roomCode,
                item.hostedAt,
                (item.playerResults ?? [])
                    .map((player) =>
                        [
                            player.sessionId,
                            player.name,
                            player.placement ?? "",
                            player.score ?? "",
                            player.elapsedMs ?? ""
                        ].join("/")
                    )
                    .join(";")
            ].join(":")
        )
        .join(",");
    const expandedHostedGamesSignature = [...expandedAccountHostedGameIds].sort().join(",");
    const playedSourceSignature = Object.values(state.account.playedSources)
        .filter((item): item is AccountPlayedSourceItem => Boolean(item))
        .map((item) => [item.id, item.mode, item.playerCount, item.roomCode, item.playedAt, item.sourceText.slice(0, 96)].join(":"))
        .join(",");
    const sourceHistorySignature = state.account.sourceHistory
        .map((item) => [item.id, item.mode, item.playerCount, item.roomCode, item.playedAt, item.sourceText.slice(0, 96)].join(":"))
        .join(",");
    const preferenceSignature = Object.entries(state.account.preferences)
        .map(([mode, preference]) => [mode, preference?.updatedAt, preference?.sourceText?.slice(0, 96)].join(":"))
        .join(",");
    const wordListSignature = state.account.wordLists
        .map((item) => [item.id, item.mode, item.title, item.termCount, item.updatedAt].join(":"))
        .join(",");

    return [
        getLanguage(),
        accountHistoryOpen ? "calendar" : "profile",
        accountStatus,
        accountIdentity,
        state.account.googleClientId ? "google-ready" : "google-missing",
        state.account.error,
        uploadSignature,
        hostedGameSignature,
        expandedHostedGamesSignature,
        playedSourceSignature,
        sourceHistorySignature,
        preferenceSignature,
        wordListSignature
    ].join("|");
};

const renderAccountBodyMarkup = (): string =>
    state.account.account ? renderSignedInAccountMarkup() : renderGoogleSignInPanelMarkup();

const renderAccountScreenMarkup = (): string => `
    <div class="page-shell account-shell">
        <header class="brand-header account-brand-header">
            <a class="brand-home brand-mark" href="/" data-home>ZIARCADIA</a>
            <div class="brand-actions">
                <a class="secondary-button account-back-button" href="/" data-home>${escapeHtml(t("accountBack"))}</a>
                ${renderLanguageToggleMarkup()}
            </div>
        </header>
        <div id="screen-banner">${renderBannerMarkup()}</div>
        <main
            id="account-content"
            class="account-content"
            data-account-screen-signature="${escapeHtml(getAccountScreenSignature())}"
        >
            ${renderAccountBodyMarkup()}
        </main>
    </div>
`;

const getHostModeSummaryText = (): string => {
    if (state.hostDraft.mode === RELIC_RUN_MODE) {
        const levelCount = getHostDraftLevelCount();
        const tokenCount = getHostDraftTokenCount();

        if (levelCount === 0) {
            return "";
        }

        return formatRelicRunSetupSummary(levelCount, tokenCount);
    }

    if (state.hostDraft.mode === PICTURE_DETECTIVES_MODE) {
        const entryCount = getHostDraftPictionaryEntryCount();

        if (entryCount === 0) {
            return "";
        }

        const sourceRows = getPictionarySourceRows(state.hostDraft.pictionaryRows);
        const hasTranslations =
            !state.hostDraft.pictionaryNoTranslations && sourceRows.some((row) => row.korean.trim().length > 0);
        const hasHints = !state.hostDraft.pictionaryNoHints && sourceRows.some((row) => row.stem.trim().length > 0);

        return formatPictionarySetupSummary(entryCount, hasTranslations, hasHints);
    }

    if (state.hostDraft.mode === VAULT_BREAKERS_MODE) {
        const entries = getHostDraftVaultEntries();

        if (entries.length === 0) {
            return "";
        }

        const categoryCount = new Set(entries.map((entry) => entry.categoryIndex)).size;

        return formatVaultBreakerSetupSummary(categoryCount, entries.length);
    }

    return "";
};

const syncHostModeSummary = (root: ParentNode = refs.content ?? app) => {
    const modeSummary = root.querySelector<HTMLElement>("#host-mode-summary");

    if (!modeSummary) {
        return;
    }

    const summaryText = getHostModeSummaryText();
    modeSummary.textContent = summaryText;
    modeSummary.hidden = summaryText.length === 0;
};

const renderHostSourceGutterRow = (
    label: string | null,
    badgeText: string | null = null,
    className = "",
    detailText: string | null = null
): string => {
    const classNames = ["host-source-gutter-row", className, label === null ? "is-empty" : ""]
        .filter(Boolean)
        .join(" ");

    if (label === null) {
        return `<div class="${classNames}"></div>`;
    }

    return `
        <div class="${classNames}">
            <span class="host-source-level-label">${escapeHtml(label)}</span>
            ${detailText ? `<span class="host-source-level-answer host-vault-private-answer">${escapeHtml(detailText)}</span>` : ""}
            ${badgeText ? `<span class="host-source-level-count">${escapeHtml(badgeText)}</span>` : ""}
        </div>
    `;
};

const renderHostSourceGutterMarkup = (): string => {
    if (state.hostDraft.mode === VAULT_BREAKERS_MODE) {
        return getVaultBreakerSourceGuideLines(state.hostDraft.sourceText)
            .map((line) => {
                if (line.kind === "category") {
                    return renderHostSourceGutterRow(
                        line.category,
                        null,
                        "is-vault-category"
                    );
                }

                if (line.kind === "vault") {
                    return renderHostSourceGutterRow(
                        line.answer,
                        String(line.value),
                        "is-vault-value"
                    );
                }

                return renderHostSourceGutterRow(null);
            })
            .join("");
    }

    if (state.hostDraft.mode !== RELIC_RUN_MODE) {
        return "";
    }

    const rawLines = state.hostDraft.sourceText.replace(/\r\n?/g, "\n").split("\n");

    if (state.hostDraft.singleLineMode) {
        const totalWordCount = getHostDraftTokenCount();

        return rawLines
            .map((_, index) =>
                renderHostSourceGutterRow(index === 0 ? `${t("levelShort")} 1` : null, String(totalWordCount))
            )
            .join("");
    }

    const lineWordCounts = rawLines.map((line) => tokenizeSourceText(line.trim()).length);

    if (lineWordCounts.every((count) => count === 0)) {
        return rawLines
            .map((_, index) => renderHostSourceGutterRow(index === 0 ? `${t("levelShort")} 1` : null))
            .join("");
    }

    let levelNumber = 0;

    return lineWordCounts
        .map((wordCount) =>
            wordCount > 0
                ? renderHostSourceGutterRow(`${t("levelShort")} ${++levelNumber}`, String(wordCount))
                : renderHostSourceGutterRow(null)
        )
        .join("");
};

const getHostVaultMiniBoardSignature = (): string =>
    [getLanguage(), state.hostDraft.sourceText].join("|");

const renderHostVaultMiniBoardMarkup = (): string => {
    const guideLines = getVaultBreakerSourceGuideLines(state.hostDraft.sourceText);
    const categoryLineByIndex = new Map(
        guideLines.filter((line) => line.kind === "category").map((line) => [line.categoryIndex, line])
    );
    const vaultLineBySlot = new Map(
        guideLines.filter((line) => line.kind === "vault").map((line) => [`${line.categoryIndex}:${line.valueIndex}`, line])
    );
    const maxCategoryIndex = Math.max(
        4,
        ...guideLines
            .filter((line) => line.kind === "category" || line.kind === "vault")
            .map((line) => line.categoryIndex)
    );
    const maxValueIndex = Math.max(4, ...guideLines.filter((line) => line.kind === "vault").map((line) => line.valueIndex));
    const columnCount = maxCategoryIndex + 1;
    const valueRowCount = maxValueIndex + 1;

    return `
        <div
            id="host-vault-mini-board"
            class="host-vault-mini-board"
            style="--vault-mini-columns: ${columnCount}; --vault-mini-rows: ${valueRowCount + 1};"
            aria-hidden="true"
            data-vault-mini-board-signature="${escapeHtml(getHostVaultMiniBoardSignature())}"
        >
            ${Array.from({ length: columnCount }, (_, categoryIndex) => {
                const line = categoryLineByIndex.get(categoryIndex);

                return `
                    <span
                        class="host-vault-mini-cell is-header ${line ? "is-filled" : "is-empty"}"
                        ${line ? `data-vault-line-index="${line.lineIndex}"` : ""}
                    ></span>
                `;
            }).join("")}
            ${Array.from({ length: valueRowCount }, (_, valueIndex) =>
                Array.from({ length: columnCount }, (_, categoryIndex) => {
                    const line = vaultLineBySlot.get(`${categoryIndex}:${valueIndex}`);

                    return `
                        <span
                            class="host-vault-mini-cell is-vault ${line ? "is-filled" : "is-empty"}"
                            ${line ? `data-vault-line-index="${line.lineIndex}"` : ""}
                        ></span>
                    `;
                }).join("")
            ).join("")}
        </div>
`;
};

const renderHostVaultFogOverlayMarkup = (): string => {
    const columnCount = 5;
    const valueRowCount = 5;

    return `
        <div
            class="host-source-fog-overlay host-source-fog-overlay--vault"
            aria-hidden="true"
            style="--vault-fog-columns: ${columnCount}; --vault-fog-value-rows: ${valueRowCount};"
        >
            <strong class="host-source-fog-title">${escapeHtml(t("vaultBreakers"))}</strong>
            <div class="host-vault-fog-panel">
                ${Array.from(
                    { length: columnCount },
                    () => `<span class="host-vault-fog-cell is-header"></span>`
                ).join("")}
                ${Array.from(
                    { length: columnCount * valueRowCount },
                    () => `<span class="host-vault-fog-cell is-vault"></span>`
                ).join("")}
            </div>
        </div>
    `;
};

const renderHostVaultSourceGuidesMarkup = (): string =>
    getVaultBreakerSourceGuideLines(state.hostDraft.sourceText)
        .map((line) => {
            const rowClass =
                line.kind === "category" ? "is-vault-category" : line.kind === "vault" ? "is-vault-value" : "is-empty";

            return `<span class="host-vault-source-guide-row ${rowClass}"></span>`;
        })
        .join("");

const renderHostPictionaryEntryNumber = (index: number, className = ""): string => `
    <span class="host-pictionary-entry-number ${className}" aria-hidden="true">${index + 1}</span>
`;

const renderHostPictionarySourceGutterMarkup = (): string => {
    const lines = state.hostDraft.sourceText.replace(/\r\n?/g, "\n").split("\n");
    const entryLineIndexes = getPictionarySourceEntryLineIndexes(state.hostDraft.sourceText);
    const entryNumberByLineIndex = new Map(
        entryLineIndexes.map((lineIndex, entryIndex) => [lineIndex, entryIndex])
    );

    return lines
        .map((_, lineIndex) => {
            const entryIndex = entryNumberByLineIndex.get(lineIndex);

            return `
            <span class="host-pictionary-source-gutter-row">
                ${entryIndex === undefined ? "" : renderHostPictionaryEntryNumber(entryIndex, "host-pictionary-source-number")}
            </span>
        `;
        })
        .join("");
};

const renderHostPictionaryFogOverlayMarkup = (): string => `
    <div class="host-source-fog-overlay host-source-fog-overlay--pictionary" aria-hidden="true">
        <strong class="host-source-fog-title">${escapeHtml(t("pictureDetectives"))}</strong>
        <div class="host-pictionary-fog-sheet">
            <span class="host-pictionary-fog-tab"></span>
            <span class="host-pictionary-fog-mark">${PICTIONARY_FOG_MARK_ICON}</span>
            <span class="host-pictionary-fog-photo"></span>
            <span class="host-pictionary-fog-lines"></span>
            <span class="host-pictionary-fog-clue-grid">
                ${Array.from({ length: 12 }, () => `<span></span>`).join("")}
            </span>
        </div>
    </div>
`;

const renderHostSourceImportButtonMarkup = (label = t("importTextFile")): string => `
    <button
        id="host-source-import-button"
        class="host-source-import-button"
        type="button"
        aria-label="${escapeHtml(label)}"
        title="${escapeHtml(label)}"
    >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path class="host-source-import-file" d="M6.5 3.75h7.2l3.8 3.8v12.7h-11z" />
            <path class="host-source-import-fold" d="M13.7 3.75v4.1h3.8" />
            <path class="host-source-import-arrow" d="M12 16.25v-6.5m-3 3 3-3 3 3" />
        </svg>
    </button>
`;

const renderHostVaultFogButtonMarkup = (): string => {
    const fogLabel = t(state.hostVaultSourceFogged ? "showVaultBoard" : "fogVaultBoard");

    return `
        <button
            id="host-vault-fog-button"
            class="secondary-button host-vault-fog-button ${state.hostVaultSourceFogged ? "is-active" : ""}"
            type="button"
            aria-pressed="${state.hostVaultSourceFogged ? "true" : "false"}"
            aria-label="${escapeHtml(fogLabel)}"
            title="${escapeHtml(fogLabel)}"
        >${VAULT_FOG_ICON}</button>
    `;
};

const syncHostSourceTrackScroll = (
    textarea: HTMLTextAreaElement | null,
    track: HTMLElement | null
) => {
    if (!track) {
        return;
    }

    track.style.transform = textarea ? `translateY(${-textarea.scrollTop}px)` : "";
};

let hostSourceMeasureCanvas: HTMLCanvasElement | null = null;

const getHostSourceMeasureContext = (): CanvasRenderingContext2D | null => {
    hostSourceMeasureCanvas ??= document.createElement("canvas");

    return hostSourceMeasureCanvas.getContext("2d");
};

const getTextareaLineHeight = (style: CSSStyleDeclaration): number => {
    const lineHeight = Number.parseFloat(style.lineHeight);

    if (Number.isFinite(lineHeight) && lineHeight > 0) {
        return lineHeight;
    }

    const fontSize = Number.parseFloat(style.fontSize);
    return Number.isFinite(fontSize) && fontSize > 0 ? fontSize * 1.4 : 22;
};

const estimateWrappedLineCount = (
    line: string,
    availableWidth: number,
    context: CanvasRenderingContext2D | null
): number => {
    if (!line || availableWidth <= 0 || !context) {
        return 1;
    }

    const tokens = line.replace(/\t/g, "    ").match(/\S+\s*|\s+/g) ?? [line];
    let lineCount = 1;
    let usedWidth = 0;

    for (const token of tokens) {
        const tokenWidth = context.measureText(token).width;

        if (tokenWidth <= 0) {
            continue;
        }

        if (tokenWidth > availableWidth) {
            if (usedWidth > 0) {
                lineCount += 1;
                usedWidth = 0;
            }

            const tokenLineCount = Math.max(1, Math.ceil(tokenWidth / availableWidth));
            lineCount += tokenLineCount - 1;
            usedWidth = tokenWidth % availableWidth || availableWidth;
            continue;
        }

        if (usedWidth > 0 && usedWidth + tokenWidth > availableWidth) {
            lineCount += 1;
            usedWidth = tokenWidth;
            continue;
        }

        usedWidth += tokenWidth;
    }

    return lineCount;
};

const syncHostSourceTrackRowHeights = (
    textarea: HTMLTextAreaElement | null,
    track: HTMLElement | null
) => {
    if (!textarea || !track) {
        return;
    }

    const style = window.getComputedStyle(textarea);
    const lineHeight = getTextareaLineHeight(style);
    const availableWidth = Math.max(
        1,
        textarea.clientWidth - Number.parseFloat(style.paddingLeft || "0") - Number.parseFloat(style.paddingRight || "0")
    );
    const context = getHostSourceMeasureContext();

    if (context) {
        context.font =
            style.font ||
            `${style.fontStyle} ${style.fontVariant} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    }

    const visualLineCounts = textarea.value
        .replace(/\r\n?/g, "\n")
        .split("\n")
        .map((line) => estimateWrappedLineCount(line, availableWidth, context));

    Array.from(track.children).forEach((row, index) => {
        if (!(row instanceof HTMLElement)) {
            return;
        }

        const rowHeight = `${(visualLineCounts[index] ?? 1) * lineHeight}px`;
        row.style.height = rowHeight;
        row.style.minHeight = rowHeight;
    });
};

const syncHostSourceTrackLayout = (
    textarea: HTMLTextAreaElement | null,
    track: HTMLElement | null
) => {
    syncHostSourceTrackRowHeights(textarea, track);
    syncHostSourceTrackScroll(textarea, track);
};

const getTextareaCaretLineIndex = (textarea: HTMLTextAreaElement | null): number => {
    if (!textarea) {
        return -1;
    }

    const selectionStart = Math.max(0, textarea.selectionStart ?? 0);

    return textarea.value.slice(0, selectionStart).replace(/\r\n?/g, "\n").split("\n").length - 1;
};

const syncHostVaultMiniBoardActiveLine = (textarea: HTMLTextAreaElement | null) => {
    const miniBoard = app.querySelector<HTMLElement>("#host-vault-mini-board");

    if (!miniBoard) {
        return;
    }

    const activeLineIndex = getTextareaCaretLineIndex(textarea);

    miniBoard.querySelectorAll<HTMLElement>("[data-vault-line-index]").forEach((cell) => {
        cell.classList.toggle("is-active", Number(cell.dataset.vaultLineIndex) === activeLineIndex);
    });
};

const syncHostSourceEditorHeight = (textarea: HTMLTextAreaElement | null) => {
    if (!textarea) {
        return;
    }

    const isPictionaryPad = textarea.classList.contains("host-pictionary-source-pad");
    if (isPictionaryPad) {
        textarea.style.height = "";
        textarea.style.overflowY = "auto";
        return;
    }

    const isVaultPad = textarea.closest("#host-source-editor")?.classList.contains("is-vault-grid") ?? false;
    if (isVaultPad) {
        textarea.style.height = "";
        textarea.style.overflowY = "auto";
        return;
    }

    textarea.style.height = "auto";
    const minHeight = 164;
    const maxHeight = Number.POSITIVE_INFINITY;
    const nextHeight = Math.max(textarea.scrollHeight, minHeight);
    textarea.style.height = `${Math.min(nextHeight, maxHeight)}px`;
    textarea.style.overflowY = nextHeight > maxHeight ? "auto" : "hidden";
};

const refreshHostSourceEditorState = (textarea: HTMLTextAreaElement | null) => {
    const sourceEditor = app.querySelector<HTMLElement>("#host-source-editor");
    const sourceGutter = app.querySelector<HTMLElement>("#host-source-gutter");
    const vaultSourceGuides = app.querySelector<HTMLElement>("#host-vault-source-guides");
    const vaultMiniBoard = app.querySelector<HTMLElement>("#host-vault-mini-board");
    const pictionarySourceGutter = app.querySelector<HTMLElement>("#host-pictionary-source-gutter");
    const pictionaryFogButton = app.querySelector<HTMLButtonElement>("#host-pictionary-fog-button");
    const vaultFogButton = app.querySelector<HTMLButtonElement>("#host-vault-fog-button");
    const startButton = app.querySelector<HTMLButtonElement>("#start-room-button");

    syncHostModeSummary();

    if (sourceEditor) {
        sourceEditor.classList.toggle(
            "is-source-fogged",
            state.hostDraft.mode === PICTURE_DETECTIVES_MODE
                ? state.hostPictionarySourceFogged
                : state.hostDraft.mode === VAULT_BREAKERS_MODE
                  ? state.hostVaultSourceFogged
                  : false
        );
    }

    if (pictionaryFogButton) {
        const fogLabel = t(state.hostPictionarySourceFogged ? "showCaseWords" : "fogCaseWords");

        pictionaryFogButton.setAttribute("aria-label", fogLabel);
        pictionaryFogButton.setAttribute("aria-pressed", state.hostPictionarySourceFogged ? "true" : "false");
        pictionaryFogButton.title = fogLabel;
        pictionaryFogButton.classList.toggle("is-active", state.hostPictionarySourceFogged);
    }

    if (vaultFogButton) {
        const fogLabel = t(state.hostVaultSourceFogged ? "showVaultBoard" : "fogVaultBoard");

        vaultFogButton.setAttribute("aria-label", fogLabel);
        vaultFogButton.setAttribute("aria-pressed", state.hostVaultSourceFogged ? "true" : "false");
        vaultFogButton.title = fogLabel;
        vaultFogButton.classList.toggle("is-active", state.hostVaultSourceFogged);
    }

    if (sourceGutter) {
        sourceGutter.innerHTML = renderHostSourceGutterMarkup();
        syncHostSourceTrackLayout(textarea, sourceGutter);
    }

    if (vaultSourceGuides) {
        vaultSourceGuides.innerHTML = renderHostVaultSourceGuidesMarkup();
        syncHostSourceTrackLayout(textarea, vaultSourceGuides);
    }

    if (vaultMiniBoard) {
        const nextSignature = getHostVaultMiniBoardSignature();

        if (vaultMiniBoard.dataset.vaultMiniBoardSignature !== nextSignature) {
            vaultMiniBoard.outerHTML = renderHostVaultMiniBoardMarkup();
        }

        syncHostVaultMiniBoardActiveLine(textarea);
    }

    if (pictionarySourceGutter) {
        pictionarySourceGutter.innerHTML = renderHostPictionarySourceGutterMarkup();
        syncHostSourceTrackLayout(textarea, pictionarySourceGutter);
    }

    if (startButton) {
        syncHostStartButton(startButton);
    }

    syncHostSourceEditorHeight(textarea);
};

const getTextFingerprint = (value: string): string => {
    let hash = 0;

    for (let index = 0; index < value.length; index += 1) {
        hash = (hash * 33 + value.charCodeAt(index)) >>> 0;
    }

    return hash.toString(36);
};

const getHostPictionaryPreviewSignature = (): string =>
    [
        state.hostDraft.pictionaryNoTranslations ? "1" : "0",
        state.hostDraft.pictionaryNoHints ? "1" : "0",
        getTextFingerprint(state.hostDraft.sourceText)
    ].join(":");

const getHostPictionaryTableSignature = (): string =>
    [
        state.hostDraft.pictionaryNoTranslations ? "1" : "0",
        state.hostDraft.pictionaryNoHints ? "1" : "0",
        getTextFingerprint(
            state.hostDraft.pictionaryRows
                .map((row) => [row.id, row.english, row.korean, row.stem].join("\u0001"))
                .join("\u0002")
        )
    ].join(":");

const getHostVaultTableSignature = (): string =>
    getTextFingerprint(
        state.hostDraft.vaultRows
            .map((row) => [row.id, row.category, row.value, row.prompt, row.answer, row.korean].join("\u0001"))
            .join("\u0002")
    );

const renderHostPictionaryPreviewMarkup = (): string => {
    const noTranslations = state.hostDraft.pictionaryNoTranslations;
    const noHints = state.hostDraft.pictionaryNoHints;
    const rows = getPictionarySourceRows(state.hostDraft.pictionaryRows);

    if (rows.length === 0) {
        return `<div class="host-pictionary-preview-empty" aria-hidden="true"></div>`;
    }

    return rows
        .map((row, index) => {
            return `
                <div class="host-pictionary-preview-row">
                    <span class="host-pictionary-preview-cell host-pictionary-preview-word">
                        ${renderHostPictionaryEntryNumber(index, "host-pictionary-preview-number")}
                        <span class="host-pictionary-preview-word-text">${escapeHtml(row.english)}</span>
                    </span>
                    ${
                        noTranslations
                            ? ""
                            : `
                                <span class="host-pictionary-preview-cell host-pictionary-preview-translation">${escapeHtml(
                                    row.korean
                                )}</span>
                            `
                    }
                    ${
                        noHints
                            ? ""
                            : `<span class="host-pictionary-preview-cell host-pictionary-preview-stem">${escapeHtml(
                                  row.stem
                              )}</span>`
                    }
                </div>
            `;
        })
        .join("");
};

const renderHostPictionaryTableInput = (
    row: PictionaryDraftRow,
    field: PictionaryDraftField,
    rowIndex: number,
    label: string
): string => `
    <input
        class="host-pictionary-table-input"
        type="text"
        value="${escapeHtml(row[field])}"
        data-pictionary-row-id="${escapeHtml(row.id)}"
        data-pictionary-field="${field}"
        aria-label="${escapeHtml(`${label} ${rowIndex + 1}`)}"
        autocomplete="off"
        spellcheck="false"
    />
`;

const renderHostPictionaryTableRowsMarkup = (): string =>
    state.hostDraft.pictionaryRows
        .map(
            (row, index) => `
                <div class="host-pictionary-preview-row host-pictionary-table-row">
                    <span class="host-pictionary-preview-cell host-pictionary-table-cell host-pictionary-preview-word">
                        ${renderHostPictionaryEntryNumber(index, "host-pictionary-table-number")}
                        ${renderHostPictionaryTableInput(row, "english", index, t("word"))}
                    </span>
                    ${
                        state.hostDraft.pictionaryNoTranslations
                            ? ""
                            : `
                                <span class="host-pictionary-preview-cell host-pictionary-table-cell host-pictionary-preview-translation">
                                    ${renderHostPictionaryTableInput(row, "korean", index, t("translation"))}
                                </span>
                            `
                    }
                    ${
                        state.hostDraft.pictionaryNoHints
                            ? ""
                            : `
                                <span class="host-pictionary-preview-cell host-pictionary-table-cell host-pictionary-preview-stem">
                                    ${renderHostPictionaryTableInput(row, "stem", index, t("hint"))}
                                </span>
                            `
                    }
                </div>
            `
        )
        .join("");

const renderHostPictionaryTableMarkup = (): string => `
    <div class="host-pictionary-preview-shell host-pictionary-table-shell">
        <div class="host-pictionary-grid-head ${
            state.hostDraft.pictionaryNoTranslations ? "is-no-translations" : ""
        }">
            <span class="host-pictionary-grid-label host-pictionary-word-label">${escapeHtml(t("word"))}</span>
            ${
                state.hostDraft.pictionaryNoTranslations
                    ? ""
                    : `<span class="host-pictionary-grid-label host-pictionary-translation-label">${escapeHtml(
                          t("translation")
                      )}</span>`
            }
            ${
                state.hostDraft.pictionaryNoHints
                    ? ""
                    : `<span class="host-pictionary-grid-label host-pictionary-stem-label">${escapeHtml(
                          t("hint")
                      )}</span>`
            }
        </div>
        <div
            id="host-pictionary-table"
            class="host-pictionary-grid host-pictionary-table"
            data-pictionary-table-signature="${escapeHtml(getHostPictionaryTableSignature())}"
        >${renderHostPictionaryTableRowsMarkup()}</div>
    </div>
`;

const renderHostPictionaryEditorModeSwitch = (): string => {
    const modes: Array<{ mode: PictionaryEditorMode; label: string }> = [
        { mode: "text", label: t("textInput") },
        { mode: "table", label: t("tableView") }
    ];

    return `
        <div class="host-pictionary-editor-mode-switch" role="group" aria-label="${escapeHtml(t("editorView"))}">
            ${modes
                .map(
                    ({ mode, label }) => `
                        <button
                            class="host-pictionary-editor-mode-button ${
                                state.hostPictionaryEditorMode === mode ? "is-selected" : ""
                            }"
                            type="button"
                            data-pictionary-editor-mode="${mode}"
                            aria-pressed="${state.hostPictionaryEditorMode === mode ? "true" : "false"}"
                        >${escapeHtml(label)}</button>
                    `
                )
                .join("")}
        </div>
    `;
};

const renderHostVaultTableInput = (
    row: VaultDraftRow,
    field: VaultDraftField,
    rowIndex: number,
    label: string
): string => `
    <input
        class="host-pictionary-table-input host-vault-table-input"
        type="text"
        value="${escapeHtml(row[field])}"
        data-vault-row-id="${escapeHtml(row.id)}"
        data-vault-field="${field}"
        aria-label="${escapeHtml(`${label} ${rowIndex + 1}`)}"
        autocomplete="off"
        spellcheck="false"
        ${field === "value" ? 'inputmode="numeric"' : ""}
    />
`;

const renderHostVaultTableRowsMarkup = (): string =>
    state.hostDraft.vaultRows
        .map(
            (row, index) => `
                <div class="host-pictionary-preview-row host-pictionary-table-row host-vault-table-row">
                    <span class="host-pictionary-preview-cell host-pictionary-table-cell host-pictionary-preview-word host-vault-table-cell host-vault-table-category">
                        ${renderHostPictionaryEntryNumber(index, "host-vault-table-number")}
                        ${renderHostVaultTableInput(row, "category", index, t("category"))}
                    </span>
                    <span class="host-pictionary-preview-cell host-pictionary-table-cell host-vault-table-cell host-vault-table-value">
                        ${renderHostVaultTableInput(row, "value", index, t("value"))}
                    </span>
                    <span class="host-pictionary-preview-cell host-pictionary-table-cell host-vault-table-cell host-vault-table-prompt">
                        ${renderHostVaultTableInput(row, "prompt", index, t("prompt"))}
                    </span>
                    <span class="host-pictionary-preview-cell host-pictionary-table-cell host-vault-table-cell host-vault-table-answer">
                        ${renderHostVaultTableInput(row, "answer", index, t("answer"))}
                    </span>
                    <span class="host-pictionary-preview-cell host-pictionary-table-cell host-vault-table-cell host-vault-table-info">
                        ${renderHostVaultTableInput(row, "korean", index, t("info"))}
                    </span>
                </div>
            `
        )
        .join("");

const renderHostVaultTableMarkup = (): string => `
    <div class="host-pictionary-preview-shell host-pictionary-table-shell host-vault-table-shell">
        <div class="host-pictionary-grid-head host-vault-grid-head">
            <span class="host-pictionary-grid-label host-vault-grid-label">${escapeHtml(t("category"))}</span>
            <span class="host-pictionary-grid-label host-vault-grid-label">${escapeHtml(t("value"))}</span>
            <span class="host-pictionary-grid-label host-vault-grid-label">${escapeHtml(t("prompt"))}</span>
            <span class="host-pictionary-grid-label host-vault-grid-label">${escapeHtml(t("answer"))}</span>
            <span class="host-pictionary-grid-label host-vault-grid-label">${escapeHtml(t("info"))}</span>
        </div>
        <div
            id="host-vault-table"
            class="host-pictionary-grid host-vault-table"
            data-vault-table-signature="${escapeHtml(getHostVaultTableSignature())}"
        >${renderHostVaultTableRowsMarkup()}</div>
    </div>
`;

const renderHostVaultEditorModeSwitch = (): string => {
    const modes: Array<{ mode: VaultEditorMode; label: string }> = [
        { mode: "text", label: t("textInput") },
        { mode: "table", label: t("tableView") }
    ];

    return `
        <div class="host-pictionary-editor-mode-switch host-vault-editor-mode-switch" role="group" aria-label="${escapeHtml(
            t("editorView")
        )}">
            ${modes
                .map(
                    ({ mode, label }) => `
                        <button
                            class="host-pictionary-editor-mode-button host-vault-editor-mode-button ${
                                state.hostVaultEditorMode === mode ? "is-selected" : ""
                            }"
                            type="button"
                            data-vault-editor-mode="${mode}"
                            aria-pressed="${state.hostVaultEditorMode === mode ? "true" : "false"}"
                        >${escapeHtml(label)}</button>
                    `
                )
                .join("")}
        </div>
    `;
};

const isHostControllerSeatClaimed = (): boolean =>
    state.hostDraft.pictionaryHostIsController || Boolean(state.room?.setup.pictionaryControllerConnected);

const isExternalHostControllerSeatClaimed = (): boolean => {
    const setup = state.room?.setup;

    return Boolean(setup?.pictionaryControllerConnected && !setup.pictionaryHostIsController);
};

const renderHostControllerAccessMarkup = (): string => {
    const isClaimed = isHostControllerSeatClaimed();
    const externalControllerClaimed = isExternalHostControllerSeatClaimed();
    const showInlineQr = !isClaimed || state.hostControllerQrExpanded;

    return `
        <div class="host-controller-access-panel ${isClaimed ? "is-claimed" : ""} ${
            externalControllerClaimed ? "is-external-claimed" : ""
        }">
            <button
                id="host-controller-qr-inline"
                class="host-controller-qr-panel ${isClaimed ? "is-connected" : ""} ${
                    showInlineQr ? "is-expanded" : "is-collapsed"
                }"
                type="button"
                ${
                    showInlineQr
                        ? 'data-open-qr-preview data-qr-kind="controller"'
                        : "data-expand-controller-qr"
                }
                aria-label="${escapeHtml(t("showHostControllerQr"))}"
                title="${escapeHtml(showInlineQr ? t("hostControllerPurpose") : t("showHostControllerQr"))}"
            >
                <span class="host-controller-qr-copy">
                    <span class="card-label host-controller-qr-label">${escapeHtml(t("controllerLabel"))}</span>
                </span>
                ${
                    showInlineQr
                        ? `
                            <span class="room-qr-frame host-controller-qr-frame">
                                <img
                                    data-room-qr-image
                                    data-room-qr-target="controller"
                                    alt="${escapeHtml(t("showHostControllerQr"))}"
                                    hidden
                                />
                                <span class="room-qr-placeholder">QR</span>
                            </span>
                        `
                        : `<span class="host-controller-qr-expand-icon" aria-hidden="true">${EXPAND_ICON}</span>`
                }
            </button>
            ${
                externalControllerClaimed
                    ? ""
                    : `<label class="toggle-field host-pictionary-device-controller-field">
                        <input
                            id="host-pictionary-device-controller-toggle"
                            type="checkbox"
                            ${state.hostDraft.pictionaryHostIsController ? "checked" : ""}
                        />
                        <span class="host-pictionary-device-controller-label">${escapeHtml(
                            t("makeThisDeviceController")
                        )}</span>
                    </label>`
            }
        </div>
    `;
};

const renderHostSourceImportMenuMarkup = (): string => {
    if (!state.hostSourceImportOpen || !state.hostDraft.mode) {
        return "";
    }

    const recentUploads = getRecentUploadsForMode(state.hostDraft.mode);
    const hasModeArchive =
        state.account.uploads.some((upload) => upload.mode === state.hostDraft.mode) ||
        Boolean(getLastPlayedSourceForMode(state.hostDraft.mode));
    const archiveButton =
        state.account.account && hasModeArchive
            ? `
                <button
                    class="host-source-import-option"
                    type="button"
                    data-open-account-archive
                >${escapeHtml(t("hostedArchive"))}</button>
            `
            : "";

    return `
        <div class="host-source-import-menu" data-host-source-import-menu>
            ${
                recentUploads.length > 0
                    ? recentUploads
                          .map(
                              (upload) => `
                                <button
                                    class="host-source-import-option"
                                    type="button"
                                    data-account-upload-id="${escapeHtml(upload.id)}"
                                >
                                    <span>${escapeHtml(upload.fileName)}</span>
                                    <small>${escapeHtml(formatAccountDate(upload.uploadedAt))}</small>
                                </button>
                            `
                          )
                          .join("")
                    : ""
            }
            ${archiveButton}
            <button
                class="host-source-import-option is-computer"
                type="button"
                data-import-from-computer
            >${escapeHtml(t("fromComputer"))}</button>
        </div>
    `;
};

const renderHostSourceTranslationControlMarkup = (): string => {
    const items = getHostSourceTranslationItems();
    const missingCount = items.filter((item) => item.currentTranslation.length === 0).length;
    const translatedCount = items.length - missingCount;
    const translateLabel = t("translateSource");
    const loading = state.hostSourceTranslationLoading;

    return `
        <div class="host-source-translation-control ${state.hostSourceTranslationOpen ? "is-open" : ""}">
            <button
                id="host-source-translate-button"
                class="secondary-button host-source-translate-button ${loading ? "is-loading" : ""}"
                type="button"
                aria-label="${escapeHtml(translateLabel)}"
                title="${escapeHtml(translateLabel)}"
                aria-haspopup="menu"
                aria-expanded="${state.hostSourceTranslationOpen ? "true" : "false"}"
                ${items.length === 0 || loading ? "disabled" : ""}
            >${SOURCE_TRANSLATE_ICON}</button>
            ${
                state.hostSourceTranslationOpen
                    ? `
                        <div
                            class="host-source-translation-menu"
                            data-host-source-translation-menu
                            role="menu"
                            aria-label="${escapeHtml(translateLabel)}"
                            aria-busy="${loading ? "true" : "false"}"
                        >
                            <button
                                class="host-source-translation-option"
                                type="button"
                                role="menuitem"
                                data-source-translation-action="add"
                                ${missingCount === 0 || loading ? "disabled" : ""}
                            >
                                <span>${escapeHtml(t("translateAddMissing"))}</span>
                                <small>${missingCount}</small>
                            </button>
                            ${
                                translatedCount > 0
                                    ? `
                                        <button
                                            class="host-source-translation-option is-replace"
                                            type="button"
                                            role="menuitem"
                                            data-source-translation-action="replace"
                                            ${loading ? "disabled" : ""}
                                        >
                                            <span>${escapeHtml(t("translateReplaceAll"))}</span>
                                            <small>${items.length}</small>
                                        </button>
                                    `
                                    : ""
                            }
                            ${
                                loading
                                    ? `<p class="host-source-translation-status" role="status">${escapeHtml(
                                          t("translateLoading")
                                      )}</p>`
                                    : state.hostSourceTranslationError
                                      ? `<p class="host-source-translation-error" role="alert">${escapeHtml(
                                            state.hostSourceTranslationError
                                        )}</p>`
                                      : ""
                            }
                        </div>
                    `
                    : ""
            }
        </div>
    `;
};

const renderHostModeSetupMarkup = (): string => state.hostDraft.mode === PLANET_HOPPERS_MODE ? `<section id="host-mode-setup-panel" class="ph-setup"><div id="ph-editor-root"></div></section>` : `
    <section
        id="host-mode-setup-panel"
        class="host-mode-inline-panel ${state.hostDraft.mode ? "is-open is-selected" : ""} ${
            state.hostDraft.mode === PICTURE_DETECTIVES_MODE ? "is-pictionary" : ""
        } ${state.hostDraft.mode === VAULT_BREAKERS_MODE ? "is-vault" : ""}"
        ${state.hostDraft.mode ? "" : "hidden"}
    >
        ${
            state.hostDraft.mode === RELIC_RUN_MODE
                ? `
                    <div class="host-mode-toggle-row">
                        <label class="toggle-field host-mode-toggle-field">
                            <input
                                id="host-single-line-toggle"
                                type="checkbox"
                                ${state.hostDraft.singleLineMode ? "checked" : ""}
                            />
                            <span class="host-relic-single-line-label">${escapeHtml(getTreatAsOneLevelLabel())}</span>
                        </label>
                        <label class="toggle-field host-mode-toggle-field host-participation-toggle-field">
                            <input
                                id="host-participation-toggle"
                                type="checkbox"
                                ${state.hostDraft.hostParticipates ? "checked" : ""}
                            />
                            <span class="host-relic-participation-label">${escapeHtml(
                                getHostParticipationLabel()
                            )}</span>
                        </label>
                    </div>
                `
                : ""
        }
        ${
            state.hostDraft.mode === PICTURE_DETECTIVES_MODE
                ? `
                    <div class="host-mode-toggle-row host-pictionary-toggle-row">
                        <label class="toggle-field host-mode-toggle-field">
                            <input
                                id="host-pictionary-no-translations-toggle"
                                type="checkbox"
                                ${state.hostDraft.pictionaryNoTranslations ? "checked" : ""}
                            />
                            <span class="host-pictionary-no-translations-label">${escapeHtml(t("noTranslations"))}</span>
                        </label>
                        <label class="toggle-field host-mode-toggle-field">
                            <input
                                id="host-pictionary-no-hints-toggle"
                                type="checkbox"
                                ${state.hostDraft.pictionaryNoHints ? "checked" : ""}
                            />
                            <span class="host-pictionary-no-hints-label">${escapeHtml(t("noHints"))}</span>
                        </label>
                    </div>
                `
                : ""
        }
        <div class="field host-source-field">
            ${
                state.hostDraft.mode === PICTURE_DETECTIVES_MODE
                    ? `
                        <div
                            id="host-source-editor"
                            class="host-source-editor is-pictionary-grid ${
                                state.hostDraft.pictionaryNoTranslations ? "is-no-translations" : ""
                            } ${state.hostDraft.pictionaryNoHints ? "is-no-hints" : ""} ${
                                state.hostPictionarySourceFogged ? "is-source-fogged" : ""
                            } ${
                                state.hostPictionaryEditorMode === "table"
                                    ? "is-pictionary-table-mode"
                                    : "is-pictionary-text-mode"
                            }"
                        >
                            <input
                                id="host-source-file-input"
                                class="host-source-file-input"
                                type="file"
                                accept=".txt,.csv,.tsv,.xlsx,text/plain,text/csv,text/tab-separated-values,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                aria-label="${escapeHtml(t("importSourceFile"))}"
                                hidden
                            />
                            <div class="host-pictionary-editor-shell">
                                <div class="host-pictionary-pad-shell">
                                    <div class="host-pictionary-pad-toolbar">
                                        <button
                                            id="host-source-import-button"
                                            class="secondary-button host-pictionary-upload-button"
                                            type="button"
                                            aria-label="${escapeHtml(t("importSourceFile"))}"
                                            title="${escapeHtml(t("importSourceFile"))}"
                                        >
                                            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                                <path class="host-source-import-file" d="M6.5 3.75h7.2l3.8 3.8v12.7h-11z" />
                                                <path class="host-source-import-fold" d="M13.7 3.75v4.1h3.8" />
                                                <path class="host-source-import-arrow" d="M12 16.25v-6.5m-3 3 3-3 3 3" />
                                            </svg>
                                        </button>
                                        ${renderHostSourceImportMenuMarkup()}
                                        ${
                                            state.hostDraft.pictionaryNoTranslations
                                                ? ""
                                                : renderHostSourceTranslationControlMarkup()
                                        }
                                        ${renderHostPictionaryEditorModeSwitch()}
                                        <button
                                            id="host-pictionary-fog-button"
                                            class="secondary-button host-pictionary-fog-button ${
                                                state.hostPictionarySourceFogged ? "is-active" : ""
                                            }"
                                            type="button"
                                            aria-pressed="${state.hostPictionarySourceFogged ? "true" : "false"}"
                                            aria-label="${escapeHtml(
                                                t(state.hostPictionarySourceFogged ? "showCaseWords" : "fogCaseWords")
                                            )}"
                                            title="${escapeHtml(
                                                t(state.hostPictionarySourceFogged ? "showCaseWords" : "fogCaseWords")
                                            )}"
                                        >${FOG_ICON}</button>
                                    </div>
                                    ${
                                        state.hostPictionaryEditorMode === "table"
                                            ? renderHostPictionaryTableMarkup()
                                            : `
                                                <div class="host-pictionary-source-pad-shell">
                                                    <textarea
                                                        id="host-source-text"
                                                        class="host-pictionary-source-pad"
                                                        rows="7"
                                                        wrap="soft"
                                                        spellcheck="false"
                                                        aria-label="${escapeHtml(t("addWords"))}"
                                                        placeholder="${escapeHtml(t("pictionarySourcePlaceholder"))}"
                                                    >${escapeHtml(state.hostDraft.sourceText)}</textarea>
                                                    <div
                                                        id="host-pictionary-source-gutter"
                                                        class="host-pictionary-source-gutter"
                                                        aria-hidden="true"
                                                    >${renderHostPictionarySourceGutterMarkup()}</div>
                                                </div>
                                            `
                                    }
                                    ${renderHostControllerAccessMarkup()}
                                </div>
                            </div>
                            ${renderHostPictionaryFogOverlayMarkup()}
                        </div>
                    `
                    : `
                        <div
                            id="host-source-editor"
                            class="host-source-editor ${state.hostDraft.singleLineMode ? "is-single-line" : ""} ${
                                state.hostDraft.mode === VAULT_BREAKERS_MODE ? "is-vault-grid" : ""
                            } ${
                                state.hostDraft.mode === VAULT_BREAKERS_MODE && state.hostVaultEditorMode === "table"
                                    ? "is-vault-table-mode"
                                    : ""
                            } ${
                                state.hostDraft.mode === VAULT_BREAKERS_MODE && state.hostVaultEditorMode === "text"
                                    ? "is-vault-text-mode"
                                    : ""
                            } ${
                                state.hostDraft.mode === VAULT_BREAKERS_MODE && state.hostVaultSourceFogged
                                    ? "is-source-fogged"
                                    : ""
                            }"
                        >
                            <input
                                id="host-source-file-input"
                                class="host-source-file-input"
                                type="file"
                                accept="${escapeHtml(
                                    state.hostDraft.mode === VAULT_BREAKERS_MODE
                                        ? ".txt,.csv,.tsv,.xlsx,text/plain,text/csv,text/tab-separated-values,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                        : ".txt,text/plain"
                                )}"
                                aria-label="${escapeHtml(
                                    state.hostDraft.mode === VAULT_BREAKERS_MODE ? t("importSourceFile") : t("importTextFile")
                                )}"
                                hidden
                            />
                            ${
                                state.hostDraft.mode === VAULT_BREAKERS_MODE
                                    ? `
                                        <div class="host-vault-tool-rail">
                                            ${renderHostSourceImportButtonMarkup(t("importSourceFile"))}
                                            ${renderHostSourceTranslationControlMarkup()}
                                            ${renderHostVaultEditorModeSwitch()}
                                            ${renderHostVaultFogButtonMarkup()}
                                        </div>
                                    `
                                    : renderHostSourceImportButtonMarkup()
                            }
                            ${renderHostSourceImportMenuMarkup()}
                            ${
                                state.hostDraft.mode === VAULT_BREAKERS_MODE
                                    ? state.hostVaultEditorMode === "table"
                                        ? renderHostVaultTableMarkup()
                                        : `
                                            <div
                                                id="host-source-gutter-shell"
                                                class="host-source-gutter-shell"
                                                aria-hidden="true"
                                            >
                                                <div id="host-source-gutter" class="host-source-gutter">${renderHostSourceGutterMarkup()}</div>
                                            </div>
                                            <textarea
                                                id="host-source-text"
                                                rows="5"
                                                wrap="soft"
                                                aria-label="${escapeHtml(t("pasteVaults"))}"
                                                placeholder="${escapeHtml(t("vaultSourcePlaceholder"))}"
                                            >${escapeHtml(state.hostDraft.sourceText)}</textarea>
                                            <div
                                                id="host-vault-source-guides"
                                                class="host-vault-source-guides"
                                                aria-hidden="true"
                                            >${renderHostVaultSourceGuidesMarkup()}</div>
                                            ${renderHostVaultMiniBoardMarkup()}
                                            ${renderHostVaultFogOverlayMarkup()}
                                        `
                                    : `
                                        <div
                                            id="host-source-gutter-shell"
                                            class="host-source-gutter-shell"
                                            aria-hidden="true"
                                        >
                                            <div id="host-source-gutter" class="host-source-gutter">${renderHostSourceGutterMarkup()}</div>
                                        </div>
                                        <textarea
                                            id="host-source-text"
                                            rows="5"
                                            wrap="soft"
                                            aria-label="${escapeHtml(t("pasteText"))}"
                                            placeholder="${escapeHtml(t("pastePlaceholder"))}"
                                        >${escapeHtml(state.hostDraft.sourceText)}</textarea>
                                    `
                            }
                        </div>
                    `
            }
        </div>
    </section>
`;

const renderHostModeSelectionMarkup = (): string => `
    <div id="host-mode-card-body" class="host-mode-selection-view">
        <div class="host-mode-button-grid">
            ${renderHostModeButton(RELIC_RUN_MODE)}
            ${renderHostModeButton(PICTURE_DETECTIVES_MODE)}
            ${renderHostModeButton(VAULT_BREAKERS_MODE)}
            ${renderHostModeButton(PLANET_HOPPERS_MODE)}
        </div>
    </div>
`;

const renderHostModeConfigMarkup = (): string => {
    if (!state.hostDraft.mode) {
        return "";
    }

    const summaryText = getHostModeSummaryText();

    return `
        <div id="host-mode-card-body" class="host-mode-config-view">
            <div class="host-mode-config-head">
                <div class="host-mode-config-nav">
                    <button
                        id="return-to-mode-selection-button"
                        class="ghost-button host-mode-return-button"
                        type="button"
                        aria-label="${escapeHtml(t("returnToSelection"))}"
                        title="${escapeHtml(t("returnToSelection"))}"
                    >${renderHostModeBackIcon()}</button>
                    <div class="host-mode-focus-pill">
                        <span class="host-mode-focus-icon" aria-hidden="true">${renderModeIcon(state.hostDraft.mode, "host-mode-focus-glyph")}</span>
                        <span class="host-mode-focus-label">${escapeHtml(localizeModeLabel(state.hostDraft.mode) ?? "")}</span>
                    </div>
                </div>
                <div class="host-mode-config-actions">
                    <div
                        id="host-mode-summary"
                        class="setup-meta host-mode-summary-pill"
                        ${summaryText ? "" : "hidden"}
                    >
                        ${escapeHtml(summaryText)}
                    </div>
                    <button
                        id="start-room-button"
                        class="primary-button host-start-button ${isHostStartPending() ? "is-starting" : ""}"
                        type="button"
                        aria-busy="${isHostStartPending() ? "true" : "false"}"
                        ${canStartHostDraft() && !isHostStartPending() ? "" : "disabled"}
                    >${renderHostStartButtonContent()}</button>
                </div>
            </div>
            ${renderHostModeSetupMarkup()}
        </div>
    `;
};

const renderRoomShellMarkup = (): string => `
    <div class="page-shell room-shell">
        <header class="room-topbar">
            <a class="brand-home brand-mark room-brand" href="/" data-home>ZIARCADIA</a>
            <div class="room-topbar-actions">
                ${renderAccountButtonMarkup()}
                <button id="leave-room-button" class="ghost-button">${escapeHtml(t("leave"))}</button>
                ${renderLanguageToggleMarkup()}
            </div>
        </header>
        <div id="screen-banner">${renderBannerMarkup()}</div>
        <div id="room-utility-slot" class="room-utility-slot"></div>
        <div id="room-content" class="room-content"></div>
        <div id="room-overlay-root" class="room-overlay-root"></div>
        <footer class="room-footer"></footer>
    </div>
`;

const renderActiveHostUtilityMarkup = (room: RoomView): string => `
    <div class="active-host-join-panel">
        <button
            class="active-room-qr-button"
            type="button"
            data-open-qr-preview
            data-qr-kind="join"
            aria-label="${escapeHtml(t("showQrCodeToJoin"))}"
        >
            <span class="room-qr-frame active-room-qr-frame">
                <img data-room-qr-image data-room-qr-target="join" alt="${escapeHtml(t("scanToJoin"))}" hidden />
                <span class="room-qr-placeholder">QR</span>
            </span>
        </button>
        <div class="active-room-code-stack">
            <div class="room-code-display active-room-code-display" data-room-code-text>${escapeHtml(
                room.roomCode
            )}</div>
        </div>
    </div>
    <div class="active-host-utility-actions">
        <label class="toggle-field active-host-participation-field">
            <input
                id="host-participation-toggle"
                type="checkbox"
                ${room.setup.hostParticipates ? "checked" : ""}
            />
            <span class="host-participation-toggle-label">${escapeHtml(getHostParticipationLabel())}</span>
        </label>
        <button class="secondary-button danger-button" data-end-game>${escapeHtml(t("endGame"))}</button>
    </div>
`;

const renderHostLobbyMarkup = (): string => `
    <section class="host-lobby-shell">
        <article class="card host-waiting-card host-lobby-stage-card">
            <div id="host-lobby-playground-root" data-lobby-playground-root></div>
        </article>
        <div class="host-lobby-overlays">
            <article
                class="card host-mode-card ${
                    state.hostDraft.mode === RELIC_RUN_MODE
                        ? "is-relic-selected"
                        : state.hostDraft.mode === PICTURE_DETECTIVES_MODE
                          ? "is-pictionary-selected"
                          : state.hostDraft.mode === VAULT_BREAKERS_MODE
                            ? "is-vault-selected"
                            : state.hostDraft.mode === PLANET_HOPPERS_MODE
                              ? "is-planet-selected"
                            : "is-awaiting-selection"
                } ${isHostModeSelectionOpen() ? "is-selection-open" : "is-config-open"}"
            >
                ${isHostModeSelectionOpen() ? renderHostModeSelectionMarkup() : renderHostModeConfigMarkup()}
            </article>
            <div class="host-lobby-side-rail">
                <aside class="card host-presence-card">
                    <div class="host-presence-head">
                        <button
                            id="host-edit-name-button"
                            class="host-presence-name-button"
                            type="button"
                            aria-label="${escapeHtml(t("editName"))}"
                            title="${escapeHtml(t("editName"))}"
                        >
                            <span class="host-presence-name-label">${escapeHtml(
                                state.room?.viewer.name ?? DEFAULT_HOST_NAME
                            )}</span>
                            <span class="host-presence-edit-icon" aria-hidden="true">&#9998;</span>
                        </button>
                    </div>
                    <div class="waiting-room-roster host-presence-roster">
                        ${
                            state.room
                                ? renderPlayerHistoryHeaderMenu(
                                      state.room,
                                      String(getHostLobbyParticipants(state.room).length),
                                      {
                                          className: "host-presence-history-menu",
                                          countClassName: "host-presence-count",
                                          open: hostPlayerHistoryMenuOpen
                                      }
                                  )
                                : `<strong class="roster-count host-presence-count">0</strong>`
                        }
                        <button
                            class="host-mobile-qr-button roster-qr-button"
                            type="button"
                            data-open-qr-preview
                            data-qr-kind="join"
                            aria-label="${escapeHtml(t("showQrCodeToJoin"))}"
                        >
                            <span class="room-qr-frame roster-qr-frame host-mobile-qr-frame">
                                <img data-room-qr-image data-room-qr-target="join" alt="${escapeHtml(t("scanToJoin"))}" hidden />
                                <span class="room-qr-placeholder">QR</span>
                            </span>
                        </button>
                        <div id="lobby-participants">${renderParticipantRows(
                                getHostLobbyParticipants(),
                                t("noPlayersYet"),
                                {
                                    allowHostRename: true,
                                    allowHostPromptRename: true,
                                    allowHostKick: true,
                                    openActionsKey: openRoomParticipantRosterActionsKey
                                }
                        )}</div>
                    </div>
                </aside>
                <aside class="card host-qr-card">
                    <div class="host-qr-code-shell">
                        <button
                            class="room-qr-button"
                            type="button"
                            data-open-qr-preview
                            data-qr-kind="join"
                            aria-label="${escapeHtml(t("showQrCodeToJoin"))}"
                        >
                            <span class="room-qr-frame">
                                <img data-room-qr-image data-room-qr-target="join" alt="${escapeHtml(t("scanToJoin"))}" hidden />
                                <span class="room-qr-placeholder">QR</span>
                            </span>
                        </button>
                        <button
                            class="host-qr-copy-link-button"
                            type="button"
                            data-copy="join"
                            aria-label="${escapeHtml(t("copyJoinLink"))}"
                            title="${escapeHtml(t("copyJoinLink"))}"
                        >
                            ${COPY_JOIN_LINK_ICON}
                            <span class="host-qr-copy-link-label visually-hidden">${escapeHtml(t("copyJoinLink"))}</span>
                        </button>
                    </div>
                    <div class="host-qr-meta">
                        <div class="room-code-display host-room-code-display" data-room-code-text>--</div>
                        <p class="host-qr-caption">${escapeHtml(t("tapQrToExpand"))}</p>
                    </div>
                </aside>
                <aside class="card host-setup-library-card" id="host-setup-library"></aside>
            </div>
        </div>
        ${renderHostStartLoadingOverlayMarkup()}
    </section>
`;

const renderPlayerLobbyMarkup = (): string => `
    <section class="player-lobby-grid">
        <article class="card waiting-card">
            <div id="player-lobby-playground-root" data-lobby-playground-root></div>
        </article>
    </section>
`;

const renderControllerLobbyMarkup = (): string => `
    <section class="controller-lobby-grid">
        <article class="card controller-ready-card">
            <div class="controller-ready-copy">
                <h2 class="controller-ready-title">${escapeHtml(
                    state.room?.viewer.isController ? t("hostController") : t("controllerLocked")
                )}</h2>
                <div class="controller-room-switch">
                    <input
                        id="controller-room-code-input"
                        class="controller-room-code-input"
                        type="text"
                        value="${escapeHtml(normalizeRoomCode(state.room?.roomCode ?? ""))}"
                        maxlength="${ROOM_CODE_MAX_LENGTH}"
                        autocomplete="off"
                        spellcheck="false"
                        inputmode="text"
                        aria-label="${escapeHtml(t("controllerRoomCode"))}"
                        title="${escapeHtml(t("controllerRoomCode"))}"
                    />
                    <button
                        id="controller-room-switch-button"
                        class="secondary-button controller-room-switch-button"
                        type="button"
                        aria-label="${escapeHtml(t("switchRoom"))}"
                        title="${escapeHtml(t("switchRoom"))}"
                    >${escapeHtml(t("switchRoomShort"))}</button>
                </div>
                <p class="controller-ready-status">${escapeHtml(
                    state.room?.viewer.isController
                        ? t("waitForHostToStart")
                        : localizeRuntimeText(state.room?.statusMessage || "")
                )}</p>
            </div>
        </article>
    </section>
`;

// Keep the rename editor visually tied to the button that opened it instead of recentring a modal.
const getRenamePopoverLayout = (): RenamePopoverLayout | null => {
    if (!state.renameDialogOpen) {
        return null;
    }

    // Anchors are measured in screen pixels; the popover's CSS lengths inherit root zoom.
    const layoutScale = Number.parseFloat(getComputedStyle(document.documentElement).zoom) || 1;
    const viewportWidth = window.innerWidth / layoutScale;
    const viewportHeight = window.innerHeight / layoutScale;
    const availableWidth = viewportWidth - RENAME_POPOVER_VIEWPORT_PADDING_PX * 2;

    if (availableWidth < 140) {
        return null;
    }

    const width = Math.min(
        state.renameDialogPrompted ? RENAME_REQUEST_DIALOG_MAX_WIDTH_PX : RENAME_POPOVER_MAX_WIDTH_PX,
        availableWidth
    );
    const estimatedHeight = state.renameDialogPrompted
        ? RENAME_POPOVER_ESTIMATED_HEIGHT_PX + 42
        : RENAME_POPOVER_ESTIMATED_HEIGHT_PX;
    const minLeft = RENAME_POPOVER_VIEWPORT_PADDING_PX;
    const maxLeft = Math.max(minLeft, viewportWidth - RENAME_POPOVER_VIEWPORT_PADDING_PX - width);
    const fallbackLayout = (): RenamePopoverLayout => ({
        left: clampNumber((viewportWidth - width) / 2, minLeft, maxLeft),
        top: clampNumber(
            Math.round(
                state.renameDialogPrompted
                    ? (viewportHeight - estimatedHeight) / 2
                    : viewportHeight * 0.18
            ),
            RENAME_POPOVER_VIEWPORT_PADDING_PX,
            Math.max(RENAME_POPOVER_VIEWPORT_PADDING_PX, viewportHeight - RENAME_POPOVER_VIEWPORT_PADDING_PX - estimatedHeight)
        ),
        width,
        arrowLeft: width / 2,
        placement: "below",
        anchored: false
    });

    if (state.renameDialogPrompted || !state.renameDialogAnchorSelector) {
        return state.renameDialogPrompted ? fallbackLayout() : null;
    }

    const anchor = document.querySelector<HTMLElement>(state.renameDialogAnchorSelector);

    if (!anchor) {
        return state.renameDialogPrompted ? fallbackLayout() : null;
    }

    const screenRect = anchor.getBoundingClientRect();
    const rect = {
        left: screenRect.left / layoutScale,
        top: screenRect.top / layoutScale,
        bottom: screenRect.bottom / layoutScale,
        width: screenRect.width / layoutScale,
        height: screenRect.height / layoutScale
    };

    if (rect.width <= 0 && rect.height <= 0) {
        return state.renameDialogPrompted ? fallbackLayout() : null;
    }

    const arrowPadding = Math.min(RENAME_POPOVER_ARROW_EDGE_PADDING_PX, Math.max(18, width / 2 - 12));
    const anchorCenterX = rect.left + rect.width / 2;
    const left = clampNumber(anchorCenterX - width / 2, minLeft, maxLeft);
    const shouldPlaceAbove =
        rect.bottom + RENAME_POPOVER_OFFSET_PX + estimatedHeight >
        viewportHeight - RENAME_POPOVER_VIEWPORT_PADDING_PX;
    const placement: RenamePopoverLayout["placement"] = shouldPlaceAbove ? "above" : "below";
    const top = shouldPlaceAbove
        ? Math.max(
              RENAME_POPOVER_VIEWPORT_PADDING_PX,
              rect.top - RENAME_POPOVER_OFFSET_PX - estimatedHeight
          )
        : rect.bottom + RENAME_POPOVER_OFFSET_PX;
    const arrowLeft = clampNumber(
        anchorCenterX - left,
        arrowPadding,
        width - arrowPadding
    );

    return {
        left,
        top,
        width,
        arrowLeft,
        placement,
        anchored: true
    };
};

const buildRenamePopoverStyle = (layout: RenamePopoverLayout): string =>
    `left:${Math.round(layout.left)}px;top:${Math.round(layout.top)}px;width:${Math.round(
        layout.width
    )}px;--rename-popover-arrow-left:${Math.round(layout.arrowLeft)}px;`;

const getRenameOverlaySignature = (): string => {
    if (!state.renameDialogOpen) {
        return "closed";
    }

    const layout = getRenamePopoverLayout();

    if (!layout) {
        return `${state.renameDialogSessionId ?? "viewer"}:${state.renameDialogAnchorSelector ?? "none"}:missing`;
    }

    return `${state.renameDialogSessionId ?? "viewer"}:${state.renameDialogAnchorSelector ?? "none"}:${Math.round(
        layout.left
    )}:${Math.round(layout.top)}:${Math.round(layout.width)}:${layout.placement}:${
        layout.anchored ? "anchored" : "floating"
    }:${state.renameDialogPrompted ? "prompted" : "plain"}`;
};

const renderRoomOverlayMarkup = (): string => {
    const room = state.room;

    if (!room) {
        return "";
    }

    if (state.renameDialogOpen) {
        const layout = getRenamePopoverLayout();
        const renameTarget = getRenameTargetParticipant(room);

        if (!layout || !renameTarget) {
            return "";
        }

        const promptId = state.renameDialogPrompted ? "rename-player-request-prompt" : "";

        const promptedClass = state.renameDialogPrompted ? " is-prompted" : "";

        return `
            <div class="rename-popover-dismiss-region${promptedClass}" data-close-rename-dialog></div>
            <div
                class="rename-popover is-${layout.placement}${layout.anchored ? "" : " is-unanchored"}${promptedClass}"
                role="dialog"
                aria-modal="true"
                aria-label="${escapeHtml(t("editName"))}"
                ${promptId ? `aria-describedby="${promptId}"` : ""}
                style="${buildRenamePopoverStyle(layout)}"
            >
                ${
                    state.renameDialogPrompted
                        ? `<p id="${promptId}" class="rename-popover-note">${escapeHtml(t("renameRequestPrompt"))}</p>`
                        : ""
                }
                <label class="field rename-popover-field">
                    <input
                        id="rename-player-input"
                        type="text"
                        maxlength="24"
                        autocomplete="nickname"
                        aria-label="${escapeHtml(t("name"))}"
                        value="${escapeHtml(renameTarget.name)}"
                    />
                </label>
                <div class="rename-popover-actions">
                    <button class="secondary-button" type="button" data-close-rename-dialog>${escapeHtml(
                        t("close")
                    )}</button>
                    <button class="primary-button" type="button" id="rename-player-save-button">${escapeHtml(
                        t("save")
                    )}</button>
                </div>
            </div>
        `;
    }

    if (state.finalResultsPlayerListOpen && room.phase === "results" && room.finalResults) {
        const mode = room.finalResults.mode;
        const modeLabel = localizeModeLabel(mode) ?? t("mode");
        const modeClass = mode === PLANET_HOPPERS_MODE ? "is-planet-results" :
            mode === PICTURE_DETECTIVES_MODE
                ? "is-pictionary-results"
                : mode === RELIC_RUN_MODE
                  ? "is-relic-results"
                  : mode === VAULT_BREAKERS_MODE
                    ? "is-vault-results"
                  : "";

        return `
            <div class="room-overlay-scrim final-results-player-list-scrim" data-close-final-results-player-list></div>
            <section
                class="final-results-player-list-modal ${modeClass}"
                role="dialog"
                aria-modal="true"
                aria-labelledby="final-results-player-list-title"
            >
                <header class="final-results-player-list-head">
                    <div>
                        <p>${escapeHtml(modeLabel)}</p>
                        <h2 id="final-results-player-list-title">${escapeHtml(t("players"))}</h2>
                    </div>
                    <button
                        class="final-results-player-list-close"
                        type="button"
                        data-close-final-results-player-list
                        aria-label="${escapeHtml(t("close"))}"
                        title="${escapeHtml(t("close"))}"
                    >${renderZiarcadiaIcon("close", { className: "final-results-player-list-close-icon" })}</button>
                </header>
                ${renderPlayerResultRowsMarkup(room.finalResults.standings, mode)}
            </section>
        `;
    }

    if (!state.qrPreviewKind) {
        return "";
    }

    if (state.qrPreviewKind === "controller" && !room.viewer.isHost) {
        return "";
    }

    const isControllerPreview = state.qrPreviewKind === "controller";
    const previewTitle = isControllerPreview ? t("hostControllerQrTitle") : t("showQrCodeToJoin");
    const previewTitleMarkup = isControllerPreview
        ? `<span class="qr-preview-title">${escapeHtml(previewTitle)}</span>`
        : "";

    return `
        <div class="room-overlay-scrim" data-close-qr-preview></div>
        <section
            class="qr-preview-panel${isControllerPreview ? " has-title" : ""}"
            role="dialog"
            aria-modal="true"
            aria-label="${escapeHtml(previewTitle)}"
        >
            ${previewTitleMarkup}
            <span class="room-qr-frame qr-preview-frame">
                <img data-room-qr-image data-room-qr-target="preview" alt="${escapeHtml(previewTitle)}" hidden />
                <span class="room-qr-placeholder">QR</span>
            </span>
            <span class="room-code-display qr-preview-code" data-room-code-text>${escapeHtml(room.roomCode)}</span>
            <button class="qr-preview-close" type="button" data-close-qr-preview>${escapeHtml(t("close"))}</button>
        </section>
    `;
};

const renderActiveMarkup = (): string => `<div id="active-room-root"></div>`;

const formatFinalResultTime = (elapsedMs: number): string => {
    const safeMs = Math.max(0, Math.round(elapsedMs));
    const minutes = Math.floor(safeMs / 60_000);
    const seconds = (safeMs % 60_000) / 1000;

    if (minutes > 0) {
        return `${minutes}:${seconds.toFixed(2).padStart(5, "0")}`;
    }

    return `${(safeMs / 1000).toFixed(2)}s`;
};

const formatFinalResultMoney = (score: number): string => `$${Math.max(0, Math.trunc(score)).toLocaleString("en-US")}`;

const formatFinalResultPlacement = (placement: FinalResultGroupView["placement"]): string => {
    if (placement === 1) {
        return t("firstPlace");
    }

    if (placement === 2) {
        return t("secondPlace");
    }

    return t("thirdPlace");
};

type PlayerResultDisplayItem = {
    sessionId: string;
    name: string;
    placement: number | null;
    score: number | null;
    elapsedMs: number | null;
};

const formatFinalResultStandingPlacement = (placement: number | null): string => {
    if (placement === null) {
        return t("finalResultNoPlacement");
    }

    if (placement === 1 || placement === 2 || placement === 3) {
        return formatFinalResultPlacement(placement);
    }

    if (getLanguage() === "ko") {
        return `${placement}위`;
    }

    const finalTwoDigits = placement % 100;
    const suffix =
        finalTwoDigits >= 11 && finalTwoDigits <= 13
            ? "th"
            : placement % 10 === 1
              ? "st"
              : placement % 10 === 2
                ? "nd"
                : placement % 10 === 3
                  ? "rd"
                  : "th";

    return `${placement}${suffix}`;
};

const formatFinalResultValue = (group: FinalResultGroupView, mode: RoomMode | null): string => {
    if (mode === RELIC_RUN_MODE && group.elapsedMs !== null) {
        return formatFinalResultTime(group.elapsedMs);
    }

    if (mode === VAULT_BREAKERS_MODE && group.score !== null) {
        return formatFinalResultMoney(group.score);
    }

    if ((mode === PICTURE_DETECTIVES_MODE || mode === PLANET_HOPPERS_MODE) && group.score !== null) {
        return t("finalResultPointCount", { count: group.score });
    }

    return "";
};

const formatPlayerResultValue = (player: PlayerResultDisplayItem, mode: RoomMode | null): string => {
    if (mode === RELIC_RUN_MODE) {
        return player.elapsedMs !== null ? formatFinalResultTime(player.elapsedMs) : t("finalResultNoTime");
    }

    if (mode === VAULT_BREAKERS_MODE) {
        return formatFinalResultMoney(player.score ?? 0);
    }

    if (mode === PICTURE_DETECTIVES_MODE || mode === PLANET_HOPPERS_MODE) {
        return t("finalResultPointCount", { count: player.score ?? 0 });
    }

    return "";
};

const getPlayerResultPlacementClass = (placement: number | null): string => {
    if (placement === 1 || placement === 2 || placement === 3) {
        return `is-place-${placement}`;
    }

    return placement === null ? "is-unplaced" : "is-placed";
};

const renderPlayerResultRowsMarkup = (
    players: readonly PlayerResultDisplayItem[],
    mode: RoomMode | null,
    options: { compact?: boolean; fitRows?: boolean } = {}
): string => {
    if (players.length === 0) {
        return `<p class="player-results-empty">${escapeHtml(t("noPlayerResults"))}</p>`;
    }

    const metricLabel = mode === RELIC_RUN_MODE ? t("finalResultBestTime") : t("finalResultScore");
    const listClass = ["player-results-list", options.compact ? "is-compact" : "", options.fitRows ? "is-fitted" : ""]
        .filter(Boolean)
        .join(" ");
    const fitStyle = options.fitRows
        ? ` style="--player-results-row-count: ${Math.max(1, Math.ceil(players.length / 2))};"`
        : "";

    return `
        <div class="${listClass}"${fitStyle}>
            ${players
                .map(
                    (player) => `
                        <article
                            class="player-result-row ${getPlayerResultPlacementClass(player.placement)}"
                            data-player-result-row
                        >
                            <span class="player-result-place">${escapeHtml(
                                formatFinalResultStandingPlacement(player.placement)
                            )}</span>
                            <span class="player-result-name">${escapeHtml(player.name)}</span>
                            <span class="player-result-value" aria-label="${escapeHtml(metricLabel)}">${escapeHtml(
                                formatPlayerResultValue(player, mode)
                            )}</span>
                        </article>
                    `
                )
                .join("")}
        </div>
    `;
};

const getFinalResultNameLengthClass = (group: FinalResultGroupView): string => {
    const longestNameLength = Math.max(...group.players.map((player) => Array.from(player.name.trim()).length), 0);

    if (longestNameLength >= 28) {
        return "has-very-long-name";
    }

    if (longestNameLength >= 18) {
        return "has-long-name";
    }

    return "";
};

const getFinalResultPlayerCountClass = (playerCount: number): string => {
    if (playerCount >= 5) {
        return "has-5-plus-players";
    }

    return `has-${Math.max(1, playerCount)}-players`;
};

const renderFinalResultGroupMarkup = (
    group: FinalResultGroupView,
    mode: RoomMode | null,
    revealIndex: number
): string => {
    const value = formatFinalResultValue(group, mode);
    const metricLabel = mode === RELIC_RUN_MODE ? t("finalResultBestTime") : t("finalResultScore");
    const playerListClass = [
        group.players.length > 4 ? "has-many-players" : "",
        getFinalResultPlayerCountClass(group.players.length),
        group.players.length === 1 ? "has-single-player" : "has-tied-players"
    ]
        .filter(Boolean)
        .join(" ");
    const resultNameLengthClass = getFinalResultNameLengthClass(group);
    const resultClass = [
        `final-result-place is-place-${group.placement} is-reveal-${revealIndex}`,
        resultNameLengthClass
    ]
        .filter(Boolean)
        .join(" ");

    return `
        <article
            class="${resultClass}"
            style="--final-result-reveal-index: ${revealIndex}; --final-result-reveal-delay: ${getFinalResultRevealDelayMs(
                revealIndex
            )}ms"
            data-final-result-place="${group.placement}"
        >
            <div class="final-result-rank">${escapeHtml(formatFinalResultPlacement(group.placement))}</div>
            <div class="final-result-core">
                <ul class="final-result-player-list ${playerListClass}">
                    ${group.players
                        .map(
                            (player) => `
                                <li class="final-result-player">
                                    ${mode === PLANET_HOPPERS_MODE ? createCapyShieldElement({className:"ph-podium-capy ph-astronaut",hue: state.room?.planetHoppers?.crew.find(c => c.id === player.sessionId)?.hue ?? 184}).outerHTML : ""}
                                    <span class="final-result-player-name">${escapeHtml(player.name)}</span>
                                </li>
                            `
                        )
                        .join("")}
                </ul>
                <div class="final-result-metric">
                    <span>${escapeHtml(metricLabel)}</span>
                    <strong>${escapeHtml(value)}</strong>
                </div>
            </div>
        </article>
    `;
};

const renderFinalResultsMiniPodiumMarkup = (groups: readonly FinalResultGroupView[], mode: RoomMode | null): string => {
    if (mode !== PICTURE_DETECTIVES_MODE && mode !== VAULT_BREAKERS_MODE && mode !== PLANET_HOPPERS_MODE) {
        return "";
    }

    const topGroups = groups
        .filter((group) => group.placement >= 1 && group.placement <= 3)
        .slice()
        .sort((left, right) => left.placement - right.placement);

    if (topGroups.length === 0) {
        return "";
    }

    return `
        <div class="final-results-mini-podium" aria-label="${escapeHtml(t("finalResults"))}" data-final-mini-podium>
            ${topGroups
                .map((group) => {
                    const visiblePlayers = group.players.slice(0, 2);
                    const hiddenPlayerCount = Math.max(0, group.players.length - visiblePlayers.length);

                    return `
                        <article class="final-results-mini-place is-place-${group.placement}">
                            <span class="final-results-mini-rank">${escapeHtml(
                                formatFinalResultPlacement(group.placement)
                            )}</span>
                            <span class="final-results-mini-names">
                                ${visiblePlayers.map((player) => escapeHtml(player.name)).join(", ")}
                                ${
                                    hiddenPlayerCount > 0
                                        ? `<span class="final-results-mini-extra">+${hiddenPlayerCount}</span>`
                                        : ""
                                }
                            </span>
                            <strong class="final-results-mini-value">${escapeHtml(
                                formatFinalResultValue(group, mode)
                            )}</strong>
                        </article>
                    `;
                })
                .join("")}
        </div>
    `;
};

const renderFinalResultsMarkup = (): string => {
    const room = state.room;
    const results = room?.finalResults ?? null;
    const mode = results?.mode ?? room?.mode ?? null;
    const modeLabel = localizeModeLabel(mode) ?? t("mode");
    const groups = (results?.groups ?? []).slice().sort((left, right) => right.placement - left.placement);
    const standings = results?.standings ?? [];
    const ownStanding = room?.viewer.isHost || room?.viewer.isController
        ? undefined
        : standings.find((player) => player.sessionId === room?.viewer.sessionId);
    const podiumPlayerSessionIds =
        mode === PICTURE_DETECTIVES_MODE || mode === VAULT_BREAKERS_MODE || mode === PLANET_HOPPERS_MODE
            ? new Set(groups.flatMap((group) => group.players.map((player) => player.sessionId)))
            : new Set<string>();
    const standingsRows =
        podiumPlayerSessionIds.size > 0
            ? standings.filter((player) => !podiumPlayerSessionIds.has(player.sessionId))
            : standings;
    const sequenceKey = room ? getFinalResultsSequenceKey(room) : "";
    const sequenceClass = sequenceKey && sequenceKey === finalResultsSequenceRenderKey
        ? "is-sequenced-results"
        : "is-static-results";
    const fullListDelayMs =
        sequenceClass === "is-sequenced-results" ? getFinalResultsFullListDelayMs(groups.length) : 0;
    const modeClass =
        mode === PICTURE_DETECTIVES_MODE
            ? "is-pictionary-results"
            : mode === RELIC_RUN_MODE
              ? "is-relic-results"
              : mode === VAULT_BREAKERS_MODE
                ? "is-vault-results"
              : "";

    return `
        <section
            class="final-results-shell ${modeClass} ${sequenceClass}${ownStanding ? " is-player-results" : ""}"
            style="--final-results-full-list-delay: ${fullListDelayMs}ms"
            data-final-results
        >
            <div class="final-results-actions">
                ${
                    room?.viewer.isHost
                        ? `<button
                                class="primary-button final-results-continue-button"
                                type="button"
                                data-end-game
                                data-final-results-continue
                            >${escapeHtml(t("continueToModes"))}</button>`
                        : `<span class="final-results-waiting">${escapeHtml(t("waitingForHostContinue"))}</span>`
                }
            </div>
            <header class="final-results-header">
                <p class="final-results-kicker">${escapeHtml(ownStanding ? t("yourPlacement") : modeLabel)}</p>
                <h1>${escapeHtml(ownStanding ? formatFinalResultStandingPlacement(ownStanding.placement) : t("finalResults"))}</h1>
            </header>
            <div class="final-results-podium${groups.length === 0 ? " is-empty" : ""}" aria-live="polite">
                ${
                    groups.length > 0
                        ? groups
                              .map((group, index) => renderFinalResultGroupMarkup(group, mode, index))
                              .join("")
                        : `<p class="final-results-empty">${escapeHtml(t("noFinalResults"))}</p>`
                }
            </div>
            ${
                standings.length > 0
                    ? `<section class="final-results-standings" aria-live="polite">
                            ${renderFinalResultsMiniPodiumMarkup(groups, mode)}
                            ${standingsRows.length > 0 ? renderPlayerResultRowsMarkup(standingsRows, mode, { fitRows: true }) : ""}
                        </section>`
                    : ""
            }
        </section>
    `;
};

const getFinalResultsSignature = (room: RoomView | null): string => {
    const results = room?.finalResults ?? null;
    const mode = results?.mode ?? room?.mode ?? null;

    return JSON.stringify({
        language: getLanguage(),
        roomCode: room?.roomCode ?? "",
        isHost: room?.viewer.isHost ?? false,
        viewerSessionId: room?.viewer.sessionId ?? null,
        isController: room?.viewer.isController ?? false,
        mode,
        groups: (results?.groups ?? []).map((group) => ({
            placement: group.placement,
            score: group.score ?? null,
            elapsedMs: group.elapsedMs ?? null,
            players: group.players.map((player) => ({
                sessionId: player.sessionId,
                name: player.name
            }))
        })),
        standings: (results?.standings ?? []).map((player) => ({
            sessionId: player.sessionId,
            name: player.name,
            placement: player.placement,
            score: player.score ?? null,
            elapsedMs: player.elapsedMs ?? null
        }))
    });
};

const syncFinalResultsSequenceAfterRender = (room: RoomView) => {
    const sequenceKey = getFinalResultsSequenceKey(room);

    if (!sequenceKey || sequenceKey !== finalResultsSequenceRenderKey) {
        return;
    }

    scheduleFinalResultsSequenceAudio(room);
    markFinalResultsSequenceSeen(sequenceKey);
    finalResultsSequenceRenderKey = "";
};

const mountScreen = (screen: Screen) => {
    if (screen === currentScreen) {
        return;
    }

    destroyInteractiveViews();
    currentRoomVariant = null;
    currentOverlaySignature = "";
    currentFinalResultsSignature = "";
    currentScreen = screen;

    if (screen === "landing") {
        app.innerHTML = renderLandingMarkup();
        refs.banner = app.querySelector("#screen-banner");
        refs.viewerBadge = null;
        refs.playerStrip = null;
        refs.utilitySlot = null;
        refs.overlayRoot = null;
        refs.content = null;
        refs.connectingMessage = null;
        refs.toolContent = null;
        return;
    }

    if (screen === "qr-join") {
        app.innerHTML = renderQrJoinMarkup();
        refs.banner = app.querySelector("#screen-banner");
        refs.viewerBadge = null;
        refs.playerStrip = null;
        refs.utilitySlot = null;
        refs.overlayRoot = null;
        refs.content = null;
        refs.connectingMessage = null;
        refs.toolContent = null;
        return;
    }

    if (screen === "connecting") {
        app.innerHTML = renderConnectingMarkup();
        refs.banner = app.querySelector("#screen-banner");
        refs.connectingMessage = app.querySelector("#connecting-message");
        refs.viewerBadge = null;
        refs.playerStrip = null;
        refs.utilitySlot = null;
        refs.overlayRoot = null;
        refs.content = null;
        refs.toolContent = null;
        return;
    }

    if (screen === "tool-random-teams") {
        app.innerHTML = renderRandomTeamsToolScreenMarkup();
        refs.banner = app.querySelector("#screen-banner");
        refs.viewerBadge = null;
        refs.playerStrip = null;
        refs.utilitySlot = null;
        refs.overlayRoot = null;
        refs.content = null;
        refs.connectingMessage = null;
        refs.toolContent = app.querySelector("#tool-content");
        return;
    }

    if (screen === "tool-word-list" || screen === "tool-vocabulary-review") {
        app.innerHTML = screen === "tool-word-list" ? renderWordListToolScreenMarkup() : renderVocabularyReviewToolScreenMarkup();
        refs.banner = app.querySelector("#screen-banner");
        refs.viewerBadge = null;
        refs.playerStrip = null;
        refs.utilitySlot = null;
        refs.overlayRoot = null;
        refs.content = null;
        refs.connectingMessage = null;
        refs.toolContent = app.querySelector("#tool-content");
        return;
    }

    if (screen === "tool-autotranslator") {
        app.innerHTML = renderAutotranslatorToolScreenMarkup();
        refs.banner = app.querySelector("#screen-banner");
        refs.viewerBadge = null;
        refs.playerStrip = null;
        refs.utilitySlot = null;
        refs.overlayRoot = null;
        refs.content = null;
        refs.connectingMessage = null;
        refs.toolContent = app.querySelector("#tool-content");
        return;
    }

    if (screen === "tool-master1000") {
        app.innerHTML = renderMaster1000ToolScreenMarkup();
        refs.banner = app.querySelector("#screen-banner");
        refs.viewerBadge = null;
        refs.playerStrip = null;
        refs.utilitySlot = null;
        refs.overlayRoot = null;
        refs.content = null;
        refs.connectingMessage = null;
        refs.toolContent = null;
        return;
    }

    if (screen === "tool-gimcheon-school") {
        app.innerHTML = renderGimcheonSchoolScreenMarkup();
        refs.banner = app.querySelector("#screen-banner");
        refs.viewerBadge = null;
        refs.playerStrip = null;
        refs.utilitySlot = null;
        refs.overlayRoot = null;
        refs.content = null;
        refs.connectingMessage = null;
        refs.toolContent = null;
        return;
    }

    if (screen === "account") {
        app.innerHTML = renderAccountScreenMarkup();
        refs.banner = app.querySelector("#screen-banner");
        refs.viewerBadge = null;
        refs.playerStrip = null;
        refs.utilitySlot = null;
        refs.overlayRoot = null;
        refs.content = null;
        refs.connectingMessage = null;
        refs.toolContent = null;
        return;
    }

    app.innerHTML = renderRoomShellMarkup();
    refs.banner = app.querySelector("#screen-banner");
    refs.playerStrip = app.querySelector(".player-strip");
    refs.viewerBadge = app.querySelector("#viewer-badge");
    refs.utilitySlot = app.querySelector("#room-utility-slot");
    refs.overlayRoot = app.querySelector("#room-overlay-root");
    refs.content = app.querySelector("#room-content");
    refs.connectingMessage = null;
    refs.toolContent = null;
};

const updateBannerSlot = (options: { showInfo?: boolean } = {}) => {
    if (refs.banner) {
        refs.banner.innerHTML = renderBannerMarkup(options);
    }
};

const setText = (selector: string, value: string, root: ParentNode = app) => {
    const element = root.querySelector<HTMLElement>(selector);

    if (element) {
        element.textContent = value;
    }
};

const setTexts = (selector: string, value: string, root: ParentNode = app) => {
    root.querySelectorAll<HTMLElement>(selector).forEach((element) => {
        element.textContent = value;
    });
};

const setModeDescription = (selector: string, mode: RoomMode, value: string, root: ParentNode = app) => {
    const element = root.querySelector<HTMLElement>(selector);

    if (element) {
        element.innerHTML = renderModeDescriptionMarkup(mode, value);
    }
};

const setAttribute = (
    selector: string,
    attributeName: string,
    value: string,
    root: ParentNode = app
) => {
    const element = root.querySelector<HTMLElement>(selector);

    if (element) {
        element.setAttribute(attributeName, value);
    }
};

const setAttributes = (
    selector: string,
    attributeName: string,
    value: string,
    root: ParentNode = app
) => {
    root.querySelectorAll<HTMLElement>(selector).forEach((element) => {
        element.setAttribute(attributeName, value);
    });
};

const setInputValue = (selector: string, value: string, root: ParentNode = app) => {
    const input = root.querySelector<HTMLInputElement>(selector);

    if (input && document.activeElement !== input) {
        input.value = value;
    }
};

const refreshToolsBackLabels = (root: ParentNode = app) => {
    const label = t("backToTools");

    root.querySelectorAll<HTMLElement>("[data-tools-back]").forEach((button) => {
        const inlineLabel = button.querySelector<HTMLElement>("[data-tools-back-label]");

        if (inlineLabel) {
            inlineLabel.textContent = label;
            button.setAttribute("aria-label", label);
            button.setAttribute("title", label);
            return;
        }

        button.textContent = label;
    });
};

const setInputPlaceholder = (selector: string, value: string, root: ParentNode = app) => {
    const input = root.querySelector<HTMLInputElement | HTMLTextAreaElement>(selector);

    if (input) {
        input.placeholder = value;
    }
};

const setFieldLabelFor = (selector: string, value: string, root: ParentNode = app) => {
    const control = root.querySelector<HTMLElement>(selector);
    const label = control?.closest("label");
    const labelText = label?.querySelector<HTMLElement>(".landing-field-label");

    if (labelText) {
        labelText.textContent = value;
    }
};

const refreshLanguageToggleState = (root: ParentNode = app) => {
    const isKorean = getLanguage() === "ko";
    const label = t("toggleLanguageAria");

    root.querySelectorAll<HTMLButtonElement>("[data-language-toggle]").forEach((toggle) => {
        toggle.classList.toggle("is-korean", isKorean);
        toggle.classList.toggle("is-english", !isKorean);
        toggle.setAttribute("aria-checked", isKorean ? "true" : "false");
        toggle.setAttribute("aria-label", label);
        toggle.setAttribute("title", label);
    });
};

const refreshLandingLanguage = () => {
    refreshLanguageToggleState();
    updateBannerSlot();
    syncAccountControls();

    setText(".landing-card-join .landing-title", state.joinFromRoomLink ? t("placeName") : t("joinRoom"));
    setFieldLabelFor("#join-name-input", t("name"));
    setInputPlaceholder("#join-name-input", t("defaultPlayerPlaceholder"));
    setFieldLabelFor("#join-room-code-input", t("roomCode"));
    setText("#join-room-button", t("joinRoom"));

    if (state.hostPanelExpanded) {
        setText(".landing-host-card .landing-title", t("hostRoom"));
        setAttribute(".landing-host-card [data-collapse-panel]", "aria-label", t("minimize"));
        setFieldLabelFor("#host-name-input", t("name"));
        setInputPlaceholder("#host-name-input", t("defaultHostPlaceholder"));
        setText("#host-room-button", t("hostRoom"));
    } else {
        setText("#host-expand-button", t("hostRoom"));
    }

    if (state.toolsPanelExpanded) {
        setText(".landing-tools-card .landing-title", t("tools"));
        setAttribute(".landing-tools-card [data-collapse-panel]", "aria-label", t("minimize"));
        setText('[data-tool-option-title="random-teams"]', t("createRandomTeams"));
        setText('[data-tool-option-note="random-teams"] em', t("randomTeamsNote"));
        setText('[data-tool-option-title="word-list"]', t("createWordListTool"));
        setText('[data-tool-option-note="word-list"] em', t("wordListToolNote"));
        setText('[data-tool-option-title="vocabulary-review"]', t("vocabularyReviewTool"));
        setText('[data-tool-option-note="vocabulary-review"] em', t("vocabularyReviewNote"));
        setText('[data-tool-option-title="autotranslator"]', t("createAutotranslatorTool"));
        setText('[data-tool-option-note="autotranslator"] em', t("autotranslatorNote"));
        setText('[data-tool-option-title="master1000"]', t("playMaster1000"));
        setText('[data-tool-option-note="master1000"] em', t("master1000Note"));
    } else {
        setText("#tools-expand-button", t("tools"));
    }
};

const refreshQrJoinLanguage = () => {
    updateBannerSlot();
    setText(".qr-join-title", t("placeName"));
    setFieldLabelFor("#join-name-input", t("name"));
    setInputPlaceholder("#join-name-input", t("defaultPlayerPlaceholder"));
    setText("#join-room-button", t("join"));
};

const refreshRoomShellLanguage = () => {
    setText("#leave-room-button", t("leave"));
    refreshLanguageToggleState();
    syncAccountControls();
};

const refreshConnectingLanguage = () => {
    refreshLanguageToggleState();
    updateConnectingScreen();
};

const refreshRandomTeamsToolLanguage = () => {
    refreshLanguageToggleState();
    updateBannerSlot();
    syncAccountControls();
    setText(".random-teams-header-title", t("createRandomTeams"));
    refreshToolsBackLabels();
    randomTeamsToolView?.refreshLanguage();
};

const refreshWordListToolLanguage = () => {
    refreshLanguageToggleState();
    updateBannerSlot();
    syncAccountControls();
    setText(".tool-hero-title", t("createWordListTool"));
    refreshToolsBackLabels();
    wordListToolView?.refreshLanguage();
};

const refreshVocabularyReviewToolLanguage = () => {
    refreshLanguageToggleState();
    updateBannerSlot();
    syncAccountControls();
    setText(".tool-hero-title", t("vocabularyReviewTool"));
    refreshToolsBackLabels();
    vocabularyReviewToolView?.refreshLanguage();
};

const refreshAutotranslatorToolLanguage = () => {
    updateBannerSlot();
    refreshToolsBackLabels();
    autotranslatorToolView?.refreshLanguage();
};

const refreshMaster1000ToolLanguage = () => {
    refreshLanguageToggleState();
    updateBannerSlot();
    syncAccountControls();
    refreshToolsBackLabels();
    setText(".master1000-tool-empty-title", t("master1000MissingTitle"));
    setText(".master1000-tool-empty-body", t("master1000MissingBody", { path: MASTER1000_TOOL_SOURCE_DIR }));
    setAttribute(".master1000-tool-frame", "title", t("playMaster1000"));
    syncMaster1000FullscreenButton();
};

const refreshGimcheonSchoolLanguage = () => {
    updateGimcheonSchoolScreen();
};

const refreshAccountLanguage = () => {
    app.innerHTML = renderAccountScreenMarkup();
    refs.banner = app.querySelector("#screen-banner");
    refreshLanguageToggleState();
    renderGoogleSignInButtons();
    syncActivityHistory();
};

const updateLandingScreen = () => {
    app.innerHTML = renderLandingMarkup(true);
    refs.banner = app.querySelector("#screen-banner");
    refs.viewerBadge = null;
    refs.playerStrip = null;
    refs.utilitySlot = null;
    refs.overlayRoot = null;
    refs.content = null;
    refs.connectingMessage = null;
    refs.toolContent = null;
    currentOverlaySignature = "";
};

const updateQrJoinScreen = () => {
    app.innerHTML = renderQrJoinMarkup(true);
    refs.banner = app.querySelector("#screen-banner");
    refs.viewerBadge = null;
    refs.playerStrip = null;
    refs.utilitySlot = null;
    refs.overlayRoot = null;
    refs.content = null;
    refs.connectingMessage = null;
    refs.toolContent = null;
    currentOverlaySignature = "";
    syncQrJoinCardTheme();
    syncQrJoinSummaryPolling();
};

const updateConnectingScreen = () => {
    if (refs.connectingMessage) {
        refs.connectingMessage.textContent = localizeRuntimeText(state.info || "Connecting...");
    }

    updateBannerSlot({ showInfo: false });
};

const updateRandomTeamsToolScreen = () => {
    updateBannerSlot();

    if (refs.toolContent && !randomTeamsToolView) {
        randomTeamsToolView = createRandomTeamsTool(refs.toolContent, state.randomTeamsTool);
    }
};

const updateWordListToolScreen = () => {
    updateBannerSlot();

    if (refs.toolContent && !wordListToolView) {
        wordListToolView = createWordListTool(refs.toolContent, state.wordListTool, {
            onSaveHistory: (wordListState) => {
                void recordWordListHistory(wordListState);
            }
        });
    }
};

const updateAutotranslatorToolScreen = () => {
    updateBannerSlot();

    if (refs.toolContent && !autotranslatorToolView) {
        autotranslatorToolView = createAutotranslatorTool(refs.toolContent, state.autotranslator);
    }
};

const updateVocabularyReviewToolScreen = () => {
    updateBannerSlot();
    if (refs.toolContent && !vocabularyReviewToolView) {
        vocabularyReviewToolView = createVocabularyReviewTool(refs.toolContent, state.vocabularyReviewTool, {
            onSourceChange: source => vocabularyDraftSync.changed(source),
            onRetrySync: () => {
                if (state.account.loading) return;
                if (vocabularySyncStatus === "identity-error") {
                    void loadAccountSnapshot().then(() => syncAccountControls());
                } else {
                    void vocabularyDraftSync.retry();
                }
            },
            syncStatus: vocabularySyncStatus
        });
        if (state.account.initialized && !state.account.error) {
            void vocabularyDraftSync.restore(state.account.account?.accountId ?? null);
        }
    }
};

const updateMaster1000ToolScreen = () => {
    updateBannerSlot();
    const frame = app.querySelector<HTMLIFrameElement>(".master1000-tool-frame");

    if (frame && frame.dataset.master1000LoadBound !== "true") {
        frame.dataset.master1000LoadBound = "true";
        frame.addEventListener("load", () => {
            scheduleViewportTopReset();
            syncMaster1000FullscreenButton();
        });
    }

    restoreMaster1000FullscreenPreference();
    syncMaster1000FullscreenButton();
};

const updateGimcheonSchoolScreen = () => {
    const restoreChatInputFocus = shouldRestoreGimcheonSchoolChatInputFocus();
    app.innerHTML = renderGimcheonSchoolScreenMarkup();
    refs.banner = app.querySelector("#screen-banner");
    refs.viewerBadge = null;
    refs.playerStrip = null;
    refs.utilitySlot = null;
    refs.overlayRoot = null;
    refs.content = null;
    refs.connectingMessage = null;
    refs.toolContent = null;
    syncAccountControls();

    const chatBody = app.querySelector<HTMLElement>("[data-gimcheon-chat-body]");
    if (chatBody) {
        chatBody.scrollTop = chatBody.scrollHeight;
    }

    if (restoreChatInputFocus) {
        focusGimcheonSchoolChatInput();
    }
};

const syncActivityHistory = () => {
    const root = app.querySelector<HTMLElement>("[data-activity-history]");
    if (accountActivityHistory?.element === root) return;
    accountActivityHistory?.destroy(); accountActivityHistory = null;
    const accountId = state.account.account?.accountId;
    if (root && accountId) accountActivityHistory = new ActivityHistory(root, accountId, item => {
        if (state.account.account?.accountId !== accountId || !isGameActivity(item.mode)) return;
        if (state.room && state.room.phase !== "lobby") { showError(t("historyFinishGame")); return; }
        applyAccountPlayedSourceToHostDraft({ ...item, mode: item.mode, playedAt: item.hostedAt });
    });
};

const updateAccountScreen = () => {
    const content = app.querySelector<HTMLElement>("#account-content");
    const nextSignature = getAccountScreenSignature();

    if (!content) {
        app.innerHTML = renderAccountScreenMarkup();
        refs.banner = app.querySelector("#screen-banner");
    } else if (content.dataset.accountScreenSignature !== nextSignature) {
        content.innerHTML = renderAccountBodyMarkup();
        content.dataset.accountScreenSignature = nextSignature;
    }

    updateBannerSlot();
    refreshLanguageToggleState();
    renderGoogleSignInButtons();
    syncActivityHistory();
};

const mountRoomVariant = (variant: RoomVariant) => {
    if (!refs.content) {
        return;
    }

    destroyInteractiveViews();
    currentRoomVariant = variant;
    if (variant !== "host-lobby") {
        currentHostLobbyStructureSignature = "";
    }
    if (variant !== "results") {
        currentFinalResultsSignature = "";
        finalResultsSequenceRenderKey = "";
        clearFinalResultsSoundTimers();
    }
    currentActiveHostUtilitySignature = "";
    refs.content.innerHTML =
        variant === "host-lobby"
            ? renderHostLobbyMarkup()
            : variant === "player-lobby"
              ? renderPlayerLobbyMarkup()
              : variant === "controller-lobby"
                ? renderControllerLobbyMarkup()
                : variant === "results"
                  ? renderFinalResultsMarkup()
                  : renderActiveMarkup();

    if (variant === "results") {
        currentFinalResultsSignature = getFinalResultsSignature(state.room);
        scheduleViewportTopReset();
    }

    if (variant === "active") {
        const activeRoot = refs.content.querySelector<HTMLElement>("#active-room-root");

        if (activeRoot) {
            if (state.room?.mode === PICTURE_DETECTIVES_MODE) {
                pictionaryView = createPictionaryView(activeRoot, {
                    getRoleHint: () => state.currentConnection?.role ?? null,
                    onGuess: (guess: string) => {
                        sendMessage({
                            type: "pictionary-guess",
                            payload: {
                                guess
                            }
                        });
                    },
                    onNextRound: (
                        entryIndex: number,
                        pointValue: number,
                        revealDurationSeconds: number,
                        hintMode
                    ) => {
                        sendMessage({
                            type: "pictionary-next-round",
                            payload: {
                                entryIndex,
                                pointValue,
                                revealDurationSeconds,
                                hintMode
                            }
                        });
                    },
                    onEndRound: () => {
                        sendMessage({
                            type: "pictionary-end-round"
                        });
                    },
                    onDraw: (payload: {
                        strokeId: string;
                        begin: boolean;
                        end?: boolean;
                        points: PictionaryCanvasPoint[];
                    }) => {
                        sendMessage({
                            type: "pictionary-draw",
                            payload
                        });
                    },
                    onUndo: () => {
                        sendMessage({
                            type: "pictionary-undo-stroke"
                        });
                    },
                    onClear: () => {
                        sendMessage({
                            type: "pictionary-clear-canvas"
                        });
                    },
                    onControllerRoomChange: (roomCode: string) => {
                        void switchControllerRoom(roomCode);
                    },
                    onRenameParticipant: (sessionId: string, anchorSelector: string) => {
                        openViewerRename(anchorSelector, sessionId);
                    },
                    onPromptRenameParticipant: (sessionId: string) => {
                        sendRenamePromptToParticipant(sessionId);
                    },
                    onKickParticipant: (sessionId: string) => {
                        sendMessage({
                            type: "host-kick-player",
                            payload: {
                                targetSessionId: sessionId
                            }
                        });
                    },
                    onAdjustScore: (sessionId: string, delta: number) => {
                        sendMessage({
                            type: "pictionary-adjust-score",
                            payload: {
                                targetSessionId: sessionId,
                                delta
                            }
                        });
                    },
                    onPreviewWorth: (pointValue: number, manual: boolean) => {
                        sendMessage({
                            type: "pictionary-preview-worth",
                            payload: {
                                pointValue,
                                manual
                            }
                        });
                    },
                    onTranslationUsage: payload => sendMessage({ type: "pictionary-translation-usage", payload }, { silentIfDisconnected: true }),
                    onVocabularyReview: (open: boolean, index: number) => {
                        sendMessage({ type: "pictionary-vocabulary-review", payload: { open, index } });
                    }
                });
            } else if (state.room?.mode === PLANET_HOPPERS_MODE) {
                planetHoppersView = new PlanetHoppersView(activeRoot, message => sendMessage(message), status => sendMessage({ type: "viewer-translation", payload: { status } }, { silentIfDisconnected: true }));
            } else if (state.room?.mode === VAULT_BREAKERS_MODE) {
                vaultBreakersView = createVaultBreakersView(activeRoot, {
                    onChooseVault: (entryId: string) => {
                        sendActiveRoomActionMessage(
                            {
                                type: "vault-choose",
                                payload: {
                                    vaultId: entryId
                                }
                            },
                            "vault-choose"
                        );
                    },
                    onSelectChoice: (choiceId: string) => {
                        sendActiveRoomActionMessage(
                            {
                                type: "vault-select",
                                payload: {
                                    choiceId
                                }
                            },
                            "vault-select"
                        );
                    },
                    onNextCrack: (identity) => {
                        sendActiveRoomActionMessage(
                            {
                                type: "vault-next-crack",
                                payload: identity
                            },
                            "vault-next-crack"
                        );
                    },
                    onFinishRound: () => {
                        sendActiveRoomActionMessage(
                            {
                                type: "vault-finish-round"
                            },
                            "vault-finish-round"
                        );
                    },
                    onRollChooser: () => {
                        sendActiveRoomActionMessage(
                            {
                                type: "vault-roll-chooser"
                            },
                            "vault-roll-chooser"
                        );
                    },
                    onToggleFinalVault: () => {
                        sendActiveRoomActionMessage(
                            {
                                type: "vault-toggle-final"
                            },
                            "vault-toggle-final"
                        );
                    },
                    onUnlockNextValue: () => {
                        sendActiveRoomActionMessage(
                            {
                                type: "vault-unlock-next-value"
                            },
                            "vault-unlock-next-value"
                        );
                    },
                    onAdjustCrackDuration: (durationSeconds: number) => {
                        sendActiveRoomActionMessage(
                            {
                                type: "vault-adjust-crack-duration",
                                payload: {
                                    durationSeconds
                                }
                            },
                            "vault-adjust-crack-duration"
                        );
                    },
                    onPromptRenameParticipant: (sessionId: string) => {
                        sendRenamePromptToParticipant(sessionId);
                    },
                    onKickParticipant: (sessionId: string) => {
                        sendMessage({
                            type: "host-kick-player",
                            payload: {
                                targetSessionId: sessionId
                            }
                        });
                    },
                    onAdjustScore: (sessionId: string, delta: number) => {
                        sendActiveRoomActionMessage(
                            {
                                type: "vault-adjust-score",
                                payload: {
                                    targetSessionId: sessionId,
                                    delta
                                }
                            },
                            "vault-adjust-score"
                        );
                    },
                    onAfterRender: () => {
                        const room = state.room;

                        if (
                            !room ||
                            room.mode !== VAULT_BREAKERS_MODE ||
                            room.phase !== "active" ||
                            !room.viewer.isHost
                        ) {
                            return;
                        }

                        syncRoomQrImages("join", buildJoinLink(room.roomCode));
                    }
                });
            } else {
                mazeView = createMazeView(activeRoot, {
                    onMove: (target: GridPosition) => {
                        mazeView?.recordAttempt(target);
                        sendMessage({
                            type: "maze-move",
                            payload: target
                        });
                    },
                    onReady: () => {
                        sendMessage(
                            {
                                type: "maze-ready"
                            },
                            { silentIfDisconnected: true }
                        );
                    },
                    onReplay: () => {
                        sendMessage({
                            type: "maze-replay"
                        });
                    },
                    onPromptRenameParticipant: (sessionId: string) => {
                        sendRenamePromptToParticipant(sessionId);
                    },
                    onKickParticipant: (sessionId: string) => {
                        sendMessage({
                            type: "host-kick-player",
                            payload: {
                                targetSessionId: sessionId
                            }
                        });
                    }
                });
            }
        }

        return;
    }

    if (variant === "host-lobby" || variant === "player-lobby") {
        const playgroundRoot = refs.content.querySelector<HTMLElement>("[data-lobby-playground-root]");

        if (playgroundRoot) {
            lobbyPlayground =
                variant === "host-lobby"
                    ? createLobbyPlayground(playgroundRoot, {
                          interactive: false,
                          showViewerNameButton: false,
                          showTitle: false,
                          hideViewerAvatar: true
                      })
                    : createLobbyPlayground(playgroundRoot, {
                          cameraMode: "follow-viewer",
                          allowViewerRename: false,
                          onMove: (position) => {
                              sendMessage(
                                  {
                                      type: "lobby-move",
                                      payload: position
                                  },
                                  {
                                      silentIfDisconnected: true
                                  }
                              );
                          }
                      });
        }
    }
};

const updateRoomUtilitySlot = () => {
    if (!refs.utilitySlot) {
        return;
    }

    refs.utilitySlot.innerHTML = "";
};

const updateRoomOverlay = () => {
    if (!refs.overlayRoot) {
        return;
    }

    const markup = renderRoomOverlayMarkup();
    const finalResultsPlayerListSignature =
        state.finalResultsPlayerListOpen && state.room?.phase === "results"
            ? getFinalResultsSignature(state.room)
            : "closed";
    const signature =
        markup.length > 0
            ? `${getLanguage()}:${state.room?.roomCode ?? ""}:${state.qrPreviewKind ?? "none"}:${getRenameOverlaySignature()}:${finalResultsPlayerListSignature}`
            : "none";

    if (signature === currentOverlaySignature) {
        return;
    }

    const previousRenameInput = refs.overlayRoot.querySelector<HTMLInputElement>("#rename-player-input");
    const wasRenameInputFocused = previousRenameInput === document.activeElement;
    const previousRenameValue = previousRenameInput?.value ?? "";
    const previousSelectionStart = previousRenameInput?.selectionStart ?? null;
    const previousSelectionEnd = previousRenameInput?.selectionEnd ?? null;

    currentOverlaySignature = signature;
    refs.overlayRoot.innerHTML = markup;

    const previewRoom = state.room;
    const canShowPreview =
        Boolean(previewRoom) &&
        Boolean(state.qrPreviewKind) &&
        (state.qrPreviewKind === "join" || Boolean(previewRoom?.viewer.isHost));

    if (previewRoom && state.qrPreviewKind && canShowPreview) {
        syncRoomQrImages(
            "preview",
            state.qrPreviewKind === "controller"
                ? buildControllerLink(previewRoom.roomCode)
                : buildJoinLink(previewRoom.roomCode)
        );
    } else {
        clearRoomQrImages("preview");
    }

    const renameInput = refs.overlayRoot.querySelector<HTMLInputElement>("#rename-player-input");

    if (renameInput) {
        if (wasRenameInputFocused) {
            renameInput.value = previousRenameValue;
            renameInput.focus({ preventScroll: true });

            if (previousSelectionStart !== null && previousSelectionEnd !== null) {
                renameInput.setSelectionRange(previousSelectionStart, previousSelectionEnd);
            }
        } else {
            renameInput.focus({ preventScroll: true });
            renameInput.select();
        }
    }
};

const setRoomQrImages = (target: RoomQrTarget, dataUrl: string | null) => {
    app.querySelectorAll<HTMLImageElement>(`[data-room-qr-target="${target}"]`).forEach((image) => {
        const frame = image.closest<HTMLElement>(".room-qr-frame");

        if (!dataUrl) {
            image.hidden = true;
            image.removeAttribute("src");
            frame?.classList.remove("is-ready");
            return;
        }

        image.src = dataUrl;
        image.hidden = false;
        frame?.classList.add("is-ready");
    });
};

const clearRoomQrImages = (target: RoomQrTarget) => {
    currentQrLinks[target] = "";
    setRoomQrImages(target, null);
};

const clearAllRoomQrImages = () => {
    clearRoomQrImages("join");
    clearRoomQrImages("controller");
    clearRoomQrImages("preview");
};

const syncRoomQrImages = (target: RoomQrTarget, link: string) => {
    currentQrLinks[target] = link;
    const cachedQr = roomQrCache.get(link);

    if (cachedQr) {
        setRoomQrImages(target, cachedQr);
        return;
    }

    setRoomQrImages(target, null);

    void QRCode.toDataURL(link, {
        margin: SITE_TUNING.qr.bitmapMarginModules,
        width: SITE_TUNING.qr.bitmapWidthPx,
        color: {
            dark: SITE_TUNING.qr.bitmapDarkColorHex,
            light: SITE_TUNING.qr.bitmapLightColorHex
        }
    })
        .then((dataUrl: string) => {
            roomQrCache.set(link, dataUrl);

            if (currentQrLinks[target] !== link) {
                return;
            }

            setRoomQrImages(target, dataUrl);
        })
        .catch((error: unknown) => {
            logClientDebug("qr generation failed", {
                error: error instanceof Error ? error.message : "unknown"
            });
        });
};

const syncRoomChrome = (room: RoomView) => {
    if (refs.viewerBadge) {
        refs.viewerBadge.textContent =
            room.phase === "active"
                ? formatViewerBadge(
                      room.viewer.isHost,
                      room.viewer.isController,
                      room.viewer.name,
                      room.roomCode
                  )
                : "";
    }

    if (refs.playerStrip) {
        refs.playerStrip.hidden = room.phase !== "active";
    }

    app.querySelectorAll<HTMLElement>("[data-room-code-text]").forEach((element) => {
        element.textContent = room.roomCode;
        element.dataset.roomCodeLength = String(room.roomCode.length);
    });

    const roomShell = app.querySelector<HTMLElement>(".room-shell");
    // When the host backs out to mode selection, clear the lobby tint immediately
    // instead of waiting for the server draft echo to catch up.
    const hostLobbyMode =
        room.viewer.isHost && room.phase === "lobby"
            ? isHostModeSelectionOpen()
                ? null
                : state.hostDraft.mode
            : room.mode;
    const hasLobbyRelicSelection =
        room.phase === "lobby" && hostLobbyMode === RELIC_RUN_MODE;
    const hasLobbyPictionarySelection =
        room.phase === "lobby" && hostLobbyMode === PICTURE_DETECTIVES_MODE;
    const hasLobbyVaultSelection =
        room.phase === "lobby" && hostLobbyMode === VAULT_BREAKERS_MODE;
    const hasLobbyPlanetSelection =
        room.phase === "lobby" && hostLobbyMode === PLANET_HOPPERS_MODE;
    const isActivePictionary = room.phase === "active" && room.mode === PICTURE_DETECTIVES_MODE;
    const isActiveVaultBreakers = room.phase === "active" && room.mode === VAULT_BREAKERS_MODE;
    roomShell?.classList.toggle("is-active-layout", room.phase === "active");
    roomShell?.classList.toggle("is-results-layout", room.phase === "results");
    roomShell?.classList.toggle("is-host-lobby", room.viewer.isHost && room.phase === "lobby");
    roomShell?.classList.toggle("is-active-pictionary-host", isActivePictionary && room.viewer.isHost);
    roomShell?.classList.toggle("is-active-vault-host", isActiveVaultBreakers && room.viewer.isHost);
    roomShell?.classList.toggle(
        "is-active-pictionary-host-controller",
        isActivePictionary && room.viewer.isHost && room.viewer.isController
    );
    roomShell?.classList.toggle("has-lobby-relic-selection", hasLobbyRelicSelection);
    roomShell?.classList.toggle("has-lobby-pictionary-selection", hasLobbyPictionarySelection);
    roomShell?.classList.toggle("has-lobby-vault-selection", hasLobbyVaultSelection);
    roomShell?.classList.toggle("has-lobby-planet-selection", hasLobbyPlanetSelection);
};

const updateHostLobby = (room: RoomView) => {
    const controllerSeatClaimed =
        state.hostDraft.mode === PICTURE_DETECTIVES_MODE && isHostControllerSeatClaimed();
    const externalControllerClaimed =
        state.hostDraft.mode === PICTURE_DETECTIVES_MODE && isExternalHostControllerSeatClaimed();

    if (!controllerSeatClaimed || !previousHostControllerSeatClaimed) {
        state.hostControllerQrExpanded = false;
    }

    previousHostControllerSeatClaimed = controllerSeatClaimed;

    const structureSignature = `${getLanguage()}:${state.hostDraft.mode ?? "none"}:${state.hostModePanelView}:${
        state.hostDraft.mode === PICTURE_DETECTIVES_MODE ? (state.hostDraft.pictionaryNoTranslations ? "1" : "0") : "0"
    }:${state.hostDraft.mode === PICTURE_DETECTIVES_MODE ? (state.hostDraft.pictionaryNoHints ? "1" : "0") : "0"}:${
        state.hostDraft.mode === PICTURE_DETECTIVES_MODE ? (controllerSeatClaimed ? "1" : "0") : "0"
    }:${externalControllerClaimed ? "external-controller" : "local-controller"
    }:${state.hostDraft.mode === PICTURE_DETECTIVES_MODE && controllerSeatClaimed && state.hostControllerQrExpanded ? "qr-expanded" : "qr-resting"
    }:${state.hostDraft.mode === PICTURE_DETECTIVES_MODE ? state.hostPictionaryEditorMode : "text"
    }:${state.hostDraft.mode === VAULT_BREAKERS_MODE ? state.hostVaultEditorMode : "text"
    }:${state.hostSourceImportOpen ? "import-open" : "import-closed"}:${
        state.hostSourceTranslationOpen ? "translation-open" : "translation-closed"
    }:${state.hostSourceTranslationLoading ? "translation-loading" : "translation-idle"}:${
        state.hostSourceTranslationError ? "translation-error" : "translation-clean"
    }:${
        hostPlayerHistoryMenuOpen ? "history-open" : "history-closed"
    }:${getRecentUploadsForMode(state.hostDraft.mode)
        .map((upload) => upload.id)
        .join(",")}`;

    if (currentRoomVariant !== "host-lobby" || structureSignature !== currentHostLobbyStructureSignature) {
        mountRoomVariant("host-lobby");
        currentHostLobbyStructureSignature = structureSignature;
    }

    syncRoomChrome(room);
    const phRoot = refs.content?.querySelector<HTMLElement>("#ph-editor-root");
    if (phRoot && phRoot !== planetEditorRoot) {
        planetHoppersEditor?.destroy(); planetEditorRoot = phRoot;
        planetHoppersEditor = new PlanetHoppersEditor(phRoot, state.hostDraft.sourceText, source => { if(state.hostDraft.mode !== PLANET_HOPPERS_MODE)return; state.hostDraft.sourceText = source; state.hostDraftDirty = true; pushHostDraftDebounced(); const b = refs.content?.querySelector<HTMLButtonElement>("#start-room-button"); if(b)syncHostStartButton(b); }, async blob => {
            const response = await fetch(`/api/rooms/${room.roomCode}/ph-media`, { method: "POST", headers: { Authorization: `Bearer ${state.currentConnection?.hostToken || ""}` }, body: blob });
            if (!response.ok) throw new Error(await response.text()); return (await response.json()).url;
        });
    }

    const setupRoot = refs.content?.querySelector<HTMLElement>("#host-setup-library");
    if (setupRoot && setupRoot !== setupLibraryRoot) {
        setupLibraryRoot = setupRoot;
        setupLibrary ??= new SetupLibrary({
            current: (): SavedSetup | null => state.hostDraft.mode ? ({
                format: "ziarcadia-setup", version: 1, mode: state.hostDraft.mode as SetupMode,
                title: state.hostDraft.mode === PLANET_HOPPERS_MODE ? (() => { try { return JSON.parse(state.hostDraft.sourceText).title || localizeModeLabel(state.hostDraft.mode) || "Planet Hoppers"; } catch { return "Planet Hoppers"; } })() : getPlayedSourceTitle(state.hostDraft.sourceText),
                sourceText: state.hostDraft.sourceText,
                settings: { singleLineMode: state.hostDraft.singleLineMode, hostParticipates: state.hostDraft.hostParticipates, pictionaryStem: state.hostDraft.pictionaryStem, pictionaryNoHints: state.hostDraft.pictionaryNoHints, pictionaryNoTranslations: state.hostDraft.pictionaryNoTranslations, pictionarySameStem: state.hostDraft.pictionarySameStem, pictionaryHostIsController: state.hostDraft.pictionaryHostIsController }
            }) : null,
            account: () => state.account.account?.accountId || "",
            legacy: () => {
                const mode = state.hostDraft.mode;
                if (!mode) return [];
                const defaults = { singleLineMode: false, hostParticipates: false, pictionaryStem: "", pictionaryNoHints: false, pictionaryNoTranslations: false, pictionarySameStem: false, pictionaryHostIsController: false };
                const uploads = state.account.uploads.filter(item => item.mode === mode).map(item => ({ id: `upload-${item.id}`, title: item.fileName, mode: item.mode as SetupMode, revision: 0, updatedAt: item.uploadedAt, preview: JSON.stringify(item.sourceText.split(/\r?\n/).filter(Boolean).slice(0, 3)), source: JSON.stringify({ format: "ziarcadia-setup", version: 1, mode: item.mode, title: item.fileName, sourceText: item.sourceText, settings: defaults }) }));
                const played = getPlayedSourceHistoryForMode(mode).map(item => ({ id: `played-${item.id}`, title: getPlayedSourceTitle(item.sourceText), mode: item.mode as SetupMode, revision: 0, updatedAt: item.playedAt, preview: JSON.stringify(item.sourceText.split(/\r?\n/).filter(Boolean).slice(0, 3)), source: JSON.stringify({ format: "ziarcadia-setup", version: 1, mode: item.mode, title: getPlayedSourceTitle(item.sourceText), sourceText: item.sourceText, settings: { ...defaults, ...(state.account.preferences[mode] ? { singleLineMode: state.account.preferences[mode].singleLineMode, hostParticipates: state.account.preferences[mode].hostParticipates, pictionaryNoHints: state.account.preferences[mode].pictionaryNoHints, pictionaryNoTranslations: state.account.preferences[mode].pictionaryNoTranslations, pictionarySameStem: state.account.preferences[mode].pictionarySameStem, pictionaryHostIsController: state.account.preferences[mode].pictionaryHostIsController } : {}) } }) }));
                return [...uploads, ...played] as SetupLibraryItem[];
            },
            apply: setup => {
                state.hostDraft.mode = setup.mode; state.hostModePanelView = "config"; state.hostSourceImportOpen = false;
                state.hostDraft.sourceText = setup.sourceText;
                state.hostDraft.singleLineMode = setup.settings.singleLineMode; state.hostDraft.hostParticipates = setup.settings.hostParticipates;
                state.hostDraft.pictionaryStem = setup.settings.pictionaryStem; state.hostDraft.pictionaryNoHints = setup.settings.pictionaryNoHints;
                state.hostDraft.pictionaryNoTranslations = setup.settings.pictionaryNoTranslations; state.hostDraft.pictionarySameStem = setup.settings.pictionarySameStem;
                state.hostDraft.pictionaryHostIsController = setup.settings.pictionaryHostIsController;
                if (setup.mode === PICTURE_DETECTIVES_MODE) { state.hostDraft.pictionarySourceText = setup.sourceText; syncHostDraftPictionarySourceText(); }
                if (setup.mode === VAULT_BREAKERS_MODE) { state.hostDraft.vaultSourceText = setup.sourceText; syncHostDraftVaultSourceText(); }
                if (setup.mode === RELIC_RUN_MODE) state.hostDraft.relicSourceText = setup.sourceText;
                if (setup.mode === PLANET_HOPPERS_MODE) { try { localStorage.setItem("ziarcadia:planet-hoppers:draft:v1", setup.sourceText); } catch {} }
                state.hostDraftDirty = true; resetHostSourceTranslation(); pushHostDraftDebounced(); updateUi();
            },
            planet: action => planetHoppersEditor?.libraryAction(action),
            planetStatus: slot => planetHoppersEditor?.mountLibraryStatus(slot)
        });
    }
    setupLibrary?.mount(setupRoot!, state.hostDraft.mode as SetupMode | null);

    const modeDescription = localizeModeDescription(RELIC_RUN_MODE) ?? t("relicRunDescription");
    const modeLabel = localizeModeLabel(RELIC_RUN_MODE) ?? t("relicRun");
    const pictionaryDescription =
        localizeModeDescription(PICTURE_DETECTIVES_MODE) ?? t("pictureDetectivesDescription");
    const pictionaryLabel = localizeModeLabel(PICTURE_DETECTIVES_MODE) ?? t("pictureDetectives");
    const vaultDescription = localizeModeDescription(VAULT_BREAKERS_MODE) ?? t("vaultBreakersDescription");
    const vaultLabel = localizeModeLabel(VAULT_BREAKERS_MODE) ?? t("vaultBreakers");
    const modeButton = refs.content?.querySelector<HTMLButtonElement>("#select-relic-run-button");
    const pictionaryModeButton =
        refs.content?.querySelector<HTMLButtonElement>("#select-picture-detectives-button");
    const vaultModeButton = refs.content?.querySelector<HTMLButtonElement>("#select-vault-breakers-button");
    const modeCard = refs.content?.querySelector<HTMLElement>(".host-mode-card");
    const startLoadingOverlay = refs.content?.querySelector<HTMLElement>("#host-start-loading-overlay");
    const modeSetupPanel = refs.content?.querySelector<HTMLElement>("#host-mode-setup-panel");
    const modeSummary = refs.content?.querySelector<HTMLElement>("#host-mode-summary");
    const modeFocusLabel = refs.content?.querySelector<HTMLElement>(".host-mode-focus-label");
    const sourceEditor = app.querySelector<HTMLElement>("#host-source-editor");
    const textarea = app.querySelector<HTMLTextAreaElement>("#host-source-text");
    const sourceGutterShell = app.querySelector<HTMLElement>("#host-source-gutter-shell");
    const sourceGutter = app.querySelector<HTMLElement>("#host-source-gutter");
    const vaultSourceGuides = app.querySelector<HTMLElement>("#host-vault-source-guides");
    const vaultMiniBoard = app.querySelector<HTMLElement>("#host-vault-mini-board");
    const pictionarySourceGutter = app.querySelector<HTMLElement>("#host-pictionary-source-gutter");
    const pictionaryPreview = app.querySelector<HTMLElement>("#host-pictionary-preview");
    const pictionaryTable = app.querySelector<HTMLElement>("#host-pictionary-table");
    const vaultTable = app.querySelector<HTMLElement>("#host-vault-table");
    const sourceImportButton = app.querySelector<HTMLButtonElement>("#host-source-import-button");
    const pictionaryFogButton = app.querySelector<HTMLButtonElement>("#host-pictionary-fog-button");
    const vaultFogButton = app.querySelector<HTMLButtonElement>("#host-vault-fog-button");
    const sourceFileInput = app.querySelector<HTMLInputElement>("#host-source-file-input");
    const singleLineToggle = app.querySelector<HTMLInputElement>("#host-single-line-toggle");
    const hostParticipationToggle = app.querySelector<HTMLInputElement>("#host-participation-toggle");
    const pictionaryNoTranslationsToggle = app.querySelector<HTMLInputElement>("#host-pictionary-no-translations-toggle");
    const pictionaryNoHintsToggle = app.querySelector<HTMLInputElement>("#host-pictionary-no-hints-toggle");
    const pictionaryDeviceControllerToggle = app.querySelector<HTMLInputElement>(
        "#host-pictionary-device-controller-toggle"
    );
    const startButton = app.querySelector<HTMLButtonElement>("#start-room-button");
    const rosterCount = refs.content?.querySelector<HTMLElement>(".host-presence-count");
    const rosterHistoryControl = refs.content?.querySelector<HTMLElement>(
        ".host-presence-roster > [data-player-history-menu], .host-presence-roster > .host-presence-count"
    );
    const participants = refs.content?.querySelector<HTMLElement>("#lobby-participants");
    const controllerQrPanel = app.querySelector<HTMLElement>("#host-controller-qr-inline");
    const controllerAccessPanel = app.querySelector<HTMLElement>(".host-controller-access-panel");
    const hostNameLabel = refs.content?.querySelector<HTMLElement>(".host-presence-name-label");
    const returnToSelectionButton =
        refs.content?.querySelector<HTMLButtonElement>("#return-to-mode-selection-button");
    const hostParticipants = getHostLobbyParticipants(room);
    const showInlineControllerQr = !controllerSeatClaimed || state.hostControllerQrExpanded;
    const controllerQrButtonTitle = showInlineControllerQr ? t("hostControllerPurpose") : t("showHostControllerQr");

    if (state.hostDraft.mode !== PICTURE_DETECTIVES_MODE && state.qrPreviewKind === "controller") {
        state.qrPreviewKind = null;
    }

    setAttributes('[data-open-qr-preview][data-qr-kind="join"]', "aria-label", t("showQrCodeToJoin"), refs.content ?? app);
    setAttributes('[data-room-qr-target="join"]', "alt", t("scanToJoin"), refs.content ?? app);
    setAttributes('[data-room-qr-target="controller"]', "alt", t("hostControllerQrTitle"), refs.content ?? app);
    setAttributes("#host-controller-qr-inline", "title", controllerQrButtonTitle, refs.content ?? app);
    setText(".host-qr-caption", t("tapQrToExpand"), refs.content ?? app);
    setAttributes('[data-copy="join"]', "aria-label", t("copyJoinLink"), refs.content ?? app);
    setAttributes('[data-copy="join"]', "title", t("copyJoinLink"), refs.content ?? app);
    setText(".host-qr-copy-link-label", t("copyJoinLink"), refs.content ?? app);
    setInputPlaceholder(
        "#host-source-text",
        state.hostDraft.mode === PICTURE_DETECTIVES_MODE
            ? t("pictionarySourcePlaceholder")
            : state.hostDraft.mode === VAULT_BREAKERS_MODE
              ? t("vaultSourcePlaceholder")
              : t("pastePlaceholder")
    );
    setAttribute(
        "#host-source-text",
        "aria-label",
        state.hostDraft.mode === PICTURE_DETECTIVES_MODE
            ? t("addWords")
            : state.hostDraft.mode === VAULT_BREAKERS_MODE
              ? t("pasteVaults")
              : t("pasteText")
    );
    setText(".host-relic-single-line-label", getTreatAsOneLevelLabel(), refs.content ?? app);
    setText(".host-relic-participation-label", getHostParticipationLabel(), refs.content ?? app);
    setText(".host-pictionary-no-translations-label", t("noTranslations"), refs.content ?? app);
    setText(".host-pictionary-no-hints-label", t("noHints"), refs.content ?? app);
    setText(".host-pictionary-device-controller-label", t("makeThisDeviceController"), refs.content ?? app);
    setText(".host-pictionary-word-label", t("word"), refs.content ?? app);
    setText(".host-pictionary-translation-label", t("translation"), refs.content ?? app);
    setText(".host-pictionary-stem-label", t("hint"), refs.content ?? app);
    setText(".relic-mode-title", modeLabel, refs.content ?? app);
    setModeDescription("#select-relic-run-button .relic-mode-kicker", RELIC_RUN_MODE, modeDescription, refs.content ?? app);
    setText(".pictionary-mode-title", pictionaryLabel, refs.content ?? app);
    setModeDescription(
        "#select-picture-detectives-button .relic-mode-kicker",
        PICTURE_DETECTIVES_MODE,
        pictionaryDescription,
        refs.content ?? app
    );
    setText(".vault-mode-title", vaultLabel, refs.content ?? app);
    setModeDescription(
        "#select-vault-breakers-button .relic-mode-kicker",
        VAULT_BREAKERS_MODE,
        vaultDescription,
        refs.content ?? app
    );
    setText(".host-controller-qr-label", t("controllerLabel"), refs.content ?? app);

    const importFileLabel =
        state.hostDraft.mode === PICTURE_DETECTIVES_MODE || state.hostDraft.mode === VAULT_BREAKERS_MODE
            ? t("importSourceFile")
            : t("importTextFile");

    if (modeButton) {
        modeButton.title = modeDescription;
    }

    if (pictionaryModeButton) {
        pictionaryModeButton.title = pictionaryDescription;
    }

    if (vaultModeButton) {
        vaultModeButton.title = vaultDescription;
    }

    if (returnToSelectionButton) {
        returnToSelectionButton.title = t("returnToSelection");
    }

    if (sourceImportButton) {
        sourceImportButton.setAttribute("aria-label", importFileLabel);
        sourceImportButton.title = importFileLabel;
    }

    if (pictionaryFogButton) {
        const fogLabel = t(state.hostPictionarySourceFogged ? "showCaseWords" : "fogCaseWords");

        pictionaryFogButton.setAttribute("aria-label", fogLabel);
        pictionaryFogButton.setAttribute("aria-pressed", state.hostPictionarySourceFogged ? "true" : "false");
        pictionaryFogButton.title = fogLabel;
        pictionaryFogButton.classList.toggle("is-active", state.hostPictionarySourceFogged);
    }

    if (vaultFogButton) {
        const fogLabel = t(state.hostVaultSourceFogged ? "showVaultBoard" : "fogVaultBoard");

        vaultFogButton.setAttribute("aria-label", fogLabel);
        vaultFogButton.setAttribute("aria-pressed", state.hostVaultSourceFogged ? "true" : "false");
        vaultFogButton.title = fogLabel;
        vaultFogButton.classList.toggle("is-active", state.hostVaultSourceFogged);
    }

    if (sourceFileInput) {
        sourceFileInput.setAttribute("aria-label", importFileLabel);
        sourceFileInput.setAttribute(
            "accept",
            state.hostDraft.mode === PICTURE_DETECTIVES_MODE
                ? ".txt,.csv,.tsv,.xlsx,text/plain,text/csv,text/tab-separated-values,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                : state.hostDraft.mode === VAULT_BREAKERS_MODE
                  ? ".txt,.csv,.tsv,.xlsx,text/plain,text/csv,text/tab-separated-values,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  : ".txt,text/plain"
        );
    }

    if (controllerQrPanel) {
        controllerQrPanel.setAttribute("aria-label", t("showHostControllerQr"));
        controllerQrPanel.setAttribute("title", controllerQrButtonTitle);
        controllerQrPanel.classList.toggle("is-connected", controllerSeatClaimed);
        controllerQrPanel.classList.toggle("is-collapsed", !showInlineControllerQr);
        controllerQrPanel.classList.toggle("is-expanded", showInlineControllerQr);

        if (showInlineControllerQr) {
            controllerQrPanel.setAttribute("data-open-qr-preview", "");
            controllerQrPanel.setAttribute("data-qr-kind", "controller");
            controllerQrPanel.removeAttribute("data-expand-controller-qr");
        } else {
            controllerQrPanel.setAttribute("data-expand-controller-qr", "");
            controllerQrPanel.removeAttribute("data-open-qr-preview");
            controllerQrPanel.removeAttribute("data-qr-kind");
        }
    }

    if (controllerAccessPanel) {
        controllerAccessPanel.classList.toggle("is-claimed", controllerSeatClaimed);
        controllerAccessPanel.classList.toggle("is-external-claimed", externalControllerClaimed);
    }

    modeButton?.classList.toggle("selected", state.hostDraft.mode === RELIC_RUN_MODE && !isHostModeSelectionOpen());
    modeButton?.setAttribute(
        "aria-expanded",
        state.hostDraft.mode === RELIC_RUN_MODE && !isHostModeSelectionOpen() ? "true" : "false"
    );
    pictionaryModeButton?.classList.toggle(
        "selected",
        state.hostDraft.mode === PICTURE_DETECTIVES_MODE && !isHostModeSelectionOpen()
    );
    pictionaryModeButton?.setAttribute(
        "aria-expanded",
        state.hostDraft.mode === PICTURE_DETECTIVES_MODE && !isHostModeSelectionOpen() ? "true" : "false"
    );
    vaultModeButton?.classList.toggle(
        "selected",
        state.hostDraft.mode === VAULT_BREAKERS_MODE && !isHostModeSelectionOpen()
    );
    vaultModeButton?.setAttribute(
        "aria-expanded",
        state.hostDraft.mode === VAULT_BREAKERS_MODE && !isHostModeSelectionOpen() ? "true" : "false"
    );
    modeCard?.classList.toggle("is-awaiting-selection", state.hostDraft.mode === null);
    modeCard?.classList.toggle("is-relic-selected", state.hostDraft.mode === RELIC_RUN_MODE);
    modeCard?.classList.toggle("is-pictionary-selected", state.hostDraft.mode === PICTURE_DETECTIVES_MODE);
    modeCard?.classList.toggle("is-vault-selected", state.hostDraft.mode === VAULT_BREAKERS_MODE);
    modeCard?.classList.toggle("is-selection-open", isHostModeSelectionOpen());
    modeCard?.classList.toggle("is-config-open", !isHostModeSelectionOpen());
    const hostStartPending = isHostStartPending(room);
    const hostStartOverlayVisible = isHostStartLoadingOverlayVisible(room);
    modeCard?.classList.toggle("is-starting-game", hostStartPending);
    modeCard?.setAttribute("aria-busy", hostStartPending ? "true" : "false");

    if (startLoadingOverlay) {
        startLoadingOverlay.hidden = !hostStartOverlayVisible;
        startLoadingOverlay.classList.toggle("is-visible", hostStartOverlayVisible);
        startLoadingOverlay.setAttribute("aria-busy", hostStartOverlayVisible ? "true" : "false");
    }

    if (modeSetupPanel) {
        const expanded = state.hostDraft.mode !== null;
        modeSetupPanel.hidden = !expanded;
        modeSetupPanel.classList.toggle("is-open", expanded);
        modeSetupPanel.classList.toggle("is-selected", expanded);
        modeSetupPanel.classList.toggle("is-pictionary", state.hostDraft.mode === PICTURE_DETECTIVES_MODE);
        modeSetupPanel.classList.toggle("is-vault", state.hostDraft.mode === VAULT_BREAKERS_MODE);
    }

    if (modeSummary) {
        syncHostModeSummary(refs.content ?? app);
    }

    if (modeFocusLabel) {
        modeFocusLabel.textContent = localizeModeLabel(state.hostDraft.mode) ?? "";
    }

    if (hostNameLabel) {
        hostNameLabel.textContent = room.viewer.name;
    }

    if (
        textarea &&
        textarea.value !== state.hostDraft.sourceText &&
        (document.activeElement !== textarea || !state.hostDraftDirty)
    ) {
        textarea.value = state.hostDraft.sourceText;
    }

    if (sourceEditor) {
        sourceEditor.classList.toggle("is-single-line", state.hostDraft.singleLineMode);
        sourceEditor.classList.toggle("is-pictionary-grid", state.hostDraft.mode === PICTURE_DETECTIVES_MODE);
        sourceEditor.classList.toggle("is-vault-grid", state.hostDraft.mode === VAULT_BREAKERS_MODE);
        sourceEditor.classList.toggle("is-no-translations", state.hostDraft.pictionaryNoTranslations);
        sourceEditor.classList.toggle("is-no-hints", state.hostDraft.pictionaryNoHints);
        sourceEditor.classList.toggle(
            "is-source-fogged",
            state.hostDraft.mode === PICTURE_DETECTIVES_MODE
                ? state.hostPictionarySourceFogged
                : state.hostDraft.mode === VAULT_BREAKERS_MODE
                  ? state.hostVaultSourceFogged
                  : false
        );
        sourceEditor.classList.toggle(
            "is-pictionary-table-mode",
            state.hostDraft.mode === PICTURE_DETECTIVES_MODE && state.hostPictionaryEditorMode === "table"
        );
        sourceEditor.classList.toggle(
            "is-pictionary-text-mode",
            state.hostDraft.mode === PICTURE_DETECTIVES_MODE && state.hostPictionaryEditorMode === "text"
        );
        sourceEditor.classList.toggle(
            "is-vault-table-mode",
            state.hostDraft.mode === VAULT_BREAKERS_MODE && state.hostVaultEditorMode === "table"
        );
        sourceEditor.classList.toggle(
            "is-vault-text-mode",
            state.hostDraft.mode === VAULT_BREAKERS_MODE && state.hostVaultEditorMode === "text"
        );
    }

    if (textarea) {
        textarea.wrap = "soft";
        const syncVaultMiniBoard = () => syncHostVaultMiniBoardActiveLine(textarea);
        textarea.onscroll = () => {
            syncHostSourceTrackScroll(textarea, sourceGutter);
            syncHostSourceTrackScroll(textarea, vaultSourceGuides);
            syncHostSourceTrackScroll(textarea, pictionarySourceGutter);
        };
        textarea.onclick = syncVaultMiniBoard;
        textarea.onkeyup = syncVaultMiniBoard;
        textarea.onselect = syncVaultMiniBoard;
        syncHostSourceEditorHeight(textarea);
    }

    if (sourceGutterShell) {
        sourceGutterShell.hidden = false;
    }

    if (sourceGutter) {
        sourceGutter.innerHTML = renderHostSourceGutterMarkup();
        syncHostSourceTrackLayout(textarea, sourceGutter);
    }

    if (vaultSourceGuides) {
        vaultSourceGuides.innerHTML = renderHostVaultSourceGuidesMarkup();
        syncHostSourceTrackLayout(textarea, vaultSourceGuides);
    }

    if (vaultMiniBoard) {
        const nextSignature = getHostVaultMiniBoardSignature();

        if (vaultMiniBoard.dataset.vaultMiniBoardSignature !== nextSignature) {
            vaultMiniBoard.outerHTML = renderHostVaultMiniBoardMarkup();
        }

        syncHostVaultMiniBoardActiveLine(textarea);
    }

    if (pictionarySourceGutter) {
        pictionarySourceGutter.innerHTML = renderHostPictionarySourceGutterMarkup();
        syncHostSourceTrackLayout(textarea, pictionarySourceGutter);
    }

    if (singleLineToggle) {
        singleLineToggle.checked = state.hostDraft.singleLineMode;
    }

    if (hostParticipationToggle) {
        hostParticipationToggle.checked = state.hostDraft.hostParticipates;
    }

    if (pictionaryNoTranslationsToggle) {
        pictionaryNoTranslationsToggle.checked = state.hostDraft.pictionaryNoTranslations;
    }

    if (pictionaryNoHintsToggle) {
        pictionaryNoHintsToggle.checked = state.hostDraft.pictionaryNoHints;
    }

    if (pictionaryDeviceControllerToggle) {
        pictionaryDeviceControllerToggle.checked = state.hostDraft.pictionaryHostIsController;
    }

    if (pictionaryPreview) {
        const nextSignature = getHostPictionaryPreviewSignature();

        if (pictionaryPreview.dataset.pictionaryPreviewSignature !== nextSignature) {
            pictionaryPreview.innerHTML = renderHostPictionaryPreviewMarkup();
            pictionaryPreview.dataset.pictionaryPreviewSignature = nextSignature;
        }
    }

    if (pictionaryTable) {
        const nextSignature = getHostPictionaryTableSignature();

        if (pictionaryTable.dataset.pictionaryTableSignature !== nextSignature) {
            pictionaryTable.innerHTML = renderHostPictionaryTableRowsMarkup();
            pictionaryTable.dataset.pictionaryTableSignature = nextSignature;
        }
    }

    if (vaultTable) {
        const nextSignature = getHostVaultTableSignature();

        if (vaultTable.dataset.vaultTableSignature !== nextSignature) {
            vaultTable.innerHTML = renderHostVaultTableRowsMarkup();
            vaultTable.dataset.vaultTableSignature = nextSignature;
        }
    }

    if (startButton) {
        syncHostStartButton(startButton);
    }

    if (participants) {
        replaceInnerHtmlPreservingScroll(
            participants,
            renderParticipantRows(hostParticipants, t("noPlayersYet"), {
                allowHostRename: true,
                allowHostPromptRename: true,
                allowHostKick: true,
                openActionsKey: openRoomParticipantRosterActionsKey
            })
        );
        scheduleHostLobbyRosterLayoutSync();
    }

    const rosterPlayerCount = String(hostParticipants.length);

    if (rosterHistoryControl) {
        const historyControlSignature = [
            getLanguage(),
            rosterPlayerCount,
            room.viewer.isHost ? "host" : "guest",
            hostPlayerHistoryMenuOpen ? "open" : "closed"
        ].join(":");

        if (rosterHistoryControl.dataset.playerHistoryMenuSignature !== historyControlSignature) {
            const template = document.createElement("template");
            template.innerHTML = renderPlayerHistoryHeaderMenu(room, rosterPlayerCount, {
                className: "host-presence-history-menu",
                countClassName: "host-presence-count",
                open: hostPlayerHistoryMenuOpen
            }).trim();

            const nextControl = template.content.firstElementChild;

            if (nextControl instanceof HTMLElement) {
                nextControl.dataset.playerHistoryMenuSignature = historyControlSignature;
                rosterHistoryControl.replaceWith(nextControl);
            }
        }
    } else if (rosterCount) {
        rosterCount.textContent = rosterPlayerCount;
    }

    lobbyPlayground?.setRoom({
        ...room,
        mode: isHostModeSelectionOpen() ? null : state.hostDraft.mode
    });

    syncRoomQrImages("join", buildJoinLink(room.roomCode));

    if (state.hostDraft.mode === PICTURE_DETECTIVES_MODE) {
        syncRoomQrImages("controller", buildControllerLink(room.roomCode));
    } else {
        clearRoomQrImages("controller");
    }
};

const updatePlayerLobby = (room: RoomView) => {
    if (currentRoomVariant !== "player-lobby") {
        mountRoomVariant("player-lobby");
    }

    lobbyPlayground?.setRoom(room);
};

const updateControllerLobby = (room: RoomView) => {
    if (currentRoomVariant !== "controller-lobby") {
        mountRoomVariant("controller-lobby");
    }

    setText(
        ".controller-ready-title",
        room.viewer.isController ? t("hostController") : t("controllerLocked"),
        refs.content ?? app
    );
    setInputValue("#controller-room-code-input", room.roomCode, refs.content ?? app);
    setAttributes("#controller-room-code-input", "aria-label", t("controllerRoomCode"), refs.content ?? app);
    setAttributes("#controller-room-code-input", "title", t("controllerRoomCode"), refs.content ?? app);
    setAttributes("#controller-room-switch-button", "aria-label", t("switchRoom"), refs.content ?? app);
    setAttributes("#controller-room-switch-button", "title", t("switchRoom"), refs.content ?? app);
    setText("#controller-room-switch-button", t("switchRoomShort"), refs.content ?? app);
    setText(
        ".controller-ready-status",
        room.viewer.isController ? t("waitForHostToStart") : localizeRuntimeText(room.statusMessage),
        refs.content ?? app
    );
};

const updateActiveHostUtility = (room: RoomView) => {
    if (room.mode !== RELIC_RUN_MODE) {
        currentActiveHostUtilitySignature = "none";
        return;
    }

    const utilityCard = refs.content?.querySelector<HTMLElement>("[data-active-host-utility-root]");
    const sideCard = refs.content?.querySelector<HTMLElement>(".active-side-card");
    const shouldShow = room.viewer.isHost;

    sideCard?.classList.toggle("has-host-utility", shouldShow);

    if (!utilityCard) {
        return;
    }

    utilityCard.hidden = !shouldShow;

    if (!shouldShow) {
        utilityCard.innerHTML = "";
        currentActiveHostUtilitySignature = "none";
        return;
    }

    const signature = `${getLanguage()}:${room.roomCode}:${room.setup.hostParticipates ? "1" : "0"}`;

    if (signature !== currentActiveHostUtilitySignature) {
        utilityCard.innerHTML = renderActiveHostUtilityMarkup(room);
        currentActiveHostUtilitySignature = signature;
    }

    syncRoomQrImages("join", buildJoinLink(room.roomCode));
};

const updateActiveRoom = (room: RoomView) => {
    if (currentRoomVariant !== "active") {
        mountRoomVariant("active");
    }

    if (room.mode === PICTURE_DETECTIVES_MODE) {
        pictionaryView?.setRoom(room);

        if (room.viewer.isHost) {
            syncRoomQrImages("join", buildJoinLink(room.roomCode));

            if (room.pictionary && !room.pictionary.controllerConnected && !room.pictionary.controllerIsHost) {
                syncRoomQrImages("controller", buildControllerLink(room.roomCode));
            } else {
                clearRoomQrImages("controller");
            }
        }

        return;
    }

    if (room.mode === PLANET_HOPPERS_MODE) { planetHoppersView?.setRoom(room); if (room.viewer.isHost) syncRoomQrImages("join", buildJoinLink(room.roomCode)); updateActiveHostUtility(room); return; }
    if (room.mode === VAULT_BREAKERS_MODE) {
        vaultBreakersView?.setRoom(room);

        if (room.viewer.isHost) {
            syncRoomQrImages("join", buildJoinLink(room.roomCode));
        }

        updateActiveHostUtility(room);
        return;
    }

    mazeView?.setRoom(room);
    updateActiveHostUtility(room);
};

const updateFinalResultsRoom = (room: RoomView) => {
    const signature = getFinalResultsSignature(room);

    if (currentRoomVariant !== "results") {
        mountRoomVariant("results");
        currentFinalResultsSignature = signature;
    } else if (refs.content && signature !== currentFinalResultsSignature) {
        refs.content.innerHTML = renderFinalResultsMarkup();
        currentFinalResultsSignature = signature;
    }

    syncFinalResultsSequenceAfterRender(room);
    syncRoomChrome(room);
};

const updateRoomScreen = () => {
    const room = state.room;

    if (!room) {
        resetRoomAudioState();
        finalResultsSequenceRenderKey = "";
        clearFinalResultsSoundTimers();
        stopLobbySummaryPolling();
        clearActiveRoomBoundaryResyncTimers();
        clearActiveRoomActionResyncTimers();
        clearVaultAnswerRecovery();
        state.screen = "connecting";
        mountScreen("connecting");
        updateConnectingScreen();
        return;
    }

    syncLobbySummaryPolling();
    syncActiveRoomBoundaryResync();
    syncVaultAnswerRecovery();
    updateBannerSlot();
    updateRoomUtilitySlot();
    syncRoomAudioFeedback(room);

    if (room.phase === "lobby") {
        if (room.viewer.isHost) {
            updateHostLobby(room);
            updateRoomOverlay();
        } else if (state.currentConnection?.role === "controller") {
            state.qrPreviewKind = null;
            clearAllRoomQrImages();
            updateControllerLobby(room);
            updateRoomOverlay();
        } else {
            state.qrPreviewKind = null;
            clearAllRoomQrImages();
            updatePlayerLobby(room);
            updateRoomOverlay();
        }
    } else if (room.phase === "results") {
        state.qrPreviewKind = null;
        clearAllRoomQrImages();
        updateFinalResultsRoom(room);
        updateRoomOverlay();
    } else {
        if (!room.viewer.isHost) {
            state.qrPreviewKind = null;
            clearAllRoomQrImages();
        }
        updateActiveRoom(room);
        updateRoomOverlay();
    }

    syncRoomChrome(room);
};

const updateUi = () => {
    const discoveryFooter = document.querySelector<HTMLElement>("body > .discovery-footer");
    if (discoveryFooter && state.screen !== "landing") discoveryFooter.hidden = true;
    if (state.screen !== "account") { accountActivityHistory?.destroy(); accountActivityHistory = null; }
    syncDocumentTitle();
    syncThemeClass();
    if (state.screen !== "qr-join") {
        stopQrJoinSummaryPolling();
    }

    if (state.screen !== "tool-master1000") {
        master1000FullscreenRestoreAttempted = false;
        master1000ShellImmersive = false;
        if (isMaster1000StageFullscreen()) {
            void exitMaster1000NativeFullscreen();
        }
    }

    app.classList.toggle("master1000-shell-active", state.screen === "tool-master1000");
    syncMaster1000ShellPresentationClass();
    syncViewportLock();
    syncMaster1000ShellScrollLock();
    mountScreen(state.screen);

    if (state.screen === "landing") {
        updateLandingScreen();
        syncAccountControls();
        // Only the actual home screen may reveal this footer, never a room's
        // temporary landing shell while its summary/sign-in requests are pending.
        const params = new URLSearchParams(window.location.search);
        if (discoveryFooter) discoveryFooter.hidden = window.location.pathname !== "/" ||
            ["room", "join", "host", "controller", "account", "tool"].some(key => params.has(key));
        return;
    }

    if (state.screen === "qr-join") {
        updateQrJoinScreen();
        return;
    }

    if (state.screen === "connecting") {
        updateConnectingScreen();
        return;
    }

    if (state.screen === "tool-random-teams") {
        updateRandomTeamsToolScreen();
        syncAccountControls();
        return;
    }

    if (state.screen === "tool-word-list") {
        updateWordListToolScreen();
        syncAccountControls();
        return;
    }

    if (state.screen === "tool-vocabulary-review") {
        updateVocabularyReviewToolScreen();
        syncAccountControls();
        return;
    }

    if (state.screen === "tool-autotranslator") {
        updateAutotranslatorToolScreen();
        syncAccountControls();
        return;
    }

    if (state.screen === "tool-master1000") {
        updateMaster1000ToolScreen();
        syncAccountControls();
        return;
    }

    if (state.screen === "tool-gimcheon-school") {
        updateGimcheonSchoolScreen();
        return;
    }

    if (state.screen === "account") {
        updateAccountScreen();
        return;
    }

    updateRoomScreen();
    syncAccountControls();
};

const persistViewerName = (room: RoomView) => {
    if (state.currentConnection) {
        state.currentConnection.name = room.viewer.name;
    }

    if (room.viewer.isHost) {
        state.hostName = room.viewer.name;
        writeStoredValue(STORAGE_KEYS.hostName, room.viewer.name);
    } else {
        if (!room.viewer.renamePending) {
            state.joinName = room.viewer.name;
            writeStoredValue(STORAGE_KEYS.playerName, room.viewer.name);
        }
    }
};

const sendMessage = (
    message: ClientMessage,
    options?: {
        silentIfDisconnected?: boolean;
    }
): boolean => {
    logClientDebug("sending message", {
        type: message.type,
        readyState: socket?.readyState ?? null
    });

    if (!socket || socket.readyState !== WebSocket.OPEN) {
        if (!options?.silentIfDisconnected) {
            showError("The room connection is not ready.");
        }
        return false;
    }

    try {
        socket.send(JSON.stringify(message));
        return true;
    } catch (error) {
        logClientDebug("message send failed", {
            type: message.type,
            error: error instanceof Error ? error.message : String(error)
        });
        if (!options?.silentIfDisconnected) {
            showError("The room connection is not ready.");
        }
        return false;
    }
};

const requestRoomResync = (reason: string) => {
    logRoomSyncDiagnostic(`room-resync-request reason=${reason} room=${state.room?.roomCode ?? "none"}`);
    sendMessage(
        {
            type: "room-resync"
        },
        {
            silentIfDisconnected: true
        }
    );
};

const getInvalidVaultAnswerState = (
    room = state.room
): { signature: string; issue: "viewer-missing" | "choices-empty" } | null => {
    if (
        !room ||
        room.phase !== "active" ||
        room.mode !== VAULT_BREAKERS_MODE ||
        !room.vaultBreakers ||
        room.viewer.isHost ||
        room.viewer.isController
    ) {
        return null;
    }

    const vault = room.vaultBreakers;
    const issue = !vault.viewer
        ? "viewer-missing"
        : vault.roundActive &&
            vault.currentVaultId !== null &&
            vault.crackNumber !== null &&
            vault.viewer.choices.length === 0
          ? "choices-empty"
          : null;

    if (!issue) {
        return null;
    }

    return {
        signature: [
            room.roomCode,
            room.viewer.sessionId,
            vault.roundNumber,
            vault.currentVaultId ?? "none",
            vault.crackNumber ?? "none",
            issue
        ].join(":"),
        issue
    };
};

const clearVaultAnswerRecovery = () => {
    if (vaultAnswerRecoveryTimer !== null) {
        window.clearTimeout(vaultAnswerRecoveryTimer);
        vaultAnswerRecoveryTimer = null;
    }

    vaultAnswerRecoverySignature = "";
};

const syncVaultAnswerRecovery = () => {
    const invalidState = getInvalidVaultAnswerState();

    if (!invalidState) {
        clearVaultAnswerRecovery();
        return;
    }

    if (vaultAnswerRecoverySignature === invalidState.signature) {
        return;
    }

    clearVaultAnswerRecovery();
    vaultAnswerRecoverySignature = invalidState.signature;
    logRoomSyncDiagnostic(
        `vault-answer-state-invalid room=${state.room?.roomCode ?? "none"} ` +
            `session=${state.room?.viewer.sessionId ?? "none"} issue=${invalidState.issue} action=resync`
    );
    requestRoomResync(`vault-${invalidState.issue}`);
    vaultAnswerRecoveryTimer = window.setTimeout(() => {
        vaultAnswerRecoveryTimer = null;

        if (
            vaultAnswerRecoverySignature !== invalidState.signature ||
            getInvalidVaultAnswerState()?.signature !== invalidState.signature
        ) {
            return;
        }

        const connection = state.currentConnection;

        if (!connection) {
            return;
        }

        logRoomSyncDiagnostic(
            `vault-answer-state-invalid room=${connection.roomCode} session=${connection.sessionId} ` +
                `issue=${invalidState.issue} action=reconnect`
        );

        if (socket?.readyState === WebSocket.OPEN) {
            socket.close(4000, "Vault viewer state missing");
        } else {
            scheduleRoomReconnect(connection, true);
        }
    }, VAULT_ANSWER_RECOVERY_RECONNECT_MS);
};

const clearActiveRoomBoundaryResyncTimers = () => {
    for (const timer of activeRoomBoundaryResyncTimers) {
        window.clearTimeout(timer);
    }

    activeRoomBoundaryResyncTimers = [];
    activeRoomBoundaryResyncSignature = "";
};

const clearActiveRoomActionResyncTimers = () => {
    for (const timer of activeRoomActionResyncTimers) {
        window.clearTimeout(timer);
    }

    activeRoomActionResyncTimers = [];
    activeRoomActionResyncSignature = "";
};

const getActiveRoomResyncSignature = (room = state.room): string | null => {
    if (
        !room ||
        room.phase !== "active" ||
        !state.currentConnection ||
        state.currentConnection.roomCode !== room.roomCode
    ) {
        return null;
    }

    const vault = room.vaultBreakers;

    return [
        room.roomCode,
        room.viewer.sessionId,
        room.mode ?? "none",
        vault?.roundNumber ?? "none",
        vault?.crackNumber ?? "none",
        vault?.crackStartedAt ?? "none",
        vault?.roundCompletedAt ?? "none"
    ].join(":");
};

const getVaultBreakersBoundaryResyncTarget = (
    room: RoomView,
    now = Date.now()
): { signature: string; delayMs: number; reason: string } | null => {
    if (room.phase !== "active" || room.mode !== VAULT_BREAKERS_MODE || !room.vaultBreakers) {
        return null;
    }

    const roomSignature = getActiveRoomResyncSignature(room);

    if (!roomSignature) {
        return null;
    }

    const candidates: Array<{ label: string; boundaryAt: number; resyncAt: number }> = [];
    const addBoundary = (label: string, boundaryAt: number | null) => {
        if (typeof boundaryAt !== "number" || !Number.isFinite(boundaryAt)) {
            return;
        }

        const resyncAt = boundaryAt + ACTIVE_ROOM_BOUNDARY_RESYNC_GRACE_MS;

        if (resyncAt >= now - ACTIVE_ROOM_BOUNDARY_RESYNC_GRACE_MS) {
            candidates.push({ label, boundaryAt, resyncAt });
        }
    };

    addBoundary("massive-loot", room.vaultBreakers.massiveLootRevealEndsAt);
    addBoundary("arming", room.vaultBreakers.armingEndsAt);
    addBoundary("crack-start", room.vaultBreakers.crackStartedAt);
    addBoundary("crack-resolve", room.vaultBreakers.crackResolveAt);

    const next = candidates.sort((left, right) => left.resyncAt - right.resyncAt)[0] ?? null;

    if (!next) {
        return null;
    }

    return {
        signature: `${roomSignature}:${next.label}:${next.boundaryAt}`,
        delayMs: Math.max(0, next.resyncAt - now),
        reason: `vault-boundary-${next.label}`
    };
};

const syncActiveRoomBoundaryResync = () => {
    const room = state.room;
    const target = room ? getVaultBreakersBoundaryResyncTarget(room) : null;

    if (!target) {
        clearActiveRoomBoundaryResyncTimers();
        return;
    }

    if (
        activeRoomBoundaryResyncSignature === target.signature &&
        activeRoomBoundaryResyncTimers.length > 0
    ) {
        return;
    }

    clearActiveRoomBoundaryResyncTimers();
    activeRoomBoundaryResyncSignature = target.signature;
    activeRoomBoundaryResyncTimers = [target.delayMs, target.delayMs + ACTIVE_ROOM_BOUNDARY_RESYNC_RETRY_MS].map(
        (delayMs) =>
            window.setTimeout(() => {
                if (activeRoomBoundaryResyncSignature !== target.signature) {
                    return;
                }

                requestRoomResync(target.reason);
            }, delayMs)
    );
};

const queueActiveRoomActionResync = (reason: string) => {
    const signature = getActiveRoomResyncSignature();

    if (!signature) {
        return;
    }

    clearActiveRoomActionResyncTimers();
    activeRoomActionResyncSignature = `${signature}:${reason}:${Date.now()}`;
    activeRoomActionResyncTimers = ACTIVE_ROOM_ACTION_RESYNC_DELAYS_MS.map((delayMs) =>
        window.setTimeout(() => {
            if (!activeRoomActionResyncSignature.startsWith(`${signature}:${reason}:`)) {
                return;
            }

            requestRoomResync(`active-action-${reason}`);
        }, delayMs)
    );
};

const sendActiveRoomActionMessage = (message: ClientMessage, reason: string) => {
    if (sendMessage(message)) {
        queueActiveRoomActionResync(reason);
    }
};

const shouldRequestLobbyRosterResync = (room: RoomView): boolean => {
    if (room.phase !== "lobby" || !room.lobbyPlayground) {
        return false;
    }

    const visibleParticipantCount = getVisibleLobbyParticipantCount(room);
    const visibleLobbyPlayerCount = getVisibleLobbyPlaygroundPlayerCount(room);

    if (!hasLobbyRosterMismatch(room)) {
        lastLobbyRosterResyncSignature = "";
        return false;
    }

    const now = performance.now();
    const signature = `${room.roomCode}:${JSON.stringify(getConnectedLobbyRosterIds(room))}:${JSON.stringify(getLobbyPlaygroundIds(room))}`;

    if (
        signature === lastLobbyRosterResyncSignature &&
        now - lastLobbyRosterResyncAt < LOBBY_ROSTER_RESYNC_COOLDOWN_MS
    ) {
        return false;
    }

    lastLobbyRosterResyncSignature = signature;
    lastLobbyRosterResyncAt = now;
    logRoomSyncDiagnostic(
        `lobby-roster-mismatch room=${room.roomCode} localVisible=${visibleParticipantCount} ` +
            `lobbyPlayers=${visibleLobbyPlayerCount} rosterIds=${getConnectedLobbyRosterIds(room).join(",")} ` +
            `lobbyIds=${getLobbyPlaygroundIds(room).join(",")}`
    );
    return true;
};

// Only a genuinely hidden document means the student left this tab. Window blur also fires
// for harmless cases such as DevTools, browser chrome, or another app beside the game.
const getViewerPresenceHiddenState = (): boolean => document.visibilityState === "hidden";

const isViewerPresenceDebugEnabled = (): boolean => {
    try {
        return localStorage.getItem("ziarcadia:presence-debug") !== "0";
    } catch {
        return true;
    }
};

const flushViewerPresence = (reason: "connect" | "interaction" | "visibility") => {
    if (!state.currentConnection) {
        return;
    }

    const hidden = getViewerPresenceHiddenState();
    const interacted = viewerPresenceInteractionPending;
    const hiddenChanged = lastViewerPresenceHidden !== hidden;

    if (
        reason !== "connect" &&
        !interacted &&
        lastViewerPresenceHidden === hidden
    ) {
        return;
    }

    sendMessage(
        {
            type: "viewer-presence",
            payload: {
                hidden,
                interacted
            }
        },
        {
            silentIfDisconnected: true
        }
    );
    viewerPresenceInteractionPending = false;
    lastViewerPresenceHidden = hidden;
    lastViewerPresenceSentAt = performance.now();

    if (hiddenChanged && isViewerPresenceDebugEnabled()) {
        console.info(
            `[Ziarcadia][Presence] reason=${reason} hidden=${hidden ? 1 : 0} visibility=${document.visibilityState} focus=${typeof document.hasFocus === "function" && document.hasFocus() ? 1 : 0} interacted=${interacted ? 1 : 0}`
        );
    }
};

const startViewerPresenceTracking = () => {
    // A transport reconnect is not evidence of student interaction.
    viewerPresenceInteractionPending = false;
    lastViewerPresenceHidden = null;
    flushViewerPresence("connect");
    planetHoppersView?.reportTranslation();
};

const stopViewerPresenceTracking = () => {
    viewerPresenceInteractionPending = false;
    lastViewerPresenceHidden = null;
    lastViewerPresenceSentAt = 0;
};

const noteViewerPresenceInteraction = () => {
    if (!state.currentConnection) {
        return;
    }

    viewerPresenceInteractionPending = true;

    if (performance.now() - lastViewerPresenceSentAt >= PRESENCE_INTERACTION_THROTTLE_MS) {
        flushViewerPresence("interaction");
    }
};

const openViewerRename = (
    anchorSelector?: string,
    targetSessionId?: string,
    options: { prompted?: boolean } = {}
) => {
    const room = state.room;

    if (!room) {
        return;
    }

    state.renameDialogPrompted = options.prompted === true;
    state.renameDialogAnchorSelector = state.renameDialogPrompted
        ? null
        : anchorSelector ?? (room.viewer.isHost ? HOST_RENAME_BUTTON_SELECTOR : PLAYER_RENAME_BUTTON_SELECTOR);
    state.renameDialogSessionId = targetSessionId ?? room.viewer.sessionId;
    state.renameDialogOpen = true;
    updateUi();
};

const closeViewerRename = () => {
    state.renameDialogOpen = false;
    state.renameDialogAnchorSelector = null;
    state.renameDialogSessionId = null;
    state.renameDialogPrompted = false;
    updateUi();
};

const submitViewerRename = () => {
    const room = state.room;

    if (!room) {
        return;
    }

    const renameTarget = getRenameTargetParticipant(room);

    if (!renameTarget) {
        closeViewerRename();
        return;
    }

    const input = app.querySelector<HTMLInputElement>("#rename-player-input");
    const nextName = normalizePlayerName(input?.value ?? "", renameTarget.name);

    if (!nextName || nextName === renameTarget.name) {
        closeViewerRename();
        return;
    }

    if (state.currentConnection && renameTarget.sessionId === room.viewer.sessionId) {
        state.currentConnection.name = nextName;
    }

    sendMessage({
        type: "rename-player",
        payload: {
            name: nextName,
            targetSessionId:
                renameTarget.sessionId !== room.viewer.sessionId ? renameTarget.sessionId : undefined
        }
    });
    closeViewerRename();
};

const renamePromptFeedback = new RenamePromptFeedback(SITE_TUNING.room.renamePromptFeedbackTimeoutMs);
let renamePromptFeedbackTimer: number | null = null;

const syncRenamePromptFeedbackTimer = () => {
    if (renamePromptFeedbackTimer !== null) window.clearTimeout(renamePromptFeedbackTimer);
    renamePromptFeedbackTimer = null;
    const expiry = renamePromptFeedback.nextExpiry;
    if (expiry === null) return;
    renamePromptFeedbackTimer = window.setTimeout(() => {
        renamePromptFeedbackTimer = null;
        if (!state.room) return;
        state.room = renamePromptFeedback.expire(state.room, performance.now());
        logRoomSyncDiagnostic(`rename-prompt-timeout room=${state.room.roomCode} waitMs=${SITE_TUNING.room.renamePromptFeedbackTimeoutMs} action=resync`);
        requestRoomResync("rename-prompt-timeout");
        updateUi();
        syncRenamePromptFeedbackTimer();
    }, Math.max(1, Math.ceil(expiry - performance.now())));
};

const sendRenamePromptToParticipant = (targetSessionId: string) => {
    const room = state.room;
    const sent = sendMessage({
        type: "host-prompt-rename",
        payload: {
            targetSessionId
        }
    });
    if (sent && room) {
        state.room = renamePromptFeedback.request(room, targetSessionId, performance.now());
        syncRenamePromptFeedbackTimer();
        logRoomSyncDiagnostic(`rename-prompt-sent room=${room.roomCode} target=${targetSessionId} phase=${room.phase}`);
    }
    updateUi();
};

const pushHostDraft = () => {
    if (!state.room?.viewer.isHost || state.room.phase !== "lobby") {
        return;
    }

    if (!socket || socket.readyState !== WebSocket.OPEN) {
        return;
    }

    state.pendingHostSync = createHostSyncSnapshot();

    sendMessage({
        type: "host-configure",
        payload: {
            ...state.pendingHostSync
        }
    });
};

const pushHostDraftDebounced = debounce(pushHostDraft, 220);

const translateHostSource = async (action: SourceTranslationAction) => {
    syncHostTranslationRowsFromText();

    const mode = state.hostDraft.mode;

    if (mode !== PICTURE_DETECTIVES_MODE && mode !== VAULT_BREAKERS_MODE && mode !== PLANET_HOPPERS_MODE) {
        return;
    }

    const items = getHostSourceTranslationItems();
    const targets = action === "replace" ? items : items.filter((item) => item.currentTranslation.length === 0);

    if (targets.length === 0) {
        return;
    }

    const sourceText = state.hostDraft.sourceText;
    const requestId = ++hostSourceTranslationRequestId;
    state.hostSourceImportOpen = false;
    state.hostSourceTranslationOpen = true;
    state.hostSourceTranslationLoading = true;
    state.hostSourceTranslationError = "";
    updateUi();

    try {
        const response = await fetchJson<SourceTranslationResponse>("/api/source-translation/generate", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                mode,
                action,
                sourceText,
                items
            })
        });

        if (requestId !== hostSourceTranslationRequestId) {
            return;
        }

        if (state.hostDraft.mode !== mode || state.hostDraft.sourceText !== sourceText) {
            state.hostSourceTranslationError = t("translateSourceChanged");
            return;
        }

        const expectedIndexes = new Set(targets.map((item) => item.index));
        const translationsByIndex = new Map<number, string>();

        if (!Array.isArray(response.items) || response.items.length !== targets.length) {
            throw new Error("The translation response did not match the requested terms.");
        }

        response.items.forEach((item) => {
            const translation = typeof item?.translation === "string" ? item.translation.replace(/\s+/g, " ").trim() : "";

            if (
                !Number.isSafeInteger(item?.index) ||
                !expectedIndexes.has(item.index) ||
                translationsByIndex.has(item.index) ||
                !translation
            ) {
                throw new Error("The translation response contained an invalid term.");
            }

            translationsByIndex.set(item.index, translation);
        });

        let itemIndex = 0;

        if (mode === PICTURE_DETECTIVES_MODE) {
            state.hostDraft.pictionaryRows = state.hostDraft.pictionaryRows.map((row) => {
                if (!row.english.trim()) {
                    return row;
                }

                const translation = translationsByIndex.get(itemIndex++);

                return translation && (action === "replace" || !row.korean.trim())
                    ? { ...row, korean: translation }
                    : row;
            });
            syncHostDraftPictionaryRowsToSourceText();
        } else {
            state.hostDraft.vaultRows = state.hostDraft.vaultRows.map((row) => {
                if (!row.answer.trim()) {
                    return row;
                }

                const translation = translationsByIndex.get(itemIndex++);

                return translation && (action === "replace" || !row.korean.trim())
                    ? { ...row, korean: translation }
                    : row;
            });
            syncHostDraftVaultRowsToSourceText();
        }

        state.hostDraftDirty = true;
        state.hostSourceTranslationOpen = false;
        state.hostSourceTranslationError = "";
        pushHostDraftDebounced();
        scheduleAccountPreferenceSave(mode);
    } catch (error) {
        if (requestId !== hostSourceTranslationRequestId) {
            return;
        }

        console.warn(
            "[Ziarcadia][SourceTranslation] request failed",
            error instanceof Error ? error.message : "unknown"
        );
        state.hostSourceTranslationError = t("translateFailed");
    } finally {
        if (requestId === hostSourceTranslationRequestId) {
            state.hostSourceTranslationLoading = false;
            updateUi();
        }
    }
};

const clearHostStartLoadingOverlay = () => {
    if (hostStartLoadingOverlayTimer !== null) {
        window.clearTimeout(hostStartLoadingOverlayTimer);
        hostStartLoadingOverlayTimer = null;
    }

    hostStartLoadingOverlayVisible = false;
};

const queueHostStartLoadingOverlay = () => {
    clearHostStartLoadingOverlay();
    hostStartLoadingOverlayTimer = window.setTimeout(() => {
        hostStartLoadingOverlayTimer = null;

        if (!isHostStartPending()) {
            return;
        }

        hostStartLoadingOverlayVisible = true;
        updateUi();
    }, HOST_START_LOADING_OVERLAY_DELAY_MS);
};

const clearHostStartConfirmTimers = () => {
    for (const timer of hostStartConfirmTimers) {
        window.clearTimeout(timer);
    }

    hostStartConfirmTimers = [];
};

const stopLobbySummaryPolling = () => {
    if (lobbySummaryPollTimer !== null) {
        window.clearInterval(lobbySummaryPollTimer);
        lobbySummaryPollTimer = null;
    }

    clearHostStartConfirmTimers();
    lobbySummaryPollController?.abort();
    lobbySummaryPollController = null;
};

const normalizeSummaryMode = (mode: RoomMode | null): RoomMode | null =>
    mode === RELIC_RUN_MODE
        ? RELIC_RUN_MODE
        : mode === PICTURE_DETECTIVES_MODE
          ? PICTURE_DETECTIVES_MODE
          : mode === VAULT_BREAKERS_MODE
            ? VAULT_BREAKERS_MODE
            : null;

const syncQrJoinCardTheme = () => {
    const card = app.querySelector<HTMLElement>(".qr-join-card");

    if (!card) {
        return;
    }

    card.classList.toggle("is-relic-selected", state.qrJoinMode === RELIC_RUN_MODE);
    card.classList.toggle("is-pictionary-selected", state.qrJoinMode === PICTURE_DETECTIVES_MODE);
    card.classList.toggle("is-vault-selected", state.qrJoinMode === VAULT_BREAKERS_MODE);
};

const stopQrJoinSummaryPolling = () => {
    if (qrJoinSummaryPollTimer !== null) {
        window.clearInterval(qrJoinSummaryPollTimer);
        qrJoinSummaryPollTimer = null;
    }

    qrJoinSummaryPollInFlight = false;
    qrJoinSummaryRoomCode = "";
};

const pollQrJoinSummary = async () => {
    const roomCode = state.joinRoomCode;

    if (
        state.screen !== "qr-join" ||
        !isValidRoomCode(roomCode) ||
        qrJoinSummaryPollInFlight
    ) {
        return;
    }

    qrJoinSummaryPollInFlight = true;

    try {
        const summary = await fetchJson<RoomSummaryResponse>(`/api/rooms/${encodeURIComponent(roomCode)}`, { cache: "no-store" });

        if (state.screen !== "qr-join" || state.joinRoomCode !== roomCode) {
            return;
        }

        state.qrJoinMode = normalizeSummaryMode(summary.mode);
        syncQrJoinCardTheme();
    } catch {
        if (state.screen === "qr-join" && state.joinRoomCode === roomCode) {
            state.qrJoinMode = null;
            syncQrJoinCardTheme();
        }
    } finally {
        qrJoinSummaryPollInFlight = false;
    }
};

const syncQrJoinSummaryPolling = () => {
    const roomCode = state.joinRoomCode;

    if (state.screen !== "qr-join" || !isValidRoomCode(roomCode)) {
        stopQrJoinSummaryPolling();
        state.qrJoinMode = null;
        syncQrJoinCardTheme();
        return;
    }

    syncQrJoinCardTheme();

    if (qrJoinSummaryPollTimer !== null && qrJoinSummaryRoomCode === roomCode) {
        return;
    }

    stopQrJoinSummaryPolling();
    qrJoinSummaryRoomCode = roomCode;
    void pollQrJoinSummary();
    qrJoinSummaryPollTimer = window.setInterval(() => {
        void pollQrJoinSummary();
    }, LOBBY_SUMMARY_POLL_INTERVAL_MS);
};

const shouldPollLobbySummary = (): boolean =>
    state.screen === "room" &&
    state.room?.phase === "lobby" &&
    state.room.viewer.isHost &&
    Boolean(state.currentConnection) &&
    socket?.readyState === WebSocket.OPEN;

const pollLobbySummary = async () => {
    const connection = state.currentConnection;
    const localRoom = state.room;
    const pollingSocket = socket;

    if (!connection || !localRoom || localRoom.phase !== "lobby" || lobbySummaryPollController) {
        return;
    }

    const controller = new AbortController();
    lobbySummaryPollController = controller;
    const timeout = window.setTimeout(() => {
        logRoomSyncDiagnostic(`lobby-summary-timeout room=${connection.roomCode} timeoutMs=${SITE_TUNING.room.lobbySummaryTimeoutMs}`);
        controller.abort();
    }, SITE_TUNING.room.lobbySummaryTimeoutMs);

    try {
        const summary = await fetchJson<RoomSummaryResponse>(
            `/api/rooms/${encodeURIComponent(connection.roomCode)}`,
            { cache: "no-store", signal: controller.signal }
        );

        const currentRoom = state.room;

        if (
            controller.signal.aborted ||
            lobbySummaryPollController !== controller ||
            state.currentConnection !== connection ||
            socket !== pollingSocket ||
            !currentRoom ||
            currentRoom.roomCode !== connection.roomCode ||
            currentRoom.phase !== "lobby"
        ) {
            return;
        }

        if (summary.phase === "active" || summary.started) {
            stopLobbySummaryPolling();
            void connectToRoom(connection.roomCode, {
                name: connection.name,
                hostToken: connection.hostToken,
                role: connection.role,
                skipSummary: true,
                preserveRoomState: true
            }).catch((error: unknown) => {
                showError(error instanceof Error ? error.message : "Could not reconnect to the room.");
            });
            return;
        }

        const summaryMode = normalizeSummaryMode(summary.mode);
        const localVisibleParticipantCount = getVisibleLobbyParticipantCount(currentRoom);
        const localLobbyPlayerCount = currentRoom.lobbyPlayground
            ? getVisibleLobbyPlaygroundPlayerCount(currentRoom)
            : null;
        const summaryConnectedPlayerCount = Number.isFinite(summary.connectedPlayerCount)
            ? summary.connectedPlayerCount!
            : null;
        const summaryLobbyPlayerCount = Number.isFinite(summary.lobbyPlayerCount)
            ? summary.lobbyPlayerCount!
            : null;
        const modeMismatch = summaryMode !== normalizeSummaryMode(currentRoom.mode);
        const visibleCountMismatch =
            summaryConnectedPlayerCount !== null &&
            summaryConnectedPlayerCount !== localVisibleParticipantCount;
        const lobbyPlayerCountMismatch =
            summaryLobbyPlayerCount !== null &&
            localLobbyPlayerCount !== null &&
            summaryLobbyPlayerCount !== localLobbyPlayerCount;
        const connectedCountMismatch = summary.connectedCount !== currentRoom.connectedCount;
        const rosterCountMismatch = Number.isFinite(summary.playerCount) &&
            summary.playerCount !== getHostLobbyParticipants(currentRoom).length;

        if (modeMismatch || connectedCountMismatch || visibleCountMismatch || lobbyPlayerCountMismatch || rosterCountMismatch || hasLobbyRosterMismatch(currentRoom)) {
            logRoomSyncDiagnostic(
                `lobby-roster-resync room=${connection.roomCode} localCount=${currentRoom.connectedCount} ` +
                    `summaryCount=${summary.connectedCount} localVisiblePlayers=${localVisibleParticipantCount} ` +
                    `summaryVisiblePlayers=${summaryConnectedPlayerCount ?? "unknown"} ` +
                    `localLobbyPlayers=${localLobbyPlayerCount ?? "unknown"} ` +
                    `summaryLobbyPlayers=${summaryLobbyPlayerCount ?? "unknown"} ` +
                    `localRoster=${getHostLobbyParticipants(currentRoom).length} summaryRoster=${summary.playerCount ?? "unknown"} ` +
                    `localMode=${currentRoom.mode ?? "none"} summaryMode=${summaryMode ?? "none"}`
            );
            requestRoomResync("lobby-summary-mismatch");
        }
    } catch {
        // The WebSocket remains the primary room path; this watchdog only self-heals missed lobby broadcasts.
    } finally {
        window.clearTimeout(timeout);
        // An obsolete request must not unlock a newer connection's watchdog.
        if (lobbySummaryPollController === controller) lobbySummaryPollController = null;
    }
};

const queueHostStartConfirmation = () => {
    clearHostStartConfirmTimers();
    queueHostStartLoadingOverlay();

    const resyncTimers = HOST_START_ROOM_RESYNC_DELAYS_MS.map((delayMs) =>
        window.setTimeout(() => {
            if (
                state.screen !== "room" ||
                state.room?.phase !== "lobby" ||
                state.currentConnection?.role !== "host"
            ) {
                return;
            }

            requestRoomResync("host-start-confirmation");
        }, delayMs)
    );
    const pollTimers = HOST_START_CONFIRM_DELAYS_MS.map((delayMs) =>
        window.setTimeout(() => {
            if (
                state.screen !== "room" ||
                state.room?.phase !== "lobby" ||
                state.currentConnection?.role !== "host"
            ) {
                return;
            }

            void pollLobbySummary();
        }, delayMs)
    );

    hostStartConfirmTimers = [...resyncTimers, ...pollTimers];
};

const syncLobbySummaryPolling = () => {
    if (!shouldPollLobbySummary()) {
        stopLobbySummaryPolling();
        return;
    }

    if (lobbySummaryPollTimer !== null) {
        return;
    }

    lobbySummaryPollTimer = window.setInterval(() => {
        void pollLobbySummary();
    }, LOBBY_SUMMARY_POLL_INTERVAL_MS);
};

const clearConnectionLostNotice = () => {
    if (connectionLostNoticeTimer !== null) {
        window.clearTimeout(connectionLostNoticeTimer);
        connectionLostNoticeTimer = null;
    }

    if (state.info === "Connection lost. Reconnecting...") {
        state.info = "";
        updateUi();
    }
};

const stopConnectionHeartbeat = (targetSocket?: WebSocket) => {
    if (targetSocket && connectionHeartbeatSocket !== targetSocket) {
        return;
    }

    if (connectionHeartbeatTimer !== null) {
        window.clearInterval(connectionHeartbeatTimer);
        connectionHeartbeatTimer = null;
    }

    connectionHeartbeatSocket = null;
    lastConnectionMessageAt = 0;
};

const startConnectionHeartbeat = (targetSocket: WebSocket) => {
    stopConnectionHeartbeat();
    connectionHeartbeatSocket = targetSocket;
    lastConnectionMessageAt = performance.now();
    connectionHeartbeatTimer = window.setInterval(() => {
        if (socket !== targetSocket || targetSocket.readyState !== WebSocket.OPEN) {
            return;
        }

        const now = performance.now();

        if (
            isConnectionHeartbeatTimedOut(
                lastConnectionMessageAt,
                now,
                SITE_TUNING.room.connectionHeartbeatTimeoutMs
            )
        ) {
            logRoomSyncDiagnostic(
                `connection-heartbeat-timeout room=${state.currentConnection?.roomCode ?? "none"} ` +
                    `silentMs=${Math.round(now - lastConnectionMessageAt)}`
            );
            targetSocket.close(4000, "Connection heartbeat timed out");
            return;
        }

        try {
            targetSocket.send(ROOM_CONNECTION_HEARTBEAT_MESSAGE);
        } catch {
            targetSocket.close(4000, "Connection heartbeat failed");
        }
    }, SITE_TUNING.room.connectionHeartbeatIntervalMs);
};

const isCurrentConnection = (connection: ConnectionDetails): boolean =>
    state.currentConnection?.roomCode === connection.roomCode &&
    state.currentConnection.sessionId === connection.sessionId &&
    state.currentConnection.role === connection.role;

const scheduleRoomReconnect = (connection: ConnectionDetails, immediate = false) => {
    if (!isCurrentConnection(connection)) {
        return;
    }

    if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
    }

    const attempt = reconnectAttempt;
    reconnectAttempt += 1;
    const delayMs = immediate
        ? Math.round(40 + Math.random() * 160)
        : calculateReconnectDelayMs(attempt, {
              baseDelayMs: SITE_TUNING.room.reconnectBaseDelayMs,
              maxDelayMs: SITE_TUNING.room.reconnectMaxDelayMs,
              jitterRatio: SITE_TUNING.room.reconnectJitterRatio
          });

    logRoomSyncDiagnostic(
        `reconnect-scheduled room=${connection.roomCode} attempt=${attempt + 1} delayMs=${delayMs} ` +
            `online=${navigator.onLine === false ? 0 : 1}`
    );

    reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;

        if (!isCurrentConnection(connection)) {
            return;
        }

        void connectToRoom(connection.roomCode, {
            name: connection.name,
            hostToken: connection.hostToken,
            role: connection.role,
            skipSummary: true,
            preserveRoomState: true,
            isReconnect: true
        }).catch((error: unknown) => {
            logRoomSyncDiagnostic(
                `reconnect-attempt-failed room=${connection.roomCode} ` +
                    `error=${error instanceof Error ? error.message : String(error)}`
            );
            scheduleRoomReconnect(connection);
        });
    }, delayMs);
};

const closeSocket = (options: { resetReconnectAttempt?: boolean } = {}) => {
    state.room = renamePromptFeedback.clear(state.room);
    syncRenamePromptFeedbackTimer();
    if (reconnectTimer !== null) {
        window.clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }

    if (options.resetReconnectAttempt !== false) {
        reconnectAttempt = 0;
    }

    stopLobbySummaryPolling();
    clearActiveRoomBoundaryResyncTimers();
    clearActiveRoomActionResyncTimers();
    clearVaultAnswerRecovery();
    clearConnectionLostNotice();
    stopViewerPresenceTracking();
    stopConnectionHeartbeat();

    if (socket) {
        intentionalDisconnect = true;
        intentionallyClosedSockets.add(socket);
        socket.close();
        socket = null;
    }
};

const rerenderForLanguageChange = () => {
    syncDocumentTitle();
    document.querySelectorAll<HTMLElement>("[data-discovery-copy]").forEach(element => {
        element.textContent = element.dataset.discoveryCopy === "about" ? t("aboutZiarcadia") : t("siteSummary");
    });

    if (state.screen === "landing") {
        refreshLandingLanguage();
        return;
    }

    if (state.screen === "qr-join") {
        refreshQrJoinLanguage();
        return;
    }

    if (state.screen === "connecting") {
        refreshConnectingLanguage();
        return;
    }

    if (state.screen === "tool-random-teams") {
        refreshRandomTeamsToolLanguage();
        return;
    }

    if (state.screen === "tool-word-list") {
        refreshWordListToolLanguage();
        return;
    }

    if (state.screen === "tool-vocabulary-review") {
        refreshVocabularyReviewToolLanguage();
        return;
    }

    if (state.screen === "tool-autotranslator") {
        refreshAutotranslatorToolLanguage();
        return;
    }

    if (state.screen === "tool-master1000") {
        refreshMaster1000ToolLanguage();
        return;
    }

    if (state.screen === "tool-gimcheon-school") {
        refreshGimcheonSchoolLanguage();
        return;
    }

    if (state.screen === "account") {
        refreshAccountLanguage();
        return;
    }

    if (state.screen === "room") {
        refreshRoomShellLanguage();
        updateRoomScreen();
        return;
    }

    updateUi();
};

const setLandingActiveSection = (section: "join" | "host" | "tools") => {
    state.hostPanelExpanded = section === "host";
    state.toolsPanelExpanded = section === "tools";
};

const focusJoinRoomCodeInput = () => {
    window.requestAnimationFrame(() => {
        const roomCodeInput = app.querySelector<HTMLInputElement>("#join-room-code-input");
        roomCodeInput?.focus();
        roomCodeInput?.select();
    });
};

const focusJoinNameInput = () => {
    window.requestAnimationFrame(() => {
        const nameInput = app.querySelector<HTMLInputElement>("#join-name-input");
        nameInput?.focus();
        nameInput?.select();
    });
};

const showJoinFlowError = (message: string) => {
    const shouldStayOnQrJoin = state.joinFromQrLink && Boolean(state.joinRoomCode);
    state.screen = shouldStayOnQrJoin ? "qr-join" : "landing";

    if (!shouldStayOnQrJoin) {
        setLandingActiveSection("join");
    }

    showError(message);
    if (!shouldStayOnQrJoin) {
        scheduleViewportTopReset();
    }

    if (message.toLowerCase().includes("name")) {
        focusJoinNameInput();
    } else if (!shouldStayOnQrJoin) {
        focusJoinRoomCodeInput();
    }
};

const goHome = () => {
    if (state.screen === "room" || state.screen === "connecting") {
        leaveRoom();
        return;
    }

    if (state.screen === "account" && state.room && state.currentConnection) {
        clearBanners();
        state.screen = "room";
        window.history.replaceState(
            {},
            "",
            buildRoomUrl(state.currentConnection.roomCode, state.currentConnection.hostToken, state.currentConnection.role)
        );
        updateUi();
        return;
    }

    clearBanners();
    state.screen = "landing";
    state.hostPanelExpanded = false;
    state.toolsPanelExpanded = false;
    state.hostSourceImportOpen = false;
    resetHostSourceTranslation();
    state.joinFromRoomLink = false;
    state.joinFromQrLink = false;
    state.qrJoinMode = null;
    state.finalResultsPlayerListOpen = false;
    window.history.replaceState({}, "", "/");
    updateUi();
};

const openRandomTeamsTool = () => {
    clearBanners();
    state.screen = "tool-random-teams";
    state.hostPanelExpanded = false;
    state.toolsPanelExpanded = false;
    state.hostSourceImportOpen = false;
    resetHostSourceTranslation();
    state.joinFromRoomLink = false;
    state.joinFromQrLink = false;
    state.qrJoinMode = null;
    window.history.replaceState({}, "", SITE_PATHS.randomTeams);
    updateUi();
};

const openWordListTool = () => {
    clearBanners();
    state.screen = "tool-word-list";
    state.hostPanelExpanded = false;
    state.toolsPanelExpanded = false;
    state.hostSourceImportOpen = false;
    resetHostSourceTranslation();
    state.joinFromRoomLink = false;
    state.joinFromQrLink = false;
    state.qrJoinMode = null;
    window.history.replaceState({}, "", SITE_PATHS.wordList);
    updateUi();
    scheduleViewportTopReset();
};

const openVocabularyReviewTool = () => {
    clearBanners();
    state.screen = "tool-vocabulary-review";
    state.hostPanelExpanded = false;
    state.toolsPanelExpanded = false;
    state.hostSourceImportOpen = false;
    resetHostSourceTranslation();
    state.joinFromRoomLink = false;
    state.joinFromQrLink = false;
    state.qrJoinMode = null;
    window.history.replaceState({}, "", SITE_PATHS.vocabularyReview);
    updateUi();
    scheduleViewportTopReset();
};

const openAutotranslatorTool = () => {
    if (!isAutotranslatorAccount()) {
        openAccountScreen();
        state.error = t("autotranslatorPrivateOnly");
        updateUi();
        return;
    }

    clearBanners();
    state.screen = "tool-autotranslator";
    state.hostPanelExpanded = false;
    state.toolsPanelExpanded = false;
    state.hostSourceImportOpen = false;
    resetHostSourceTranslation();
    state.joinFromRoomLink = false;
    state.joinFromQrLink = false;
    state.qrJoinMode = null;
    window.history.replaceState({}, "", SITE_PATHS.autotranslator);
    updateUi();
    scheduleViewportTopReset();
};

const openMaster1000Tool = () => {
    window.location.assign(MASTER1000_CANONICAL_URL);
};

const openGimcheonSchoolDemo = () => {
    window.location.assign("/school/");
};

const PICTIONARY_CANVAS_STATE_REFRESH_COOLDOWN_MS = 2500;
const PICTIONARY_CANVAS_SYNC_LOG_KEY = "ziarcadia:pictionary-sync-repair-log";

const shouldLogPictionaryCanvasRepair = (): boolean => {
    try {
        return localStorage.getItem(PICTIONARY_CANVAS_SYNC_LOG_KEY) !== "0";
    } catch {
        return true;
    }
};

const getPictionaryCanvasRoundMismatchReason = (message: PictionaryCanvasSegmentMessage): string | null => {
    const room = state.room;
    const pictionary = room?.pictionary ?? null;

    if (!room || !pictionary || room.roomCode !== message.roomCode) {
        return null;
    }

    if (message.roundNumber !== pictionary.roundNumber) {
        return "round-number";
    }

    if (message.currentWordIndex !== pictionary.currentWordIndex) {
        return "word-index";
    }

    if (message.roundStartedAt !== pictionary.roundStartedAt) {
        return "round-started-at";
    }

    return null;
};

const refreshPictionaryRoomStateForCanvasSegment = (
    message: PictionaryCanvasSegmentMessage,
    reason: string
): boolean => {
    const room = state.room;
    const pictionary = room?.pictionary ?? null;

    if (!room || !pictionary || room.roomCode !== message.roomCode) {
        return false;
    }

    const now = Date.now();

    if (now - lastPictionaryCanvasStateRefreshAt < PICTIONARY_CANVAS_STATE_REFRESH_COOLDOWN_MS) {
        return true;
    }

    lastPictionaryCanvasStateRefreshAt = now;

    if (shouldLogPictionaryCanvasRepair()) {
        console.info(
            `${CLIENT_DEBUG_PREFIX} pictionary-sync-repair reason=${reason} room=${message.roomCode} ` +
                `localRound=${pictionary.roundNumber} segmentRound=${message.roundNumber} ` +
                `localWord=${pictionary.currentWordIndex ?? "none"} segmentWord=${message.currentWordIndex ?? "none"}`
        );
    }

    sendMessage(
        {
            type: "room-resync"
        },
        {
            silentIfDisconnected: true
        }
    );

    if (shouldLogPictionaryCanvasRepair()) {
        console.info(`${CLIENT_DEBUG_PREFIX} pictionary-sync-repair-requested room=${message.roomCode}`);
    }

    return true;
};

const toggleGimcheonSchoolChat = (open?: boolean) => {
    state.gimcheonSchoolChat.open = open ?? !state.gimcheonSchoolChat.open;
    updateUi();

    if (state.gimcheonSchoolChat.open) {
        focusGimcheonSchoolChatInput();
    }
};

const submitGimcheonSchoolChat = async (forcedPrompt?: string) => {
    const prompt = (forcedPrompt ?? state.gimcheonSchoolChat.input).trim();

    if (!prompt || state.gimcheonSchoolChat.loading) {
        return;
    }

    const history = state.gimcheonSchoolChat.messages.slice(-8);
    state.gimcheonSchoolChat.open = true;
    state.gimcheonSchoolChat.input = "";
    state.gimcheonSchoolChat.loading = true;
    state.gimcheonSchoolChat.messages = [
        ...state.gimcheonSchoolChat.messages,
        {
            id: createClientId(),
            role: "user",
            text: prompt
        }
    ];
    updateUi();

    try {
        const response = await fetchJson<GimcheonSchoolChatResponse>("/api/gimcheon-school/chat", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                message: prompt,
                history: history.map((message) => ({
                    role: message.role,
                    text: message.text
                }))
            })
        });

        state.gimcheonSchoolChat.messages = [
            ...state.gimcheonSchoolChat.messages,
            {
                id: createClientId(),
                role: "assistant",
                text: response.answer
            }
        ];
    } catch (error) {
        state.gimcheonSchoolChat.messages = [
            ...state.gimcheonSchoolChat.messages,
            {
                id: createClientId(),
                role: "assistant",
                text:
                    error instanceof Error
                        ? `지금은 답변을 가져오지 못했어요. ${localizeRuntimeText(error.message)}`
                        : "지금은 답변을 가져오지 못했어요."
            }
        ];
    } finally {
        state.gimcheonSchoolChat.loading = false;
        updateUi();
    }
};

const openAccountScreen = (history = false) => {
    accountHistoryOpen = history;
    clearBanners();
    state.screen = "account";
    state.hostSourceImportOpen = false;
    resetHostSourceTranslation();
    state.joinFromRoomLink = false;
    state.joinFromQrLink = false;
    state.qrJoinMode = null;
    window.history.replaceState({}, "", `${SITE_PATHS.account}${history ? "?history=1" : ""}`);
    updateUi();
    scheduleViewportTopReset();
};

const returnToToolsMenu = () => {
    clearBanners();
    state.screen = "landing";
    state.hostPanelExpanded = false;
    state.toolsPanelExpanded = true;
    state.hostSourceImportOpen = false;
    resetHostSourceTranslation();
    state.joinFromRoomLink = false;
    state.joinFromQrLink = false;
    state.qrJoinMode = null;
    window.history.replaceState({}, "", "/");
    updateUi();
    scheduleViewportTopReset();
};

const leaveRoom = () => {
    closeSocket();
    resetRoomAudioState();
    state.room = null;
    state.currentConnection = null;
    state.screen = "landing";
    state.hostPanelExpanded = false;
    state.toolsPanelExpanded = false;
    state.joinFromRoomLink = false;
    state.joinFromQrLink = false;
    state.hostDraft = createDefaultHostDraft();
    applyAccountPreferencesToHostDraft({ preserveMode: false });
    state.hostDraftDirty = false;
    state.hostModePanelView = "selection";
    state.hostSourceImportOpen = false;
    resetHostSourceTranslation();
    state.qrPreviewKind = null;
    state.finalResultsPlayerListOpen = false;
    state.joinFromRoomLink = false;
    state.joinFromQrLink = false;
    state.qrJoinMode = null;
    state.renameDialogOpen = false;
    state.renameDialogAnchorSelector = null;
    state.renameDialogSessionId = null;
    state.renameDialogPrompted = false;
    state.pendingHostSync = null;
    clearAllRoomQrImages();
    clearBanners();
    window.history.replaceState({}, "", "/");
    updateUi();
};

const connectToRoom = async (
    roomCodeInput: string,
    options: {
        name: string;
        hostToken: string | null;
        role?: SessionRole;
        skipSummary?: boolean;
        preserveRoomState?: boolean;
        isReconnect?: boolean;
    }
) => {
    const roomCode = normalizeRoomCode(roomCodeInput);
    const role: SessionRole = options.hostToken ? "host" : options.role === "controller" ? "controller" : "player";
    const shouldPreserveRoomState =
        options.preserveRoomState === true &&
        state.screen === "room" &&
        state.room?.roomCode === roomCode;

    if (!isValidRoomCode(roomCode)) {
        throw new Error("Enter a valid room code.");
    }

    const normalizedName = options.hostToken
        ? normalizePlayerName(options.name, DEFAULT_HOST_NAME)
        : options.name.trim()
          ? normalizePlayerName(options.name, DEFAULT_PLAYER_NAME)
          : "";

    if (role === "player" && normalizedName.length === 0) {
        throw new Error("Enter your name before joining.");
    }

    if (!options.skipSummary) {
        await fetchJson<RoomSummaryResponse>(`/api/rooms/${encodeURIComponent(roomCode)}`, { cache: "no-store" });
    }

    const connection: ConnectionDetails = {
        roomCode,
        name: normalizedName,
        hostToken: options.hostToken,
        role,
        sessionId: getSessionId(roomCode, role)
    };

    closeSocket({ resetReconnectAttempt: options.isReconnect !== true });
    intentionalDisconnect = false;
    state.joinFromRoomLink = false;
    state.joinFromQrLink = false;
    state.qrJoinMode = null;
    state.currentConnection = connection;
    if (shouldPreserveRoomState) {
        state.screen = "room";
        state.error = "";
    } else {
        resetRoomAudioState();
        state.hostStartPending = false;
        state.screen = "connecting";
        state.room = null;
        state.qrPreviewKind = null;
        clearAllRoomQrImages();
        clearBanners();
    }
    window.history.replaceState({}, "", buildRoomUrl(roomCode, options.hostToken, role));
    updateUi();

    const nextSocket = new WebSocket(buildSocketUrl(connection));
    socket = nextSocket;
    // Reconnects retain the same room/session IDs. Only this socket owns updates now.
    const ownsConnection = () => socket === nextSocket && state.currentConnection === connection;
    let staleMessageLogged = false;

    logClientDebug("connecting websocket", {
        roomCode: connection.roomCode,
        role: connection.role,
        sessionId: connection.sessionId
    });

    nextSocket.addEventListener("open", () => {
        if (!ownsConnection()) return;
        logClientDebug("websocket open", {
            roomCode: connection.roomCode,
            role: connection.role
        });
        clearConnectionLostNotice();
        reconnectAttempt = 0;
        startConnectionHeartbeat(nextSocket);
        startViewerPresenceTracking();
    });

    nextSocket.addEventListener("message", (event) => {
        if (!ownsConnection()) {
            if (!staleMessageLogged) {
                staleMessageLogged = true;
                logRoomSyncDiagnostic(`stale-room-message-ignored room=${connection.roomCode}`);
            }
            return;
        }
        if (connectionHeartbeatSocket === nextSocket) {
            lastConnectionMessageAt = performance.now();
        }

        let message: ServerMessage;

        try {
            message = JSON.parse(String(event.data)) as ServerMessage;
        } catch {
            logRoomSyncDiagnostic(`invalid-server-message room=${connection.roomCode}`);
            return;
        }
        let shouldResyncHostDraft = false;

        logClientDebug("websocket message", {
            type: message.type
        });

        if (message.type === "connection-heartbeat") {
            return;
        }

        if (message.type === "error") {
            logClientDebug("room error", {
                message: message.message
            });
            state.hostStartPending = false;
            state.room = renamePromptFeedback.clear(state.room);
            syncRenamePromptFeedbackTimer();
            showError(message.message);
            return;
        }

        if (message.type === "lobby-state") {
            if (!state.room || message.roomCode !== state.room.roomCode) {
                return;
            }

            const nextRoom = {
                ...state.room,
                lobbyPlayground: message.lobby
            };
            state.room = nextRoom;
            lobbyPlayground?.setRoom(state.room);

            if (shouldRequestLobbyRosterResync(nextRoom)) {
                requestRoomResync("lobby-state-roster-mismatch");
            }
            return;
        }

        if (message.type === "pictionary-canvas-segment") {
            const canvasMessage = message as PictionaryCanvasSegmentMessage;
            const mismatchReason = getPictionaryCanvasRoundMismatchReason(canvasMessage);

            if (mismatchReason) {
                refreshPictionaryRoomStateForCanvasSegment(canvasMessage, mismatchReason);
                return;
            }

            pictionaryView?.applyCanvasSegment(canvasMessage);
            return;
        }

        if (message.type === "rename-request") {
            if (!state.room || message.roomCode !== state.room.roomCode) {
                return;
            }

            openViewerRename(undefined, state.room.viewer.sessionId, { prompted: true });
            return;
        }

        logClientDebug("room state received", {
            phase: message.room.phase,
            roomCode: message.room.roomCode,
            progressIndex: message.room.player?.progressIndex ?? null,
            position: message.room.player?.position ?? null,
            lastEvent: message.room.player?.lastEvent?.kind ?? null
        });

        clearActiveRoomActionResyncTimers();
        state.room = renamePromptFeedback.receive(message.room, performance.now());
        syncRenamePromptFeedbackTimer();
        if (
            message.room.viewer.renamePending &&
            !message.room.viewer.isHost &&
            (!state.renameDialogOpen || !state.renameDialogPrompted)
        ) {
            state.renameDialogPrompted = true;
            state.renameDialogAnchorSelector = null;
            state.renameDialogSessionId = message.room.viewer.sessionId;
            state.renameDialogOpen = true;
        } else if (
            !message.room.viewer.renamePending &&
            state.renameDialogOpen &&
            state.renameDialogPrompted &&
            state.renameDialogSessionId === message.room.viewer.sessionId
        ) {
            state.renameDialogOpen = false;
            state.renameDialogAnchorSelector = null;
            state.renameDialogSessionId = null;
            state.renameDialogPrompted = false;
        }
        if (message.room.phase !== "lobby" || !message.room.viewer.isHost) {
            state.hostStartPending = false;
        }
        if (message.room.phase !== "lobby") {
            clearHostStartConfirmTimers();
            clearHostStartLoadingOverlay();
        }
        if (message.room.phase !== "results") {
            state.finalResultsPlayerListOpen = false;
        }
        if (state.screen !== "account") {
            state.screen = "room";
        }
        clearBanners();
        persistViewerName(message.room);
        if (message.room.viewer.isHost && message.room.phase !== "lobby" && message.room.activityId && message.room.activityId !== activeHostedGameHistoryId) {
            void recordHostedGame(message.room);
        }
        void recordHostedGameResults(message.room);

        if (state.renameDialogOpen && !getRenameTargetParticipant(message.room)) {
            state.renameDialogOpen = false;
            state.renameDialogAnchorSelector = null;
            state.renameDialogSessionId = null;
            state.renameDialogPrompted = false;
        }

        if (message.room.viewer.isHost && message.room.phase === "lobby") {
            const serverDraft: HostSyncSnapshot = {
                mode: message.room.setup.selectedMode,
                sourceText: message.room.setup.sourceText,
                singleLineMode: message.room.setup.singleLineMode,
                hostParticipates: message.room.setup.hostParticipates,
                pictionaryStem: message.room.setup.pictionaryStem,
                pictionaryNoHints: message.room.setup.pictionaryNoHints,
                pictionaryNoTranslations: message.room.setup.pictionaryNoTranslations,
                pictionarySameStem: message.room.setup.pictionarySameStem,
                pictionaryHostIsController: message.room.setup.pictionaryHostIsController
            };
            const matchesPending = areHostDraftsEqual(state.pendingHostSync, serverDraft);
            const localSnapshot = createHostSyncSnapshot(state.hostDraft);
            const localMatchesPending = areHostDraftsEqual(localSnapshot, state.pendingHostSync);
            const localDiffersFromServer = !areHostDraftsEqual(localSnapshot, serverDraft);

            if (!state.hostDraftDirty) {
                state.hostDraft = applySetupToHostDraft(message.room.setup, state.hostDraft);
                if (message.room.setup.selectedMode === null) {
                    applyAccountPreferencesToHostDraft({ preserveMode: false });
                }
            } else if (matchesPending && localMatchesPending) {
                state.hostDraft = applySetupToHostDraft(message.room.setup, state.hostDraft);
            } else if (!localDiffersFromServer) {
                state.hostDraftDirty = false;
                state.pendingHostSync = null;
            } else {
                if (matchesPending) {
                    state.pendingHostSync = null;
                }
                shouldResyncHostDraft = true;
            }

            if (matchesPending && localMatchesPending) {
                state.hostDraftDirty = false;
                state.pendingHostSync = null;
            }

            if (state.hostDraft.mode === null) {
                state.hostModePanelView = "selection";
            } else if (message.room.setup.selectedMode === PLANET_HOPPERS_MODE && state.hostModePanelView === "selection") {
                // Re-open the editor when a host reconnects to a configured Planet Hoppers room.
                // The draft is already stored in the room setup, so this keeps an accidental
                // refresh from looking like it erased the quiz.
                state.hostModePanelView = "config";
            }
        } else {
            state.hostDraftDirty = false;
            state.pendingHostSync = null;
            state.hostModePanelView = "selection";
        }

        if (message.room.viewer.isHost) {
            state.hostDraft.hostParticipates = message.room.setup.hostParticipates;
        }

        updateUi();

        if (shouldResyncHostDraft) {
            pushHostDraftDebounced();
        }
    });

    nextSocket.addEventListener("error", () => {
        if (!ownsConnection()) return;
        logClientDebug("websocket error", {
            roomCode: connection.roomCode,
            role: connection.role
        });
    });

    nextSocket.addEventListener("close", (event) => {
        logClientDebug("websocket close", {
            roomCode: connection.roomCode,
            role: connection.role,
            code: event.code,
            reason: event.reason
        });

        const wasCurrentSocket = socket === nextSocket;

        if (wasCurrentSocket) {
            socket = null;
            stopConnectionHeartbeat(nextSocket);
            stopViewerPresenceTracking();
            if (renamePromptFeedback.nextExpiry !== null) {
                state.room = renamePromptFeedback.clear(state.room);
                syncRenamePromptFeedbackTimer();
                updateUi();
            }
        }

        if (intentionallyClosedSockets.has(nextSocket)) {
            intentionallyClosedSockets.delete(nextSocket);
            intentionalDisconnect = false;
            return;
        }

        if (!isCurrentConnection(connection) || (socket !== null && socket !== nextSocket)) {
            return;
        }

        if (event.code === HOST_KICK_CLOSE_CODE && event.reason === HOST_KICK_CLOSE_REASON) {
            leaveRoom();
            return;
        }

        if (connectionLostNoticeTimer !== null) {
            window.clearTimeout(connectionLostNoticeTimer);
        }

        connectionLostNoticeTimer = window.setTimeout(() => {
            connectionLostNoticeTimer = null;

            if (!state.currentConnection) {
                return;
            }

            if (socket && socket.readyState === WebSocket.OPEN) {
                return;
            }

            state.info = "Connection lost. Reconnecting...";
            updateUi();
        }, CONNECTION_LOST_NOTICE_GRACE_MS);

        scheduleRoomReconnect(connection);
    });
};

const switchControllerRoom = async (roomCodeInput: string) => {
    const roomCode = normalizeRoomCode(roomCodeInput);

    if (!isValidRoomCode(roomCode)) {
        showError("Enter a valid room code.");
        return;
    }

    if (state.currentConnection?.role === "controller" && state.currentConnection.roomCode === roomCode) {
        return;
    }

    try {
        await connectToRoom(roomCode, {
            name: state.currentConnection?.name ?? state.joinName,
            hostToken: null,
            role: "controller"
        });
    } catch (error) {
        showError(error instanceof Error ? error.message : "Could not switch controller rooms.");
    }
};

const createRoom = async () => {
    state.screen = "connecting";
    state.room = null;
    clearBanners();
    state.info = "Creating room...";
    updateUi();

    const created = await fetchJson<CreateRoomResponse>("/api/rooms", {
        method: "POST"
    });

    state.joinRoomCode = created.roomCode;
    const historyDraft = pendingHistoryDraft;
    pendingHistoryDraft = null;
    state.hostDraft = historyDraft ?? createDefaultHostDraft();
    if (!historyDraft) applyAccountPreferencesToHostDraft({ preserveMode: false });
    state.hostDraftDirty = Boolean(historyDraft);
    state.hostModePanelView = historyDraft ? "config" : "selection";
    state.hostSourceImportOpen = false;
    resetHostSourceTranslation();
    state.qrPreviewKind = null;
    state.joinFromQrLink = false;
    state.renameDialogOpen = false;
    state.renameDialogAnchorSelector = null;
    state.renameDialogSessionId = null;
    state.renameDialogPrompted = false;
    state.pendingHostSync = null;

    await connectToRoom(created.roomCode, {
        name: state.hostName.trim() || DEFAULT_HOST_NAME,
        hostToken: created.hostToken,
        skipSummary: true
    });
};

const copyText = async (value: string) => {
    if (navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(value);
            return;
        } catch {
            // In-app browsers often expose Clipboard but reject it outside their preferred gesture path.
        }
    }

    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "0";
    textarea.style.left = "-9999px";
    textarea.style.opacity = "0";
    document.body.append(textarea);
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);

    try {
        if (!document.execCommand("copy")) {
            throw new Error("Clipboard copy rejected.");
        }
    } finally {
        textarea.remove();
    }
};

const submitJoinFlow = () => {
    void connectToRoom(state.joinRoomCode, {
        name: state.joinName,
        hostToken: null
    }).catch((error: unknown) => {
        showJoinFlowError(error instanceof Error ? error.message : "Could not join the room.");
    });
};

app.addEventListener("input", (event) => {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement | null;

    if (!target) {
        return;
    }

    if (target.id === "host-name-input") {
        state.hostName = target.value;
        return;
    }

    if (target.id === "join-name-input") {
        state.joinName = target.value;

        if (state.screen === "qr-join") {
            if (target.value.trim()) {
                writeStoredValue(STORAGE_KEYS.qrJoinName, target.value);
            } else {
                removeStoredValue(STORAGE_KEYS.qrJoinName);
            }
        }

        return;
    }

    if (target.id === "join-room-code-input") {
        const normalized = normalizeRoomCode(target.value);
        state.joinRoomCode = normalized;
        target.value = normalized;
        state.joinFromRoomLink = false;
        state.joinFromQrLink = false;
        return;
    }

    if (target.id === "controller-room-code-input") {
        target.value = normalizeRoomCode(target.value).slice(0, ROOM_CODE_MAX_LENGTH);
        return;
    }

    if (target.id === "gimcheon-chat-input") {
        state.gimcheonSchoolChat.input = target.value;
        return;
    }

    if (target.id === "host-source-text" && target instanceof HTMLTextAreaElement) {
        state.hostDraft.sourceText = target.value;

        if (state.hostDraft.mode === PICTURE_DETECTIVES_MODE) {
            state.hostDraft.pictionarySourceText = target.value;
            // Keep one pasteable pad as the source of truth; the aligned preview stays derived.
            syncHostDraftPictionarySourceText();
        } else if (state.hostDraft.mode === VAULT_BREAKERS_MODE) {
            state.hostDraft.vaultSourceText = target.value;
        } else {
            state.hostDraft.relicSourceText = target.value;
        }

        state.hostDraftDirty = true;
        if (state.room) {
            updateHostLobby(state.room);
        } else {
            refreshHostSourceEditorState(target);
        }
        pushHostDraftDebounced();
        scheduleAccountPreferenceSave();
        return;
    }

    if (
        target instanceof HTMLInputElement &&
        target.classList.contains("host-pictionary-table-input") &&
        state.hostDraft.mode === PICTURE_DETECTIVES_MODE
    ) {
        const rowId = target.dataset.pictionaryRowId ?? "";
        const field = target.dataset.pictionaryField as PictionaryDraftField | undefined;

        if (!rowId || field !== "english" && field !== "korean" && field !== "stem") {
            return;
        }

        const selectionStart = target.selectionStart;
        const selectionEnd = target.selectionEnd;
        const previousRowIds = state.hostDraft.pictionaryRows.map((row) => row.id).join("\u0001");
        let updated = false;

        state.hostDraft.pictionaryRows = state.hostDraft.pictionaryRows.map((row) => {
            if (row.id !== rowId) {
                return row;
            }

            updated = true;
            return {
                ...row,
                [field]: target.value
            };
        });

        if (!updated) {
            return;
        }

        syncHostDraftPictionaryRowsToSourceText();
        state.hostDraftDirty = true;

        if (state.room) {
            const nextRowIds = state.hostDraft.pictionaryRows.map((row) => row.id).join("\u0001");
            const pictionaryTable = app.querySelector<HTMLElement>("#host-pictionary-table");

            if (!pictionaryTable || previousRowIds !== nextRowIds) {
                updateHostLobby(state.room);
                focusHostPictionaryTableInput(rowId, field, selectionStart, selectionEnd);
            } else {
                pictionaryTable.dataset.pictionaryTableSignature = getHostPictionaryTableSignature();
                refreshHostSourceEditorState(null);
            }
        } else {
            refreshHostSourceEditorState(null);
        }

        pushHostDraftDebounced();
        scheduleAccountPreferenceSave(PICTURE_DETECTIVES_MODE);
        return;
    }

    if (
        target instanceof HTMLInputElement &&
        target.classList.contains("host-vault-table-input") &&
        state.hostDraft.mode === VAULT_BREAKERS_MODE
    ) {
        const rowId = target.dataset.vaultRowId ?? "";
        const field = target.dataset.vaultField as VaultDraftField | undefined;

        if (
            !rowId ||
            (field !== "category" && field !== "value" && field !== "prompt" && field !== "answer" && field !== "korean")
        ) {
            return;
        }

        const selectionStart = target.selectionStart;
        const selectionEnd = target.selectionEnd;
        const previousRowIds = state.hostDraft.vaultRows.map((row) => row.id).join("\u0001");
        let updated = false;

        state.hostDraft.vaultRows = state.hostDraft.vaultRows.map((row) => {
            if (row.id !== rowId) {
                return row;
            }

            updated = true;
            return {
                ...row,
                [field]: target.value
            };
        });

        if (!updated) {
            return;
        }

        syncHostDraftVaultRowsToSourceText();
        state.hostDraftDirty = true;

        if (state.room) {
            const nextRowIds = state.hostDraft.vaultRows.map((row) => row.id).join("\u0001");
            const vaultTable = app.querySelector<HTMLElement>("#host-vault-table");

            if (!vaultTable || previousRowIds !== nextRowIds) {
                updateHostLobby(state.room);
                focusHostVaultTableInput(rowId, field, selectionStart, selectionEnd);
            } else {
                vaultTable.dataset.vaultTableSignature = getHostVaultTableSignature();
                refreshHostSourceEditorState(null);
            }
        } else {
            refreshHostSourceEditorState(null);
        }

        pushHostDraftDebounced();
        scheduleAccountPreferenceSave(VAULT_BREAKERS_MODE);
        return;
    }

    if (target.id === "host-single-line-toggle" && target instanceof HTMLInputElement) {
        state.hostDraft.singleLineMode = target.checked;
        state.hostDraftDirty = true;
        if (state.room) {
            updateHostLobby(state.room);
        } else {
            const startButton = app.querySelector<HTMLButtonElement>("#start-room-button");

            if (startButton) {
                syncHostStartButton(startButton);
            }
        }
        pushHostDraftDebounced();
        scheduleAccountPreferenceSave(RELIC_RUN_MODE);
        return;
    }

    if (target.id === "host-participation-toggle" && target instanceof HTMLInputElement) {
        state.hostDraft.hostParticipates = target.checked;

        if (state.room?.phase === "active") {
            sendMessage({
                type: "host-set-participation",
                payload: {
                    participates: target.checked
                }
            });
            updateUi();
            return;
        }

        state.hostDraftDirty = true;
        if (state.room) {
            updateHostLobby(state.room);
        }
        pushHostDraftDebounced();
        scheduleAccountPreferenceSave(RELIC_RUN_MODE);
        return;
    }

    if (target.id === "host-pictionary-no-translations-toggle" && target instanceof HTMLInputElement) {
        state.hostDraft.pictionaryNoTranslations = target.checked;
        resetHostSourceTranslation();
        state.hostDraftDirty = true;
        if (state.room) {
            updateHostLobby(state.room);
        }
        pushHostDraftDebounced();
        scheduleAccountPreferenceSave(PICTURE_DETECTIVES_MODE);
        return;
    }

    if (target.id === "host-pictionary-no-hints-toggle" && target instanceof HTMLInputElement) {
        state.hostDraft.pictionaryNoHints = target.checked;
        state.hostDraft.pictionarySameStem = false;
        state.hostDraftDirty = true;
        if (state.room) {
            updateHostLobby(state.room);
        }
        pushHostDraftDebounced();
        scheduleAccountPreferenceSave(PICTURE_DETECTIVES_MODE);
        return;
    }

    if (target.id === "host-pictionary-device-controller-toggle" && target instanceof HTMLInputElement) {
        state.hostDraft.pictionaryHostIsController = target.checked;
        state.hostDraftDirty = true;
        if (state.room) {
            updateHostLobby(state.room);
        }
        pushHostDraftDebounced();
        scheduleAccountPreferenceSave(PICTURE_DETECTIVES_MODE);
        return;
    }

    if (target.id === "rename-player-input") {
        return;
    }
});

app.addEventListener("change", (event) => {
    const target = event.target as HTMLInputElement | null;

    if (!(target instanceof HTMLInputElement) || target.id !== "host-source-file-input") {
        return;
    }

    const file = target.files?.[0];

    if (!file) {
        return;
    }

    void readImportedSourceText(file)
        .then((rawSourceText) => {
            const textarea = app.querySelector<HTMLTextAreaElement>("#host-source-text");
            const sourceText =
                state.hostDraft.mode === PICTURE_DETECTIVES_MODE
                    ? normalizeImportedPictionarySourceText(rawSourceText, file)
                    : state.hostDraft.mode === VAULT_BREAKERS_MODE
                      ? normalizeImportedVaultSourceText(rawSourceText, file)
                    : rawSourceText;

            if (state.hostDraft.mode === PICTURE_DETECTIVES_MODE) {
                state.hostDraft.sourceText = sourceText;
                state.hostDraft.pictionarySourceText = sourceText;
                syncHostDraftPictionarySourceText();

                if (textarea) {
                    textarea.value = sourceText;
                    textarea.focus();
                } else {
                    const firstTableInput = app.querySelector<HTMLInputElement>(
                        ".host-pictionary-table-input[data-pictionary-field='english']"
                    );
                    firstTableInput?.focus();
                }
            } else if (state.hostDraft.mode === VAULT_BREAKERS_MODE) {
                state.hostDraft.sourceText = sourceText;
                state.hostDraft.vaultSourceText = sourceText;
                syncHostDraftVaultSourceText();

                if (textarea) {
                    textarea.value = sourceText;
                    textarea.focus();
                } else {
                    const firstTableInput = app.querySelector<HTMLInputElement>(
                        ".host-vault-table-input[data-vault-field='prompt']"
                    );
                    firstTableInput?.focus();
                }

                refreshHostSourceEditorState(textarea);
            } else {
                state.hostDraft.sourceText = sourceText;
                state.hostDraft.relicSourceText = sourceText;

                if (textarea) {
                    textarea.value = sourceText;
                    textarea.focus();
                }

                refreshHostSourceEditorState(textarea);
            }

            state.hostSourceImportOpen = false;
            resetHostSourceTranslation();
            state.hostDraftDirty = true;
            if (state.room) {
                updateHostLobby(state.room);
            }
            pushHostDraftDebounced();
            scheduleAccountPreferenceSave();
            void recordAccountUpload(file, sourceText);
        })
        .catch(() => {
            showError("Could not read the text file.");
        })
        .finally(() => {
            target.value = "";
        });
});

app.addEventListener("click", (event) => {
    const target = event.target as HTMLElement | null;
    const isModifiedNavigation =
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey;

    if (!target) {
        return;
    }

    if (state.qrPreviewKind) {
        event.preventDefault();
        state.qrPreviewKind = null;
        updateUi();
        return;
    }

    const hostButton = target.closest("#host-room-button");
    const hostExpandButton = target.closest("#host-expand-button");
    const toolsExpandButton = target.closest("#tools-expand-button");
    const collapsePanelButton = target.closest<HTMLElement>("[data-collapse-panel]");
    const openRandomTeamsToolButton = target.closest("[data-open-random-teams-tool]");
    const openWordListToolButton = target.closest("[data-open-word-list-tool]");
    const openVocabularyReviewToolButton = target.closest("[data-open-vocabulary-review-tool]");
    const openMaster1000ToolButton = target.closest("[data-open-master1000-tool]");
    const openAutotranslatorToolButton = target.closest("[data-open-autotranslator-tool]");
    const openGimcheonSchoolButton = target.closest("[data-open-gimcheon-school-demo]");
    const toolsBackButton = target.closest("[data-tools-back]");
    const joinButton = target.closest("#join-room-button");
    const leaveButton = target.closest("#leave-room-button");
    const modeButton = target.closest("#select-relic-run-button");
    const pictionaryModeButton = target.closest("#select-picture-detectives-button");
    const planetModeButton = target.closest("#select-planet-hoppers-button");
    if (planetModeButton) { state.hostDraft.mode = PLANET_HOPPERS_MODE; state.hostDraft.sourceText = readPHLocalDraft(); state.hostModePanelView = "config"; state.hostDraftDirty = true; pushHostDraftDebounced(); updateUi(); return; }
    const vaultModeButton = target.closest("#select-vault-breakers-button");
    const returnToSelectionButton = target.closest("#return-to-mode-selection-button");
    const hostEditNameButton = target.closest("#host-edit-name-button");
    const rosterActionsToggle = target.closest<HTMLElement>('[data-action="toggle-roster-actions"]');
    const playerHistoryMenu = target.closest<HTMLElement>("[data-player-history-menu]");
    const playerHistoryMenuToggle = target.closest<HTMLElement>("[data-player-history-menu-toggle]");
    const playerHistoryDownloadButton = target.closest<HTMLElement>("[data-player-history-download]");
    const participantRenameButton = target.closest<HTMLElement>("[data-rename-participant]");
    const participantRenamePromptButton = target.closest<HTMLElement>("[data-prompt-rename-participant]");
    const participantKickButton = target.closest<HTMLElement>("[data-kick-participant]");
    const sourceImportButton = target.closest("#host-source-import-button");
    const sourceTranslateButton = target.closest("#host-source-translate-button");
    const sourceTranslationMenu = target.closest("[data-host-source-translation-menu]");
    const sourceTranslationActionButton = target.closest<HTMLElement>("[data-source-translation-action]");
    const pictionaryFogButton = target.closest("#host-pictionary-fog-button");
    const vaultFogButton = target.closest("#host-vault-fog-button");
    const importFromComputerButton = target.closest("[data-import-from-computer]");
    const accountUploadButton = target.closest<HTMLElement>("[data-account-upload-id]");
    const accountPlayedSourceButton = target.closest<HTMLElement>("[data-account-played-source-id]");
    const accountWordListButton = target.closest<HTMLElement>("[data-account-word-list-id]");
    const accountHistoryToggleButton = target.closest<HTMLElement>("[data-account-history-toggle]");
    const openAccountArchiveButton = target.closest("[data-open-account-archive]");
    const pictionaryEditorModeButton = target.closest<HTMLElement>("[data-pictionary-editor-mode]");
    const vaultEditorModeButton = target.closest<HTMLElement>("[data-vault-editor-mode]");
    const controllerRoomSwitchButton = target.closest("#controller-room-switch-button");
    const expandControllerQrButton = target.closest("[data-expand-controller-qr]");
    const qrButton = target.closest("[data-open-qr-preview]");
    const controllerQrButton = target.closest("[data-open-controller-qr]");
    const startButton = target.closest("#start-room-button");
    const closeQrPreviewButton = target.closest("[data-close-qr-preview]");
    const openFinalResultsPlayerListButton = target.closest("[data-open-final-results-player-list]");
    const closeFinalResultsPlayerListButton = target.closest("[data-close-final-results-player-list]");
    const closeRenameDialogButton = target.closest("[data-close-rename-dialog]");
    const renameSaveButton = target.closest("#rename-player-save-button");
    const copyButton = target.closest<HTMLElement>("[data-copy]");
    const endGameButton = target.closest("[data-end-game]");
    const homeButton = target.closest("[data-home]");
    const master1000FullscreenButton = target.closest("[data-master1000-fullscreen]");
    const languageToggle = target.closest("[data-language-toggle]");
    const accountButton = target.closest("[data-account-button]");
    const accountSignOutButton = target.closest("[data-account-sign-out]");
    const gimcheonChatToggle = target.closest("[data-gimcheon-chat-toggle]");
    const gimcheonChatClose = target.closest("[data-gimcheon-chat-close]");
    const gimcheonChatSend = target.closest("[data-gimcheon-chat-send]");
    const gimcheonChatPrompt = target.closest<HTMLElement>("[data-gimcheon-chat-prompt]");

    if (state.hostSourceTranslationOpen && !sourceTranslateButton && !sourceTranslationMenu) {
        queueMicrotask(() => {
            if (state.hostSourceTranslationOpen) {
                state.hostSourceTranslationOpen = false;
                state.hostSourceTranslationError = "";
                updateUi();
            }
        });
    }

    if (hostPlayerHistoryMenuOpen && !playerHistoryMenu) {
        hostPlayerHistoryMenuOpen = false;
        updateUi();
    }

    if (languageToggle) {
        event.preventDefault();
        if (!state.account.initialized) startupLanguageChoiceMade = true;
        const language = toggleLanguage();
        void accountLanguageSync.changed(language);
        rerenderForLanguageChange();
        return;
    }

    if (target.closest("[data-open-activity-history]")) {
        event.preventDefault(); openAccountScreen(true); return;
    }
    if (target.closest("[data-activity-back]")) {
        event.preventDefault(); openAccountScreen(); return;
    }
    if (accountButton) {
        event.preventDefault();
        openAccountScreen();
        return;
    }

    if (accountSignOutButton) {
        event.preventDefault();
        void signOutAccount();
        return;
    }

    if (openGimcheonSchoolButton) {
        if (isModifiedNavigation) {
            return;
        }
        event.preventDefault();
        openGimcheonSchoolDemo();
        return;
    }

    if (gimcheonChatToggle) {
        event.preventDefault();
        toggleGimcheonSchoolChat();
        return;
    }

    if (gimcheonChatClose) {
        event.preventDefault();
        toggleGimcheonSchoolChat(false);
        return;
    }

    if (gimcheonChatSend) {
        event.preventDefault();
        void submitGimcheonSchoolChat();
        return;
    }

    if (gimcheonChatPrompt) {
        event.preventDefault();
        void submitGimcheonSchoolChat(gimcheonChatPrompt.getAttribute("data-gimcheon-chat-prompt") ?? "");
        return;
    }

    if (homeButton) {
        if (isModifiedNavigation) {
            return;
        }
        event.preventDefault();
        if (app.querySelector(".is-random-teams-presenting")) {
            return;
        }
        goHome();
        return;
    }

    if (master1000FullscreenButton) {
        event.preventDefault();
        void toggleMaster1000Fullscreen();
        return;
    }

    if (hostExpandButton) {
        state.hostPanelExpanded = true;
        state.toolsPanelExpanded = false;
        clearBanners();
        updateUi();
        return;
    }

    if (toolsExpandButton) {
        state.toolsPanelExpanded = true;
        state.hostPanelExpanded = false;
        clearBanners();
        updateUi();
        return;
    }

    if (collapsePanelButton) {
        const panel = collapsePanelButton.getAttribute("data-collapse-panel");

        if (panel === "host") {
            state.hostPanelExpanded = false;
        }

        if (panel === "tools") {
            state.toolsPanelExpanded = false;
        }

        clearBanners();
        updateUi();
        return;
    }

    if (openRandomTeamsToolButton) {
        if (isModifiedNavigation) {
            return;
        }
        event.preventDefault();
        openRandomTeamsTool();
        return;
    }

    if (openVocabularyReviewToolButton) {
        if (isModifiedNavigation) return;
        event.preventDefault();
        openVocabularyReviewTool();
        return;
    }

    if (openWordListToolButton) {
        if (isModifiedNavigation) {
            return;
        }
        event.preventDefault();
        openWordListTool();
        return;
    }

    if (openAutotranslatorToolButton) {
        if (isModifiedNavigation) {
            return;
        }
        event.preventDefault();
        openAutotranslatorTool();
        return;
    }

    if (openMaster1000ToolButton) {
        if (isModifiedNavigation) {
            return;
        }
        event.preventDefault();
        openMaster1000Tool();
        return;
    }

    if (toolsBackButton) {
        if (isModifiedNavigation) {
            return;
        }
        event.preventDefault();
        returnToToolsMenu();
        return;
    }

    if (hostButton) {
        void createRoom().catch((error: unknown) => {
            state.screen = "landing";
            showError(error instanceof Error ? error.message : "Could not create the room.");
        });
        return;
    }

    if (joinButton) {
        submitJoinFlow();
        return;
    }

    if (leaveButton) {
        leaveRoom();
        return;
    }

    if (closeQrPreviewButton) {
        state.qrPreviewKind = null;
        updateUi();
        return;
    }

    if (openFinalResultsPlayerListButton) {
        state.finalResultsPlayerListOpen = true;
        updateRoomOverlay();
        return;
    }

    if (closeFinalResultsPlayerListButton) {
        state.finalResultsPlayerListOpen = false;
        updateRoomOverlay();
        return;
    }

    if (closeRenameDialogButton) {
        closeViewerRename();
        return;
    }

    if (hostEditNameButton) {
        openViewerRename(HOST_RENAME_BUTTON_SELECTOR);
        return;
    }

    if (playerHistoryMenuToggle) {
        event.preventDefault();
        hostPlayerHistoryMenuOpen = !hostPlayerHistoryMenuOpen;
        openRoomParticipantRosterActionsKey = null;
        updateUi();
        return;
    }

    if (playerHistoryDownloadButton) {
        event.preventDefault();
        hostPlayerHistoryMenuOpen = false;
        if (state.room) {
            downloadPlayerHistory(state.room);
        }
        updateUi();
        return;
    }

    if (rosterActionsToggle?.closest('[data-roster-actions-scope="room-participants"]')) {
        event.preventDefault();
        const actionsKey = rosterActionsToggle.getAttribute("data-roster-action-key") ?? "";
        openRoomParticipantRosterActionsKey =
            actionsKey && openRoomParticipantRosterActionsKey !== actionsKey ? actionsKey : null;
        hostPlayerHistoryMenuOpen = false;
        updateUi();
        return;
    }

    if (participantRenameButton) {
        const targetSessionId = participantRenameButton.getAttribute("data-rename-participant") ?? "";
        openRoomParticipantRosterActionsKey = null;

        if (targetSessionId) {
            openViewerRename(
                `#${getParticipantActionsTriggerId(targetSessionId)}`,
                targetSessionId
            );
        }
        return;
    }

    if (participantRenamePromptButton) {
        const targetSessionId = participantRenamePromptButton.getAttribute("data-prompt-rename-participant") ?? "";
        openRoomParticipantRosterActionsKey = null;

        if (targetSessionId) {
            sendRenamePromptToParticipant(targetSessionId);
        }
        return;
    }

    if (participantKickButton) {
        const targetSessionId = participantKickButton.getAttribute("data-kick-participant") ?? "";
        openRoomParticipantRosterActionsKey = null;

        if (targetSessionId) {
            sendMessage({
                type: "host-kick-player",
                payload: {
                    targetSessionId
                }
            });
        }
        return;
    }

    if (returnToSelectionButton) {
        const previousMode = state.hostDraft.mode;

        if (state.hostDraft.mode === RELIC_RUN_MODE) {
            state.hostDraft.relicSourceText = state.hostDraft.sourceText;
        } else if (state.hostDraft.mode === PICTURE_DETECTIVES_MODE) {
            state.hostDraft.pictionarySourceText = state.hostDraft.sourceText;
        } else if (state.hostDraft.mode === VAULT_BREAKERS_MODE) {
            state.hostDraft.vaultSourceText = state.hostDraft.sourceText;
        }

        state.hostDraft.mode = null;
        state.hostDraft.sourceText = "";
        state.hostModePanelView = "selection";
        state.hostPictionaryEditorMode = "text";
        state.hostVaultEditorMode = "text";
        state.hostSourceImportOpen = false;
        resetHostSourceTranslation();
        state.hostDraftDirty = true;
        state.qrPreviewKind = null;
        pushHostDraftDebounced();
        scheduleAccountPreferenceSave(previousMode);
        updateUi();
        return;
    }

    if (modeButton) {
        const modeChanged = state.hostDraft.mode !== RELIC_RUN_MODE;
        state.hostDraft.mode = RELIC_RUN_MODE;
        state.hostDraft.sourceText = state.hostDraft.relicSourceText;
        state.hostModePanelView = "config";
        state.hostPictionaryEditorMode = "text";
        state.hostVaultEditorMode = "text";
        state.hostSourceImportOpen = false;
        resetHostSourceTranslation();
        state.qrPreviewKind = null;

        if (modeChanged) {
            state.hostDraftDirty = true;
            pushHostDraftDebounced();
            scheduleAccountPreferenceSave(RELIC_RUN_MODE);
        }

        updateUi();
        return;
    }

    if (pictionaryModeButton) {
        const modeChanged = state.hostDraft.mode !== PICTURE_DETECTIVES_MODE;
        state.hostDraft.mode = PICTURE_DETECTIVES_MODE;
        state.hostDraft.sourceText = state.hostDraft.pictionarySourceText;
        state.hostModePanelView = "config";
        state.hostPictionaryEditorMode = "text";
        state.hostVaultEditorMode = "text";
        state.hostSourceImportOpen = false;
        resetHostSourceTranslation();
        state.qrPreviewKind = null;

        syncHostDraftPictionarySourceText();

        if (modeChanged) {
            state.hostDraftDirty = true;
            pushHostDraftDebounced();
            scheduleAccountPreferenceSave(PICTURE_DETECTIVES_MODE);
        }

        updateUi();
        return;
    }

    if (vaultModeButton) {
        const modeChanged = state.hostDraft.mode !== VAULT_BREAKERS_MODE;
        state.hostDraft.mode = VAULT_BREAKERS_MODE;
        state.hostDraft.sourceText = state.hostDraft.vaultSourceText;
        state.hostModePanelView = "config";
        state.hostPictionaryEditorMode = "text";
        state.hostVaultEditorMode = "text";
        state.hostSourceImportOpen = false;
        resetHostSourceTranslation();
        state.qrPreviewKind = null;

        syncHostDraftVaultSourceText();

        if (modeChanged) {
            state.hostDraftDirty = true;
            pushHostDraftDebounced();
            scheduleAccountPreferenceSave(VAULT_BREAKERS_MODE);
        }

        updateUi();
        return;
    }

    if (sourceTranslateButton) {
        event.preventDefault();
        syncHostTranslationRowsFromText();
        state.hostSourceImportOpen = false;
        state.hostSourceTranslationError = "";
        state.hostSourceTranslationOpen =
            getHostSourceTranslationItems().length > 0 && !state.hostSourceTranslationOpen;
        updateUi();
        return;
    }

    if (sourceTranslationActionButton) {
        event.preventDefault();
        const action = sourceTranslationActionButton.getAttribute("data-source-translation-action");

        if (action === "add" || action === "replace") {
            void translateHostSource(action);
        }
        return;
    }

    if (sourceImportButton) {
        resetHostSourceTranslation();

        if (state.account.account && getRecentUploadsForMode(state.hostDraft.mode).length > 0) {
            state.hostSourceImportOpen = !state.hostSourceImportOpen;
            updateUi();
            return;
        }

        const sourceFileInput = app.querySelector<HTMLInputElement>("#host-source-file-input");
        sourceFileInput?.click();
        return;
    }

    if (pictionaryFogButton) {
        state.hostPictionarySourceFogged = !state.hostPictionarySourceFogged;
        refreshHostSourceEditorState(null);
        return;
    }

    if (vaultFogButton) {
        state.hostVaultSourceFogged = !state.hostVaultSourceFogged;
        refreshHostSourceEditorState(app.querySelector<HTMLTextAreaElement>("#host-source-text"));
        return;
    }

    if (importFromComputerButton) {
        state.hostSourceImportOpen = false;
        updateUi();
        const sourceFileInput = app.querySelector<HTMLInputElement>("#host-source-file-input");
        sourceFileInput?.click();
        return;
    }

    if (accountUploadButton) {
        const uploadId = accountUploadButton.getAttribute("data-account-upload-id") ?? "";
        const upload = state.account.uploads.find((candidate) => candidate.id === uploadId);

        if (upload) {
            leaveAccountForHostSetup();
            applyAccountUploadToHostDraft(upload);
        }
        return;
    }

    if (accountPlayedSourceButton) {
        const sourceId = accountPlayedSourceButton.getAttribute("data-account-played-source-id") ?? "";
        const source =
            state.account.sourceHistory.find((candidate) => candidate.id === sourceId) ??
            Object.values(state.account.playedSources).find((candidate) => candidate?.id === sourceId);

        if (source) {
            applyAccountPlayedSourceToHostDraft(source);
        }
        return;
    }

    if (accountWordListButton) {
        const wordListId = accountWordListButton.getAttribute("data-account-word-list-id") ?? "";
        const wordList = state.account.wordLists.find((candidate) => candidate.id === wordListId);

        if (wordList) {
            applyAccountWordListToTool(wordList);
        }
        return;
    }

    if (accountHistoryToggleButton) {
        const historyId = accountHistoryToggleButton.getAttribute("data-account-history-toggle") ?? "";

        if (historyId) {
            if (expandedAccountHostedGameIds.has(historyId)) {
                expandedAccountHostedGameIds.delete(historyId);
            } else {
                expandedAccountHostedGameIds.add(historyId);
            }

            updateUi();
        }
        return;
    }

    if (openAccountArchiveButton) {
        openAccountScreen();
        return;
    }

    if (pictionaryEditorModeButton) {
        const nextMode = pictionaryEditorModeButton.getAttribute("data-pictionary-editor-mode");

        if (nextMode === "text" || nextMode === "table") {
            state.hostPictionaryEditorMode = nextMode;

            if (nextMode === "table") {
                syncHostDraftPictionarySourceText();
            }

            updateUi();

            if (nextMode === "text") {
                app.querySelector<HTMLTextAreaElement>("#host-source-text")?.focus();
            } else {
                app.querySelector<HTMLInputElement>(".host-pictionary-table-input[data-pictionary-field='english']")
                    ?.focus();
            }
        }

        return;
    }

    if (vaultEditorModeButton) {
        const nextMode = vaultEditorModeButton.getAttribute("data-vault-editor-mode");

        if (nextMode === "text" || nextMode === "table") {
            state.hostVaultEditorMode = nextMode;

            if (nextMode === "table") {
                syncHostDraftVaultSourceText();
            }

            updateUi();

            if (nextMode === "text") {
                app.querySelector<HTMLTextAreaElement>("#host-source-text")?.focus();
            } else {
                app.querySelector<HTMLInputElement>(".host-vault-table-input[data-vault-field='prompt']")?.focus();
            }
        }

        return;
    }

    if (controllerRoomSwitchButton) {
        const input = app.querySelector<HTMLInputElement>("#controller-room-code-input");
        void switchControllerRoom(input?.value ?? "");
        return;
    }

    if (expandControllerQrButton) {
        state.hostControllerQrExpanded = true;
        updateUi();
        return;
    }

    if (qrButton) {
        state.qrPreviewKind =
            qrButton instanceof HTMLElement && qrButton.getAttribute("data-qr-kind") === "controller"
                ? "controller"
                : "join";
        updateUi();
        return;
    }

    if (controllerQrButton) {
        state.qrPreviewKind = "controller";
        updateUi();
        return;
    }

    if (startButton) {
        if (isHostStartPending()) {
            return;
        }

        if (!canStartHostDraft()) {
            return;
        }

        state.hostStartPending = true;
        updateUi();
        activeHostedGameHistoryId = null;
        activeHostedGameHistoryRoomCode = state.room?.roomCode ?? "";
        recordedHostedGameResultsKey = "";
        pushHostDraft();
        if (!sendMessage({ type: "host-start" })) {
            state.hostStartPending = false;
            clearHostStartLoadingOverlay();
            updateUi();
            return;
        }
        queueHostStartConfirmation();
        return;
    }

    if (endGameButton) {
        const action =
            state.room?.phase === "results" ||
            (endGameButton instanceof HTMLElement && endGameButton.hasAttribute("data-final-results-continue"))
                ? "return-lobby"
                : "show-results";

        sendMessage({
            type: "host-end",
            payload: {
                action
            }
        });
        return;
    }

    if (renameSaveButton) {
        submitViewerRename();
        return;
    }

    if (copyButton) {
        const kind = copyButton.getAttribute("data-copy");
        const roomCode = state.room?.roomCode ?? state.joinRoomCode;

        if (!roomCode) {
            return;
        }

        const copyValue =
            kind === "host"
                ? buildHostLink(roomCode, state.currentConnection?.hostToken ?? null)
                : kind === "join"
                  ? buildJoinLink(roomCode)
                  : roomCode;
        void copyText(copyValue)
            .then(() => {
                showCopyFeedback(copyButton);
            })
            .catch(() => {
                showError("Copy failed. Your browser did not allow clipboard access.");
            });
        return;
    }
});

app.addEventListener("keydown", (event) => {
    const target = event.target as HTMLElement | null;

    if (
        event.key === "Enter" &&
        state.screen === "qr-join" &&
        target instanceof HTMLInputElement &&
        target.id === "join-name-input"
    ) {
        event.preventDefault();
        submitJoinFlow();
        return;
    }

    if (
        event.key === "Enter" &&
        !event.shiftKey &&
        state.screen === "tool-gimcheon-school" &&
        target instanceof HTMLTextAreaElement &&
        target.id === "gimcheon-chat-input"
    ) {
        event.preventDefault();
        void submitGimcheonSchoolChat();
        return;
    }

    if (
        event.key === "Enter" &&
        target instanceof HTMLInputElement &&
        target.id === "controller-room-code-input"
    ) {
        event.preventDefault();
        void switchControllerRoom(target.value);
        return;
    }

    if (!(target instanceof HTMLInputElement) || target.id !== "rename-player-input") {
        return;
    }

    if (event.key === "Enter") {
        event.preventDefault();
        submitViewerRename();
        return;
    }

    if (event.key === "Escape") {
        event.preventDefault();
        closeViewerRename();
    }
});

document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && state.renameDialogOpen) {
        event.preventDefault();
        closeViewerRename();
        return;
    }

    if (event.key === "Escape" && state.finalResultsPlayerListOpen) {
        event.preventDefault();
        state.finalResultsPlayerListOpen = false;
        updateRoomOverlay();
        return;
    }

    if (event.key === "Escape" && state.hostSourceTranslationOpen) {
        event.preventDefault();
        state.hostSourceTranslationOpen = false;
        state.hostSourceTranslationError = "";
        updateUi();
        return;
    }

    if (event.key !== "Escape" || state.screen !== "tool-master1000" || !isMaster1000PresentationActive()) {
        return;
    }

    event.preventDefault();
    void toggleMaster1000Fullscreen();
});

window.addEventListener(
    "pointerdown",
    () => {
        noteViewerPresenceInteraction();
    },
    {
        capture: true,
        passive: true
    }
);

window.addEventListener(
    "keydown",
    () => {
        noteViewerPresenceInteraction();
    },
    {
        capture: true
    }
);

document.addEventListener("visibilitychange", () => {
    flushViewerPresence("visibility");
});

window.addEventListener("online", () => {
    const connection = state.currentConnection;

    if (!connection || socket?.readyState === WebSocket.OPEN || socket?.readyState === WebSocket.CONNECTING) {
        return;
    }

    scheduleRoomReconnect(connection, true);
});

window.addEventListener("offline", () => {
    if (state.currentConnection) {
        logRoomSyncDiagnostic(`connection-offline room=${state.currentConnection.roomCode}`);
    }
});

const syncDisplayTypographyAndHostRoster = () => {
    syncClassroomDisplayTypography();
    scheduleHostLobbyRosterLayoutSync();
};

window.addEventListener("resize", syncDisplayTypographyAndHostRoster);
window.visualViewport?.addEventListener("resize", syncDisplayTypographyAndHostRoster);
window.visualViewport?.addEventListener("scroll", syncDisplayTypographyAndHostRoster);
(window.screen as unknown as Screen & EventTarget).addEventListener?.("change", syncDisplayTypographyAndHostRoster);

window.addEventListener("focus", () => {
    flushViewerPresence("visibility");
});

window.addEventListener("blur", () => {
    flushViewerPresence("visibility");
});

const bootstrap = async () => {
    const canonicalRedirectUrl = canonicalPageUrl(window.location.href);

    if (canonicalRedirectUrl) {
        window.location.replace(canonicalRedirectUrl);
        return;
    }

    const params = new URLSearchParams(window.location.search);
    const pathJoinMatch = window.location.pathname.match(/^\/join\/([^/?#]+)\/?$/i);
    const qrJoinRoomCode = normalizeRoomCode(params.get("join") ?? pathJoinMatch?.[1] ?? "");
    const roomCode = normalizeRoomCode(params.get("room") ?? "");
    const hostToken = params.get("host");
    const controllerRequest = ["1", "true", "yes"].includes(
        (params.get("controller") ?? "").trim().toLowerCase()
    );
    const tool = appToolForPath(window.location.pathname);
    const accountRequested = window.location.pathname === SITE_PATHS.account || params.has("account");

    // Public navigation must not wait for locale, sign-in, or account storage.
    // Refresh the current surface in place: a late preference must not reset typing or navigation.
    const preferencesReady = (async () => {
        const languageReady = initializeLanguage().then(() => { rerenderForLanguageChange(); });
        await loadAccountSnapshot(languageReady);
        syncAccountControls();
        if (state.screen === "account") refreshAccountLanguage();
        if (state.screen === "landing" && state.toolsPanelExpanded) {
            const template = document.createElement("template");
            template.innerHTML = renderLandingMarkup(true);
            const tools = template.content.querySelector(".landing-tools-card");
            const currentTools = app.querySelector(".landing-tools-card");
            const focusedHref = currentTools?.contains(document.activeElement)
                ? document.activeElement?.getAttribute("href") : null;
            if (tools && currentTools) {
                currentTools.replaceWith(tools);
                if (focusedHref) Array.from(tools.querySelectorAll<HTMLAnchorElement>("a"))
                    .find(link => link.getAttribute("href") === focusedHref)?.focus();
            }
        }
    })();

    if (qrJoinRoomCode && !roomCode && !hostToken && !controllerRequest) {
        state.joinRoomCode = qrJoinRoomCode;
        state.joinName = readStoredValue(STORAGE_KEYS.qrJoinName) ?? "";
        state.joinFromRoomLink = false;
        state.joinFromQrLink = true;
        state.qrJoinMode = null;
        state.hostPanelExpanded = false;
        state.toolsPanelExpanded = false;
        state.screen = "qr-join";
        clearBanners();
        updateUi();
        focusJoinNameInput();
        return;
    }

    if (roomCode) {
        state.joinRoomCode = roomCode;
    }

    if (!roomCode && tool === RANDOM_TEAMS_TOOL_QUERY) {
        state.screen = "tool-random-teams";
    }

    if (!roomCode && tool === WORD_LIST_TOOL_QUERY) {
        state.screen = "tool-word-list";
    }

    if (!roomCode && tool === VOCABULARY_REVIEW_TOOL_QUERY) {
        state.screen = "tool-vocabulary-review";
    }

    if (!roomCode && tool === AUTOTRANSLATOR_TOOL_QUERY) {
        state.screen = "account";
        updateUi();
        const requestedUrl = window.location.href;
        await preferencesReady;
        if (window.location.href !== requestedUrl || state.screen !== "account") return;
        if (isAutotranslatorAccount()) {
            state.screen = "tool-autotranslator";
        } else {
            state.screen = "account";
            state.error = t("autotranslatorPrivateOnly");
        }
    }

    if (!roomCode && accountRequested) {
        state.screen = "account";
    }

    updateUi();

    if (!roomCode && (tool === WORD_LIST_TOOL_QUERY || tool === VOCABULARY_REVIEW_TOOL_QUERY)) {
        scheduleViewportTopReset();
    }

    if (!roomCode && tool === AUTOTRANSLATOR_TOOL_QUERY) {
        scheduleViewportTopReset();
    }

    if (!roomCode) {
        return;
    }

    if (!hostToken && !controllerRequest) {
        state.joinRoomCode = roomCode;
        state.joinFromRoomLink = true;
        state.joinFromQrLink = false;
        state.qrJoinMode = null;
        state.hostPanelExpanded = false;
        state.toolsPanelExpanded = false;
        state.screen = "landing";
        updateUi();
        focusJoinNameInput();
        return;
    }

    try {
        await connectToRoom(roomCode, {
            name: hostToken ? state.hostName || DEFAULT_HOST_NAME : state.joinName,
            hostToken: hostToken ?? null,
            role: controllerRequest ? "controller" : "player",
            skipSummary: false
        });
    } catch (error) {
        if (hostToken) {
            state.screen = "landing";
            showError(error instanceof Error ? error.message : "Could not open the host room.");
        } else {
            showJoinFlowError(error instanceof Error ? error.message : "Could not join the room.");
        }
    }
};

document.addEventListener("fullscreenchange", handleMaster1000FullscreenChange);
document.addEventListener("webkitfullscreenchange", handleMaster1000FullscreenChange);

installPlayerRosterActionsMenuPositioning();
void bootstrap();
